/**
 * Pokémon TCG board — Pocket / Live-style playmat for the Preact client.
 *
 * @license AGPLv3
 */

import preact from "../js/lib/preact";
import { PS } from "./client-main";
import { Net } from "./client-connection";
import { lookupAttackHit } from "./battle-tcg-attack-fx";
import {
	boardChoiceOf, choiceFromText, choiceSourceFor, findChoiceMon, planChoiceBar, planChoicePicker,
	type BoardChoice, type BoardChoiceCard, type BoardChoiceLocal, type BoardChoiceSource, type ChoiceRect,
	type ChoiceBarSize, type TcgChoiceFrom,
} from "./battle-tcg-choice";
export function isTcgBattleId(id: string): boolean {
	const format = id.split('-')[1] || '';
	return format.startsWith('tcg');
}

export type TcgSlot = 'active' | number;
export type TcgAction = { type: string, [k: string]: any };
/** WaveTCG TcgEvent — animate in seq order (docs/graphics.md). */
export type TcgEvent = { seq: number, type: string, [k: string]: any };
/** @deprecated use TcgEvent */
export type TcgFxEvent = TcgEvent;

export interface TcgPokemonView {
	iid: string;
	cardId: string;
	name: string;
	hp: number;
	maxHp: number;
	types?: string[];
	energy?: string[];
	energyCards?: string[];
	tools?: string[];
	attacks?: { name?: string, cost?: string[], damage?: string, text?: string }[];
	abilities?: { name?: string, text?: string }[];
	status?: string | null;
	poisoned?: boolean;
	burned?: boolean;
	image?: string;
	lastDelta?: number;
	/** Opponent's setup Pokémon: placed face down until both players are ready (client-side placeholder). */
	faceDown?: boolean;
}

export interface TcgPlayerView {
	id: string;
	name: string;
	active: TcgPokemonView | null;
	bench: TcgPokemonView[];
	hand: string[] | { count: number };
	deck: { count: number };
	discard: string[];
	prizes?: { count: number };
	lostZone?: { count: number };
	stadium?: string | null;
	/** Both halves of a two-card Legendary Stadium in play, `stadium` first. Absent for a one-card Stadium. */
	stadiumIds?: string[];
	setup?: string;
	energyZone?: { type: string, next?: string, ready?: boolean };
	points?: number;
	attachedEnergyThisTurn?: boolean;
	playedSupporterThisTurn?: boolean;
	playedStadiumThisTurn?: boolean;
	retreatedThisTurn?: boolean;
}

export interface TcgSnapshot {
	status?: string;
	winner?: number | null;
	winReason?: string;
	turn?: number;
	turnNumber?: number;
	/** The game id. */
	id?: string;
	/** Printed names for the card ids in this snapshot (from the host's catalog), for display. */
	cardNames?: { [id: string]: string };
	/** Who took turn 1; meaningful once `pendingFirst` is gone. */
	firstPlayer?: 0 | 1;
	you?: number | null;
	actions: TcgAction[];
	players: TcgPlayerView[];
	format?: { id?: string, benchSize?: number, energyZone?: boolean, prizes?: number, name?: string };
	pendingSearch?: {
		kind?: string, left?: number, upTo?: boolean, zone?: string, dest?: string, seen?: string[],
		/** Deck order, or only the looked-at window. */
		ids?: string[],
		/** Card ids inside `ids` that may be taken. */
		legal?: string[],
	};
	pendingDiscard?: { need: number };
	/** The open hand-discard prompt, for every viewer: whose hand, and where picks go (deck / Lost Zone). */
	discardRequest?: { fromFoe?: boolean, to?: string };
	pendingRetreatPay?: { need: number };
	/** Attached Energy an effect is discarding. `cardIds` is every attachment, in order, so `discardEnergyPick.index` matches. */
	pendingDiscardEnergy?: { need: number, iid: string, cardIds: string[] };
	/** Index from the `discardEnergyPick` or `payEnergy` act in this batch. The following movement uses it once. */
	energyDetachIndex?: number;
	pendingConfirm?: { title?: string, text?: string };
	/** `ask` / `from`: the chooser's instruction and what an earlier step chose (only the chooser gets them). */
	pendingCopyAttack?: { title: string, text: string, ask?: string, from?: TcgChoiceFrom };
	pendingSlotPick?: { left: number, hint?: string, side?: string, ask?: string, from?: TcgChoiceFrom };
	pendingPromote?: number;
	pendingFirst?: number;
	pendingPrize?: { seat: number, n: number };
	/** Armed modifiers from a `field` event or a join snapshot. Empty means none. */
	effectsActive?: { id?: string, seat?: 0 | 1, summary?: string }[];
	/** Highest event seq already folded into this board. Replaying those seqs must not append again. */
	appliedSeq?: number;
}

function cloneTcgSnapshot(snap: TcgSnapshot): TcgSnapshot {
	return JSON.parse(JSON.stringify(snap)) as TcgSnapshot;
}

const ENERGY_NAME_MAP: { [k: string]: string } = {
	grass: 'grass', fire: 'fire', water: 'water', lightning: 'lightning', electric: 'lightning',
	psychic: 'psychic', fighting: 'fighting', darkness: 'darkness', dark: 'darkness',
	metal: 'metal', steel: 'metal', fairy: 'fairy', dragon: 'dragon', colorless: 'colorless',
};

function namedEnergyType(raw?: string): string {
	const k = String(raw || '').toLowerCase().replace(/[^a-z]/g, '');
	if (ENERGY_NAME_MAP[k]) return ENERGY_NAME_MAP[k];
	const stripped = k.replace(/^special/, '').replace(/^basic/, '').replace(/energy$/, '');
	return ENERGY_NAME_MAP[stripped] || '';
}

function energyTypeFromCardId(cardId?: string, types?: string[]): string {
	if (types) {
		for (const t of types) {
			const n = namedEnergyType(t);
			if (n && n !== 'colorless') return n;
		}
		const first = namedEnergyType(types[0]);
		if (first) return first;
	}
	if (!cardId) return 'colorless';
	const id = cardId.toLowerCase();
	const listed = /^(?:energy|zone-energy)-(grass|fire|water|lightning|psychic|fighting|darkness|metal|fairy|dragon|colorless)$/i.exec(id);
	if (listed) return listed[1].toLowerCase();
	const sve = /^sve-(\d+)$/.exec(id);
	if (sve) {
		const cycle = ['grass', 'fire', 'water', 'lightning', 'psychic', 'fighting', 'darkness', 'metal'];
		const n = Number(sve[1]);
		if (n >= 1 && n <= 16) return cycle[(n - 1) % 8];
	}
	const classic: { [k: string]: string } = {
		'base1-97': 'grass', 'base1-98': 'fire', 'base1-99': 'water',
		'base1-100': 'lightning', 'base1-101': 'psychic', 'base1-102': 'fighting',
	};
	if (classic[id]) return classic[id];
	const fromName = /(grass|fire|water|lightning|electric|psychic|fighting|darkness|metal|fairy|dragon|colorless)/i.exec(id);
	if (fromName) return namedEnergyType(fromName[1]) || 'colorless';
	return 'colorless';
}

function energyPipsFromEvent(e: TcgEvent): string[] {
	const listed = (e.types || (e.energyType ? [e.energyType] : [])) as string[];
	const fromTypes = listed.map(t => namedEnergyType(t) || energyTypeFromCardId(undefined, [t])).filter(Boolean);
	const useful = fromTypes.filter(t => t !== 'colorless');
	if (useful.length) return useful;
	if (fromTypes.length) return fromTypes;
	return [energyTypeFromCardId(e.cardId)];
}

function normalizeMonEnergy(mon: TcgPokemonView | null | undefined) {
	if (!mon) return;
	const cards = mon.energyCards || [];
	const pips = (mon.energy || []).map(t => namedEnergyType(t) || energyTypeFromCardId(undefined, [t]));
	if (cards.length && (!pips.length || (pips.every(t => t === 'colorless') && pips.length <= cards.length))) {
		// One Colorless pip per card is the card's own type. More Colorless pips than cards
		// is what that card provides, such as Twin Energy.
		mon.energy = cards.map(id => energyTypeFromCardId(id));
	} else if (pips.length) {
		mon.energy = pips;
	}
}

export function normalizeTcgEnergy(snap: TcgSnapshot | null | undefined) {
	if (!snap?.players) return snap;
	for (const p of snap.players) {
		if (!p) continue;
		normalizeMonEnergy(p.active);
		for (const mon of p.bench || []) normalizeMonEnergy(mon);
	}
	return snap;
}

function handAsIds(hand: TcgPlayerView['hand']): string[] | null {
	return Array.isArray(hand) ? hand.slice() : null;
}

/**
 * Same rules as Wave-TCG `viewEvents`.
 * A revealed search keeps its card ids for every viewer. A private search does not.
 * A host archive on `gameEnd` is rewritten to the public spectator replay.
 */
export function filterTcgEventsForViewer(
	events: TcgEvent[] | undefined,
	you: 0 | 1 | null
): TcgEvent[] | undefined {
	if (!events?.length) return events;
	return events.map(e => {
		if (e.type === 'gameEnd') return publicGameEnd(e);
		if (e.type === 'find') {
			if (e.revealed) return e;
			if (you == null || e.seat !== you) {
				const { ids: _ids, ...rest } = e;
				return rest as TcgEvent;
			}
			return e;
		}
		if (e.type === 'request' && e.kind === 'search' && (e.ids || e.legal || e.seen)) {
			const waiting = Array.isArray(e.waiting) ? e.waiting : [];
			const chooser = waiting[0];
			if (you == null || chooser == null || you !== chooser) {
				const { ids: _ids, legal: _legal, seen: _seen, ...rest } = e;
				return rest as TcgEvent;
			}
		}
		if (e.type === 'act' && e.hiddenPick && you !== e.seat) {
			const { hiddenPick: _hidden, ...rest } = e;
			return { ...rest, action: { ...e.action, pick: '' } } as TcgEvent;
		}
		if (e.type === 'act' && e.hiddenPick) {
			const { hiddenPick: _hidden, ...rest } = e;
			return rest as TcgEvent;
		}
		if (e.type === 'act' && e.action?.type === 'searchPick' && you !== e.seat) {
			const pick = String(e.action.pick || '');
			const shown = !!pick && events.some(f =>
				f.type === 'find' && f.revealed && f.seat === e.seat &&
				Array.isArray(f.ids) && f.ids.includes(pick)
			);
			if (!shown) return { ...e, action: { ...e.action, pick: '' } };
		}
		if (e.type === 'draw' || e.type === 'prizeTake' || e.type === 'fromHand' || e.type === 'fromDeck') {
			if (you == null || e.seat !== you) {
				const { ids: _ids, ...rest } = e;
				return rest as TcgEvent;
			}
		}
		// A hidden hand (or deck) card put into the deck or the Lost Zone keeps its id for its owner only.
		if ((e.type === 'toDeck' && e.from === 'hand' && !e.revealed) ||
			(e.type === 'toLost' && (e.from === 'hand' || e.from === 'deck'))) {
			if (you == null || e.seat !== you) {
				const { ids: _ids, ...rest } = e;
				return rest as TcgEvent;
			}
		}
		if (e.type === 'deal') {
			const out = { ...e };
			if (you !== 0) delete (out as { ids0?: string[] }).ids0;
			if (you !== 1) delete (out as { ids1?: string[] }).ids1;
			return out as TcgEvent;
		}
		if (e.type === 'shuffle') {
			const { ids: _ids, ...rest } = e as TcgEvent & { ids?: string[] };
			return rest as TcgEvent;
		}
		return e;
	});
}

/** Drop seed and deck lists. Playback uses `replay.spectator` only. */
export function publicGameEnd(e: TcgEvent): TcgEvent {
	const replay = e.replay;
	if (!replay || typeof replay !== 'object') return e;
	const players = Array.isArray(replay.players) ? replay.players : [];
	const leaked = replay.audience === 'host' || replay.seed != null ||
		players.some((p: { deck?: unknown }) => Array.isArray(p?.deck));
	if (!leaked) return e;
	const spec = replay.spectator || {};
	const events = Array.isArray(spec.events) ? spec.events : [];
	const result = replay.result;
	return {
		...e,
		replay: {
			audience: 'public',
			id: replay.id,
			formatId: replay.formatId,
			players: players.map((p: { id?: string, name?: string }) => ({ id: p?.id, name: p?.name })),
			events,
			spectator: { snapshot: spec.snapshot, events },
			result: result ? { winner: result.winner, reason: result.reason } : undefined,
		},
	};
}

/**
 * A finished-game payload. Host archives and `gameEnd` play `spectator` only.
 * Returns null when `data` is already a normal public replay.
 */
export function spectatorReplayFrom(data: any): {
	snapshot: TcgSnapshot, events: TcgEvent[], winnerName: string | null,
} | null {
	if (!data || typeof data !== 'object') return null;
	let replay = data.replay;
	let result = data.result;
	if (data.type === 'gameEnd') {
		const clean = publicGameEnd(data);
		replay = clean.replay;
		result = clean.replay?.result || data.result;
	}
	const players = Array.isArray(data.players) ? data.players : [];
	const hosted = data.audience === 'host' || data.seed != null ||
		players.some((p: { deck?: unknown }) => Array.isArray(p?.deck));
	if (data.type !== 'gameEnd' && !hosted) return null;
	const spec = (replay || data).spectator;
	if (!spec?.snapshot) return null;
	const seat = result?.winner;
	const winnerName = seat == null ? null : (spec.snapshot.players?.[seat]?.name || null);
	return {
		snapshot: spec.snapshot,
		events: Array.isArray(spec.events) ? spec.events : [],
		winnerName,
	};
}

/** Defense in depth when `you` is null (spectator / public watch snapshot). */
export function scrubTcgSpectatorView(snap: TcgSnapshot): TcgSnapshot {
	if (snap.you != null) return snap;
	const out = cloneTcgSnapshot(snap);
	out.actions = [];
	delete out.pendingSearch;
	delete out.pendingDiscard;
	delete out.pendingRetreatPay;
	delete out.pendingDiscardEnergy;
	delete out.pendingConfirm;
	delete out.pendingSlotPick;
	delete out.pendingCopyAttack;
	for (const p of out.players) {
		if (!p) continue;
		if (Array.isArray(p.hand)) p.hand = { count: p.hand.length };
	}
	return out;
}

/**
 * Pre-batch hand indexes stay clickable. Cards that arrived during this batch are shown
 * with i -1 so a click cannot target a hand the server has not committed yet.
 */
function handFan(
	committed: string[] | null,
	viewHand: TcgPlayerView['hand'] | undefined
): { id: string, i: number, arrive: boolean }[] | null {
	const viewIds = handAsIds(viewHand ?? null);
	if (!committed) {
		return viewIds?.length ? viewIds.map(id => ({ id, i: -1, arrive: true })) : null;
	}
	if (!viewIds) return committed.map((id, i) => ({ id, i, arrive: false }));
	const pool = viewIds.slice();
	const kept: { id: string, i: number, arrive: boolean }[] = [];
	for (let i = 0; i < committed.length; i++) {
		const at = pool.indexOf(committed[i]);
		if (at < 0) continue;
		pool.splice(at, 1);
		kept.push({ id: committed[i], i, arrive: false });
	}
	const gained = pool.map(id => ({ id, i: -1, arrive: true }));
	const fan = kept.concat(gained);
	return fan.length ? fan : null;
}

/** A copy the player sent from the hand, by its place in the player's own order. */
export type HandGone = { id: string, pos: number, at: number };

/**
 * The player's hand order (ids, in display order) once the hand is `hand`. A copy the player sent leaves from its own
 * place; any other loss leaves from the end of that card's copies; new cards join at the end, in hand order.
 * `left` is the marks that were not used yet.
 */
export function reconcileHandOrder(
	prev: string[], hand: string[], gone: HandGone[] = []
): { order: string[], left: HandGone[] } {
	const want: { [id: string]: number } = Object.create(null);
	for (const id of hand) want[id] = (want[id] || 0) + 1;
	const order = prev.slice();
	const count = (id: string) => order.filter(x => x === id).length;
	const left: HandGone[] = [];
	for (const g of gone.slice().sort((a, b) => b.pos - a.pos)) {
		if (order[g.pos] === g.id && count(g.id) > (want[g.id] || 0)) order.splice(g.pos, 1);
		else left.push(g);
	}
	for (let i = order.length - 1; i >= 0; i--) {
		if (count(order[i]) > (want[order[i]] || 0)) order.splice(i, 1);
	}
	const have: { [id: string]: number } = Object.create(null);
	for (const id of order) have[id] = (have[id] || 0) + 1;
	for (const id of hand) {
		if (have[id] > 0) have[id]--;
		else order.push(id);
	}
	return { order, left };
}

/** The fan in the player's order. Each entry keeps its true hand index; entries the order does not name stay last. */
export function orderHandFan<T extends { id: string }>(fan: T[], order: string[]): T[] {
	const used = fan.map(() => false);
	const out: T[] = [];
	for (const id of order) {
		for (let j = 0; j < fan.length; j++) {
			if (used[j] || fan[j].id !== id) continue;
			used[j] = true;
			out.push(fan[j]);
			break;
		}
	}
	fan.forEach((f, j) => {
		if (!used[j]) out.push(f);
	});
	return out;
}

/** Move the card at display place `from` so it ends at `to`. */
export function moveHandOrder(order: string[], from: number, to: number): string[] {
	if (from < 0 || from >= order.length) return order;
	const next = order.slice();
	const [card] = next.splice(from, 1);
	next.splice(Math.max(0, Math.min(next.length, to)), 0, card);
	return next;
}

const HAND_ORDER_KEY = 'tcg-hand-order';
/** Only the current game's order is kept; it survives a reload of this browser. */
function readHandOrder(game: string): string[] | null {
	try {
		const raw = JSON.parse(localStorage.getItem(HAND_ORDER_KEY) || 'null');
		if (raw?.game !== game || !Array.isArray(raw.order)) return null;
		return raw.order.filter((x: unknown) => typeof x === 'string');
	} catch {
		return null;
	}
}
function writeHandOrder(game: string, order: string[]) {
	try {
		localStorage.setItem(HAND_ORDER_KEY, JSON.stringify({ game, order }));
	} catch {}
}

function setHand(p: TcgPlayerView, hand: string[] | { count: number }) {
	p.hand = hand;
}

function removeHandIndex(p: TcgPlayerView, index: number) {
	const ids = handAsIds(p.hand);
	if (ids && index >= 0 && index < ids.length) {
		ids.splice(index, 1);
		setHand(p, ids);
		return;
	}
	if (!Array.isArray(p.hand) && p.hand && typeof p.hand.count === 'number' && p.hand.count > 0) {
		p.hand = { count: p.hand.count - 1 };
	}
}

function bumpPile(pile: { count: number } | undefined, delta: number): { count: number } {
	return { count: Math.max(0, (pile?.count || 0) + delta) };
}

function takeIds(list: string[] | undefined, ids: string[]): string[] {
	const left = (list || []).slice();
	for (const id of ids) {
		const at = left.indexOf(id);
		if (at >= 0) left.splice(at, 1);
	}
	return left;
}

function removeHandIds(p: TcgPlayerView, ids: string[]) {
	const hand = handAsIds(p.hand);
	if (hand) {
		setHand(p, takeIds(hand, ids));
		return;
	}
	if (p.hand && typeof p.hand.count === 'number') p.hand = bumpPile(p.hand, -ids.length);
}

function isBasicEnergyId(id: string): boolean {
	return /^sve-\d+$/i.test(id) || !!{
		'base1-97': 1, 'base1-98': 1, 'base1-99': 1,
		'base1-100': 1, 'base1-101': 1, 'base1-102': 1,
	}[id.toLowerCase()];
}

/**
 * One group of pips per attached card. A basic is one pip. A Special Energy that
 * provides several Colorless takes the Colorless pips that sit before the next basic.
 */
function partitionEnergyPips(cards: string[], pips: string[]): string[][] | null {
	if (pips.length === cards.length) return pips.map(p => [p]);
	const groups: string[][] = [];
	let i = 0;
	for (let c = 0; c < cards.length; c++) {
		const id = cards[c];
		if (isBasicEnergyId(id)) {
			if (i >= pips.length) return null;
			groups.push([pips[i]]);
			i++;
			continue;
		}
		const nextBasic = cards.slice(c + 1).find(isBasicEnergyId);
		const take: string[] = [];
		if (!nextBasic) {
			while (i < pips.length) take.push(pips[i++]);
		} else {
			const want = energyTypeFromCardId(nextBasic);
			while (i < pips.length && pips[i] === 'colorless') take.push(pips[i++]);
			if (i < pips.length && pips[i] !== want) return null;
		}
		if (!take.length) return null;
		groups.push(take);
	}
	if (i !== pips.length || groups.length !== cards.length) return null;
	return groups;
}

function detachPlayedCards(mon: TcgPokemonView, ids: string[], atIndex?: number) {
	if (mon.energyCards?.length) {
		const cards = mon.energyCards.slice();
		const pips = (mon.energy || []).slice();
		const groups = partitionEnergyPips(cards, pips);
		let preferred = atIndex;
		for (const id of ids) {
			let at = -1;
			if (preferred != null && preferred >= 0 && preferred < cards.length && cards[preferred] === id) {
				at = preferred;
				preferred = undefined;
			} else {
				at = cards.indexOf(id);
			}
			if (at < 0) continue;
			cards.splice(at, 1);
			if (groups) groups.splice(at, 1);
		}
		mon.energyCards = cards;
		mon.energy = groups ? groups.flat() : cards.map(id => energyTypeFromCardId(id));
	}
	if (mon.tools?.length) mon.tools = takeIds(mon.tools, ids);
}

function stubMon(e: TcgEvent): TcgPokemonView {
	const hp = e.hp != null ? Number(e.hp) : 0;
	return {
		iid: e.iid,
		cardId: e.cardId || '',
		name: e.name || e.cardId || 'Pokémon',
		hp,
		maxHp: e.maxHp != null ? Number(e.maxHp) : hp,
		energy: [],
		tools: [],
		faceDown: !!e.faceDown,
	};
}

function applyPlace(p: TcgPlayerView, slot: TcgSlot, mon: TcgPokemonView) {
	// Already in this slot. A second copy of the same place must not swap them back.
	if (slot === 'active') {
		if (p.active?.iid === mon.iid) return;
	} else {
		const at = Number(slot);
		if (Number.isFinite(at) && at >= 0 && p.bench?.[at]?.iid === mon.iid) return;
	}
	const fromBench = (p.bench || []).findIndex(m => m && m.iid === mon.iid);
	const wasActive = p.active?.iid === mon.iid;
	const oldActive = p.active && p.active.iid !== mon.iid ? p.active : null;
	dropMonByIid(p, mon.iid);
	if (slot === 'active') {
		p.active = mon;
		if (oldActive) {
			if (fromBench >= 0) p.bench.splice(fromBench, 0, oldActive);
			else p.bench.push(oldActive);
		}
		return;
	}
	const i = Number(slot);
	if (!Number.isFinite(i) || i < 0) {
		p.bench.push(mon);
		return;
	}
	// Active → occupied bench index is a switch. Inserting would empty the
	// Active spot and slide the rest of the Bench down.
	if (wasActive && i < p.bench.length && p.bench[i]) {
		p.active = p.bench[i];
		p.bench[i] = mon;
		return;
	}
	if (i >= p.bench.length) p.bench.push(mon);
	else p.bench.splice(i, 0, mon);
}

function dropMonByIid(p: TcgPlayerView, iid: string) {
	if (p.active?.iid === iid) p.active = null;
	const next: TcgPokemonView[] = [];
	for (const mon of p.bench || []) {
		if (mon && mon.iid !== iid) next.push(mon);
	}
	p.bench = next;
}

function clearPending(snap: TcgSnapshot) {
	delete snap.pendingSearch;
	delete snap.pendingDiscard;
	delete snap.pendingRetreatPay;
	delete snap.pendingDiscardEnergy;
	delete snap.pendingConfirm;
	delete snap.pendingCopyAttack;
	delete snap.pendingSlotPick;
	delete snap.pendingPromote;
	delete snap.pendingFirst;
	delete snap.pendingMulligan;
	delete snap.pendingPrize;
	delete snap.energyDetachIndex;
}

/**
 * Apply a TcgEvent batch onto a prior snapshot when the server omitted a full board.
 */
function laterCoversDiscard(events: TcgEvent[], index: number, ids: string[], iid?: string): boolean {
	const want: { [id: string]: 1 } = Object.create(null);
	for (const id of ids) if (id) want[id] = 1;
	const listed = !!ids.filter(Boolean).length;
	if (!listed && !iid) return false;
	for (let j = index + 1; j < events.length; j++) {
		const n = events[j];
		if (!n || n.type === 'request' || n.type === 'turn') break;
		// A played Prism Star card goes to the Lost Zone (a `toLost` row), not the discard pile.
		if (n.type !== 'toDiscard' && !(n.type === 'toLost' && n.from === 'play')) continue;
		if (iid && n.iid === iid) return true;
		const got = Array.isArray(n.ids) ? n.ids as string[] : [];
		if (got.some(id => want[id])) return true;
	}
	return false;
}

/**
 * A played Trainer that went back into the hand instead of the discard pile (Tate & Liza's Training): a later
 * face-up `find` of that card with no `dest`, `from`, or `iid`. It has no `toDiscard`, and must not get the fallback.
 */
function laterReturnsToHand(events: TcgEvent[], index: number, id: string): boolean {
	for (let j = index + 1; j < events.length; j++) {
		const n = events[j];
		if (!n || n.type === 'request' || n.type === 'turn') break;
		if (n.type !== 'find' || !n.revealed || n.dest || n.from || n.iid) continue;
		if (Array.isArray(n.ids) && n.ids.includes(id)) return true;
	}
	return false;
}

/** One event, with a fallback for replays recorded before toDiscard existed. */
function applyBatchEvent(snap: TcgSnapshot, events: TcgEvent[], index: number) {
	const e = events[index];
	if (!e) return;
	if (e.type === 'ko' && e.iid) {
		const found = findMonSlot(snap.players || [], e.iid);
		const cards = found ?
			[found.mon.cardId, ...(found.mon.energyCards || []), ...(found.mon.tools || [])].filter(Boolean) :
			[];
		const seat = e.seat as number;
		applyTcgEvent(snap, e);
		if (cards.length && !laterCoversDiscard(events, index, cards, e.iid)) {
			const p = snap.players[seat];
			if (p) {
				if (!p.discard) p.discard = [];
				for (const id of cards) p.discard.push(id);
			}
		}
		return;
	}
	if (e.type === 'trainer' && e.cardId && !laterCoversDiscard(events, index, [e.cardId]) &&
		!laterReturnsToHand(events, index, e.cardId)) {
		applyTcgEvent(snap, e);
		const p = snap.players[e.seat as number];
		if (p) {
			if (!p.discard) p.discard = [];
			p.discard.push(e.cardId);
		}
		return;
	}
	applyTcgEvent(snap, e);
}

/**
 * True when `events` skips a sequence number after `seen`, the last `seq` applied.
 * The host numbers events 1, 2, 3, ... and sends every one of them to each viewer
 * (filtering rewrites a row, it never drops one), so the first row past `seen` must be
 * `seen + 1` and each row after it the next. Rows at or below `seen` are repeats.
 * Nothing is applied yet (`seen <= 0`): there is no sequence to be ahead of.
 */
export function tcgSeqGap(seen: number, events: TcgEvent[] | undefined): boolean {
	if (!(seen > 0)) return false;
	let next = seen + 1;
	for (const ev of events || []) {
		if (typeof ev?.seq !== 'number' || ev.seq < next) continue;
		if (ev.seq !== next) return true;
		next++;
	}
	return false;
}

/** How long a requested resync may stay unanswered before a new gap asks again. */
export const TCG_RESYNC_RETRY_MS = 5000;

export interface TcgSeqState {
	/** Last event `seq` applied (0: none yet). */
	seen: number;
	/** When a resync was asked for and not yet answered; null when none is pending. */
	pendingAt: number | null;
}

/**
 * Decide what a live `|tcg|` payload does to the sequence. A payload that skips ahead is
 * dropped whole, so a batch is never half-applied, and asks for one resync. Another gap
 * while that resync is pending asks for nothing (until `TCG_RESYNC_RETRY_MS`). The resync
 * snapshot (`resync` with a `snapshot`) is the one thing that ends the wait.
 */
export function tcgSeqStep(
	state: TcgSeqState,
	data: { events?: TcgEvent[], seq?: number, snapshot?: boolean, resync?: boolean },
	now: number
): TcgSeqState & { drop: boolean, request: boolean } {
	let top = typeof data.seq === 'number' ? data.seq : 0;
	for (const ev of data.events || []) if (typeof ev?.seq === 'number' && ev.seq > top) top = ev.seq;
	if (data.resync && data.snapshot) {
		// A board older than what is already applied would put the board back; it ends no wait either.
		if (typeof data.seq === 'number' && data.seq < state.seen) {
			return { seen: state.seen, pendingAt: state.pendingAt, drop: true, request: false };
		}
		return { seen: Math.max(state.seen, top), pendingAt: null, drop: false, request: false };
	}
	if (tcgSeqGap(state.seen, data.events)) {
		const request = state.pendingAt == null || now - state.pendingAt >= TCG_RESYNC_RETRY_MS;
		return { seen: state.seen, pendingAt: request ? now : state.pendingAt, drop: true, request };
	}
	return { seen: Math.max(state.seen, top), pendingAt: state.pendingAt, drop: false, request: false };
}

/** What the battle room keeps for the sequence; the battle room class has these fields. */
export interface TcgSeqRoom {
	tcgSeqSeen: number;
	tcgResyncAt: number | null;
	tcgReplayMode: boolean;
	requestTcgResync(): void;
	/** The wait ended (a resync board was taken): stop any retry. */
	tcgResyncDone?(): void;
}

/**
 * Ignore a setup identity resync that brings no events while a beat is playing or queued: the login resync must not cut
 * the opening coin and deal. Never when it seats this viewer on a board that is not theirs yet, and never on an idle
 * board. After a reload, or a spectator's rename, the page draws the public board first; dropping the resync then left
 * the player watching with no actions, and the spectator on the board from before the coin choice.
 */
export function tcgDropSetupResync(
	board: { you?: number | null } | null | undefined, animating: boolean, asked: boolean,
	data: { kind?: string, events?: unknown[], snapshot?: { status?: string, you?: number | null } }
): boolean {
	const setup = !data.snapshot?.status || data.snapshot.status === 'setup';
	if (!setup || !animating || asked || data.events?.length) return false;
	const seats = data.kind === 'you' && data.snapshot?.you != null && board?.you !== data.snapshot.you;
	return !seats;
}

/**
 * Run a live payload past the sequence check. `drop` means apply none of it (a resync was asked for, or is
 * already pending). Otherwise the room records `seqAfter` once the payload's own rows have been counted
 * with `tcgFreshEvents`, so those rows are not mistaken for repeats.
 */
export function tcgSeqGate(
	room: TcgSeqRoom,
	data: { events?: TcgEvent[], seq?: number, snapshot?: boolean, resync?: boolean },
	skipFx: boolean,
	now = Date.now()
): { drop: boolean, seqAfter: number } {
	if (skipFx || room.tcgReplayMode) return { drop: false, seqAfter: 0 };
	const step = tcgSeqStep({ seen: room.tcgSeqSeen, pendingAt: room.tcgResyncAt }, data, now);
	const wasPending = room.tcgResyncAt != null;
	room.tcgResyncAt = step.pendingAt;
	if (step.drop && step.request) room.requestTcgResync();
	else if (wasPending && step.pendingAt == null) room.tcgResyncDone?.();
	return { drop: step.drop, seqAfter: step.seen };
}

/** Requests in one cycle: the first, then retries. At 5 s apart that is at most about 10 s of asking. */
export const TCG_RESYNC_MAX_TRIES = 3;

export interface TcgResyncTimers {
	set(fn: () => void, ms: number): unknown;
	clear(handle: unknown): void;
}

/**
 * Keeps asking for the board while a resync is unanswered, a bounded number of times. The first request goes out
 * at once; each retry waits `TCG_RESYNC_RETRY_MS`, which is longer than the host's one-second throttle, and runs
 * only while the room is still waiting (`pending`) and still live (`alive`). A call while a cycle is running does
 * nothing, so there is never more than one timer. When the tries run out the cycle ends; the next gapped payload
 * (at least `TCG_RESYNC_RETRY_MS` after the last ask) may start one more. `cancel` drops the timer, and a
 * timer that fires anyway after `cancel` does nothing.
 */
export function createTcgResyncRetry(opts: {
	send(): void,
	pending(): boolean,
	alive(): boolean,
	timers?: TcgResyncTimers,
	delay?: number,
	max?: number,
}) {
	const timers: TcgResyncTimers = opts.timers || {
		set: (fn, ms) => setTimeout(fn, ms),
		clear: handle => clearTimeout(handle as ReturnType<typeof setTimeout>),
	};
	const delay = opts.delay ?? TCG_RESYNC_RETRY_MS;
	const max = opts.max ?? TCG_RESYNC_MAX_TRIES;
	let handle: unknown = null;
	let tries = 0;
	let cycle = 0;
	const arm = () => {
		const mine = cycle;
		handle = timers.set(() => {
			if (mine !== cycle) return;
			handle = null;
			if (!opts.alive() || !opts.pending()) {
				tries = 0;
				return;
			}
			ask();
		}, delay);
	};
	const ask = () => {
		opts.send();
		tries++;
		if (tries < max) arm();
	};
	return {
		/** Ask now and retry; nothing happens if a cycle is already running. */
		request() {
			if (handle != null) return;
			if (!opts.alive()) return;
			tries = 0;
			ask();
		},
		/** Stop: the wait ended, or the room reset, closed, or went away. */
		cancel() {
			cycle++;
			if (handle != null) timers.clear(handle);
			handle = null;
			tries = 0;
		},
		running: () => handle != null,
	};
}

/** The rows of `events` not seen before, counting them as seen. A silent echo is not counted. */
export function tcgFreshEvents(room: { tcgSeqSeen: number }, events: TcgEvent[] | undefined, skipFx: boolean) {
	if (skipFx) return events;
	return events?.filter(ev => {
		if (typeof ev.seq !== 'number') return true;
		if (ev.seq <= room.tcgSeqSeen) return false;
		room.tcgSeqSeen = ev.seq;
		return true;
	});
}

/** The seat whose action started this batch (its first `act` row). Null when no row names one: deal, coin, game end. */
export function tcgBatchActor(events: TcgEvent[] | undefined): number | null {
	const act = events?.find(e => e.type === 'act');
	return typeof act?.seat === 'number' ? act.seat : null;
}

/**
 * Whether a seated player's input waits on the batch's own choices rather than the server's wait flag: a batch someone
 * else started, or one that hands you the turn (a setup that starts your first turn). Every batch, your own included,
 * plays on the pre-batch board with each beat folded in as it plays (`TcgBoard.acts` offers nothing until the request
 * beat); painting the post-batch board at the start showed the turn, the drawn card, a Special Condition or the new
 * actions under the animation that caused them.
 */
export function tcgInputWaitsOnBatch(events: TcgEvent[] | undefined, you: number | null | undefined): boolean {
	if (you == null) return true;
	if (tcgBatchActor(events) !== you) return true;
	return !!events?.some(e => e.type === 'turn' && e.seat === you);
}

/**
 * How far to scroll the hand sideways so a card spanning `left`..`right` sits inside the visible `viewLeft`..`viewRight`
 * (with `pad` to spare). 0 when it is already inside.
 */
export function handScrollToReveal(left: number, right: number, viewLeft: number, viewRight: number, pad = 8): number {
	if (left < viewLeft + pad) return left - (viewLeft + pad);
	if (right > viewRight - pad) return right - (viewRight - pad);
	return 0;
}

/**
 * A wheel turn over a hand that is wider than its box. A mostly vertical wheel scrolls the hand sideways, so a mouse
 * can reach every card; a horizontal gesture (trackpad) is left to the browser. Returns the new `scrollLeft`, or null
 * to leave the event alone (nothing to scroll, already at that end, or a horizontal gesture).
 */
export function handWheelTarget(
	deltaX: number, deltaY: number, scrollLeft: number, clientWidth: number, scrollWidth: number
): number | null {
	const max = scrollWidth - clientWidth;
	if (!(max > 0)) return null;
	if (Math.abs(deltaX) >= Math.abs(deltaY)) return null;
	const next = Math.min(max, Math.max(0, scrollLeft + deltaY));
	return next === scrollLeft ? null : next;
}

export function applyTcgEvents(
	base: TcgSnapshot,
	events: TcgEvent[] | undefined,
	actions?: TcgAction[]
): TcgSnapshot {
	let snap = cloneTcgSnapshot(base);
	if (snap.you == null) snap = scrubTcgSpectatorView(snap);
	const list = filterTcgEventsForViewer(events, snap.you) || [];
	// Soft batches are appended (energy, draw, tool, prize, hand). The same seq can
	// be folded when a click skips the beat, and again when the silent echo's twin arrives.
	const seen = typeof snap.appliedSeq === 'number' ? snap.appliedSeq : -1;
	let maxSeq = seen;
	for (let i = 0; i < list.length; i++) {
		const ev = list[i];
		if (ev && typeof ev.seq === 'number' && ev.seq <= seen) continue;
		applyBatchEvent(snap, list, i);
		if (ev && typeof ev.seq === 'number' && ev.seq > maxSeq) maxSeq = ev.seq;
	}
	if (maxSeq >= 0) snap.appliedSeq = maxSeq;
	if (actions !== undefined) snap.actions = actions;
	else if (snap.you == null) snap.actions = [];
	normalizeTcgEnergy(snap);
	return snap;
}

/** One event, in place. Callers clone first when they need to keep the previous board. */
export function applyTcgEvent(snap: TcgSnapshot, e: TcgEvent) {
	const players = snap.players || [];
	if (!e) return;
	if (e.type === 'damage' || e.type === 'heal') {
		if (e.iid != null && e.hp != null) {
			const found = findMonSlot(players, e.iid);
			if (found) {
				const hp = Number(e.hp);
				found.mon.hp = hp;
				// The engine sends the maximum when a Tool or other bonus changed it.
				// Older events only carry remaining HP, so a remaining value above the
				// stored maximum still raises it.
				if (e.maxHp != null) found.mon.maxHp = Number(e.maxHp);
				else if (hp > (found.mon.maxHp || 0)) found.mon.maxHp = hp;
				if (e.amount != null) {
					found.mon.lastDelta = e.type === 'damage' ? -Number(e.amount) : Number(e.amount);
				}
			}
		}
	} else if (e.type === 'status') {
		if (e.iid != null) {
			const found = findMonSlot(players, e.iid);
			if (found) {
				const s = e.status == null || e.status === '' ? null : String(e.status).toLowerCase();
				if (e.poisoned != null || e.burned != null) {
					found.mon.status = s;
					found.mon.poisoned = !!e.poisoned || s === 'poisoned';
					found.mon.burned = !!e.burned || s === 'burned';
				} else if (s === 'poisoned') {
					found.mon.poisoned = true;
				} else if (s === 'burned') {
					found.mon.burned = true;
				} else if (s == null) {
					found.mon.status = null;
					found.mon.poisoned = false;
					found.mon.burned = false;
				} else {
					found.mon.status = s;
				}
			}
		}
	} else if (e.type === 'energy') {
		// `from`: the card moved off that Pokémon in play; it was not attached from the hand.
		const src = e.from && e.cardId ? findMonSlot(players, e.from) : null;
		if (src) detachPlayedCards(src.mon, [e.cardId as string]);
		if (e.iid) {
			const found = findMonSlot(players, e.iid);
			if (found) {
				if (e.cardId) {
					found.mon.energyCards = [...(found.mon.energyCards || []), e.cardId];
				}
				found.mon.energy = [...(found.mon.energy || []), ...energyPipsFromEvent(e)];
				if (e.hp != null) found.mon.hp = Number(e.hp);
				if (e.maxHp != null) found.mon.maxHp = Number(e.maxHp);
			}
		}
	} else if (e.type === 'tool') {
		const src = e.from && e.cardId ? findMonSlot(players, e.from) : null;
		if (src) detachPlayedCards(src.mon, [e.cardId as string]);
		if (e.iid) {
			const found = findMonSlot(players, e.iid);
			if (found) {
				found.mon.tools = [...(found.mon.tools || []), e.cardId || 'tool'];
				if (e.hp != null) found.mon.hp = Number(e.hp);
				if (e.maxHp != null) found.mon.maxHp = Number(e.maxHp);
			}
		}
	} else if (e.type === 'hp') {
		if (e.iid) {
			const found = findMonSlot(players, e.iid);
			if (found) {
				if (e.hp != null) found.mon.hp = Number(e.hp);
				if (e.maxHp != null) found.mon.maxHp = Number(e.maxHp);
			}
		}
	} else if (e.type === 'moveCounters') {
		// Counters moved. This is not a heal and not damage, so neither of those rows exists.
		const dest = e.iid ? findMonSlot(players, e.iid) : null;
		const src = e.fromIid ? findMonSlot(players, e.fromIid) : null;
		if (dest) {
			if (e.hp != null) dest.mon.hp = Number(e.hp);
			if (e.maxHp != null) dest.mon.maxHp = Number(e.maxHp);
			if (e.amount != null) dest.mon.lastDelta = -Number(e.amount);
		}
		if (src && src !== dest) {
			if (e.fromHp != null) src.mon.hp = Number(e.fromHp);
			if (e.fromMaxHp != null) src.mon.maxHp = Number(e.fromMaxHp);
			if (e.amount != null) src.mon.lastDelta = Number(e.amount);
		}
	} else if (e.type === 'fromHand') {
		const p = players[e.seat as number];
		if (p) {
			const ids = e.ids as string[] | undefined;
			const hand = handAsIds(p.hand);
			if (hand && ids?.length) {
				for (const id of ids) {
					const at = hand.indexOf(id);
					if (at >= 0) hand.splice(at, 1);
				}
				setHand(p, hand);
			} else if (!Array.isArray(p.hand) && p.hand) {
				p.hand = bumpPile(p.hand as { count: number }, -(Number(e.n) || ids?.length || 1));
			}
		}
	} else if (e.type === 'fromDeck') {
		// Cards left the deck to be attached or played (or face down to the Prizes).
		const p = players[e.seat as number];
		if (p) p.deck = bumpPile(p.deck, -(Number(e.n) || (e.ids as string[] | undefined)?.length || 0));
	} else if (e.type === 'prizeCount') {
		const p = players[e.seat as number];
		if (p) p.prizes = { count: Number(e.n) || 0 };
	} else if (e.type === 'draw' || e.type === 'find') {
		const seat = e.seat as number;
		const p = players[seat];
		if (p) {
			const ids = e.ids as string[] | undefined;
			// `find` with dest other than hand (e.g. Nighttime Maneuvers → top of deck)
			// must not enter the hand — that desyncs hand indices vs the server.
			const findDest = e.type === 'find' ? String(e.dest || 'hand') : 'hand';
			const toHand = e.type === 'draw' || findDest === 'hand';
			// `iid`: attached cards came off that Pokémon, which stays in play.
			if (e.type === 'find' && e.iid && ids?.length) {
				const found = findMonSlot(players, e.iid);
				if (found) detachPlayedCards(found.mon, ids, snap.energyDetachIndex);
				delete snap.energyDetachIndex;
			}
			if (toHand) {
				if (ids?.length && Array.isArray(p.hand)) {
					setHand(p, p.hand.concat(ids));
				} else if (ids?.length && p.hand) {
					// A hidden hand is a count: revealed ids add to it, they do not replace it.
					p.hand = bumpPile(p.hand as { count: number }, ids.length);
				} else if (!Array.isArray(p.hand) && p.hand) {
					const n = e.type === 'draw' ? (Number(e.n) || 0) : Math.max(1, ids?.length || Number(e.n) || 1);
					if (n) p.hand = bumpPile(p.hand as { count: number }, n);
				}
			}
			if (e.type === 'draw' && e.n) p.deck = bumpPile(p.deck, -(Number(e.n) || 0));
			// `find` with `from: "deck"`: the card left the deck (one card per find when ids are hidden).
			if (e.type === 'find' && e.from === 'deck') p.deck = bumpPile(p.deck, -(ids?.length || 1));
			// `from: "prizes"`: a Prize-zone search result left the Prizes.
			if (e.type === 'find' && e.from === 'prizes') p.prizes = bumpPile(p.prizes, -(ids?.length || 1));
		}
	} else if (e.type === 'deal') {
		const counts = e.counts as [number, number] | undefined;
		for (let seat = 0; seat < 2; seat++) {
			const p = players[seat];
			if (!p) continue;
			const ids = (seat === 0 ? e.ids0 : e.ids1) as string[] | undefined;
			const n = ids ? ids.length : (counts?.[seat]) || 0;
			if (ids?.length) setHand(p, ids.slice());
			else p.hand = { count: n };
			if (n) p.deck = bumpPile(p.deck, -n);
		}
	} else if (e.type === 'suddenDeath') {
		// Sudden death: a fresh setup. Every card is back in its owner's deck (`deck` sizes); the old game's Pokémon,
		// piles, Stadium, and turn are gone. The restart's `coin`, `first`, and `deal` follow.
		const decks = e.deck as [number, number] | undefined;
		const prizes = e.prizes as [number, number] | undefined;
		snap.status = 'setup';
		snap.turn = 0;
		snap.turnNumber = 0;
		snap.firstPlayer = 0;
		snap.suddenDeath = true;
		snap.winner = null;
		snap.winReason = '';
		clearPending(snap);
		delete snap.effectsActive;
		for (let seat = 0; seat < 2; seat++) {
			const p = players[seat];
			if (!p) continue;
			p.active = null;
			p.bench = [];
			p.hand = Array.isArray(p.hand) ? [] : { count: 0 };
			p.discard = [];
			p.lostZone = { count: 0 };
			// During setup only the owner sees its own count (the snapshot shows 0 for the other side); `setupEnd` sets both.
			p.prizes = { count: seat === snap.you ? Number(prizes?.[seat]) || 0 : 0 };
			p.deck = { count: Number(decks?.[seat]) || 0 };
			p.stadium = null;
			delete p.stadiumIds;
			p.setup = 'active';
			p.points = 0;
			delete p.energyZone;
			p.attachedEnergyThisTurn = false;
			p.playedSupporterThisTurn = false;
			p.playedStadiumThisTurn = false;
			p.retreatedThisTurn = false;
		}
	} else if (e.type === 'setupEnd') {
		// Setup is over: both players are ready, and `counts` is each Prize count (points still needed in a points
		// format). The `place` rows after it turn the setup Pokémon face up.
		if (snap.status === 'setup') snap.status = 'playing';
		const counts = e.counts as [number, number] | undefined;
		for (let seat = 0; seat < 2; seat++) {
			const p = players[seat];
			if (!p) continue;
			p.setup = 'done';
			if (counts) p.prizes = { count: Number(counts[seat]) || 0 };
		}
	} else if (e.type === 'prizeSet') {
		const counts = e.counts as [number, number] | undefined;
		for (let seat = 0; seat < 2; seat++) {
			const p = players[seat];
			if (!p) continue;
			const n = (counts?.[seat]) || 0;
			p.prizes = { count: n };
			if (n) p.deck = bumpPile(p.deck, -n);
		}
	} else if (e.type === 'energyZone') {
		const p = players[e.seat as number];
		if (p) {
			const ready = !!e.ready;
			p.energyZone = {
				type: String(e.energyType || e.zone || 'grass'),
				...(ready && e.next ? { next: e.next } : {}),
				ready,
			};
		}
	} else if (e.type === 'place') {
		const p = players[e.seat as number];
		if (p) {
			const existing = findMonSlot(players, e.iid)?.mon;
			const hide = !existing && snap.status === 'setup' && (snap.you == null || e.seat !== snap.you);
			const mon = existing || (hide ?
				stubMon({ ...e, faceDown: true, name: 'Pokémon', cardId: '', hp: 0, maxHp: 0 }) :
				stubMon(e));
			if (existing) {
				// A `place` for a Pokémon already in play publishes it again: after `setupEnd` that turns a face-down
				// setup Pokémon face up (it used to stay a blank card until a snapshot).
				if (e.cardId && e.cardId !== existing.cardId) {
					existing.cardId = e.cardId as string;
					delete existing.image;
				}
				if (e.cardId) existing.faceDown = false;
				if (e.hp != null) existing.hp = Number(e.hp);
				if (e.maxHp != null) existing.maxHp = Number(e.maxHp);
				if (e.name) existing.name = e.name;
			}
			applyPlace(p, placeSlotOf(e), mon);
			// The retreat action only opens the payment. The switch is what spends the turn's retreat.
			if (e.source?.kind === 'retreat') p.retreatedThisTurn = true;
			if (p.setup && p.setup !== 'done' && !p.active) p.setup = 'active';
			else if (p.setup === 'active') p.setup = 'bench';
		}
	} else if (e.type === 'evolve') {
		const found = findMonSlot(players, e.iid);
		if (found) {
			if (e.cardId && e.cardId !== found.mon.cardId) delete found.mon.image;
			found.mon.cardId = e.cardId || found.mon.cardId;
			if (e.name) found.mon.name = e.name;
			if (e.hp != null) found.mon.hp = Number(e.hp);
			if (e.maxHp != null) found.mon.maxHp = Number(e.maxHp);
			// The event names the server slot. A drifted client was painting the
			// new print on whichever object had this iid, then offering that
			// cell's actions (the other Pokémon's Ability).
			const owner = players[found.seat];
			if (owner && e.slot != null && e.slot !== '') applyPlace(owner, placeSlotOf(e), found.mon);
		}
		if (e.fromHand && e.cardId) {
			const owner = players[e.seat as number];
			if (owner) removeHandIds(owner, [e.cardId]);
		}
	} else if (e.type === 'ko') {
		const p = players[e.seat as number];
		if (p && e.iid) dropMonByIid(p, e.iid);
	} else if (e.type === 'leavePlay') {
		// The Pokémon left its slot; its cards move in the find / toDeck / toDiscard / toLost rows that follow.
		const p = players[e.seat as number];
		if (p && e.iid) dropMonByIid(p, e.iid);
	} else if (e.type === 'shuffle') {
		const p = players[e.seat as number];
		if (p) {
			if (e.from === 'hand') setHand(p, Array.isArray(p.hand) ? [] : { count: 0 });
			if (e.deck != null) p.deck = { count: Number(e.deck) };
		}
	} else if (e.type === 'toDiscard') {
		const p = players[e.seat as number];
		const ids = Array.isArray(e.ids) ? e.ids.filter(Boolean) as string[] : [];
		if (p && ids.length) {
			if (e.from === 'hand') removeHandIds(p, ids);
			if (e.from === 'deck') p.deck = bumpPile(p.deck, -ids.length);
			if (e.iid) {
				const found = findMonSlot(players, e.iid);
				if (found) detachPlayedCards(found.mon, ids, snap.energyDetachIndex);
				delete snap.energyDetachIndex;
			}
			if (!p.discard) p.discard = [];
			for (const id of ids) p.discard.push(id);
		}
	} else if (e.type === 'fromDiscard') {
		const p = players[e.seat as number];
		const ids = Array.isArray(e.ids) ? e.ids.filter(Boolean) as string[] : [];
		if (p && ids.length && Array.isArray(p.discard)) {
			for (const id of ids) {
				const at = p.discard.indexOf(id);
				if (at >= 0) p.discard.splice(at, 1);
			}
		}
	} else if (e.type === 'toDeck') {
		const p = players[e.seat as number];
		const ids = Array.isArray(e.ids) ? e.ids.filter(Boolean) as string[] : [];
		// A hidden hand card arrives as a count: `n` without `ids`.
		const n = ids.length || Number(e.n) || 0;
		if (p && n) {
			if (e.from === 'hand') {
				if (ids.length) removeHandIds(p, ids);
				else if (!Array.isArray(p.hand) && p.hand) p.hand = bumpPile(p.hand as { count: number }, -n);
			}
			if (e.from === 'play' && e.iid && ids.length) {
				// Attached cards only: the Pokémon stays. Its own card listed: it left play.
				const found = findMonSlot(players, e.iid);
				if (found && !ids.includes(found.mon.cardId)) detachPlayedCards(found.mon, ids);
				else dropMonByIid(p, e.iid);
			}
			// From the discard pile, its `fromDiscard` row takes the cards off the pile.
			p.deck = bumpPile(p.deck, n);
		}
	} else if (e.type === 'toLost') {
		const p = players[e.seat as number];
		const n = Number(e.n) || 0;
		if (p) p.lostZone = bumpPile(p.lostZone, n);
		if (p && e.from === 'hand') {
			const lostIds = Array.isArray(e.ids) ? e.ids as string[] : [];
			if (lostIds.length) removeHandIds(p, lostIds);
			else if (!Array.isArray(p.hand) && p.hand) p.hand = bumpPile(p.hand as { count: number }, -n);
		}
		if (p && e.from === 'deck') p.deck = bumpPile(p.deck, -n);
		if (e.iid && Array.isArray(e.ids) && e.ids.length) {
			const found = findMonSlot(players, e.iid);
			if (found) detachPlayedCards(found.mon, e.ids as string[], snap.energyDetachIndex);
			delete snap.energyDetachIndex;
		}
	} else if (e.type === 'trainer') {
		const p = players[e.seat as number];
		if (p && e.cardId && cardKind(e.cardId).supporter) p.playedSupporterThisTurn = true;
	} else if (e.type === 'stadium') {
		const seat = e.seat as number;
		for (let s = 0; s < players.length; s++) {
			const p = players[s];
			if (!p) continue;
			// The snapshot names the Stadium in play on both players; `seat` is who played it.
			p.stadium = e.cardId || null;
			// A two-card Legendary Stadium names both halves; a one-card Stadium has no `ids`.
			const ids = e.ids as string[] | undefined;
			if (ids && ids.length > 1) p.stadiumIds = ids.slice();
			else delete p.stadiumIds;
			if (s === seat) p.playedStadiumThisTurn = true;
		}
	} else if (e.type === 'stadiumEnd') {
		for (const p of players) {
			if (!p) continue;
			p.stadium = null;
			delete p.stadiumIds;
		}
	} else if (e.type === 'prize') {
		snap.pendingPrize = { seat: e.seat as number, n: Number(e.n) || 1 };
	} else if (e.type === 'prizeTake') {
		const p = players[e.seat as number];
		if (p?.prizes) p.prizes = bumpPile(p.prizes, -1);
		if (p && e.ids?.length) {
			const hand = handAsIds(p.hand) || [];
			setHand(p, hand.concat(e.ids));
		} else if (p && !Array.isArray(p.hand) && p.hand) {
			p.hand = bumpPile(p.hand as { count: number }, 1);
		}
		if (snap.pendingPrize && snap.pendingPrize.seat === e.seat) {
			snap.pendingPrize = { ...snap.pendingPrize, n: Math.max(0, snap.pendingPrize.n - 1) };
			if (snap.pendingPrize.n <= 0) delete snap.pendingPrize;
		}
	} else if (e.type === 'points') {
		const p = players[e.seat as number];
		if (p && e.total != null) {
			p.points = Number(e.total);
			if (snap.format?.energyZone && snap.format.prizes != null) {
				p.prizes = { count: Math.max(0, Number(snap.format.prizes) - Number(e.total)) };
			}
		}
	} else if (e.type === 'field') {
		const effects = Array.isArray(e.effects) ?
			e.effects.filter((row: { summary?: string }) => row?.summary) : [];
		if (effects.length) snap.effectsActive = effects;
		else delete snap.effectsActive;
	} else if (e.type === 'turn') {
		if (e.seat != null) snap.turn = e.seat as number;
		if (e.number != null) snap.turnNumber = Number(e.number);
		if (snap.status === 'setup') snap.status = 'playing';
		// The engine clears these on the player who is starting. The other player keeps them until their own turn.
		const starting = e.seat != null ? players[e.seat as number] : null;
		for (const p of players) {
			if (!p) continue;
			p.setup = 'done';
			if (!starting || p === starting) {
				p.attachedEnergyThisTurn = false;
				p.playedSupporterThisTurn = false;
				p.playedStadiumThisTurn = false;
				p.retreatedThisTurn = false;
			}
			if (p.active) p.active.faceDown = false;
			const bench = p.bench || [];
			for (const mon of bench) {
				if (mon) mon.faceDown = false;
			}
		}
	} else if (e.type === 'first') {
		if (e.chooses) snap.pendingFirst = e.seat as number;
		else {
			delete snap.pendingFirst;
			snap.firstPlayer = e.seat as 0 | 1;
		}
	} else if (e.type === 'act') {
		const seat = e.seat as number;
		const p = players[seat];
		const action = e.action as TcgAction | undefined;
		if (p && action) {
			const handTypes = [
				'setActive', 'setBench', 'playBasic', 'evolve', 'attachEnergy', 'attachTool',
				'playTrainer', 'playStadium', 'mulliganBench',
			];
			if (handTypes.includes(action.type) && typeof action.hand === 'number') {
				removeHandIndex(p, action.hand);
			}
			if (action.type === 'setupDone') p.setup = 'done';
			if (action.type === 'chooseFirst') {
				// The choice is made: a board folded from events alone (a spectator's) has no snapshot to clear this.
				snap.firstPlayer = action.goFirst ? seat as 0 | 1 : (seat === 0 ? 1 : 0);
				delete snap.pendingFirst;
			}
			if (action.type === 'attachEnergy' || action.type === 'attachZone') p.attachedEnergyThisTurn = true;
			if ((action.type === 'discardEnergyPick' || action.type === 'payEnergy') && typeof action.index === 'number') {
				snap.energyDetachIndex = action.index;
			}
			if (action.type === 'playStadium') p.playedStadiumThisTurn = true;
			// `payEnergy`: the `toDiscard` (or Skaters' Park `find`) row with the retreating Pokémon's `iid` takes the Energy
			// off. Applying the index here as well took a second, identical card off.
			if (action.type === 'discardPick' && typeof action.hand === 'number') {
				// The pick is from the opponent's hand when the prompt said so; a pick going to the deck
				// or the Lost Zone is taken out of the hand by its own toDeck / toLost row.
				const req = snap.discardRequest;
				const owner = req?.fromFoe ? players[seat === 0 ? 1 : 0] : p;
				if (owner && !req?.to) removeHandIndex(owner, action.hand);
			}
		}
	} else if (e.type === 'request') {
		clearPending(snap);
		const kind = String(e.kind || '');
		if (kind === 'discard') snap.discardRequest = { fromFoe: !!e.fromFoe, to: e.to as string | undefined };
		else delete snap.discardRequest;
		const waiting = Array.isArray(e.waiting) ? e.waiting : [];
		const forYou = snap.you != null && waiting.some((seat: number) => seat === snap.you);
		if (forYou && kind === 'search') {
			const seen = Array.isArray(e.seen) ? e.seen.filter(Boolean) as string[] : [];
			const ids = Array.isArray(e.ids) ? e.ids.filter(Boolean) as string[] : [];
			const legal = Array.isArray(e.legal) ? e.legal.filter(Boolean) as string[] : undefined;
			snap.pendingSearch = {
				kind: e.searchKind, left: e.left, upTo: e.upTo, zone: e.zone, dest: e.dest,
				...(seen.length ? { seen } : {}),
				...(ids.length ? { ids } : {}),
				...(legal ? { legal } : {}),
			};
		} else if (forYou && kind === 'discard' && e.need != null) {
			snap.pendingDiscard = { need: Number(e.need) };
		} else if (forYou && kind === 'retreatPay' && e.need != null) {
			snap.pendingRetreatPay = { need: Number(e.need) };
		} else if (forYou && kind === 'discardEnergy' && e.iid) {
			const found = findMonSlot(players, String(e.iid));
			snap.pendingDiscardEnergy = {
				need: Number(e.need) || 0,
				iid: String(e.iid),
				cardIds: found?.mon.energyCards ? found.mon.energyCards.slice() : [],
			};
		} else if (forYou && kind === 'confirm') {
			snap.pendingConfirm = { title: e.title, text: e.text };
		} else if (forYou && kind === 'copy') {
			snap.pendingCopyAttack = {
				title: e.title || 'Make a choice',
				text: e.text || 'Pick one.',
				...(typeof e.ask === 'string' ? { ask: e.ask } : {}),
				...(e.from ? { from: e.from as TcgChoiceFrom } : {}),
			};
		} else if (kind === 'promote' || kind === 'switch') {
			snap.pendingPromote = e.promote != null ? Number(e.promote) : (e.waiting?.[0] as number);
		} else if (kind === 'first') {
			snap.pendingFirst = e.waiting?.[0] as number;
		} else if (kind === 'mulligan') {
			snap.pendingMulligan = e.waiting?.[0] as number;
		} else if (kind === 'prize') {
			snap.pendingPrize = {
				seat: e.prizeSeat != null ? Number(e.prizeSeat) : Number(e.waiting?.[0] || 0),
				n: Number(e.n) || 1,
			};
			if (e.promote != null) snap.pendingPromote = Number(e.promote);
		} else if (forYou && kind === 'slot') {
			snap.pendingSlotPick = {
				left: Number(e.left) || 1,
				hint: e.title || '',
				side: (e.text || 'mine') as 'mine' | 'foe',
				...(typeof e.ask === 'string' ? { ask: e.ask } : {}),
				...(e.from ? { from: e.from as TcgChoiceFrom } : {}),
			};
		}
		if (kind === 'over') snap.status = 'over';
		if (kind === 'aborted') snap.status = 'aborted';
		if (kind === 'setup') snap.status = 'setup';
		if (kind === 'turn' && snap.status === 'setup') snap.status = 'playing';
	} else if (e.type === 'gameEnd') {
		snap.status = 'over';
		const result = e.result || e.replay?.result;
		if (result) {
			if (result.winner !== undefined) snap.winner = result.winner;
			if (result.reason) snap.winReason = result.reason;
		}
	} else if (e.type === 'over') {
		snap.status = 'over';
		if (e.winner !== undefined) snap.winner = e.winner;
		if (e.reason) snap.winReason = e.reason;
		clearPending(snap);
	}
}

type Preview = { cardId: string, image?: string, name?: string, /** Place in my own hand order. */ handAt?: number };

/** On-card / projectile attack motion family (name keywords + type fallback). */
type HitStyle =
	| 'impact' | 'slash' | 'barrage' | 'beam' | 'flame' | 'torrent'
	| 'bolt' | 'zap' | 'bloom' | 'pulse' | 'quake' | 'veil' | 'shard'
	| 'sparkle' | 'roar' | 'whirl' | 'drain'
	| 'frost' | 'stream' | 'sting' | 'powder' | 'wave' | 'petal' | 'bite'
	| 'bubble' | 'sonic' | 'mud' | 'wrap' | 'note' | 'aura' | 'drill'
	| 'acid' | 'wisp' | 'meteor' | 'wing'
	| 'swift' | 'gold' | 'daze' | 'sand' | 'lick' | 'bone' | 'ink' | 'tri' | 'fist' | 'shade'
	| 'kick' | 'chop' | 'rage' | 'shell' | 'kiss' | 'gyro' | 'breath' | 'cannon' | 'fall' | 'iron'
	| 'boom' | 'web' | 'tail' | 'arrow' | 'sleep' | 'shield';

/** One replay-player FX beat (Unreal-Bot graphics.js fxFor + hits). */
type FxHit = {
	iid: string, amount?: number,
	/**
	 * `counters`: damage counters put on (no Weakness, not "damage"). `prevented`: an effect stopped the damage, a heal,
	 * a Special Condition, or the attack's effects. `reduced`: the damage came to 0. `moved`: counters moved onto `iid`
	 * from `fromMon`.
	 */
	kind: 'damage' | 'heal' | 'status' | 'counters' | 'prevented' | 'reduced' | 'moved',
	src?: string, label?: string, element?: string, status?: string,
	/** Under the number: Weakness or Resistance, or what prevented it. */
	note?: string,
	counters?: number,
	fromMon?: string,
	/** Attacking Pokémon iid (projectile origin). */
	fromIid?: string,
	hitStyle?: HitStyle,
	travel?: boolean,
};
type FxBeat = {
	kind: string,
	tag?: string,
	iid?: string,
	targetIid?: string,
	/** Projectile origin when iid is the damage target. */
	fromIid?: string,
	seat?: number,
	amount?: number,
	n?: number,
	extra?: string,
	/** Full board message, e.g. "Opponent is searching their deck". */
	message?: string,
	cardId?: string,
	fromCardId?: string,
	src?: string,
	onto?: string,
	ontoId?: string,
	ids?: string[],
	labels?: string[],
	hits?: FxHit[],
	/** Ordered heads/tails results for a coin beat (one or many flips). */
	coins?: boolean[],
	tie?: boolean,
	element?: string,
	hitStyle?: HitStyle,
	/** Mid-board projectile from attacker → target (or reverse for drain). */
	travel?: boolean,
	/** Physical / melee: attacker card lunges into the target. */
	contact?: boolean,
	/** Name banner only. The lunge and projectile start after it finishes. */
	announce?: boolean,
	/** The Pokémon this attack actually hit (from its events, never the legal choices): highlighted during the wind-up. */
	targets?: string[],
	/** Special Condition being applied. Empty string means it just wore off. */
	status?: string,
	slot?: TcgSlot,
	fromBench?: boolean,
	/** A card moving between zones: where it left. `fromIid` names the Pokémon when it came off one. */
	moveFrom?: FlightZoneKind,
	/** Where it went, when the beat kind alone does not say (a discard row can be to the discard pile or the hand). */
	moveTo?: FlightZoneKind,
	/** The discard of a Knocked Out Pokémon: its first card is the Pokémon (from its slot), the rest were attached. */
	koDiscard?: boolean,
	/** Both seats at once (the opening deal, the Prize cards set aside): how many cards each, and the ids this viewer has. */
	perSeat?: { n: number, ids?: string[] }[],
	/** The card leaving `fromIid` is that Pokémon itself (a setup take-back), not one of its attached cards. */
	wholeMon?: boolean,
};
type FlightZoneKind = 'hand' | 'deck' | 'discard' | 'lost' | 'prizes' | 'zone' | 'mon';
/**
 * The attack (or Ability) whose results are still arriving: its hits after a switch, a discard, a coin, or a choice
 * play from the same attacker in the same style. Cleared at the next turn, checkup, Trainer, or a new attack.
 */
type AttackCtx = {
	iid: string, src: string, label: string, element: string, style?: HitStyle, travel?: boolean, contact?: boolean,
};
/**
 * One zone on the board, by seat (not by viewer); `iid` for a Pokémon. `part: 'attach'`: the cards attached to that
 * Pokémon (the mini cards tucked under its edge), not the Pokémon itself.
 */
export type FlightZone = { zone: FlightZoneKind, seat: number, iid?: string, part?: 'attach' };
/**
 * One card in flight. `cardId` is empty for a card back: only ids this viewer already received are shown. `land`: an
 * attachment arriving on a Pokémon (its mini card appears, with a short pulse, when the flight lands).
 */
export type CardFlight = { from: FlightZone, to: FlightZone, cardId: string, delay: number, land?: 'energy' | 'tool' };

/**
 * The cards a beat moves, from where to where. Pure: it reads only the beat, so what it shows is what the viewer's
 * events already carry (an opponent's draw has no ids, so it flies face down). In-play moves (a switch, a retreat)
 * are not here: the slot glide (FLIP) shows those.
 */
export function cardFlights(fx: FxBeat | null | undefined): CardFlight[] {
	if (!fx) return [];
	if ((fx.kind === 'deal' || fx.kind === 'prizeSet') && fx.perSeat) {
		// Both seats at once: the deck to the hand (the deal), the deck to the Prize cards (set aside, face down).
		const out: CardFlight[] = [];
		fx.perSeat.forEach((side, s) => {
			const count = Math.max(0, Math.min(side.n || 0, 5));
			for (let k = 0; k < count; k++) {
				out.push({
					from: { zone: 'deck', seat: s }, to: { zone: fx.kind === 'deal' ? 'hand' : 'prizes', seat: s },
					cardId: fx.kind === 'deal' ? side.ids?.[k] || '' : '', delay: k * 70,
				});
			}
		});
		return out;
	}
	const seat = fx.seat;
	if (seat !== 0 && seat !== 1) return [];
	const zone = (kind: FlightZoneKind | undefined, iid?: string, part?: 'attach'): FlightZone | null => {
		if (!kind) return null;
		if (kind === 'mon') return iid ? { zone: 'mon', seat, iid, ...(part ? { part } : {}) } : null;
		return { zone: kind, seat };
	};
	const many = (from: FlightZone | null, to: FlightZone | null, n: number, ids?: string[]): CardFlight[] => {
		if (!from || !to) return [];
		const count = Math.max(1, Math.min(n, 5));
		return Array.from({ length: count }, (_, i) => ({ from, to, cardId: ids?.[i] || '', delay: i * 70 }));
	};
	switch (fx.kind) {
	case 'draw': case 'drawEffect':
		return many(zone('deck'), zone('hand'), fx.n || 1, fx.ids);
	case 'find':
		if (fx.moveTo !== 'hand' || !fx.moveFrom) return [];
		// Off a Pokémon: an attached card, from where it sat; a setup take-back, the Pokémon itself from its slot.
		return many(zone(fx.moveFrom, fx.fromIid, fx.wholeMon ? undefined : 'attach'), zone('hand'), fx.n || 1, fx.ids);
	case 'prizeTake':
		return many(zone('prizes'), zone('hand'), fx.n || 1, fx.ids);
	case 'discard': {
		if (fx.moveFrom !== 'mon') return many(zone(fx.moveFrom, fx.fromIid), zone(fx.moveTo), fx.n || 1, fx.ids);
		// Off a Pokémon: attached cards leave from where they sat. A Knock Out's first card is the Pokémon itself.
		const out = many(zone('mon', fx.fromIid, 'attach'), zone(fx.moveTo), fx.n || 1, fx.ids);
		if (fx.koDiscard && out.length) out[0] = { ...out[0], from: { zone: 'mon', seat, iid: fx.fromIid } };
		return out;
	}
	case 'energy': case 'tool': {
		if (!fx.iid) return [];
		const from = fx.fromIid ? zone('mon', fx.fromIid, 'attach') : zone(fx.moveFrom || 'hand');
		const out = many(from, zone('mon', fx.iid, 'attach'), 1, fx.cardId ? [fx.cardId] : undefined);
		return out.map(fl => ({ ...fl, land: fx.kind as 'energy' | 'tool' }));
	}
	case 'place':
		if (!fx.iid || !fx.moveFrom) return [];
		return many(zone(fx.moveFrom), zone('mon', fx.iid), 1, fx.cardId ? [fx.cardId] : undefined);
	default:
		return [];
	}
}

/** Where an attached card came from, read from the rows before it in the same batch. */
function attachOrigin(events: TcgEvent[], i: number): FlightZoneKind {
	for (let j = i - 1; j >= 0; j--) {
		const p = events[j];
		if (!p) break;
		if (p.type === 'act') {
			const t = p.action?.type;
			if (t === 'attachZone') return 'zone';
			return 'hand';
		}
		if (p.type === 'fromDiscard') return 'discard';
		if (p.type === 'fromHand') return 'hand';
		if (p.type === 'find' && (p.dest === 'attach' || p.dest === 'attach-active')) {
			return p.from === 'prizes' ? 'prizes' : 'deck';
		}
		if (p.type === 'energy' || p.type === 'tool') continue;
		if (p.type === 'fromDeck') return 'deck';
	}
	return 'hand';
}

/** A Pokémon put onto the Bench by a search came from the deck: the `find` with `dest` "bench" before it. */
function placedFromDeck(events: TcgEvent[], i: number, seat: unknown): boolean {
	for (let j = i - 1; j >= 0 && j >= i - 6; j--) {
		const p = events[j];
		if (p?.type === 'find' && p.dest === 'bench' && p.seat === seat) return true;
		if (p?.type === 'act') break;
	}
	return false;
}
type PkFx = {
	cls: string, dataFx?: string, tick?: number, element?: string, status?: string,
	moveTag?: string, moveName?: string,
	/** TCG energy type for attach FX — renders `/fx/tcg/energy/{type}.png`. */
	energyType?: string,
	hitStyle?: HitStyle,
	/** Contact attack: lunge this card into `targetIid`. */
	contact?: boolean,
	targetIid?: string,
	/** Under the number: Weakness, Resistance, or what prevented it. */
	note?: string,
};
type KoGhost = {
	iid: string,
	cardId: string,
	name: string,
	image?: string,
	hp?: number,
	maxHp?: number,
	seat: number,
	slot: TcgSlot,
};

function whoName(players: TcgPlayerView[] | undefined, seat: number | undefined): string {
	if (seat == null) return 'a player';
	return players?.[seat]?.name || `Player ${seat + 1}`;
}
/** "You" / "Opponent" / display name for board FX copy. */
function actorLabel(
	players: TcgPlayerView[] | undefined,
	seat: number | undefined,
	you?: number | null
): string {
	if (seat == null) return 'A player';
	if (you != null && seat === you) return 'You';
	if (you != null) return 'Opponent';
	return whoName(players, seat);
}
function actorIsYou(seat: number | undefined, you?: number | null): boolean {
	return you != null && seat != null && seat === you;
}
function findMonView(players: TcgPlayerView[] | undefined, iid?: string): TcgPokemonView | null {
	return findMonSlot(players, iid)?.mon || null;
}
function findMonSlot(
	players: TcgPlayerView[] | undefined,
	iid?: string
): { seat: number, slot: TcgSlot, mon: TcgPokemonView } | null {
	if (!iid || !players) return null;
	for (let seat = 0; seat < players.length; seat++) {
		const p = players[seat];
		if (!p) continue;
		if (p.active?.iid === iid) return { seat, slot: 'active', mon: p.active };
		const bench = p.bench || [];
		for (let i = 0; i < bench.length; i++) {
			if (bench[i]?.iid === iid) return { seat, slot: i, mon: bench[i] };
		}
	}
	return null;
}
function placeSlotOf(e: TcgEvent): TcgSlot {
	if (e.slot === 'active' || e.slot == null || e.slot === '') return 'active';
	const n = Number(e.slot);
	return Number.isFinite(n) ? n : 'active';
}
const seenMonNames: { [iid: string]: string } = Object.create(null);
const seenCardNames: { [id: string]: string } = Object.create(null);
export function noteTcgMons(players: TcgPlayerView[] | undefined) {
	if (!players) return;
	for (const p of players) {
		const mons = [p?.active, ...(p?.bench || [])];
		for (const mon of mons) {
			if (mon?.iid && mon.name) seenMonNames[mon.iid] = mon.name;
			if (mon?.cardId && mon.name && mon.name !== mon.cardId) seenCardNames[mon.cardId] = mon.name;
		}
	}
}
/**
 * The printed names the host sends with a snapshot (`cardNames`), for card ids this viewer already receives.
 * A name is per printing: two copies share it and stay two cards (the hand and every pick go by index).
 */
export function noteTcgCardNames(snap: TcgSnapshot | null | undefined) {
	const names = snap?.cardNames;
	if (!names) return;
	let added = false;
	for (const id in names) {
		if (!names[id] || seenCardNames[id] === names[id]) continue;
		seenCardNames[id] = names[id];
		added = true;
	}
	if (added) rewriteLoggedCardIds();
}
function monName(players: TcgPlayerView[] | undefined, iid?: string): string {
	return findMonView(players, iid)?.name || (iid && seenMonNames[iid]) || 'a Pokémon';
}
const printedNames: { [id: string]: string } = {
	'base1-97': 'Grass Energy',
	'base1-98': 'Fire Energy',
	'base1-99': 'Water Energy',
	'base1-100': 'Lightning Energy',
	'base1-101': 'Psychic Energy',
	'base1-102': 'Fighting Energy',
	// Scarlet & Violet basic Energy (the Pocket Energy Zone attaches these ids).
	'sve-1': 'Grass Energy', 'sve-2': 'Fire Energy', 'sve-3': 'Water Energy', 'sve-4': 'Lightning Energy',
	'sve-5': 'Psychic Energy', 'sve-6': 'Fighting Energy', 'sve-7': 'Darkness Energy', 'sve-8': 'Metal Energy',
	'sve-9': 'Grass Energy', 'sve-10': 'Fire Energy', 'sve-11': 'Water Energy', 'sve-12': 'Lightning Energy',
	'sve-13': 'Psychic Energy', 'sve-14': 'Fighting Energy', 'sve-15': 'Darkness Energy', 'sve-16': 'Metal Energy',
};
const printedNamesLoaded: { [file: string]: boolean } = {};
/** Card index ships with this client. `Net` would send a root path to the sprite host. */
function namesUrl(file: string): string {
	if (file.charAt(0) === '/' && typeof document !== 'undefined' && document.location?.host) {
		return document.location.protocol + '//' + document.location.host + file;
	}
	return file;
}
/** Lines already on screen were written before the index arrived. */
function rewriteLoggedCardIds() {
	if (typeof document === 'undefined') return;
	const nodes = document.querySelectorAll('.tcg-log-text');
	for (let i = 0; i < nodes.length; i++) {
		const el = nodes[i] as HTMLElement;
		const text = el.textContent || '';
		const next = text.replace(/\b[a-z][a-z0-9]*-\d+[a-z0-9]*\b/gi, id => seenCardNames[id] || printedName(id) || id);
		if (next !== text) el.textContent = next;
	}
}
function loadPrintedNames(file = '/tcg-names.json') {
	if (printedNamesLoaded[file]) return;
	printedNamesLoaded[file] = true;
	Net(namesUrl(file)).get().then(text => {
		const data = JSON.parse(text);
		if (data && typeof data === 'object') Object.assign(printedNames, data);
		rewriteLoggedCardIds();
	}).catch(() => {});
}
loadPrintedNames();
/** The paper (Standard / Expanded / GLC) card index is ~20k entries; only pull it for a paper game. */
export function loadPaperNames() {
	loadPrintedNames('/tcg-names-paper.json');
}
function printedName(id?: string): string {
	if (!id) return '';
	if (printedNames[id]) return printedNames[id];
	const promo = /^P-([A-Za-z])-0*(\d+)$/.exec(id);
	if (promo) {
		const key = `PROMO-${promo[1].toUpperCase()}-${promo[2]}`;
		if (printedNames[key]) return printedNames[key];
	}
	const num = /^(.*)-0*(\d+)$/.exec(id);
	if (num && printedNames[`${num[1]}-${num[2]}`]) return printedNames[`${num[1]}-${num[2]}`];
	const energy = /^energy-([a-z]+)$/i.exec(id);
	if (energy) {
		const t = energy[1].toLowerCase();
		return t.charAt(0).toUpperCase() + t.slice(1) + ' Energy';
	}
	return '';
}
function cardName(players: TcgPlayerView[] | undefined, cardId?: string, iid?: string): string {
	const fromIid = findMonView(players, iid)?.name;
	if (fromIid && fromIid !== cardId) return fromIid;
	if (cardId && players) {
		for (const p of players) {
			const mons = [p?.active, ...(p?.bench || [])];
			for (const mon of mons) {
				if (mon?.cardId === cardId && mon.name && mon.name !== cardId) return mon.name;
			}
		}
	}
	if (iid && seenMonNames[iid] && seenMonNames[iid] !== cardId) return seenMonNames[iid];
	if (cardId && seenCardNames[cardId]) return seenCardNames[cardId];
	return printedName(cardId);
}
/** Printed card name when the index knows it; falls back to the raw id so nothing is blank. */
function cardLabel(id?: string): string {
	if (!id) return 'a card';
	return seenCardNames[id] || printedName(id) || id;
}
function statusWord(s: string | null | undefined): string {
	const t = String(s || '').toLowerCase();
	if (t === 'asleep') return 'Asleep';
	if (t === 'burned') return 'Burned';
	if (t === 'confused') return 'Confused';
	if (t === 'paralyzed') return 'Paralyzed';
	if (t === 'poisoned') return 'Poisoned';
	return s || '';
}
/** Special Conditions as Showdown-style short badges (class = PS statbar colour). */
function statusBadges(mon: TcgPokemonView): { id: string, label: string, title: string }[] {
	const out: { id: string, label: string, title: string }[] = [];
	const main = String(mon.status || '').toLowerCase();
	if (main === 'asleep') out.push({ id: 'slp', label: 'SLP', title: 'Asleep' });
	else if (main === 'paralyzed') out.push({ id: 'par', label: 'PAR', title: 'Paralyzed' });
	else if (main === 'confused') out.push({ id: 'cnf', label: 'CNF', title: 'Confused' });
	else if (main === 'poisoned') out.push({ id: 'psn', label: 'PSN', title: 'Poisoned' });
	else if (main === 'burned') out.push({ id: 'brn', label: 'BRN', title: 'Burned' });
	else if (main) out.push({ id: 'other', label: main.slice(0, 3).toUpperCase(), title: statusWord(main) });
	if (mon.poisoned && main !== 'poisoned') out.push({ id: 'psn', label: 'PSN', title: 'Poisoned' });
	if (mon.burned && main !== 'burned') out.push({ id: 'brn', label: 'BRN', title: 'Burned' });
	return out;
}
function clusterSkip(t: string): boolean {
	return t === 'request' || t === 'act' || t === 'coin' || isHitEvent(t);
}
/** An event that lands on a Pokémon as part of an attack's result: played as one of the attack's hits. */
function isHitEvent(t: string): boolean {
	return t === 'damage' || t === 'heal' || t === 'status' || t === 'noDamage' || t === 'prevent' || t === 'moveCounters';
}
/** "Weakness ×2", "Resistance −30": the steps of an attack's damage calculation a player reads off the card. */
export function weaknessNote(calc?: { steps?: { stage?: string, op?: string, value?: number }[] }): string {
	const out: string[] = [];
	for (const st of calc?.steps || []) {
		const v = Number(st.value) || 0;
		if (st.stage === 'weakness') out.push(st.op === 'multiply' ? `Weakness ×${v}` : `Weakness +${v}`);
		else if (st.stage === 'resistance') out.push(`Resistance −${Math.abs(v)}`);
	}
	return out.join(' · ');
}
/** What an attack did to one Pokémon, from one of its result events. */
export function hitFromEvent(
	n: TcgEvent, ctx: { src: string, label: string, element: string, fromIid?: string, style?: HitStyle, travel?: boolean }
): FxHit | null {
	const base = { src: ctx.src, label: ctx.label, fromIid: ctx.fromIid, hitStyle: ctx.style, travel: ctx.travel };
	const counters = (amount: unknown) => Math.max(1, Math.round((Number(amount) || 0) / 10));
	const by = (p?: { source?: { name?: string } }) => p?.source?.name || '';
	if (n.type === 'damage') {
		if (n.counters) {
			return {
				...base, iid: n.iid, amount: n.amount, kind: 'counters', counters: counters(n.amount),
				element: ctx.element || 'colorless',
			};
		}
		const note = weaknessNote(n.calc);
		return {
			...base, iid: n.iid, amount: n.amount, kind: 'damage', element: ctx.element || 'colorless',
			...(note ? { note } : {}),
		};
	}
	if (n.type === 'heal') return { ...base, iid: n.iid, amount: n.amount, kind: 'heal' };
	if (n.type === 'status') {
		return {
			iid: n.iid, kind: 'status', src: ctx.src,
			status: n.status == null ? '' : String(n.status).toLowerCase(),
			label: n.status ? statusWord(String(n.status)) : 'Recovered',
		};
	}
	if (n.type === 'noDamage') {
		const stopped = by(n.prevention);
		const prevented = n.outcome === 'prevented';
		return {
			...base, iid: n.iid, amount: 0, kind: prevented ? 'prevented' : 'reduced',
			note: prevented ? (stopped ? `Prevented · ${stopped}` : 'Prevented') : 'No damage',
		};
	}
	if (n.type === 'prevent') {
		const what = n.what === 'heal' ? 'Heal' :
			n.what === 'status' ? (n.status ? statusWord(String(n.status)) : 'Condition') : 'Effects';
		const stopped = by(n.prevention);
		return { ...base, iid: n.iid, kind: 'prevented', note: `${what} prevented${stopped ? ` · ${stopped}` : ''}` };
	}
	if (n.type === 'moveCounters') {
		return { ...base, iid: n.iid, amount: n.amount, kind: 'moved', counters: counters(n.amount), fromMon: n.fromIid };
	}
	return null;
}
/** A hit that leaves something to see: a number, a shield, a status. A 0-damage or 0-heal row shows nothing. */
function visibleHit(h: FxHit): boolean {
	return !((h.kind === 'damage' || h.kind === 'heal') && !(h.amount || 0));
}
/** A hit aimed at its Pokémon from the attacker (a heal or a counter move is not aimed). */
function aimedHit(h: FxHit, attacker?: string): boolean {
	return h.iid !== attacker && h.kind !== 'heal' && h.kind !== 'moved' && h.kind !== 'status' && visibleHit(h);
}
function afterKind(events: TcgEvent[], i: number, kind: string): boolean {
	for (let j = i - 1; j >= 0; j--) {
		const p = events[j];
		if (!p) return false;
		const t = String(p.type || '');
		if (t === kind) return true;
		// A coin is its own beat. Damage and status after it are the flip's result.
		if (t === 'coin') return false;
		if (clusterSkip(t)) continue;
		return false;
	}
	return false;
}
/** Pocket energy names. "electric" and "steel" are accepted as aliases. */
function energyElement(raw?: string): string {
	const t = String(raw || '').toLowerCase();
	if (t === 'electric' || t === 'lightning') return 'lightning';
	if (t === 'steel' || t === 'metal') return 'metal';
	if (t === 'dark' || t === 'darkness') return 'darkness';
	if (t === 'normal' || t === 'colorless') return 'colorless';
	if ([
		'grass', 'fire', 'water', 'psychic', 'fighting', 'fairy', 'dragon',
	].includes(t)) return t;
	return '';
}
function attackElement(e: TcgEvent, players?: TcgPlayerView[]): string {
	const fromEvent = energyElement(e.element || e.energyType || e.attackType || e.dmgType);
	if (fromEvent) return fromEvent;
	const mon = findMonView(players, e.iid);
	const fromMon = energyElement(mon?.types?.[0]);
	if (fromMon) return fromMon;
	const named = mon?.attacks?.find(a => a.name && e.name && a.name === e.name);
	const paid = named?.cost?.map(energyElement).find(c => c && c !== 'colorless');
	return paid || 'colorless';
}

/** Mid-board projectile. Contact / melee families stay on the target card. */
const HIT_TRAVEL: { [k in HitStyle]?: boolean } = {
	beam: true, flame: true, torrent: true, bolt: true, zap: true, bloom: true, pulse: true,
	veil: true, sparkle: true, roar: true, whirl: true, drain: true, shard: true,
	frost: true, stream: true, sting: true, powder: true, wave: true, petal: true,
	bubble: true, sonic: true, mud: true, note: true, aura: true,
	acid: true, wisp: true, meteor: true,
	swift: true, gold: true, daze: true, sand: true, bone: true, ink: true,
	tri: true, shade: true, kiss: true, breath: true, cannon: true,
	boom: true, web: true, arrow: true, sleep: true,
};
const HIT_TYPE_DEFAULT: { [type: string]: HitStyle } = {
	fire: 'flame', water: 'torrent', lightning: 'bolt', grass: 'bloom',
	psychic: 'pulse', fighting: 'quake', darkness: 'veil', metal: 'shard',
	fairy: 'sparkle', dragon: 'roar', colorless: 'impact',
};
/** Printed attack name → animation. Unlisted names use the Pokémon's type. */
function resolveHitStyle(name?: string, element?: string): { style: HitStyle, travel: boolean } {
	const mapped = lookupAttackHit(String(name || ''));
	if (mapped) return { style: mapped, travel: !!HIT_TRAVEL[mapped] };
	const el = energyElement(element) || String(element || '').toLowerCase() || 'colorless';
	const style = HIT_TYPE_DEFAULT[el] || 'impact';
	return { style, travel: !!HIT_TRAVEL[style] };
}

export function attackHits(events: TcgEvent[], i: number, players?: TcgPlayerView[]): FxHit[] {
	const start = events[i];
	if (!start || (start.type !== 'attack' && start.type !== 'ability')) return [];
	const src = start.type === 'ability' ? 'ability' : 'attack';
	const label = start.name || (src === 'ability' ? 'Ability' : 'Attack');
	const element = src === 'attack' ? attackElement(start, players) :
		(energyElement(start.element) || '');
	const resolved = resolveHitStyle(label, element || 'psychic');
	const ctx = { src, label, element, fromIid: start.iid, style: resolved.style, travel: resolved.travel };
	const hits: FxHit[] = [];
	for (let j = i + 1; j < events.length; j++) {
		const n = events[j];
		if (!n) break;
		const t = String(n.type || '');
		if (t === 'request' || t === 'act') continue;
		// Stop at the flip, or at anything that is not a result (a switch, a discard): those are beats of their own,
		// and the hits after them still play as this attack's (`attackCtx`).
		if (t === 'coin' || !isHitEvent(t)) break;
		const h = hitFromEvent(n, ctx);
		if (h) hits.push(h);
	}
	return hits;
}
/** Indexes of the damage / heal / status events attackHits walks, in the same order. */
function attackHitIndexes(events: TcgEvent[], i: number): number[] {
	const out: number[] = [];
	for (let j = i + 1; j < events.length; j++) {
		const t = events[j]?.type;
		if (t === 'request' || t === 'act') continue;
		if (t === 'coin') break;
		if (isHitEvent(String(t || ''))) out.push(j);
		else break;
	}
	return out;
}
/** Cause of a draw for Live-style left rail (trainer / attack / ability / …). Null = turn/opening draw. */
function drawCause(
	events: TcgEvent[],
	i: number,
	players?: TcgPlayerView[]
): { kind: string, name: string, cardId?: string } | null {
	const e = events[i];
	if (!e || e.type !== 'draw') return null;
	if (e.source?.kind && e.source.kind !== 'turn' && e.source.kind !== 'play' && e.source.kind !== 'checkup') {
		const name = e.source.name || (e.source.cardId ? cardLabel(e.source.cardId) : '');
		if (name) return { kind: e.source.kind, name, cardId: e.source.cardId };
	}
	for (let j = i - 1; j >= 0; j--) {
		const p = events[j];
		if (!p) return null;
		const t = String(p.type || '');
		if (t === 'request' || t === 'act' || t === 'coin') continue;
		if (t === 'draw') continue;
		if (t === 'damage' || t === 'heal' || t === 'status' || t === 'ko' || t === 'place') continue;
		if (t === 'shuffle' || t === 'toDiscard' || t === 'toLost' || t === 'fromDiscard') continue;
		if (t === 'turn' || t === 'deal' || t === 'checkup' || t === 'first' || t === 'start' || t === 'over') {
			return null;
		}
		if (t === 'trainer' || t === 'stadium') {
			return { kind: t, name: cardLabel(p.cardId), cardId: p.cardId || undefined };
		}
		if (t === 'attack' || t === 'ability') {
			const mon = findMonView(players, p.iid);
			return {
				kind: t,
				name: p.name || (t === 'ability' ? 'Ability' : 'Attack'),
				cardId: mon?.cardId || undefined,
			};
		}
		if (t === 'energy' || t === 'tool') {
			return { kind: t, name: cardLabel(p.cardId), cardId: p.cardId || undefined };
		}
		return null;
	}
	return null;
}
export function skipEvent(events: TcgEvent[], i: number): boolean {
	const e = events[i];
	if (!e) return true;
	if (e.type === 'fromHand' || e.type === 'gameEnd' || e.type === 'field' || e.type === 'hp' || e.type === 'energyZone') return true;
	// "takes N Prize cards" is only a log line. The card flip is the later prizeTake.
	if (e.type === 'prize') return true;
	if (e.type === 'request') return e.kind !== 'search' && e.kind !== 'mulligan';
	if (e.type === 'act') {
		const t = e.action?.type;
		return t !== 'discardPick' && t !== 'searchDone';
	}
	if (isHitEvent(e.type) && (afterKind(events, i, 'attack') || afterKind(events, i, 'ability'))) return true;
	return false;
}
/**
 * Motion that only moves things (a card gliding to its new spot, an attack lunge, a ticking HP bar) is skipped for a
 * reduced-motion preference as well as for the noanim option. The beats still play in order, so each step shows.
 */
export function tcgReduceMotion(): boolean {
	if (PS.prefs.noanim) return true;
	try {
		return !!window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
	} catch {
		return false;
	}
}
/** Applied to FX waits and `--fx-t` so CSS and the beat clock stay in sync. */
function tcgFxScale(): number {
	if (PS.prefs.noanim) return 0;
	const s = PS.prefs.tcgfxspeed;
	if (s === 'slow') return 2.4;
	if (s === 'fast') return 0.7;
	// Normal: ~+1s vs the old 1.0 clock (hit 1.1s → ~2.1s).
	return 1.9;
}
function fxMs(n: number): number {
	return Math.max(50, Math.round(n * (tcgFxScale() || 1)));
}
function hasOpeningFlipEvents(events?: TcgEvent[] | null) {
	if (!events) return false;
	for (let i = 0; i < events.length; i++) {
		const t = events[i].type;
		if (t === 'coin' || t === 'first') return true;
	}
	return false;
}
function fxDuration(e: TcgEvent): number {
	if (!e) return fxMs(850);
	if (e.type === 'request' && (e.kind === 'search' || e.kind === 'mulligan')) return fxMs(900);
	if (e.type === 'act' && e.action?.type === 'searchDone') return fxMs(1200);
	if (e.type === 'act' && e.action?.type === 'discardPick') return fxMs(1200);
	const t = e.type;
	if (t === 'request' || t === 'act') return 0;
	if (t === 'checkup') return fxMs(700);
	if (t === 'start') return fxMs(900);
	if (t === 'turn') return fxMs(500);
	if (t === 'first') return fxMs(2400);
	if (t === 'coin') return fxMs(2200);
	if (t === 'attack' || t === 'ability') return fxMs(1200);
	if (t === 'damage' || t === 'heal') return fxMs(1100);
	if (t === 'ko') return fxMs(1600);
	if (t === 'over') return fxMs(2800);
	if (t === 'prize' || t === 'prizeTake') return fxMs(900);
	if (t === 'points') return fxMs(1400);
	if (t === 'find' || t === 'reveal') return fxMs(1400);
	if (t === 'draw') return fxMs(850);
	if (t === 'deal') return fxMs(1400);
	if (t === 'shuffleHand') return fxMs(1200);
	if (t === 'shuffle') return fxMs(e.from === 'hand' ? 1200 : 500);
	if (t === 'toDiscard' || t === 'toLost') return 0;
	if (t === 'trainer' || t === 'stadium') return fxMs(1400);
	if (t === 'stadiumEnd') return fxMs(1200);
	if (t === 'energy' || t === 'tool') return fxMs(1200);
	if (t === 'place') return fxMs(1100);
	if (t === 'evolve') return fxMs(1400);
	if (t === 'discard') return fxMs(900);
	return fxMs(900);
}
/** Name of the card or condition that caused an event. Empty for the player's own play. */
function sourceName(src?: { kind?: string, name?: string, cardId?: string } | null): string {
	if (!src || !src.kind) return '';
	const kind = src.kind;
	if (kind === 'play' || kind === 'turn' || kind === 'search' || kind === 'promote' || kind === 'retreat' || kind === 'evolve') {
		return '';
	}
	if (src.name) return src.name;
	if (src.cardId) return cardLabel(src.cardId);
	if (kind === 'poison') return 'Poison';
	if (kind === 'burn') return 'Burn';
	if (kind === 'asleep') return 'Sleep';
	if (kind === 'paralyzed') return 'Paralysis';
	if (kind === 'confusion') return 'Confusion';
	if (kind === 'checkup') return 'Pokémon Checkup';
	if (kind === 'ko') return 'a Knock Out';
	return '';
}
function fromSource(e: { source?: { kind?: string, name?: string, cardId?: string }, ids?: string[] }): string {
	if (e.source?.kind === 'ko') return ' for the Knock Out';
	// Playing Switch discards Switch. That is the play, so it does not say "from Switch".
	const ids = Array.isArray(e.ids) ? e.ids.filter(Boolean) : [];
	if (e.source?.cardId && ids.length && ids.every((id) => id === e.source.cardId)) return '';
	const name = sourceName(e.source);
	return name ? ` from ${name}` : '';
}
/** Attack/ability user. Prefer the printing on the event so a same-batch evolve does not rename it. */
function userName(ev: TcgEvent, players?: TcgPlayerView[], batch?: TcgEvent[]): string {
	if (ev.cardId) return cardName(players, ev.cardId) || cardLabel(ev.cardId);
	const evo = (batch || []).find(e => e?.type === 'evolve' && e.iid === ev.iid && e.fromCardId);
	if (evo?.fromCardId) return cardName(players, evo.fromCardId) || cardLabel(evo.fromCardId);
	return monName(players, ev.iid);
}
function bySource(e: { source?: { kind?: string, name?: string, cardId?: string } }): string {
	const name = sourceName(e.source);
	return name ? ` by ${name}` : '';
}
/** The `place` rows right after `setupEnd` reveal the setup Pokémon already on the board; they are not placed again. */
function setupRevealAt(events: TcgEvent[] | undefined, i: number): boolean {
	if (!events || i < 0) return false;
	let j = i - 1;
	while (j >= 0 && events[j]?.type === 'place') j--;
	return events[j]?.type === 'setupEnd';
}
function placeTalk(
	e: TcgEvent, players?: TcgPlayerView[], reveal = false
): { tag: string, extra: string, text: string } {
	const actor = whoName(players, e.seat);
	const bench = e.slot !== 'active' && e.slot != null && e.slot !== '';
	if (reveal) {
		const shown = e.name || cardName(players, e.cardId, e.iid) || cardLabel(e.cardId);
		const where = bench ? 'on the Bench' : 'in the Active Spot';
		return { tag: 'REVEAL', extra: shown, text: `${actor} revealed ${shown} ${where}.` };
	}
	if (e.faceDown) {
		// Setup: the opponent's Pokémon stay face down until both players are ready.
		return bench ?
			{ tag: 'BENCH', extra: 'Face down', text: `${actor} put a Pokémon face down on the Bench.` } :
			{ tag: 'ACTIVE', extra: 'Face down', text: `${actor} put a Pokémon face down in the Active Spot.` };
	}
	const name = e.name || cardName(players, e.cardId, e.iid) || cardLabel(e.cardId);
	const kind = e.source?.kind || '';
	const by = sourceName(e.source);
	if (kind === 'retreat') {
		return bench ?
			{ tag: 'BENCH', extra: name, text: `${name} retreated to the Bench.` } :
			{ tag: 'RETREAT', extra: name, text: `${name} retreated to the Active Spot.` };
	}
	if (kind === 'promote') {
		return { tag: 'ACTIVE', extra: name, text: `${name} was sent to the Active Spot.` };
	}
	if (by && (kind === 'attack' || kind === 'ability' || kind === 'trainer' || kind === 'stadium' || kind === 'tool')) {
		return bench ?
			{ tag: 'BENCH', extra: name, text: `${name} was switched to the Bench by ${by}.` } :
			{ tag: 'ACTIVE', extra: name, text: `${name} was switched to the Active Spot by ${by}.` };
	}
	if (bench) return { tag: 'BENCH', extra: name, text: `${actor} benched ${name}.` };
	return { tag: 'ACTIVE', extra: name, text: `${name} came into the Active Spot.` };
}
function dmgSrc(events: TcgEvent[], i: number, players?: TcgPlayerView[]): { key: string, label: string } {
	const e = events[i];
	if (!e || e.type !== 'damage') return { key: 'effect', label: 'Damage' };
	const named = sourceName(e.source);
	if (e.source?.kind && named) return { key: e.source.kind, label: named };
	for (let j = i - 1; j >= 0; j--) {
		const p = events[j];
		if (!p) break;
		const t = String(p.type || '');
		if (clusterSkip(t) && t !== 'damage' && t !== 'heal' && t !== 'status') continue;
		if (t === 'attack') return { key: 'attack', label: p.name || 'Attack' };
		if (t === 'ability') return { key: 'ability', label: p.name || 'Ability' };
		if (t === 'trainer') return { key: 'trainer', label: cardLabel(p.cardId) };
		if (t === 'checkup') {
			const mon = findMonView(players, e.iid);
			if (mon?.poisoned || mon?.status === 'poisoned') return { key: 'poison', label: 'Poison' };
			if (mon?.burned || mon?.status === 'burned') return { key: 'burn', label: 'Burn' };
			return { key: 'checkup', label: 'Pokémon Checkup' };
		}
		break;
	}
	return { key: 'effect', label: 'Damage' };
}

export type TcgChatEntry = { kind: string, label: string, text: string, turn?: number, seat?: number };

/**
 * "their deck" / "their discard pile" / "their Prize cards" for a search. The sim only tells the
 * searching seat which zone it is (snapshot.pendingSearch), so the other side gets a neutral phrase.
 */
function searchZoneText(snap: TcgSnapshot | null | undefined, seat: number | undefined, own: boolean): string {
	const whose = own ? 'your' : 'their';
	const zone = snap?.you != null && seat === snap.you ? snap.pendingSearch?.zone : undefined;
	if (zone === 'discard') return `${whose} discard pile`;
	if (zone === 'prizes' || zone === 'prize') return `${whose} Prize cards`;
	if (zone === 'deck') return `${whose} deck`;
	return 'for a card';
}

function damageModifierNote(mods: { delta?: number, summary?: string }[] | undefined): string {
	if (!Array.isArray(mods) || !mods.length) return '';
	const parts = mods.map(m => {
		if (m.summary) return m.summary;
		const d = Number(m.delta);
		if (!d) return '';
		return d > 0 ? `+${d}` : `−${Math.abs(d)}`;
	}).filter(Boolean);
	return parts.length ? ` (${parts.join(', ')})` : '';
}

/** One `calc` step, as in the event reference: the arithmetic and the card or rule that did it. */
function calcStepNote(step: { op?: string, value?: number, reason?: string, source?: { kind?: string, name?: string, cardId?: string } }): string {
	const value = Number(step.value) || 0;
	const op = step.op;
	const amount = op === 'multiply' ? `×${value}` :
		op === 'subtract' || value < 0 ? `−${Math.abs(value)}` :
		`+${value}`;
	const named: { [k: string]: string } = {
		weakness: 'Weakness', resistance: 'Resistance', plusPower: 'PlusPower',
		defender: 'Defender', lessNext: 'taking less',
	};
	const who = sourceName(step.source) || named[String(step.reason || '')] || '';
	return who ? `${amount} ${who}` : amount;
}

/** `calc.steps` is the complete account. `modifiers` is the older flag list, used when there is no calc. */
function damageBreakdown(ev: { calc?: { steps?: unknown[] }, modifiers?: { delta?: number, summary?: string }[] }): string {
	const steps = Array.isArray(ev.calc?.steps) ? ev.calc.steps : [];
	const fromCalc = steps.map(s => calcStepNote(s as { op?: string, value?: number, reason?: string, source?: { kind?: string, name?: string, cardId?: string } })).filter(Boolean);
	if (fromCalc.length) return ` (${fromCalc.join(', ')})`;
	return damageModifierNote(ev.modifiers);
}

function dealCount(e: { counts?: number[] }): number {
	const counts = Array.isArray(e.counts) ? e.counts : [];
	return counts.length ? Number(counts[0]) || 0 : 7;
}

/** English battle chat from events (graphics.md / Unreal-Bot chatLine). */
/** Turn order for a seat once the opening choice is made: `''` while the coin winner is still choosing. */
export function turnOrderOf(snap: TcgSnapshot, seat: number): '' | 'first' | 'second' {
	if (snap.pendingFirst != null || (snap.firstPlayer !== 0 && snap.firstPlayer !== 1)) return '';
	return snap.firstPlayer === seat ? 'first' : 'second';
}

/**
 * The setup announcement in the middle of the board: who goes first, from the viewer's side. Empty outside setup, while
 * the coin winner is still choosing, or while the coin clip still hides the result (`hold`).
 */
export function firstPlayerBanner(snap: TcgSnapshot, you: number | null | undefined, hold = false): string {
	if (hold || snap.status !== 'setup') return '';
	if (!turnOrderOf(snap, 0)) return '';
	const first = snap.firstPlayer!;
	if (you === first) return 'You go first';
	return `${snap.players?.[first]?.name || 'Your opponent'} goes first`;
}

/** What the setup banner must not cover. An empty slot is fine: the banner lets touches and drops through. */
const FIRST_BANNER_BLOCKS = '.tcg-mon:not([data-drop-empty="1"]), .tcg-pile, .tcg-prizes, .tcg-lost-zone, .tcg-stadium, .tcg-player, .tcg-hud-top > *, ' +
	'.tcg-ezone, .tcg-live-controls > *, .tcg-float-hint, .tcg-wait-banner, .tcg-hand-mine .tcg-card';

/** The batch is a setup take-back by that seat: its leavePlay and private find are one line. */
function setupReturnBatch(batch: TcgEvent[] | undefined, seat: unknown): boolean {
	return !!batch?.some(e => e.type === 'act' && e.seat === seat && e.action?.type === 'setupReturn');
}

export function chatEntryForEvent(
	ev: TcgEvent, players?: TcgPlayerView[], snap?: TcgSnapshot | null, batch?: TcgEvent[],
): TcgChatEntry | null {
	const w = (seat: number) => whoName(players, seat);
	const poke = (iid?: string) => monName(players, iid);
	const nm = (id?: string) => cardName(players, id) || cardLabel(id);
	switch (ev.type) {
	case 'act':
		if (ev.action?.type === 'discardPick') {
			return { kind: 'trainer', label: 'Discard', text: `${w(ev.seat)} discarded a card.`, seat: ev.seat };
		}
		if (ev.action?.type === 'searchDone') {
			// A private look (Metal Maker, Dusk Ball with nothing found) closes without a shuffle: say nothing untrue.
			if (batch && !batch.some(e => e.type === 'shuffle' && e.seat === ev.seat)) return null;
			return { kind: 'draw', label: 'Search', text: `${w(ev.seat)} shuffled their deck.`, seat: ev.seat };
		}
		if (ev.action?.type === 'chooseFirst') {
			// The coin winner's choice, as the engine applied it: name the player who goes first, before setup.
			const first = ev.action.goFirst ? ev.seat : (ev.seat === 0 ? 1 : 0);
			return { kind: 'setup', label: 'First', text: `${w(first)} will go first.`, seat: first };
		}
		return null;
	case 'request':
		if (ev.kind === 'search') {
			const seat = (ev.waiting?.[0]) ?? 0;
			const own = snap?.you != null && seat === snap.you;
			return {
				kind: 'draw', label: 'Search',
				text: own ?
					`You are searching ${searchZoneText(snap, seat, true)}.` :
					`${w(seat)} is searching ${searchZoneText(snap, seat, false)}.`,
				seat,
			};
		}
		if (ev.kind === 'discardEnergy') {
			const seat = (ev.waiting?.[0]) ?? 0;
			return {
				kind: 'discard', label: 'Energy',
				text: `${w(seat)} chooses an Energy to discard.`,
				seat,
			};
		}
		if (ev.kind === 'mulligan') {
			const seat = (ev.waiting?.[0]) ?? 0;
			const other = seat === 0 ? 1 : 0;
			return {
				kind: 'draw', label: 'Mulligan',
				text: `${w(other)} took a mulligan. ${w(seat)} may bench Basics from their extra cards.`,
				seat,
			};
		}
		return null;
	case 'start':
		// The room header already prints the format's display name; the raw id adds nothing.
		return null;
	case 'first':
		return {
			kind: 'setup', label: 'First',
			text: ev.chooses ?
				`${w(ev.seat)} won the coin flip and chooses who goes first.` :
				`${w(ev.seat)} goes first.`,
			seat: ev.seat,
		};
	case 'deal': {
		const dealt = dealCount(ev);
		return { kind: 'setup', label: 'Deal', text: `Each player drew ${dealt} cards.` };
	}
	case 'coin': {
		const why = sourceName(ev.source);
		const face = ev.heads ? 'Heads.' : 'Tails.';
		return { kind: 'coin', label: 'Coin', text: why ? `${why}: ${face}` : `Coin flip: ${face}` };
	}
	case 'place': {
		// The batch may not hold this row yet (it is logged as it arrives): it then follows the batch's last row.
		const at = batch ? batch.findIndex(x => x === ev || (ev.seq != null && x.seq === ev.seq)) : -1;
		const reveal = at >= 0 ? setupRevealAt(batch, at) : !!batch && setupRevealAt([...batch, ev], batch.length);
		const talk = placeTalk(ev, players, reveal);
		const label = talk.tag === 'BENCH' ? 'Bench' : talk.tag === 'RETREAT' ? 'Retreat' : 'Active';
		return {
			kind: reveal ? 'setup' : 'switch',
			label: reveal ? 'Reveal' : label,
			text: talk.text,
			seat: ev.seat,
		};
	}
	case 'evolve': {
		const why = fromSource(ev);
		return {
			kind: 'evolve', label: 'Evolve',
			text: ev.devolved ?
				`${nm(ev.fromCardId)} devolved into ${nm(ev.cardId)}${why}.` :
				ev.fromCardId ?
					`${nm(ev.fromCardId)} evolved into ${nm(ev.cardId)}${why}.` :
					`Evolved into ${nm(ev.cardId)}${why}.`,
			seat: ev.seat,
		};
	}
	case 'attack':
		return { kind: 'attack', label: 'Attack', text: `${userName(ev, players, batch)} used ${ev.name}.` };
	case 'attackFailed': {
		const why = ev.reason === 'confusion' ? 'it is Confused' : ev.reason === 'baby' ? 'of the Baby rule' : 'the flip was tails';
		return { kind: 'attack', label: 'Attack', text: `${ev.name} failed because ${why}.` };
	}
	case 'ability':
		return { kind: 'ability', label: 'Ability', text: `${userName(ev, players, batch)} used ${ev.name}.` };
	case 'trainer':
		return {
			kind: 'trainer', label: 'Play',
			text: ev.errata ?
				`${w(ev.seat)} played ${nm(ev.cardId)} using its official errata.` :
				`${w(ev.seat)} played ${nm(ev.cardId)}.`,
			seat: ev.seat,
		};
	case 'gameEnd':
		return null;
	case 'energyZone': {
		if (!ev.ready) return null;
		const type = String(ev.energyType || 'Energy');
		const label = type.charAt(0).toUpperCase() + type.slice(1);
		return {
			kind: 'energy', label: 'Energy',
			text: `${w(ev.seat)}'s Energy Zone has ${label} Energy.`,
			seat: ev.seat,
		};
	}
	case 'energy': case 'tool': {
		const seat = ev.seat ?? findMonSlot(players, ev.iid)?.seat;
		const why = fromSource(ev);
		return {
			kind: 'energy', label: ev.type === 'tool' ? 'Tool' : 'Energy',
			text: ev.from ?
				`${nm(ev.cardId)} moved from ${poke(ev.from)} to ${poke(ev.iid)}${why}.` :
				`${w(seat)} attached ${nm(ev.cardId)} to ${poke(ev.iid)}${why}.`,
			seat,
		};
	}
	case 'stadium':
		return { kind: 'trainer', label: 'Stadium', text: `${w(ev.seat)} played ${nm(ev.cardId)}.`, seat: ev.seat };
	case 'stadiumEnd':
		return { kind: 'trainer', label: 'Stadium', text: `${nm(ev.cardId)} is no longer in play${fromSource(ev)}.` };
	case 'damage': {
		const counters = ev.counters ? Math.max(1, Math.round((Number(ev.amount) || 0) / 10)) : 0;
		const what = counters ?
			`${counters} damage counter${counters === 1 ? '' : 's'}` :
			`${ev.amount} damage`;
		return {
			kind: 'damage', label: 'Damage',
			text: `${poke(ev.iid)} took ${what}${damageBreakdown(ev)}${fromSource(ev)}.`,
		};
	}
	case 'heal': {
		const asked = ev.requested != null && Number(ev.requested) > Number(ev.amount);
		return {
			kind: 'heal', label: 'Heal',
			text: asked ?
				`${poke(ev.iid)} healed ${ev.amount} of ${ev.requested} damage${fromSource(ev)}.` :
				`${poke(ev.iid)} healed ${ev.amount} damage${fromSource(ev)}.`,
		};
	}
	case 'moveCounters': {
		const counters = Math.max(1, Math.round((Number(ev.amount) || 0) / 10));
		const why = fromSource(ev);
		return {
			kind: 'damage', label: 'Counters',
			text: ev.swap ?
				`${poke(ev.fromIid)} and ${poke(ev.iid)} switched damage counters${why}.` :
				`${poke(ev.fromIid)} moved ${counters} damage counter${counters === 1 ? '' : 's'} to ${poke(ev.iid)}${why}.`,
		};
	}
	case 'noDamage': {
		const stopped = bySource({ source: ev.prevention?.source });
		const tried = fromSource(ev);
		const breakdown = damageBreakdown(ev);
		return {
			kind: 'damage', label: 'No damage',
			text: ev.outcome === 'prevented' ?
				`The damage to ${poke(ev.iid)}${tried} was prevented${stopped}${breakdown}.` :
				`${poke(ev.iid)} took no damage${breakdown}${tried}.`,
		};
	}
	case 'prevent': {
		const what = ev.what === 'heal' ?
			(ev.amount != null ? `heal of ${ev.amount}` : 'heal') :
			ev.what === 'status' ? (ev.status ? statusWord(ev.status) : 'Special Condition') :
			"attack's effects";
		const verb = ev.what === 'attackEffects' ? 'were' : 'was';
		return {
			kind: 'note', label: 'Prevented',
			text: `The ${what} on ${poke(ev.iid)}${fromSource(ev)} ${verb} prevented${bySource({ source: ev.prevention?.source })}.`,
		};
	}
	case 'status':
		return {
			kind: 'status', label: 'Status',
			text: ev.status ?
				`${poke(ev.iid)} is ${statusWord(ev.status)}${fromSource(ev)}.` :
				`${poke(ev.iid)} recovered from Special Conditions${fromSource(ev)}.`,
		};
	case 'ko':
		return { kind: 'ko', label: 'KO', text: `${poke(ev.iid)} was Knocked Out${bySource(ev)}.`, seat: ev.seat };
	case 'leavePlay': {
		if (ev.to === 'hand' && setupReturnBatch(batch, ev.seat)) {
			return {
				kind: 'note', label: 'Setup',
				text: `${w(ev.seat)} returned ${ev.cardId ? nm(ev.cardId) : 'a face-down Pokémon'} to their hand.`,
				seat: ev.seat,
			};
		}
		const where =
			ev.to === 'hand' ? 'into the hand' :
			ev.to === 'deck' || ev.to === 'deckTop' ? 'into the deck' :
			ev.to === 'lost' ? 'into the Lost Zone' :
			'into the discard pile';
		const by = sourceName(ev.source);
		const who = nm(ev.cardId) || poke(ev.iid);
		return { kind: 'note', label: 'Left play', text: `${who} went ${where}${by ? ` by ${by}` : ''}.`, seat: ev.seat };
	}
	case 'shuffle': {
		const why = fromSource(ev);
		return {
			kind: 'note', label: 'Shuffle',
			text: ev.from === 'hand' ?
				`${w(ev.seat)} shuffled their hand into the deck${why}.` :
				`${w(ev.seat)} shuffled their deck${why}.`,
			seat: ev.seat,
		};
	}
	case 'toDiscard': {
		const ids = Array.isArray(ev.ids) ? ev.ids.filter(Boolean) : [];
		const named = ids.length ? ids.map(nm).join(', ') : '';
		return {
			kind: 'discard', label: 'Discard',
			text: named ? `${w(ev.seat)} discarded ${named}${fromSource(ev)}.` :
			`${w(ev.seat)} discarded ${ids.length || 1} card${(ids.length || 1) === 1 ? '' : 's'}${fromSource(ev)}.`,
			seat: ev.seat,
		};
	}
	case 'fromDiscard': {
		const ids = Array.isArray(ev.ids) ? ev.ids.filter(Boolean) : [];
		const named = ids.length ? ids.map(nm).join(', ') : '';
		return {
			kind: 'discard', label: 'Discard',
			text: named ? `${w(ev.seat)} took ${named} from the discard pile${fromSource(ev)}.` :
			`${w(ev.seat)} took a card from the discard pile${fromSource(ev)}.`,
			seat: ev.seat,
		};
	}
	case 'toDeck': {
		const ids = Array.isArray(ev.ids) ? ev.ids.filter(Boolean) : [];
		const named = ids.length ? ids.map(nm).join(', ') : '';
		const fromHand = ev.from === 'hand';
		return {
			kind: 'note', label: 'Deck',
			text: fromHand ?
				`${w(ev.seat)} put ${named || 'a card'} into the deck${fromSource(ev)}.` :
				`${w(ev.seat)} shuffled ${named || 'a card'} into the deck${fromSource(ev)}.`,
			seat: ev.seat,
		};
	}
	case 'toLost':
		return {
			kind: 'note', label: 'Lost Zone',
			text: (ev.n || 1) === 1 ?
				`${w(ev.seat)} put a card in the Lost Zone${fromSource(ev)}.` :
				`${w(ev.seat)} put ${ev.n} cards in the Lost Zone${fromSource(ev)}.`,
			seat: ev.seat,
		};
	case 'draw': {
		const n = ev.n || 1;
		const ids = Array.isArray(ev.ids) ? ev.ids.filter(Boolean) : [];
		const named = ids.length && ids.length === n ? ids.map(nm).join(', ') : '';
		return {
			kind: 'draw', label: 'Draw',
			text: named ? `${w(ev.seat)} drew ${named}${fromSource(ev)}.` :
			n === 1 ? `${w(ev.seat)} drew a card${fromSource(ev)}.` : `${w(ev.seat)} drew ${n} cards${fromSource(ev)}.`,
			seat: ev.seat,
		};
	}
	case 'reveal': {
		const ids = Array.isArray(ev.ids) ? ev.ids.filter(Boolean) : [];
		const names = ids.map(nm).filter(Boolean);
		const list = names.length ? names.join(', ') : 'no cards';
		const what = ev.hand ? `their hand: ${list}` : list;
		return {
			kind: 'note', label: 'Reveal',
			text: `${w(ev.seat)} revealed ${what}${fromSource(ev)}.`,
			seat: ev.seat,
		};
	}
	case 'fromHand':
		return null;
	case 'find': {
		if (!ev.dest && !ev.revealed && setupReturnBatch(batch, ev.seat)) return null;
		const ids = Array.isArray(ev.ids) ? ev.ids.filter(Boolean) : [];
		const why = fromSource(ev);
		const dest = String(ev.dest || 'hand');
		const named = ids.length ? ids.map(nm).join(', ') : '';
		const where =
			dest === 'top' ? 'on top of their deck' :
			dest === 'bench' ? 'onto their Bench' :
			dest === 'deck' ? 'into their deck' :
			dest === 'discard' ? 'into their discard pile' :
			dest === 'lost' ? 'into the Lost Zone' :
			dest === 'attach-active' ? 'onto their Active Pokémon' :
			dest === 'attach' ? 'to be attached' :
			dest === 'replace-active' ? 'into the Active Spot' :
			'into their hand';
		const text = named ?
			`${w(ev.seat)} put ${named} ${where}${why}.` :
			((ev.ids?.length) || ev.n || 1) === 1 ?
				`${w(ev.seat)} put a card ${where}${why}.` :
				`${w(ev.seat)} put ${(ev.ids?.length) || ev.n} cards ${where}${why}.`;
		return { kind: 'draw', label: 'Search', text, seat: ev.seat };
	}
	case 'prize':
		return {
			kind: 'prize', label: 'Prize',
			text: `${w(ev.seat)} takes ${ev.n} Prize card${ev.n === 1 ? '' : 's'}${fromSource(ev)}.`, seat: ev.seat,
		};
	case 'prizeTake':
	{
		const ids = Array.isArray(ev.ids) ? ev.ids.filter(Boolean) : [];
		return {
			kind: 'prize', label: 'Prize',
			text: ids.length ? `${w(ev.seat)} took a Prize card${fromSource(ev)}: ${ids.map(nm).join(', ')}.` :
			`${w(ev.seat)} took a Prize card${fromSource(ev)}.`,
			seat: ev.seat,
		};
	}
	case 'points':
		return {
			kind: 'prize', label: 'Points',
			text: `${w(ev.seat)} scored ${ev.n} point${ev.n === 1 ? '' : 's'}${fromSource(ev)} (total ${ev.total}).`,
			seat: ev.seat,
		};
	case 'turn':
		return {
			kind: 'turn', label: 'Turn', text: `${w(ev.seat)}'s turn`,
			turn: ev.number, seat: ev.seat,
		};
	case 'checkup':
		return { kind: 'note', label: 'Checkup', text: 'Pokémon Checkup.' };
	case 'prizeSet': {
		// Counts only: face-down Prize cards have no ids for anyone.
		const [a, b] = Array.isArray(ev.counts) ? ev.counts.map(Number) : [0, 0];
		const cards = (n: number) => `${n} Prize card${n === 1 ? '' : 's'}`;
		return {
			kind: 'setup', label: 'Prizes',
			text: a === b ? `Each player set aside ${cards(a)}.` : `${w(0)} set aside ${cards(a)}, ${w(1)} ${cards(b)}.`,
		};
	}
	case 'setupEnd':
		// The face-down setup Pokémon are turned up next; each reveal has its own line.
		return { kind: 'setup', label: 'Setup', text: 'Setup is done. Both players reveal their Pokémon.' };
	case 'suddenDeath':
		return {
			kind: 'note', label: 'Tiebreaker',
			text: 'Sudden death. The board is reset and a new game begins.',
		};
	case 'over':
		return {
			kind: 'win', label: 'End',
			text: ev.reason || (ev.winner == null ? 'The game is a draw.' : `${w(ev.winner)} wins.`),
			seat: ev.winner ?? undefined,
		};
	default:
		return null;
	}
}

export function chatLineForEvent(ev: TcgEvent, players?: TcgPlayerView[]): string | null {
	return chatEntryForEvent(ev, players)?.text || null;
}

export function chatHtmlForEntry(entry: TcgChatEntry): string {
	const esc = (s: string) => String(s || '')
		.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
	return `<div class="chat tcg-log-line k-${esc(entry.kind)}">` +
		`<span class="tcg-log-tag">${esc(entry.label)}</span> ` +
		`<span class="tcg-log-text">${esc(entry.text)}</span></div>`;
}

function fxFor(
	e: TcgEvent,
	events: TcgEvent[],
	i: number,
	players?: TcgPlayerView[],
	you?: number | null,
	snap?: TcgSnapshot | null
): FxBeat {
	if (!e) return { kind: '' };
	const who = (seat?: number) => actorLabel(players, seat, you);
	const yours = (seat?: number) => actorIsYou(seat, you);
	if (e.type === 'request' && e.kind === 'discardEnergy') {
		// The card has not left yet. `toDiscard`, `toLost`, or `find` is the beat.
		return { kind: '' };
	}
	if (e.type === 'act' && e.action?.type === 'discardEnergyPick') {
		return { kind: '' };
	}
	if (e.type === 'act' && e.action?.type === 'playVunion') {
		return { kind: '' };
	}
	if (e.type === 'request' && e.kind === 'search') {
		const seat = (e.waiting?.[0] != null) ? e.waiting[0] : 0;
		const zone = searchZoneText(snap, seat, yours(seat));
		return {
			kind: 'search', seat, n: 5,
			extra: `Searching ${zone}`,
			message: yours(seat) ? `You are searching ${zone}` : `${who(seat)} is searching ${zone}`,
		};
	}
	if (e.type === 'request' && e.kind === 'mulligan') {
		// The waiting seat is the one who gets to bench extra Basics; the *other* seat mulliganed.
		const seat = (e.waiting?.[0] != null) ? e.waiting[0] : 0;
		const other = seat === 0 ? 1 : 0;
		return {
			kind: 'mulligan', seat, n: 7,
			extra: `${who(other)} took a mulligan`,
			message: yours(seat) ?
				`${who(other)} took a mulligan — you may bench Basics from your extra cards` :
				`${who(other)} took a mulligan — ${who(seat)} may bench extra Basics`,
		};
	}
	if (e.type === 'act' && e.action?.type === 'searchDone') {
		return {
			kind: 'searchDone', seat: e.seat, n: 5,
			extra: 'Shuffled their deck',
			message: yours(e.seat) ? 'You shuffled your deck' : `${who(e.seat)} shuffled their deck`,
		};
	}
	if (e.type === 'reveal') {
		const ids = Array.isArray(e.ids) ? e.ids.filter(Boolean) as string[] : [];
		const names = ids.map(cardLabel);
		const found = names.length ? names.join(', ') : 'no cards';
		const what = e.hand ? `their hand: ${found}` : found;
		return {
			kind: 'reveal', seat: e.seat, n: ids.length,
			extra: e.hand ? (names.length ? 'Hand' : 'No cards') : (names.length <= 3 ? found : `${names.length} cards`),
			cardId: ids[0] || '', ids, labels: names,
			message: `${who(e.seat)} revealed ${what}`,
		};
	}
	if (e.type === 'find') {
		const ids = Array.isArray(e.ids) ? e.ids.filter(Boolean) as string[] : [];
		const names = ids.map(cardLabel);
		const found = names.length ? names.join(', ') : `${ids.length || 1} card(s)`;
		const dest = String(e.dest || 'hand');
		const where =
			dest === 'top' ? 'on top of the deck' :
			dest === 'bench' ? 'onto the Bench' :
			dest === 'deck' ? 'into the deck' :
			dest === 'discard' ? 'into the discard pile' :
			dest === 'lost' ? 'into the Lost Zone' :
			dest === 'attach-active' ? 'onto the Active Pokémon' :
			dest === 'attach' ? 'to be attached' :
			dest === 'replace-active' ? 'into the Active Spot' :
			'into the hand';
		const tag =
			dest === 'hand' ? 'TO HAND' :
			dest === 'bench' ? 'TO BENCH' :
			dest === 'top' ? 'ON TOP' :
			dest === 'deck' ? 'TO DECK' :
			dest === 'discard' ? 'TO DISCARD' :
			dest === 'lost' ? 'LOST ZONE' :
			dest === 'attach' || dest === 'attach-active' ? 'ATTACH' :
			dest === 'replace-active' ? 'SWITCH' :
			'FOUND';
		// A setup take-back: the `leavePlay` row before this one names the Pokémon going back to the hand.
		const left = events[i - 1]?.type === 'leavePlay' && events[i - 1].seat === e.seat && events[i - 1].to === 'hand' ?
			events[i - 1] : null;
		const findFrom: FlightZoneKind | undefined = e.iid || left ? 'mon' : e.from === 'prizes' ? 'prizes' :
			e.from === 'deck' ? 'deck' : undefined;
		return {
			kind: 'find', seat: e.seat, n: ids.length || 1, tag,
			moveFrom: dest === 'hand' ? findFrom : undefined, moveTo: dest === 'hand' ? 'hand' : undefined,
			fromIid: e.iid || left?.iid || undefined,
			...(left ? { wholeMon: true } : {}),
			extra: names.length ? names.join(', ') : `Put ${where}`,
			cardId: ids[0] || '', ids, labels: names,
			message: yours(e.seat) ?
				`You put ${found} ${where}` :
				`${who(e.seat)} put ${found} ${where}`,
		};
	}
	if (e.type === 'act' && e.action?.type === 'discardPick') {
		return {
			kind: 'discard', seat: e.seat, n: 1,
			extra: 'Discarded a card',
			message: yours(e.seat) ? 'You discarded a card' : `${who(e.seat)} discarded a card`,
		};
	}
	if (e.type === 'place') {
		// The setup reveal: the Pokémon turn over where they are (`revealing`); they do not come from the hand again.
		const reveal = setupRevealAt(events, i);
		const talk = placeTalk(e, players, reveal);
		const slot = placeSlotOf(e);
		const srcKind = e.source?.kind || '';
		const moveFrom: FlightZoneKind | undefined = reveal ? undefined : !srcKind || srcKind === 'play' ? 'hand' :
			placedFromDeck(events, i, e.seat) ? 'deck' : undefined;
		return {
			kind: 'place', iid: e.iid || '', seat: e.seat, slot,
			extra: talk.extra, tag: talk.tag,
			cardId: e.cardId || '',
			message: talk.text,
			moveFrom,
		};
	}
	if (e.type === 'damage') {
		const src = dmgSrc(events, i, players);
		let element = energyElement(e.element || e.energyType || e.attackType || e.dmgType);
		if (!element && src.key === 'attack') {
			for (let j = i - 1; j >= 0; j--) {
				const p = events[j];
				if (!p) break;
				const t = String(p.type || '');
				if (t === 'attack') {
					element = attackElement(p, players);
					break;
				}
				if (clusterSkip(t) && t !== 'damage' && t !== 'heal' && t !== 'status') continue;
				if (t !== 'damage' && t !== 'heal' && t !== 'status') break;
			}
		}
		return {
			kind: 'damage', iid: e.iid || '', seat: e.seat, amount: e.amount,
			extra: src.label, src: src.key,
			element: element || undefined,
			message: `${monName(players, e.iid)} took ${e.counters ? `${Math.max(1, Math.round((Number(e.amount) || 0) / 10))} damage counters` : `${e.amount} damage`}${damageBreakdown(e)}${fromSource(e)}`,
		};
	}
	if (e.type === 'draw') {
		const n = e.n || (Array.isArray(e.ids) ? e.ids.length : 1) || 1;
		const ids = Array.isArray(e.ids) ? e.ids.filter(Boolean) as string[] : [];
		const cause = drawCause(events, i, players);
		const drawLabel = n === 1 ? 'Drew a card' : `Drew ${n} cards`;
		const whoDrew = yours(e.seat) ?
			(n === 1 ? 'You drew a card' : `You drew ${n} cards`) :
			(n === 1 ? `${who(e.seat)} drew a card` : `${who(e.seat)} drew ${n} cards`);
		if (cause) {
			return {
				kind: 'drawEffect',
				seat: e.seat,
				n,
				ids: ids.length ? ids : undefined,
				labels: ids.length ? ids.map(cardLabel) : undefined,
				cardId: cause.cardId || '',
				src: cause.kind,
				extra: cause.name,
				message: `${cause.name} — ${whoDrew}`,
				moveFrom: 'deck', moveTo: 'hand',
			};
		}
		return {
			kind: 'draw', seat: e.seat, n,
			ids: ids.length ? ids : undefined,
			extra: drawLabel,
			message: whoDrew,
		};
	}
	if (e.type === 'shuffle') {
		if (e.from === 'hand') {
			return {
				kind: 'shuffleHand', seat: e.seat, n: 5,
				extra: 'Shuffled hand into the deck',
				message: yours(e.seat) ?
					'You shuffled your hand into the deck' :
					`${who(e.seat)} shuffled their hand into the deck`,
			};
		}
		return {
			kind: 'shuffle', seat: e.seat,
			extra: 'Shuffled their deck',
			message: yours(e.seat) ? 'You shuffled your deck' : `${who(e.seat)} shuffled their deck`,
		};
	}
	if (e.type === 'shuffleHand') {
		return {
			kind: 'shuffleHand', seat: e.seat, n: e.n || 5,
			extra: 'Shuffled hand into the deck',
			message: yours(e.seat) ?
				'You shuffled your hand into the deck' :
				`${who(e.seat)} shuffled their hand into the deck`,
		};
	}
	if (e.type === 'deal') {
		const dealt = dealCount(e);
		const line = `Each player drew ${dealt} cards`;
		const counts = Array.isArray(e.counts) ? e.counts.map(Number) : [dealt, dealt];
		return {
			kind: 'deal', n: dealt,
			extra: line,
			message: line,
			perSeat: [0, 1].map(s => ({ n: counts[s] || 0, ids: (s === 0 ? e.ids0 : e.ids1) as string[] | undefined })),
		};
	}
	if (e.type === 'prizeSet') {
		const counts = Array.isArray(e.counts) ? e.counts.map(Number) : [];
		return { kind: 'prizeSet', perSeat: [0, 1].map(s => ({ n: counts[s] || 0 })), extra: 'Prize cards set aside' };
	}
	if (e.type === 'turn') {
		return {
			kind: 'turn', seat: e.seat, n: Number(e.number) || 0,
			extra: `Turn ${e.number} · ${whoName(players, e.seat)}`,
			message: yours(e.seat) ? 'Your turn' : `${whoName(players, e.seat)}'s turn`,
		};
	}
	if (e.type === 'prize' || e.type === 'prizeTake') {
		const n = e.n || 1;
		const multi = e.type === 'prize' && n > 1;
		// Ids only for the player taking it (the others' rows carry none): the card flies face up for them alone.
		const ids = e.type === 'prizeTake' && Array.isArray(e.ids) ? (e.ids as string[]).filter(Boolean) : [];
		return {
			kind: e.type, seat: e.seat, n,
			...(ids.length ? { ids } : {}),
			extra: multi ? `Took ${n} Prize cards` : 'Took a Prize card',
			message: yours(e.seat) ?
				(multi ? `You took ${n} Prize cards` : 'You took a Prize card') :
				(multi ? `${who(e.seat)} took ${n} Prize cards` : `${who(e.seat)} took a Prize card`),
		};
	}
	if (e.type === 'points') {
		const n = e.n || 1;
		return {
			kind: 'points', seat: e.seat, n, amount: e.total,
			extra: `+${n}`,
			message: yours(e.seat) ?
				`You scored ${n} point${n === 1 ? '' : 's'}` :
				`${who(e.seat)} scored ${n} point${n === 1 ? '' : 's'}`,
		};
	}
	if (e.type === 'evolve') {
		let evoSeat = e.seat as number | undefined;
		if (evoSeat == null && e.iid) {
			const playersList = players || [];
			for (let s = 0; s < playersList.length; s++) {
				const p = playersList[s];
				if (p.active?.iid === e.iid || (p.bench || []).some(m => m?.iid === e.iid)) {
					evoSeat = s;
					break;
				}
			}
		}
		const into = cardLabel(e.cardId);
		return {
			kind: 'evolve',
			iid: e.iid || '',
			seat: evoSeat,
			extra: into,
			cardId: e.cardId || '',
			fromCardId: e.fromCardId || '',
			message: e.devolved ?
				`${cardLabel(e.fromCardId)} devolved into ${into}` :
				e.fromCardId ?
					`${cardLabel(e.fromCardId)} evolved into ${into}` :
					`Evolved into ${into}`,
		};
	}
	if (e.type === 'energy' || e.type === 'tool') {
		const mon = findMonView(players, e.iid);
		const label = cardLabel(e.cardId);
		const energyType = e.type === 'energy' ?
			(energyPipsFromEvent(e)[0] || energyTypeFromCardId(e.cardId) || '') : '';
		// A `tool` row has no seat: it is the seat of the Pokémon it lands on.
		const onSeat = e.seat ?? findMonSlot(players, e.iid)?.seat ?? e.source?.seat;
		return {
			kind: e.type, iid: e.iid || '', seat: onSeat,
			fromIid: e.from || undefined, moveFrom: e.from ? 'mon' : attachOrigin(events, i),
			extra: label, onto: mon?.name || 'a Pokémon',
			ontoId: mon?.cardId || '', cardId: e.cardId || '',
			element: energyType || undefined,
			message: (yours(e.seat) ?
				`You attached ${label} to ${mon?.name || 'a Pokémon'}` :
				`${who(e.seat)} attached ${label} to ${mon?.name || 'a Pokémon'}`) + fromSource(e),
		};
	}
	if (e.type === 'trainer') {
		const label = cardLabel(e.cardId);
		const errata = e.errata ? ' (official errata)' : '';
		return {
			kind: 'trainer', seat: e.seat, extra: label, cardId: e.cardId || '',
			message: (yours(e.seat) ? `You played ${label}` : `${who(e.seat)} played ${label}`) + errata,
		};
	}
	if (e.type === 'gameEnd' || e.type === 'hp') return { kind: '' };
	if (e.type === 'stadium') {
		const label = cardLabel(e.cardId);
		return {
			kind: 'stadium', seat: e.seat, extra: label, cardId: e.cardId || '',
			message: yours(e.seat) ? `You played ${label}` : `${who(e.seat)} played ${label}`,
		};
	}
	if (e.type === 'ko') {
		const memName = monName(players, e.iid);
		return {
			kind: 'ko', iid: e.iid || '', seat: e.seat,
			extra: memName !== 'a Pokémon' ? memName : '',
			message: `${memName} was Knocked Out${bySource(e)}`,
		};
	}
	if (e.type === 'heal') {
		return {
			kind: 'heal', iid: e.iid || '', seat: e.seat, amount: e.amount,
			extra: e.amount ? `+${e.amount}` : 'Healed',
			message: e.requested != null && Number(e.requested) > Number(e.amount || 0) ?
				`${monName(players, e.iid)} healed ${e.amount || 0} of ${e.requested} damage${fromSource(e)}` :
				`${monName(players, e.iid)} healed ${e.amount || 0} damage${fromSource(e)}`,
		};
	}
	if (e.type === 'moveCounters') {
		const counters = Math.max(1, Math.round((Number(e.amount) || 0) / 10));
		return {
			kind: 'status', iid: e.iid || '',
			extra: e.swap ? 'Counters switched' : `${counters} counter${counters === 1 ? '' : 's'} moved`,
			message: (e.swap ?
				`${monName(players, e.fromIid)} and ${monName(players, e.iid)} switched damage counters` :
				`${monName(players, e.fromIid)} moved ${counters} damage counter${counters === 1 ? '' : 's'} to ${monName(players, e.iid)}`) + fromSource(e),
		};
	}
	if (e.type === 'noDamage') {
		const stopped = bySource({ source: e.prevention?.source });
		const tried = fromSource(e);
		const breakdown = damageBreakdown(e);
		return {
			kind: 'status', iid: e.iid || '', extra: 'No damage',
			message: e.outcome === 'prevented' ?
				`The damage to ${monName(players, e.iid)}${tried} was prevented${stopped}${breakdown}` :
				`${monName(players, e.iid)} took no damage${breakdown}${tried}`,
		};
	}
	if (e.type === 'prevent') {
		const what = e.what === 'heal' ?
			(e.amount != null ? `heal of ${e.amount}` : 'heal') :
			e.what === 'status' ? (e.status ? statusWord(e.status) : 'Special Condition') :
			"attack's effects";
		const verb = e.what === 'attackEffects' ? 'were' : 'was';
		return {
			kind: 'status', iid: e.iid || '', extra: 'Prevented',
			message: `The ${what} on ${monName(players, e.iid)}${fromSource(e)} ${verb} prevented${bySource({ source: e.prevention?.source })}`,
		};
	}
	if (e.type === 'attack') {
		return {
			kind: 'attack', iid: e.iid || '', seat: e.seat,
			extra: e.name || 'Attack',
			message: `${userName(e, players, events)} used ${e.name || 'an attack'}`,
		};
	}
	if (e.type === 'ability') {
		return {
			kind: 'ability', iid: e.iid || '', seat: e.seat,
			extra: e.name || 'Ability',
			message: `${userName(e, players, events)} used ${e.name || 'an Ability'}`,
		};
	}
	let seat = e.seat as number | undefined;
	if (seat == null && e.iid) {
		const playersList = players || [];
		for (let s = 0; s < playersList.length; s++) {
			const p = playersList[s];
			if (p.active?.iid === e.iid || (p.bench || []).some(m => m?.iid === e.iid)) {
				seat = s;
				break;
			}
		}
	}
	if (e.type === 'over') {
		const tie = !!(e.tie || (e.winner == null && !e.winnerName && !e.reason));
		const winName = e.winnerName ||
			(e.winner != null ? whoName(players, e.winner) : '') ||
			'';
		const reason = e.reason || (tie ? 'Draw' : (winName ? `${winName} won` : 'Battle ended'));
		return {
			kind: 'over',
			seat: e.winner ?? seat,
			extra: reason,
			message: reason,
			tie,
		};
	}
	if (e.type === 'attackFailed') {
		const why = e.reason === 'confusion' ? 'Confused' : e.reason === 'baby' ? 'Baby rule' : 'Tails';
		return {
			kind: 'attackFailed', iid: e.iid || '', extra: why,
			message: `${e.name || 'The attack'} failed (${why})`,
		};
	}
	if (e.type === 'checkup') {
		return { kind: 'checkup', extra: 'Pokémon Checkup', message: 'Pokémon Checkup' };
	}
	if (e.type === 'suddenDeath') {
		return {
			kind: 'suddenDeath', extra: 'Tiebreaker',
			message: 'Sudden death. The board is reset and a new game begins.',
		};
	}
	if (e.type === 'toDeck') {
		const ids = Array.isArray(e.ids) ? e.ids.filter(Boolean) as string[] : [];
		const named = ids.length ? ids.map(cardLabel).join(', ') : '';
		const fromHand = e.from === 'hand';
		return {
			kind: 'discard',
			seat: e.seat,
			extra: named || 'To the deck',
			message: (fromHand ?
				`${who(e.seat)} put ${named || 'a card'} into the deck` :
				`${who(e.seat)} shuffled ${named || 'a card'} into the deck`) + fromSource(e),
		};
	}
	if (e.type === 'toDiscard' || e.type === 'fromDiscard' || e.type === 'toLost') {
		const ids = Array.isArray(e.ids) ? e.ids.filter(Boolean) as string[] : [];
		const named = ids.length ? ids.map(cardLabel).join(', ') : '';
		const took = e.type === 'fromDiscard';
		// A card taken from the discard pile and then attached or played flies on that row instead.
		const next = events[i + 1]?.type;
		const usedNext = took && (next === 'energy' || next === 'tool' || next === 'place' || next === 'toDeck');
		const from: FlightZoneKind | undefined = took ? (usedNext ? undefined : 'discard') :
			e.iid || e.from === 'play' ? 'mon' : e.from === 'hand' ? 'hand' : e.from === 'deck' ? 'deck' : undefined;
		const koDiscard = !took && !!e.iid && events.slice(Math.max(0, i - 3), i).some(p => p.type === 'ko' && p.iid === e.iid);
		return {
			kind: 'discard',
			seat: e.seat,
			n: ids.length || 1,
			ids: ids.length ? ids : undefined,
			moveFrom: from,
			...(koDiscard ? { koDiscard } : {}),
			moveTo: took ? 'hand' : e.type === 'toLost' ? 'lost' : 'discard',
			fromIid: e.iid || undefined,
			extra: named || (took ? 'From the discard pile' : e.type === 'toLost' ? 'Lost Zone' : 'Discarded'),
			message: (took ?
				`${who(e.seat)} took ${named || 'a card'} from the discard pile` :
				e.type === 'toLost' ?
					`${who(e.seat)} put a card in the Lost Zone` :
					`${who(e.seat)} discarded ${named || 'a card'}`) + fromSource(e),
		};
	}
	return {
		kind: e.type,
		iid: e.iid || '',
		targetIid: e.targetIid || '',
		seat,
		amount: e.type === 'points' && e.total != null ? e.total : e.amount,
		n: e.n,
		extra: e.type === 'coin' ? (e.heads ? 'Heads' : 'Tails') :
		e.type === 'turn' ? `Turn ${e.number} · ${whoName(players, e.seat)}` :
		e.type === 'first' ? (e.chooses ?
			`${whoName(players, e.seat)} chooses who goes first` :
			`${whoName(players, e.seat)} goes first`) :
		e.type === 'status' ? (e.status ? statusWord(e.status) : 'Recovered') :
		e.type === 'stadiumEnd' ? 'No longer in play' :
		(e.name || e.status || e.reason || ''),
		message: e.type === 'coin' ? ((sourceName(e.source) ? `${sourceName(e.source)}: ` : '') + (e.heads ? 'Heads!' : 'Tails!')) :
		e.type === 'first' ? (e.chooses ?
			`${who(e.seat)} chooses who goes first` :
			`${who(e.seat)} goes first`) :
		e.type === 'status' ? (e.status ?
			`${monName(players, e.iid)} is ${statusWord(e.status)}${fromSource(e)}` :
			`${monName(players, e.iid)} recovered${fromSource(e)}`) :
		e.type === 'stadiumEnd' ? `${cardLabel(e.cardId)} is no longer in play${fromSource(e)}` :
		undefined,
		cardId: e.cardId || '',
		coins: e.type === 'coin' ? [!!e.heads] : undefined,
		status: e.type === 'status' ? (e.status == null ? '' : String(e.status).toLowerCase()) : undefined,
	};
}

function statusFxClass(status: string | null | undefined): string {
	const t = String(status || '').toLowerCase();
	if (t === 'poisoned') return 'fx-psn';
	if (t === 'burned') return 'fx-brn';
	if (t === 'asleep') return 'fx-slp';
	if (t === 'paralyzed') return 'fx-par';
	if (t === 'confused') return 'fx-cnf';
	return 'fx-cured';
}

function pkClass(fx: FxBeat): string {
	const kind = fx.kind;
	if (fx.announce) return 'fx-announce';
	if (kind === 'damage') return 'fx-hit';
	if (kind === 'attack') return 'fx-lunge';
	if (kind === 'heal') return 'fx-heal';
	if (kind === 'ko') return 'fx-ko';
	if (kind === 'place') return fx.fromBench ? 'fx-switch' : 'fx-place';
	if (kind === 'evolve') return 'fx-evolve';
	if (kind === 'energy' || kind === 'tool') return 'fx-energy';
	if (kind === 'status') return 'fx-status';
	if (kind === 'ability') return 'fx-ability';
	return 'fx-pulse';
}

/** Local TCG energy symbol PNG (Showdown typeicons, named for TCG types). */
function energySymbolUrl(type?: string): string {
	const t = energyElement(type) || String(type || '').toLowerCase() || 'colorless';
	const known = [
		'grass', 'fire', 'water', 'lightning', 'psychic', 'fighting',
		'darkness', 'metal', 'fairy', 'dragon', 'colorless',
	];
	const id = known.includes(t) ? t : 'colorless';
	return `/fx/tcg/energy/${id}.png`;
}

export function buildPkFx(fx: FxBeat, tick = 0): { [iid: string]: PkFx } {
	const out: { [iid: string]: PkFx } = {};
	const put = (
		iid: string | undefined, cls: string, dataFx?: string, element?: string, status?: string,
		move?: { tag: string, name: string }, energyType?: string, hitStyle?: HitStyle,
	) => {
		if (!iid) return;
		out[iid] = {
			cls, dataFx, tick, element, status,
			moveTag: move?.tag, moveName: move?.name,
			energyType, hitStyle,
		};
	};
	if (fx.kind === 'status' && fx.iid) {
		put(fx.iid, `fx-status ${statusFxClass(fx.status)}`, undefined, undefined, fx.status ?? '');
	} else if (fx.iid) {
		let dataFx = '';
		// Do not paint TypeHit on the attacker during windup — only on damage targets.
		let element = (fx.kind === 'attack' || fx.kind === 'ability') ? undefined : (fx.element || undefined);
		let energyType: string | undefined;
		let hitStyle = fx.hitStyle;
		if (fx.kind === 'damage' && fx.amount) {
			dataFx = `-${fx.amount}`;
			element = element || fx.element || 'colorless';
			hitStyle = hitStyle || resolveHitStyle(fx.extra, element).style;
		}
		if (fx.kind === 'heal' && fx.amount) dataFx = `+${fx.amount}`;
		if (fx.kind === 'energy') {
			const type = energyElement(fx.element) || energyTypeFromCardId(fx.cardId) || 'colorless';
			element = type;
			energyType = type;
		}
		if (fx.kind === 'evolve') dataFx = '✨';
		if (fx.kind === 'ko') dataFx = 'KO';
		const move = fx.announce ? {
			tag: fx.kind === 'ability' ? 'Ability' : 'Attack',
			name: fx.extra || (fx.kind === 'ability' ? 'Ability' : 'Attack'),
		} : undefined;
		put(
			fx.iid,
			`${pkClass(fx)}${fx.src ? ` src-${fx.src}` : ''}${
				!fx.announce && (fx.kind === 'attack' || fx.kind === 'ability') && hitStyle ? ` hit-${hitStyle}` : ''
			}`,
			dataFx || undefined,
			element,
			undefined,
			move,
			energyType,
			hitStyle,
		);
	}
	// Projectile travel aims with data-target. Putting fx-hit on the defender here
	// starts the shake during the flight, and the impact beat cannot restart it.
	for (const h of fx.hits || []) {
		if (!h?.iid) continue;
		const n = h.counters || 0;
		const countersText = `${n} counter${n === 1 ? '' : 's'}`;
		const dataFx = h.kind === 'damage' && h.amount ? `-${h.amount}` :
			h.kind === 'heal' && h.amount ? `+${h.amount}` :
			h.kind === 'counters' ? countersText :
			h.kind === 'moved' ? `+${countersText}` :
			h.kind === 'reduced' || (h.kind === 'prevented' && h.amount === 0) ? '0' :
			h.kind === 'prevented' ? 'Prevented' : undefined;
		const src = h.src || fx.src;
		const cls = h.kind === 'heal' ? 'fx-heal' :
			h.kind === 'status' ? `fx-status ${statusFxClass(h.status)}` :
			h.kind === 'counters' || h.kind === 'moved' ? 'fx-hit fx-counters' :
			h.kind === 'prevented' ? 'fx-prevented' :
			h.kind === 'reduced' ? 'fx-prevented fx-reduced' : 'fx-hit';
		const struck = h.kind === 'damage' || h.kind === 'counters';
		const el = struck ? (h.element || fx.element || 'colorless') : undefined;
		put(
			h.iid,
			`${cls}${src ? ` src-${src}` : ''}`,
			dataFx,
			el,
			h.kind === 'status' ? (h.status ?? '') : undefined,
			undefined,
			undefined,
			struck ? (h.hitStyle || fx.hitStyle || resolveHitStyle(h.label || fx.extra, el).style) : undefined,
		);
		if (h.note && out[h.iid]) out[h.iid].note = h.note;
		// Counters moved: the Pokémon they came from shows what it lost.
		if (h.kind === 'moved' && h.fromMon && !out[h.fromMon]) {
			out[h.fromMon] = { cls: 'fx-moved-from', dataFx: `−${countersText}`, tick };
		}
	}
	// The wind-up marks what the attack actually hit (its events), never what could have been chosen.
	if ((fx.kind === 'attack' || fx.kind === 'ability') && !fx.announce) {
		for (const t of fx.targets || []) {
			if (t && t !== fx.iid && !out[t]) out[t] = { cls: 'fx-target', tick };
		}
	}
	if (fx.kind === 'attack' && fx.contact && fx.targetIid && fx.iid && out[fx.iid]) {
		out[fx.iid].cls += ' is-contact';
		out[fx.iid].contact = true;
		out[fx.iid].targetIid = fx.targetIid;
	}
	// Keep the attacker on the target through the hit so the lunge does not snap back.
	if (fx.contact && fx.kind === 'damage' && fx.fromIid && fx.fromIid !== fx.iid && !out[fx.fromIid]) {
		out[fx.fromIid] = {
			cls: `fx-lunge is-contact${fx.hitStyle ? ` hit-${fx.hitStyle}` : ''}`,
			tick,
			contact: true,
			targetIid: fx.iid,
			hitStyle: fx.hitStyle,
			moveTag: 'Attack',
			moveName: fx.extra,
		};
	}
	return out;
}

/** On-card Special Condition. The badge pops in after this plays. */
function StatusBurst(props: { kind: string }) {
	const kind = props.kind || 'cured';
	const n = kind === 'asleep' ? 3 : kind === 'cured' ? 4 : 6;
	return <div class={`tcg-sfx sfx-${kind}`} aria-hidden="true">
		{Array.from({ length: n }, (_, i) => <i key={i} style={{ '--i': String(i) } as any} />)}
	</div>;
}

function hitStyleParts(style: HitStyle) {
	if (style === 'slash') {
		return [
			<i class="claw c1" key="c1" />, <i class="claw c2" key="c2" />, <i class="claw c3" key="c3" />,
		];
	}
	if (style === 'barrage') {
		return [1, 2, 3, 4, 5, 6].map(n => <i class={`peck pk${n}`} key={n} />);
	}
	if (style === 'beam') {
		return [
			<i class="beam" key="beam" />, <i class="core" key="core" />,
			<i class="spark sp1" key="sp1" />, <i class="spark sp2" key="sp2" />,
			<i class="spark sp3" key="sp3" />,
		];
	}
	if (style === 'flame') {
		return [
			<i class="tongue t1" key="t1" />, <i class="tongue t2" key="t2" />,
			<i class="tongue t3" key="t3" />, <i class="tongue t4" key="t4" />,
			<i class="ember e1" key="e1" />, <i class="ember e2" key="e2" />,
			<i class="ember e3" key="e3" />, <i class="ember e4" key="e4" />,
		];
	}
	if (style === 'torrent') {
		return [
			<i class="splash" key="splash" />,
			<i class="drop d1" key="d1" />, <i class="drop d2" key="d2" />,
			<i class="drop d3" key="d3" />, <i class="drop d4" key="d4" />,
			<i class="drop d5" key="d5" />, <i class="drop d6" key="d6" />,
		];
	}
	if (style === 'bolt') {
		return [
			<i class="flash" key="flash" />,
			<i class="corona" key="corona" />,
			<i class="bolt b1" key="b1" />, <i class="bolt b2" key="b2" />, <i class="bolt b3" key="b3" />,
			<i class="fork f1" key="f1" />, <i class="fork f2" key="f2" />,
			<i class="fork f3" key="f3" />, <i class="fork f4" key="f4" />,
			<i class="spark sp1" key="sp1" />, <i class="spark sp2" key="sp2" />,
			<i class="spark sp3" key="sp3" />, <i class="spark sp4" key="sp4" />,
			<i class="spark sp5" key="sp5" />, <i class="spark sp6" key="sp6" />,
		];
	}
	if (style === 'zap') {
		return [
			<i class="flash" key="flash" />,
			<i class="corona" key="corona" />,
			<i class="bolt b1" key="b1" />, <i class="bolt b2" key="b2" />,
			<i class="fork f1" key="f1" />, <i class="fork f2" key="f2" />,
			<i class="fork f3" key="f3" />, <i class="fork f4" key="f4" />,
			<i class="spark sp1" key="sp1" />, <i class="spark sp2" key="sp2" />,
			<i class="spark sp3" key="sp3" />, <i class="spark sp4" key="sp4" />,
			<i class="spark sp5" key="sp5" />, <i class="spark sp6" key="sp6" />,
		];
	}
	if (style === 'bloom') {
		return [
			<i class="lash" key="lash" />,
			<i class="vine v1" key="v1" />, <i class="vine v2" key="v2" />,
			<i class="leaf l1" key="l1" />, <i class="leaf l2" key="l2" />, <i class="leaf l3" key="l3" />,
		];
	}
	if (style === 'pulse') {
		return [
			<i class="haze" key="haze" />,
			<i class="swirl" key="swirl" />,
			<i class="ring r1" key="r1" />, <i class="ring r2" key="r2" />, <i class="ring r3" key="r3" />,
			<i class="gem" key="gem" />,
			<i class="mote m1" key="m1" />, <i class="mote m2" key="m2" />,
			<i class="mote m3" key="m3" />, <i class="mote m4" key="m4" />,
			<i class="mote m5" key="m5" />, <i class="mote m6" key="m6" />,
		];
	}
	if (style === 'quake') {
		return [
			<i class="crack k1" key="k1" />, <i class="crack k2" key="k2" />,
			<i class="chunk c1" key="c1" />, <i class="chunk c2" key="c2" />,
			<i class="chunk c3" key="c3" />, <i class="chunk c4" key="c4" />,
			<i class="dust d1" key="d1" />, <i class="dust d2" key="d2" />,
		];
	}
	if (style === 'veil') {
		return [
			<i class="orb" key="orb" />,
			<i class="haze" key="haze" />,
			<i class="burst" key="burst" />,
			<i class="tendril t1" key="t1" />, <i class="tendril t2" key="t2" />,
			<i class="tendril t3" key="t3" />, <i class="tendril t4" key="t4" />,
			<i class="tendril t5" key="t5" />, <i class="tendril t6" key="t6" />,
		];
	}
	if (style === 'shard') {
		return [
			<i class="core" key="core" />,
			<i class="gleam g1" key="g1" />, <i class="gleam g2" key="g2" />,
			<i class="gleam g3" key="g3" />, <i class="gleam g4" key="g4" />,
			<i class="gleam g5" key="g5" />,
		];
	}
	if (style === 'sparkle') {
		return [
			<i class="flash" key="flash" />,
			<i class="star s1" key="s1" />, <i class="star s2" key="s2" />,
			<i class="star s3" key="s3" />, <i class="star s4" key="s4" />,
			<i class="star s5" key="s5" />, <i class="star s6" key="s6" />,
		];
	}
	if (style === 'roar') {
		return [
			<i class="fang f1" key="f1" />, <i class="fang f2" key="f2" />,
			<i class="coil c1" key="c1" />, <i class="spark sp1" key="sp1" />,
			<i class="spark sp2" key="sp2" />,
		];
	}
	if (style === 'whirl') {
		return [
			<i class="gust g1" key="g1" />, <i class="gust g2" key="g2" />,
			<i class="gust g3" key="g3" />, <i class="gust g4" key="g4" />,
		];
	}
	if (style === 'drain') {
		return [
			<i class="sip s1" key="s1" />, <i class="sip s2" key="s2" />,
			<i class="sip s3" key="s3" />, <i class="sip s4" key="s4" />,
			<i class="sip s5" key="s5" />,
			<i class="leaf l1" key="l1" />, <i class="leaf l2" key="l2" />,
		];
	}
	if (style === 'frost') {
		return [
			<i class="core" key="core" />,
			<i class="shard sh1" key="sh1" />, <i class="shard sh2" key="sh2" />,
			<i class="shard sh3" key="sh3" />, <i class="shard sh4" key="sh4" />,
			<i class="flake f1" key="f1" />, <i class="flake f2" key="f2" />,
		];
	}
	if (style === 'stream') {
		return [
			<i class="tongue t1" key="t1" />, <i class="tongue t2" key="t2" />,
			<i class="tongue t3" key="t3" />,
			<i class="ember e1" key="e1" />, <i class="ember e2" key="e2" />,
			<i class="ember e3" key="e3" />,
		];
	}
	if (style === 'sting') {
		return [
			<i class="dart" key="dart" />,
			<i class="drop d1" key="d1" />, <i class="drop d2" key="d2" />,
			<i class="drop d3" key="d3" />,
		];
	}
	if (style === 'powder') {
		return [1, 2, 3, 4, 5, 6, 7, 8].map(n => <i class={`spore sp${n}`} key={n} />);
	}
	if (style === 'wave') {
		return [
			<i class="crest" key="crest" />,
			<i class="foam f1" key="f1" />, <i class="foam f2" key="f2" />, <i class="foam f3" key="f3" />,
			<i class="drop d1" key="d1" />, <i class="drop d2" key="d2" />, <i class="drop d3" key="d3" />,
		];
	}
	if (style === 'petal') {
		return [1, 2, 3, 4, 5, 6].map(n => <i class={`petal p${n}`} key={n} />);
	}
	if (style === 'bite') {
		return [
			<i class="jaw j1" key="j1" />, <i class="jaw j2" key="j2" />,
			<i class="fang f1" key="f1" />, <i class="fang f2" key="f2" />,
			<i class="fang f3" key="f3" />, <i class="fang f4" key="f4" />,
		];
	}
	if (style === 'bubble') {
		return [1, 2, 3, 4, 5, 6].map(n => <i class={`bub b${n}`} key={n} />);
	}
	if (style === 'sonic') {
		return [
			<i class="ring r1" key="r1" />, <i class="ring r2" key="r2" />, <i class="ring r3" key="r3" />,
			<i class="note n1" key="n1" />, <i class="note n2" key="n2" />, <i class="note n3" key="n3" />,
		];
	}
	if (style === 'mud') {
		return [
			<i class="splat" key="splat" />,
			<i class="blob b1" key="b1" />, <i class="blob b2" key="b2" />, <i class="blob b3" key="b3" />,
			<i class="grit g1" key="g1" />, <i class="grit g2" key="g2" />,
		];
	}
	if (style === 'wrap') {
		return [
			<i class="coil c1" key="c1" />, <i class="coil c2" key="c2" />, <i class="coil c3" key="c3" />,
		];
	}
	if (style === 'note') {
		return [
			<i class="note n1" key="n1" />, <i class="note n2" key="n2" />, <i class="note n3" key="n3" />,
		];
	}
	if (style === 'aura') {
		return [
			<i class="orb" key="orb" />, <i class="ring r1" key="r1" />, <i class="ring r2" key="r2" />,
			<i class="mote m1" key="m1" />, <i class="mote m2" key="m2" />,
		];
	}
	if (style === 'drill') {
		return [
			<i class="bit" key="bit" />, <i class="spin" key="spin" />,
			<i class="chip c1" key="c1" />, <i class="chip c2" key="c2" />,
		];
	}
	if (style === 'acid') {
		return [
			<i class="goo" key="goo" />,
			<i class="drip d1" key="d1" />, <i class="drip d2" key="d2" />, <i class="drip d3" key="d3" />,
		];
	}
	if (style === 'wisp') {
		return [
			<i class="flame f1" key="f1" />, <i class="flame f2" key="f2" />, <i class="flame f3" key="f3" />,
			<i class="ember e1" key="e1" />, <i class="ember e2" key="e2" />,
		];
	}
	if (style === 'meteor') {
		return [
			<i class="rock r1" key="r1" />, <i class="rock r2" key="r2" />, <i class="rock r3" key="r3" />,
			<i class="gleam g1" key="g1" />, <i class="gleam g2" key="g2" />,
		];
	}
	if (style === 'wing') {
		return [
			<i class="feather f1" key="f1" />, <i class="feather f2" key="f2" />,
			<i class="feather f3" key="f3" />, <i class="feather f4" key="f4" />,
		];
	}
	if (style === 'swift') {
		return [
			<i class="star s1" key="s1" />, <i class="star s2" key="s2" />, <i class="star s3" key="s3" />,
			<i class="star s4" key="s4" />, <i class="star s5" key="s5" />,
			<i class="mote m1" key="m1" />, <i class="mote m2" key="m2" />,
		];
	}
	if (style === 'gold') {
		return [
			<i class="coin c1" key="c1" />, <i class="coin c2" key="c2" />, <i class="coin c3" key="c3" />,
			<i class="glint g1" key="g1" />, <i class="glint g2" key="g2" />,
		];
	}
	if (style === 'daze') {
		return [
			<i class="disc d1" key="d1" />, <i class="disc d2" key="d2" />,
			<i class="spiral" key="spiral" />,
			<i class="spark s1" key="s1" />, <i class="spark s2" key="s2" />,
		];
	}
	if (style === 'sand') {
		return [
			<i class="cloud" key="cloud" />, <i class="swirl" key="swirl" />,
			<i class="grit g1" key="g1" />, <i class="grit g2" key="g2" />, <i class="grit g3" key="g3" />,
			<i class="grit g4" key="g4" />, <i class="grit g5" key="g5" />, <i class="grit g6" key="g6" />,
		];
	}
	if (style === 'lick') {
		return [
			<i class="tongue" key="tongue" />, <i class="groove" key="groove" />,
			<i class="drip d1" key="d1" />, <i class="drip d2" key="d2" />,
		];
	}
	if (style === 'bone') {
		return [
			<i class="bone" key="bone" />,
			<i class="chip c1" key="c1" />, <i class="chip c2" key="c2" />,
		];
	}
	if (style === 'ink') {
		return [
			<i class="blot" key="blot" />,
			<i class="tendril t1" key="t1" />, <i class="tendril t2" key="t2" />,
			<i class="drip d1" key="d1" />, <i class="drip d2" key="d2" />, <i class="drip d3" key="d3" />,
		];
	}
	if (style === 'tri') {
		return [
			<i class="orb o1" key="o1" />, <i class="orb o2" key="o2" />, <i class="orb o3" key="o3" />,
		];
	}
	if (style === 'fist') {
		return [
			<i class="knuckle" key="knuckle" />,
			<i class="streak sk1" key="sk1" />, <i class="streak sk2" key="sk2" />,
			<i class="spark s1" key="s1" />, <i class="spark s2" key="s2" />,
		];
	}
	if (style === 'shade') {
		return [
			<i class="sil" key="sil" />, <i class="haze" key="haze" />,
			<i class="wisp w1" key="w1" />, <i class="wisp w2" key="w2" />,
		];
	}
	if (style === 'kick') {
		return [
			<i class="paw" key="paw" />,
			<i class="print p1" key="p1" />, <i class="print p2" key="p2" />,
			<i class="dust d1" key="d1" />, <i class="dust d2" key="d2" />,
		];
	}
	if (style === 'chop') {
		return [
			<i class="hand h1" key="h1" />, <i class="hand h2" key="h2" />,
			<i class="arc a1" key="a1" />, <i class="arc a2" key="a2" />,
		];
	}
	if (style === 'rage') {
		return [
			<i class="mark m1" key="m1" />, <i class="mark m2" key="m2" />,
			<i class="mark m3" key="m3" />, <i class="mark m4" key="m4" />,
			<i class="burst" key="burst" />,
		];
	}
	if (style === 'shell') {
		return [
			<i class="shell" key="shell" />,
			<i class="shard s1" key="s1" />, <i class="shard s2" key="s2" />,
			<i class="drop d1" key="d1" />, <i class="drop d2" key="d2" />,
		];
	}
	if (style === 'kiss') {
		return [
			<i class="heart h1" key="h1" />, <i class="heart h2" key="h2" />,
			<i class="heart h3" key="h3" />, <i class="heart h4" key="h4" />,
			<i class="heart h5" key="h5" />,
		];
	}
	if (style === 'gyro') {
		return [
			<i class="gear" key="gear" />, <i class="ring" key="ring" />,
			<i class="chip c1" key="c1" />, <i class="chip c2" key="c2" />,
		];
	}
	if (style === 'breath') {
		return [
			<i class="ball b1" key="b1" />, <i class="ball b2" key="b2" />,
			<i class="ball b3" key="b3" />,
			<i class="ember e1" key="e1" />, <i class="ember e2" key="e2" />,
		];
	}
	if (style === 'cannon') {
		return [1, 2, 3, 4, 5].map(n => <i class={`spike sp${n}`} key={n} />);
	}
	if (style === 'fall') {
		return [
			<i class="ice i1" key="i1" />, <i class="ice i2" key="i2" />,
			<i class="ice i3" key="i3" />, <i class="ice i4" key="i4" />,
			<i class="ice i5" key="i5" />, <i class="ice i6" key="i6" />,
		];
	}
	if (style === 'iron') {
		return [
			<i class="star" key="star" />, <i class="gear" key="gear" />,
			<i class="chip c1" key="c1" />, <i class="chip c2" key="c2" />,
		];
	}
	if (style === 'boom') {
		return [
			<i class="burst" key="burst" />,
			<i class="rock r1" key="r1" />, <i class="rock r2" key="r2" />,
			<i class="rock r3" key="r3" />, <i class="rock r4" key="r4" />,
			<i class="spark s1" key="s1" />, <i class="spark s2" key="s2" />,
		];
	}
	if (style === 'web') {
		return [
			<i class="net" key="net" />,
			<i class="strand s1" key="s1" />, <i class="strand s2" key="s2" />,
			<i class="strand s3" key="s3" />, <i class="strand s4" key="s4" />,
		];
	}
	if (style === 'tail') {
		return [
			<i class="whip w1" key="w1" />, <i class="whip w2" key="w2" />,
			<i class="dust d1" key="d1" />, <i class="dust d2" key="d2" />,
		];
	}
	if (style === 'arrow') {
		return <i class="shaft" key="shaft" />;
	}
	if (style === 'sleep') {
		return [
			<i class="zed z1" key="z1" />, <i class="zed z2" key="z2" />, <i class="zed z3" key="z3" />,
		];
	}
	if (style === 'shield') {
		return <i class="plate" key="plate" />;
	}
	return [
		<i class="burst" key="burst" />,
		<i class="line ln1" key="ln1" />, <i class="line ln2" key="ln2" />,
		<i class="line ln3" key="ln3" />, <i class="line ln4" key="ln4" />,
		<i class="spark sp1" key="sp1" />, <i class="spark sp2" key="sp2" />,
		<i class="spark sp3" key="sp3" />,
	];
}

function TypeHit(props: { type: string, style?: HitStyle, tick?: number }) {
	const style = props.style || 'impact';
	const type = props.type || 'colorless';
	// data-tick changes per hit so the CSS animation restarts via TcgMon's reflow.
	return <div
		class={`type-hit hit-${style} t-${type}`}
		data-tick={props.tick ?? 0}
		aria-hidden="true"
	>{hitStyleParts(style)}</div>;
}

function AtkFlyer(props: {
	fromIid: string, targetIid: string, style: HitStyle, element?: string,
}) {
	const style = props.style || 'impact';
	const type = props.element || 'colorless';
	const beamish = style === 'beam' || style === 'shard' || style === 'roar' ||
		style === 'torrent' || style === 'frost' || style === 'stream';
	return <div
		class={`fx-atk-fly hit-${style} t-${type}${beamish ? ' is-beam' : ''}`}
		data-from={props.fromIid}
		data-target={props.targetIid}
		aria-hidden="true"
	>
		<i class="proj"></i>
		<i class="trail"></i>
		{beamish ? <>
			<i class="sheath"></i><i class="core"></i><i class="grain"></i>
			<i class="mote m1"></i><i class="mote m2"></i><i class="mote m3"></i>
		</> : null}
		{style === 'zap' ? <><i class="arc a2"></i><i class="arc a3"></i></> : null}
		{style === 'bolt' ? <><i class="fork f2"></i><i class="fork f3"></i><i class="glow"></i></> : null}
		{style === 'veil' ? <><i class="core"></i><i class="wisp w1"></i><i class="wisp w2"></i><i class="wisp w3"></i></> : null}
		{style === 'flame' ? <><i class="ember e1"></i><i class="ember e2"></i></> : null}
		{style === 'bloom' ? <><i class="lash"></i><i class="leaf"></i></> : null}
		{style === 'pulse' ? <>
			<i class="disc d1"></i><i class="disc d2"></i><i class="disc d3"></i><i class="spark"></i>
		</> : null}
		{style === 'sparkle' ? <><i class="star s1"></i><i class="star s2"></i><i class="star s3"></i></> : null}
		{style === 'whirl' ? <><i class="gust g1"></i><i class="gust g2"></i></> : null}
		{style === 'barrage' ? <><i class="peck p1"></i><i class="peck p2"></i></> : null}
		{style === 'drain' ? <><i class="wisp w1"></i><i class="wisp w2"></i></> : null}
		{style === 'sting' ? <i class="barb"></i> : null}
		{style === 'powder' ? <><i class="cloud"></i><i class="spore s1"></i><i class="spore s2"></i><i class="spore s3"></i></> : null}
		{style === 'wave' ? <><i class="foam"></i><i class="grain"></i><i class="bubble b1"></i><i class="bubble b2"></i></> : null}
		{style === 'petal' ? <><i class="petal p1"></i><i class="petal p2"></i><i class="petal p3"></i></> : null}
		{style === 'bubble' ? <><i class="bub b1"></i><i class="bub b2"></i><i class="bub b3"></i></> : null}
		{style === 'sonic' ? <>
			<i class="wave w1"></i><i class="wave w2"></i><i class="wave w3"></i>
			<i class="note n1"></i><i class="note n2"></i>
		</> : null}
		{style === 'mud' ? <><i class="grit"></i></> : null}
		{style === 'wrap' ? <><i class="coil c1"></i><i class="coil c2"></i></> : null}
		{style === 'note' ? <><i class="note n1"></i><i class="note n2"></i></> : null}
		{style === 'aura' ? <><i class="ring"></i><i class="grain"></i></> : null}
		{style === 'drill' ? <><i class="spin"></i></> : null}
		{style === 'acid' ? <><i class="drip"></i></> : null}
		{style === 'wisp' ? <><i class="flame"></i><i class="ember"></i></> : null}
		{style === 'meteor' ? <><i class="rock r1"></i><i class="rock r2"></i></> : null}
		{style === 'wing' ? <><i class="feather f1"></i><i class="feather f2"></i></> : null}
		{style === 'swift' ? <>
			<i class="star s1"></i><i class="star s2"></i><i class="star s3"></i>
			<i class="star s4"></i><i class="star s5"></i><i class="mote"></i>
		</> : null}
		{style === 'gold' ? <><i class="coin c1"></i><i class="coin c2"></i><i class="coin c3"></i></> : null}
		{style === 'daze' ? <><i class="disc d1"></i><i class="spiral"></i><i class="spark"></i></> : null}
		{style === 'sand' ? <>
			<i class="cloud"></i><i class="swirl"></i><i class="grit g1"></i><i class="grit g2"></i><i class="grain"></i>
		</> : null}
		{style === 'lick' ? <><i class="tongue"></i><i class="groove"></i><i class="drip"></i></> : null}
		{style === 'bone' ? <><i class="bone"></i><i class="chip"></i></> : null}
		{style === 'ink' ? <>
			<i class="blot"></i><i class="tendril t1"></i><i class="tendril t2"></i><i class="drip"></i><i class="grain"></i>
		</> : null}
		{style === 'tri' ? <><i class="orb o1"></i><i class="orb o2"></i><i class="orb o3"></i></> : null}
		{style === 'fist' ? <><i class="knuckle"></i><i class="streak sk1"></i><i class="streak sk2"></i></> : null}
		{style === 'shade' ? <><i class="sil"></i><i class="haze"></i><i class="wisp w1"></i><i class="wisp w2"></i></> : null}
		{style === 'kiss' ? <><i class="heart h1"></i><i class="heart h2"></i><i class="heart h3"></i></> : null}
		{style === 'breath' ? <><i class="ball"></i><i class="ember e1"></i><i class="ember e2"></i></> : null}
		{style === 'cannon' ? <><i class="spike sp1"></i><i class="spike sp2"></i><i class="spike sp3"></i></> : null}
		{style === 'boom' ? <>
			<i class="burst"></i><i class="rock r1"></i><i class="rock r2"></i><i class="spark"></i>
		</> : null}
		{style === 'web' ? <i class="net"></i> : null}
		{style === 'arrow' ? <i class="shaft"></i> : null}
		{style === 'sleep' ? <><i class="zed z1"></i><i class="zed z2"></i><i class="zed z3"></i></> : null}
	</div>;
}

function FxCaption(props: { tag: string, title?: string }) {
	const slug = String(props.tag || '').toLowerCase().replace(/[^a-z0-9]+/g, '-');
	return <div class={`fx-caption k-${slug}`}>
		<b>{props.tag}</b>
		{props.title ? <span>{props.title}</span> : null}
	</div>;
}

/** Prominent mid-board status line for searches, prizes, draws, etc. */
function BoardMsg(props: { tag: string, text: string, yours?: boolean, tone?: string }) {
	const slug = String(props.tag || '').toLowerCase().replace(/[^a-z0-9]+/g, '-');
	const tone = props.tone || slug;
	return <div class={`fx-board-msg k-${tone}${props.yours ? ' yours' : ' foe'}`}>
		<b>{props.tag}</b>
		<span>{props.text}</span>
	</div>;
}

function fanCards(n: number) {
	const c = Math.min(Math.max(n || 5, 3), 7);
	const mid = (c - 1) / 2;
	return Array.from({ length: c }, (_, i) =>
		<span class="fx-mini-card back fan" style={{ '--t': String(i - mid) } as any}></span>
	);
}

function MiniArt(props: { cardId?: string, name?: string }) {
	const src = props.cardId ? cardArt(props.cardId) : '';
	if (!src) return <span class="fx-mini-card back"></span>;
	return <img src={src} alt={props.name || ''} draggable={false} />;
}

class FxOverlay extends preact.Component<{
	fx: FxBeat | null, you?: number | null, viewpoint?: 0 | 1, names?: string[],
}> {
	override render() {
		const fx = this.props.fx;
		if (!fx?.kind) return null;
		const layoutSeat = this.props.viewpoint != null ? this.props.viewpoint :
			(this.props.you != null ? this.props.you : 0);
		const seat = fx.seat === layoutSeat ? 'fx-p1' : 'fx-p2';
		const yours = actorIsYou(fx.seat, this.props.you);
		const art = fx.cardId ? <MiniArt cardId={fx.cardId} name={fx.extra} /> : null;
		const k = fx.kind;
		/** Mid-board copy only for pending waits (search / mulligan), not every FX beat. */
		const pendingMsg = (tag: string, fallback: string, tone?: string) =>
			<BoardMsg tag={tag} text={fx.message || fallback} yours={yours} tone={tone} />;
		const cap = (tag: string, title?: string) => <FxCaption tag={tag} title={title} />;
		const wrap = (kind: string, body: any, src?: string) => (
			<div class={`fx-layer kind-${kind}${src ? ` src-${src}` : ''}${yours ? ' yours' : ' foe'}`}>
				{body}
			</div>
		);

		if (k === 'coin') {
			const coins = (fx.coins?.length) ? fx.coins :
				[fx.extra === 'Heads' || fx.extra === 'HEADS'];
			const shown = coins.slice(0, 8);
			const headsN = shown.filter(Boolean).length;
			const tailsN = shown.length - headsN;
			const forFirst = fx.extra === 'First';
			const side = forFirst ? (shown[0] ? 'HEADS' : 'TAILS') :
				shown.length === 1 ?
					(shown[0] ? 'HEADS' : 'TAILS') :
					`${headsN}H · ${tailsN}T`;
			const result = fx.message || (shown.length === 1 ?
				(shown[0] ? 'Heads!' : 'Tails!') :
				`${headsN} Heads · ${tailsN} Tails`);
			const lifeMs = (forFirst ? 2700 : 2200) + Math.max(0, shown.length - 1) * 200;
			return wrap('coin', <>
				{cap(forFirst ? 'FIRST' : 'COIN', side)}
				<div
					class={`fx-coin-row n-${shown.length}`}
					style={{ animationDuration: `${lifeMs}ms` }}
				>
					{shown.map((heads, idx) => (
						<div
							class="fx-coin-scene"
							key={`coin-${idx}-${heads ? 'h' : 't'}`}
							style={{ '--coin-delay': `${idx * 0.16}s` } as any}
						>
							<div class={`fx-coin-3d ${heads ? 'land-heads' : 'land-tails'}`}>
								<div class="fx-coin-face front">
									<span>H</span>
									<em>HEADS</em>
								</div>
								<div class="fx-coin-face back">
									<span>T</span>
									<em>TAILS</em>
								</div>
							</div>
						</div>
					))}
				</div>
				<div class="fx-coin-result" style={{ animationDuration: `${lifeMs}ms` }}>{result}</div>
			</>);
		}
		if (k === 'drawEffect') {
			const n = Math.max(fx.n || 0, fx.ids?.length || 0, 1);
			const ids = (fx.ids?.length) ? fx.ids : [];
			const faces = ids.length > 0;
			const count = Math.min(faces ? ids.length : n, 8);
			const kindLabel = fx.src === 'attack' ? 'Attack' :
				fx.src === 'ability' ? 'Ability' :
				fx.src === 'trainer' ? 'Trainer' :
				fx.src === 'stadium' ? 'Stadium' :
				fx.src === 'energy' ? 'Energy' :
				fx.src === 'tool' ? 'Tool' : 'Effect';
			return wrap('drawEffect', <>
				<div class={`fx-draw-rail ${seat}${yours ? ' yours' : ' foe'}`}>
					<div class="fx-draw-source">
						{fx.cardId ?
							<span class="fx-draw-source-art"><MiniArt cardId={fx.cardId} name={fx.extra} /></span> :
							<span class="fx-draw-source-art back"><span class="fx-mini-card back"></span></span>}
						<div class="fx-draw-source-meta">
							<b>{kindLabel}</b>
							<em>{fx.extra || kindLabel}</em>
						</div>
					</div>
					<div class="fx-draw-stack">
						{Array.from({ length: count }, (_, i) =>
							faces ?
								<span class="fx-draw-card face" style={{ '--i': String(i) } as any} key={i}>
									<MiniArt cardId={ids[i]} name={(fx.labels?.[i]) || ''} />
								</span> :
								<span class="fx-draw-card back" style={{ '--i': String(i) } as any} key={i}>
									<span class="fx-mini-card back"></span>
								</span>
						)}
					</div>
					<div class="fx-draw-caption">
						{n === 1 ? 'Drew a card' : `Drew ${n} cards`}
					</div>
				</div>
			</>);
		}
		if (k === 'draw' || k === 'deal') {
			const n = fx.n || (k === 'deal' ? 7 : 1);
			// The cards themselves fly from the real deck (`cardFlights`); this marks whose pile pulses.
			void n;
			const marks = k === 'deal' ?
				<><span class="fx-move-mark fx-p1"></span><span class="fx-move-mark fx-p2"></span></> :
				<span class={`fx-move-mark ${seat}`}></span>;
			return wrap('draw', marks);
		}
		if (k === 'shuffleHand') {
			// Motion lives on the real hand (`.is-shuffling`); layer only marks the beat for CSS.
			return wrap('shuffleHand', <></>);
		}
		if (k === 'shuffle') {
			return wrap('shuffle', cap('SHUFFLE', 'Deck'));
		}
		if (k === 'search') {
			// Your own search is a prompt; don't cover the picker with a board-wide status card.
			if (yours) return null;
			return wrap('search', <>
				{pendingMsg('SEARCH', 'Searching their deck', 'search')}
				<div class={`fx-search-fan ${seat}`}>{fanCards(5)}</div>
				<div class={`fx-search-glow ${seat}`}></div>
			</>);
		}
		if (k === 'searchDone') {
			return wrap('search', <>
				<div class={`fx-search-fan ${seat} close`}>{fanCards(5)}</div>
			</>);
		}
		if (k === 'mulligan') {
			return wrap('mulligan', <>
				{pendingMsg('MULLIGAN', 'Took a mulligan', 'mulligan')}
				<div class={`fx-search-fan ${seat}`}>{fanCards(7)}</div>
			</>);
		}
		if (k === 'reveal' || k === 'find') {
			const ids = (fx.ids?.length) ? fx.ids : (fx.cardId ? [fx.cardId] : []);
			const faces = ids.length ? ids.map((id, i) => {
				const name = (fx.labels?.[i]) || '';
				return <span class="fx-found-card" key={i}>
					<span class="fx-mini-card face"><MiniArt cardId={id} name={name} /></span>
					{name ? <em>{name}</em> : null}
				</span>;
			}) : (k === 'reveal' ? null : <span class="fx-mini-card back"></span>);
			return wrap(k === 'reveal' ? 'reveal' : 'find', <>
				{k === 'reveal' ? cap('REVEAL', fx.extra || 'Hand') : cap(fx.tag || 'FOUND', fx.extra)}
				<div class={`fx-found-row ${seat}`}>{faces}</div>
			</>);
		}
		if (k === 'discard') {
			return wrap('discard', <span class={`fx-move-mark ${seat}`}></span>);
		}
		if (k === 'trainer') {
			return wrap('trainer', <>
				<div class="fx-play-card reveal">{art}<div class="fx-play-name">{fx.extra || 'Trainer'}</div></div>
			</>);
		}
		if (k === 'prize' || k === 'prizeTake') {
			// The card flies from the real Prize pile, which pulses as it gives it up (`launchFlight`).
			return wrap('prize', <span class={`fx-move-mark ${seat}`}></span>);
		}
		if (k === 'stadium' || k === 'stadiumEnd') {
			return wrap('stadium', <>
				<div class="fx-play-card reveal">{art}<div class="fx-play-name">{fx.extra || 'Stadium'}</div></div>
			</>);
		}
		if (k === 'energy' || k === 'tool') {
			// The card flies from where it was to the Pokémon (`cardFlights`); the caption names it.
			const enName = fx.extra || (k === 'tool' ? 'Pokémon Tool' : 'Energy');
			return wrap(k, cap(k === 'tool' ? 'TOOL' : 'ENERGY', enName));
		}
		if (k === 'place') {
			return wrap('place', cap(fx.tag || (fx.fromBench ? 'SWITCH' : 'INTO PLAY'), fx.extra));
		}
		if (k === 'evolve') {
			const fromArt = fx.fromCardId ? <MiniArt cardId={fx.fromCardId} /> : null;
			const toArt = art || <span class="fx-mini-card face"></span>;
			return wrap('evolve', <>
				<div class="fx-evolve-stage">
					<span class="fx-evolve-flash"></span>
					<span class="fx-evolve-ring r1"></span>
					<span class="fx-evolve-ring r2"></span>
					<span class="fx-evolve-spark s1"></span>
					<span class="fx-evolve-spark s2"></span>
					<span class="fx-evolve-spark s3"></span>
					<span class="fx-evolve-spark s4"></span>
					<span class="fx-evolve-spark s5"></span>
					<span class="fx-evolve-spark s6"></span>
					<div class="fx-evolve-morph">
						{fromArt ?
							<span class="fx-evolve-from">{fromArt}</span> :
							<span class="fx-evolve-from fx-mini-card face"></span>}
						<span class="fx-evolve-to">{toArt}</span>
					</div>
					{fx.extra ? <div class="fx-evolve-name">{fx.extra}</div> : null}
				</div>
			</>);
		}
		if (k === 'attack' || k === 'ability') {
			// Move name banner is on the attacker (TcgMon). Ranged styles fly mid-board.
			if (fx.travel && fx.iid && fx.targetIid && fx.hitStyle) {
				return wrap(k, <AtkFlyer
					fromIid={fx.hitStyle === 'drain' ? fx.targetIid : fx.iid}
					targetIid={fx.hitStyle === 'drain' ? fx.iid : fx.targetIid}
					style={fx.hitStyle}
					element={fx.element}
				/>);
			}
			return null;
		}
		if (k === 'turn') {
			// A short banner as the turn changes, under the header badge: whose turn, its number, and the first turn
			// set apart from the rest. The badge keeps saying whose turn it is after the banner goes.
			const name = (this.props.names || [])[fx.seat ?? -1] || 'Opponent';
			const whose = yours ? 'Your turn' : `${name}'s turn`;
			const first = fx.n === 1;
			return wrap(`turn${first ? ' first-turn' : ''}`, cap(first ? 'FIRST TURN' : `TURN ${fx.n || ''}`.trim(), whose));
		}
		if (k === 'first') return wrap('first', cap('FIRST', fx.extra));
		if (k === 'over') {
			// The board banner is the one win message. Drawing it here too flashed "won" twice.
			return null;
		}
		if (k === 'points') return wrap('points', <div class="fx-points-burst">+{fx.n || 1}</div>);
		if (k === 'status') return wrap('status', cap('STATUS', fx.extra ? String(fx.extra) : 'Recovered'));
		if (k === 'attackFailed') return wrap('status', cap('FAILED', fx.extra || 'Attack'));
		if (k === 'suddenDeath') return wrap('status', cap('SUDDEN DEATH', fx.extra || 'Tiebreaker'));
		if (k === 'checkup') return wrap('checkup', cap('CHECKUP', 'Pokémon Checkup'));
		if (k === 'discard') return wrap('discard', cap('DISCARD', fx.extra || 'Discard'));
		if (k === 'ko') {
			// KO stamp + motion live on the Knocked Out card (`data-fx` / fx-ko). No mid-board burst.
			return null;
		}
		if (k === 'heal') {
			// Amount shows on the Pokémon (`data-fx`); skip the big mid-board number.
			return wrap('heal', <div class="fx-heal-aura"></div>);
		}
		if (k === 'damage') {
			// Hit burst + −N are on the card. Extra flyers only for follow-up travel hits.
			if (fx.travel && fx.fromIid && fx.iid && fx.hitStyle) {
				return wrap('damage', <AtkFlyer
					fromIid={fx.hitStyle === 'drain' ? fx.iid : fx.fromIid}
					targetIid={fx.hitStyle === 'drain' ? fx.fromIid : fx.iid}
					style={fx.hitStyle}
					element={fx.element}
				/>);
			}
			return null;
		}
		return null;
	}
}

type CardSize = 'xs' | 'sm' | 'md' | 'lg';

const TYPE_COLOR: { [type: string]: string } = {
	grass: '#3dcc5a', fire: '#ff5a4a', water: '#3da9ff', lightning: '#f5d031',
	psychic: '#c45ae8', fighting: '#e86838', darkness: '#5b6ad6', metal: '#9aacb8',
	fairy: '#f472b6', dragon: '#8b6cff', colorless: '#e8eef2',
};

/** Pocket sets look like A1 / A2a / B1 / P-A; paper/Live sets look like sv3 / swsh7. */
export function isPocketCardId(cardId: string): boolean {
	if (!cardId) return false;
	// Promo-A prints: P-A-025 (hyphenated set code — must not use split('-')[0] === "P")
	if (/^P-A-\d+/i.test(cardId)) return true;
	const cut = cardId.lastIndexOf('-');
	const set = (cut >= 0 ? cardId.slice(0, cut) : cardId).trim();
	if (!set) return false;
	if (/^P-A$/i.test(set)) return true;
	// Main pocket sets: A1, A2b, B1, B2, A4a, …
	if (/^[A-Z]\d+[a-z]?$/i.test(set)) return true;
	return false;
}

/** Classic Basic Energy art (synthetic `energy-*` ids have no print images). */
const BASIC_ENERGY_ART: { [type: string]: string } = {
	grass: 'sve-1',
	fire: 'sve-2',
	water: 'sve-3',
	lightning: 'sve-4',
	psychic: 'sve-5',
	fighting: 'sve-6',
	darkness: 'sve-7',
	metal: 'sve-8',
	fairy: 'xy1-140',
	// No standalone colorless print — use Grass as a stand-in for the pip art
	colorless: 'sve-1',
	dragon: 'sve-1',
};

function paperTcgIoArt(cardId: string, large = false): string {
	const cut = cardId.lastIndexOf('-');
	if (cut < 0) return '';
	const set = cardId.slice(0, cut);
	const num = cardId.slice(cut + 1);
	if (!set || !num) return '';
	return `https://images.pokemontcg.io/${set}/${num}${large ? '_hires' : ''}.png`;
}

function scrydexArt(cardId: string, large = false): string {
	return `https://images.scrydex.com/pokemon/${encodeURIComponent(cardId)}/${large ? 'large' : 'small'}`;
}

export function pocketCardImage(cardId: string, large = false): string {
	return cardArt(cardId, { large, pocket: true });
}

export function cardArt(cardId: string, opts?: { large?: boolean, pocket?: boolean }): string {
	if (!cardId) return '';
	const large = !!opts?.large;
	const pocket = opts?.pocket ?? isPocketCardId(cardId);

	const energy = /^energy-(grass|fire|water|lightning|psychic|fighting|darkness|metal|fairy|dragon|colorless)$/i.exec(cardId);
	if (energy) {
		const printId = BASIC_ENERGY_ART[energy[1].toLowerCase()] || 'sve-1';
		return paperTcgIoArt(printId, large) || scrydexArt(printId, large);
	}

	if (pocket) {
		const cut = cardId.lastIndexOf('-');
		if (cut < 0) return '';
		const set = cardId.slice(0, cut);
		let num = cardId.slice(cut + 1);
		if (/^\d+$/.test(num)) {
			num = num.replace(/^0+/, '') || '0';
			while (num.length < 3) num = '0' + num;
		}
		const base = `https://limitlesstcg.nyc3.cdn.digitaloceanspaces.com/pocket/${set}/${set}_${num}_EN`;
		return large ? `${base}.webp` : `${base}_SM.webp`;
	}

	// Scrydex covers recent Standard prints that 404 on images.pokemontcg.io
	return scrydexArt(cardId, large);
}

/** Face-down card back shipped with this client (fx/ is tracked; sprites/ is not). */
export function cardBackArt(): string {
	return '/fx/tcg/cardback.webp';
}

const CARD_BACK_FALLBACK = '/fx/tcg/cardback.jpg';

/**
 * Scrydex answers a card it has no picture for (for now every Delta Reign card) with one generic image and HTTP 200,
 * so `onError` never fires. Its 4×4 RGB sample at each of its two sizes, as a canvas draws it.
 */
const SCRYDEX_NO_ART: { [size: string]: number[] } = {
	'245x341': [
		71, 91, 110, 119, 133, 135, 113, 130, 132, 108, 125, 135, 114, 134, 160, 155, 149, 149, 166, 135, 128, 81, 101, 131,
		78, 103, 139, 133, 149, 161, 173, 180, 183, 111, 132, 157, 120, 137, 146, 113, 132, 135, 118, 135, 136, 69, 88, 104,
	],
	'640x892': [
		64, 82, 103, 113, 128, 125, 116, 132, 129, 107, 120, 130, 75, 99, 137, 188, 157, 153, 150, 118, 119, 47, 77, 122,
		52, 79, 124, 136, 151, 167, 174, 183, 189, 74, 96, 132, 117, 131, 135, 118, 135, 130, 113, 128, 121, 60, 78, 98,
	],
};

/** Whether a picture of this size and 4×4 RGB sample is Scrydex's "no picture" image. */
export function isScrydexNoArt(w: number, h: number, px: number[]): boolean {
	const ref = SCRYDEX_NO_ART[`${w}x${h}`];
	if (ref?.length !== px.length) return false;
	let off = 0;
	for (let i = 0; i < ref.length; i++) off += Math.abs(ref[i] - px[i]);
	return off / ref.length < 10;
}

/** Card ids already seen without a picture, so a remount shows the text face at once. */
const noArtCards: { [id: string]: true } = Object.create(null);

function scrydexUrl(src: string): boolean {
	return src.startsWith('https://images.scrydex.com/');
}

/** Sample a loaded, CORS-clean image. Anything that goes wrong reads as "has a picture". */
function sampleNoArt(img: HTMLImageElement): boolean {
	try {
		if (!SCRYDEX_NO_ART[`${img.naturalWidth}x${img.naturalHeight}`]) return false;
		const c = document.createElement('canvas');
		c.width = 4;
		c.height = 4;
		const g = c.getContext('2d', { willReadFrequently: true } as any) as CanvasRenderingContext2D | null;
		if (!g) return false;
		g.drawImage(img, 0, 0, 4, 4);
		const d = g.getImageData(0, 0, 4, 4).data;
		const px: number[] = [];
		for (let i = 0; i < d.length; i += 4) px.push(d[i], d[i + 1], d[i + 2]);
		return isScrydexNoArt(img.naturalWidth, img.naturalHeight, px);
	} catch {
		return false;
	}
}

/** A face-up card with no picture: what the viewer is allowed to know, readable, and unlike a card back. */
function NoArtFace(props: { cardId: string, label: string }) {
	const k = cardKind(props.cardId);
	const kind = k.energy ? 'Energy' : k.pokemon ? 'Pokémon' : k.stadium ? 'Stadium' : k.tool ? 'Pokémon Tool' :
		k.supporter ? 'Supporter' : k.item ? 'Item' : k.trainer ? 'Trainer' : '';
	const at = props.cardId.lastIndexOf('-');
	const set = at > 0 ? props.cardId.slice(0, at) : props.cardId;
	const num = at > 0 ? props.cardId.slice(at + 1) : '';
	return <span class="tcg-card-noart">
		{kind && <span class="tcg-noart-kind">{kind}</span>}
		<strong class="tcg-noart-name">{props.label}</strong>
		<span class="tcg-noart-id">{num ? `${set.toUpperCase()} · ${num}` : set}</span>
		<span class="tcg-noart-note">No image</span>
	</span>;
}

function CardBackFace() {
	return <img
		class="tcg-card-backface"
		src={cardBackArt()}
		alt=""
		draggable={false}
		onError={(ev: any) => {
			const img = ev?.currentTarget as HTMLImageElement | null;
			if (!img) return;
			if (img.dataset.fallback === '1') return;
			img.dataset.fallback = '1';
			img.src = CARD_BACK_FALLBACK;
		}}
	/>;
}

/** A picker card whose art failed: the other art source, then the card back. */
function choiceArtFallback(img: HTMLImageElement) {
	const id = img.dataset.card || '';
	const step = Number(img.dataset.fallback || '0');
	img.dataset.fallback = String(step + 1);
	if (step === 0) img.src = cardArt(id, { large: true }) || cardBackArt();
	else if (step === 1) img.src = cardBackArt();
}

function pileCount(pile: string[] | { count: number } | undefined): number {
	if (!pile) return 0;
	return Array.isArray(pile) ? pile.length : pile.count;
}

function handIds(hand: string[] | { count: number } | undefined): string[] | null {
	return Array.isArray(hand) ? hand : null;
}

function sameSlot(a: TcgSlot | null, b: TcgSlot | undefined): boolean {
	if (a == null || b == null) return false;
	if (a === 'active' || b === 'active') return a === b;
	return Number(a) === Number(b);
}

function actionTouchesHand(a: TcgAction, hand: number): boolean {
	// A two-card Legendary Stadium is offered on its first half; `pair` is the other half's index.
	return a.hand === hand || (a.type === 'playStadium' && a.pair === hand);
}

/**
 * A two-card Legendary Stadium is offered once for each copy of its first half in the hand. Seen from the second half
 * (`hand`), rows whose first halves are the same printing are one play, so only the first of them is kept. Rows on
 * different first-half printings, and every other action, are kept.
 */
export function samePairedPlays(acts: TcgAction[], hand: number | null | undefined, ids: string[] | null): TcgAction[] {
	if (hand == null || !ids) return acts;
	const seen: { [id: string]: true } = Object.create(null);
	return acts.filter(a => {
		if (a.type !== 'playStadium' || a.pair !== hand || a.hand == null) return true;
		const first = ids[a.hand];
		if (seen[first]) return false;
		seen[first] = true;
		return true;
	});
}

/**
 * What a tap on an opponent's Pokémon may do when no hand card is selected: a board action aimed at it (an attack or
 * Ability target). A play from the hand that targets it (Boss's Orders) waits for that card to be selected or dragged:
 * the tap used to play it straight from the hand, and lit the opponent's Pokémon as tappable while it was held.
 */
export function foeSlotTargets(acts: TcgAction[], slot: TcgSlot): TcgAction[] {
	return acts.filter(a => a.hand == null && actionTouchesSlot(a, slot) && (a.foeSlot != null || a.targetSlot != null));
}

function actionTouchesSlot(a: TcgAction, slot: TcgSlot): boolean {
	return sameSlot(slot, a.slot) || sameSlot(slot, a.foeSlot) || sameSlot(slot, a.targetSlot) ||
		(typeof slot === 'number' && a.bench === slot);
}

function hasBoardTarget(a: TcgAction): boolean {
	return a.slot != null || a.bench != null || a.targetSlot != null || a.foeSlot != null;
}

type DropKind = 'slot' | 'play' | 'stadium' | 'discard';
type DropTarget = {
	kind: DropKind,
	slot?: TcgSlot,
	foe?: boolean,
	empty?: boolean,
};
type DragSource = {
	source: 'hand' | 'zone',
	hand?: number,
	cardId?: string,
	image?: string,
	name?: string,
};
type DragState = DragSource & {
	x: number,
	y: number,
	active: boolean,
	originX: number,
	originY: number,
	over: DropTarget | null,
	hint: string,
};

function dropVerb(a: TcgAction): string {
	switch (a.type) {
	case 'setActive': return 'Set Active';
	case 'setBench': case 'playBasic': case 'mulliganBench': return 'Bench';
	case 'evolve': return 'Evolve';
	case 'attachEnergy': case 'attachZone': return 'Attach Energy';
	case 'attachTool': return 'Attach Tool';
	case 'playTrainer': return 'Play';
	case 'playStadium': return 'Play Stadium';
	case 'useStadium': return 'Use';
	case 'discardPick': return 'Discard';
	case 'promote': return 'Promote';
	case 'retreat': return 'Retreat here';
	case 'pickSlot': return 'Choose';
	default: return 'Play';
	}
}

export function actionsForDrop(acts: TcgAction[], drag: DragSource, drop: DropTarget): TcgAction[] {
	if (drag.source === 'zone') {
		if (drop.kind !== 'slot' || drop.foe) return [];
		return acts.filter(a => a.type === 'attachZone' && actionTouchesSlot(a, drop.slot!));
	}
	const hand = drag.hand;
	if (hand == null) return [];
	const mine = acts.filter(a => actionTouchesHand(a, hand));
	if (drop.kind === 'play') {
		// Untargeted Trainers land on the Active-field rectangle. Stadium stays on its own slot.
		return mine.filter(a => a.type === 'playTrainer' && !hasBoardTarget(a));
	}
	if (drop.kind === 'stadium') {
		const played = mine.filter(a => a.type === 'playStadium');
		// An in-play Stadium names the hand card on `pick`, not on `hand`.
		const used = drag.cardId ?
			acts.filter(a => a.type === 'useStadium' && a.pick === drag.cardId) : [];
		return played.concat(used);
	}
	if (drop.kind === 'discard') {
		return mine.filter(a => a.type === 'discardPick');
	}
	if (drop.kind === 'slot') {
		if (drop.foe) {
			return mine.filter(a =>
				sameSlot(drop.slot!, a.foeSlot) ||
				(a.type === 'pickSlot' && actionTouchesSlot(a, drop.slot!)) ||
				(a.type === 'playTrainer' && sameSlot(drop.slot!, a.foeSlot))
			);
		}
		if (drop.empty) {
			if (drop.slot === 'active') {
				return mine.filter(a => a.type === 'setActive');
			}
			return mine.filter(a =>
				a.type === 'playBasic' || a.type === 'setBench' || a.type === 'mulliganBench'
			);
		}
		return mine.filter(a => actionTouchesSlot(a, drop.slot!));
	}
	return [];
}

function canDropAnywhere(acts: TcgAction[], drag: DragSource, drop: DropTarget): boolean {
	return actionsForDrop(acts, drag, drop).length > 0;
}

type CardKind = {
	known: boolean,
	pokemon: boolean,
	trainer: boolean,
	energy: boolean,
	basic: boolean,
	stage: boolean,
	supporter: boolean,
	item: boolean,
	stadium: boolean,
	tool: boolean,
};

function cardKind(id: string): CardKind {
	const c = id && typeof getTcgCard === 'function' ? getTcgCard(id) : undefined;
	const subs = (c?.u || []).map(s => s.toLowerCase());
	const has = (name: string) => subs.includes(name);
	return {
		known: !!c,
		pokemon: c?.s === 'P',
		trainer: c?.s === 'T',
		energy: c?.s === 'E' || /energy/i.test(id) || /^sve-\d+$/i.test(id),
		basic: has('basic'),
		stage: subs.some(s => s.startsWith('stage')),
		supporter: has('supporter'),
		item: has('item'),
		stadium: has('stadium'),
		tool: has('pokémon tool') || has('pokemon tool') || has('tool'),
	};
}

type WhyCtx = {
	hand?: number,
	drop?: DropTarget | null,
	slot?: TcgSlot,
	foe?: boolean,
	zone?: boolean,
	source?: 'hand' | 'zone',
};

function handCardId(snap: TcgSnapshot, hand: number): string {
	const you = snap.you;
	if (you == null) return '';
	const handv = snap.players[you]?.hand;
	return Array.isArray(handv) ? (handv[hand] || '') : '';
}

function benchFull(p: TcgPlayerView | undefined, snap: TcgSnapshot): boolean {
	const size = snap.format?.benchSize || (snap.format?.energyZone ? 3 : 5);
	return (p?.bench || []).filter(Boolean).length >= size;
}

function firstPlayerTurn(snap: TcgSnapshot): boolean {
	return snap.status === 'playing' && snap.turnNumber === 1 && snap.turn === snap.you;
}

/** Why this attempt is illegal. Empty when the attempt is legal, or when nothing was attempted. */
function whyCant(snap: TcgSnapshot, ctx: WhyCtx, _waiting?: boolean): string {
	const you = snap.you;
	if (you == null) return '';
	if (snap.status === 'gameover' || snap.winner != null) return 'The game is over.';
	if (snap.pendingPrize && snap.pendingPrize.seat === you) return 'Take a Prize card first.';
	if (snap.pendingSearch) return 'Finish searching your deck first.';
	if (snap.pendingDiscard) return 'Choose a card to discard first.';
	if (snap.pendingRetreatPay) return 'Choose Energy to discard for the retreat.';
	if (snap.pendingDiscardEnergy) return 'Choose an Energy card to discard.';
	if (snap.pendingConfirm) return 'Confirm this effect first.';
	if (snap.pendingPromote != null && snap.pendingPromote === you && ctx.slot == null) {
		return 'Choose a Pokémon to promote first.';
	}
	if (snap.pendingFirst === you) return 'Choose who goes first.';
	if (snap.status === 'playing' && snap.turn !== you) return 'Wait for your turn';

	if (ctx.zone || ctx.source === 'zone') {
		if (ctx.drop) {
			if (actionsForDrop(snap.actions || [], { source: 'zone' }, ctx.drop).length) return '';
			return whyBadDrop(snap, { ...ctx, source: 'zone' });
		}
		if ((snap.actions || []).some(a => a.type === 'attachZone')) return 'Drop this Energy on one of your Pokémon.';
		return whyZone(snap);
	}
	if (ctx.hand != null) {
		const drag: DragSource = { source: 'hand', hand: ctx.hand };
		if (ctx.drop) {
			if (actionsForDrop(snap.actions || [], drag, ctx.drop).length) return '';
			return whyBadDrop(snap, { ...ctx, source: 'hand' });
		}
		const mine = (snap.actions || []).filter(a => actionTouchesHand(a, ctx.hand!));
		if (!mine.length) return whyCard(snap, handCardId(snap, ctx.hand));
		return whereToPlay(mine);
	}
	if (ctx.slot != null) return whyMon(snap, ctx.slot);
	return '';
}

/**
 * Several legal rows match one tap or drop and differ only in a choice the board cannot show (a `pick`, as in
 * Erika's "draw up to 3"). The board never guesses one: it says why nothing was played. An interim message;
 * the card itself needs to ask after it is played.
 */
export function ambiguousPlayText(hits: TcgAction[]): string {
	return `This card can be played ${hits.length} different ways here, and the board can't offer that choice yet. Nothing was played.`;
}

function whereToPlay(acts: TcgAction[]): string {
	const types: { [k: string]: true } = {};
	for (const a of acts) types[a.type] = true;
	if (types['attachEnergy'] || types['attachTool']) return 'Drop this on one of your Pokémon.';
	if (types['evolve']) return 'Drop this on the Pokémon it evolves from.';
	if (types['playBasic'] || types['setBench'] || types['setActive'] || types['mulliganBench']) {
		return 'Drop this on an empty spot on your side.';
	}
	if (types['playStadium']) return 'Drop this on the Stadium spot.';
	if (types['discardPick']) return 'Drop this on the discard pile.';
	if (types['playTrainer']) return 'Drop this on the Active field.';
	return 'Drop this on a valid target.';
}

function whyZone(snap: TcgSnapshot): string {
	const me = snap.you != null ? snap.players[snap.you] : undefined;
	if (snap.status === 'setup') return "You can't attach Energy during setup.";
	if (firstPlayerTurn(snap) && snap.format?.energyZone) return "You can't attach Energy on your first turn.";
	if (me?.attachedEnergyThisTurn) return 'You already attached Energy this turn.';
	if (me?.energyZone && me.energyZone.ready === false) return 'You already attached Energy this turn.';
	return "You can't attach Energy right now.";
}

function whyCard(snap: TcgSnapshot, id: string): string {
	const me = snap.you != null ? snap.players[snap.you] : undefined;
	const k = cardKind(id);
	const pocket = !!snap.format?.energyZone;
	if (snap.status === 'setup') {
		if (!(k.pokemon && k.basic)) return 'During setup you can only play Basic Pokémon.';
		if (me?.active && benchFull(me, snap)) return 'Your Bench is full.';
		return "You can't play this Pokémon right now.";
	}
	if (k.supporter) {
		if (me?.playedSupporterThisTurn) return 'You already played a Supporter this turn.';
		if (firstPlayerTurn(snap)) return "You can't play a Supporter on your first turn.";
	}
	if (k.stadium && me?.playedStadiumThisTurn) return 'You already played a Stadium this turn.';
	if (k.energy) {
		if (me?.attachedEnergyThisTurn) return 'You already attached Energy this turn.';
		if (firstPlayerTurn(snap) && pocket) return "You can't attach Energy on your first turn.";
		return "You can't attach Energy right now.";
	}
	if (k.stage) {
		if (firstPlayerTurn(snap)) return "You can't evolve on your first turn.";
		return "This card can't evolve any of your Pokémon right now.";
	}
	if (k.pokemon && k.basic) {
		if (me?.active && benchFull(me, snap)) return 'Your Bench is full.';
		return "You can't play this Pokémon right now.";
	}
	if (k.tool) return "You can't attach this Tool right now.";
	return "You can't play this card right now.";
}

function whyBadDrop(snap: TcgSnapshot, ctx: WhyCtx): string {
	const drop = ctx.drop;
	if (!drop) return '';
	const fromZone = ctx.source === 'zone';
	const id = fromZone || ctx.hand == null ? '' : handCardId(snap, ctx.hand);
	const k = cardKind(id);
	const acts = (snap.actions || []).filter(a =>
		fromZone ? a.type === 'attachZone' : ctx.hand != null && actionTouchesHand(a, ctx.hand));
	if (!acts.length) return fromZone ? whyZone(snap) : whyCard(snap, id);
	if (drop.kind === 'discard') return "You're not discarding a card right now.";
	if (drop.kind === 'stadium') {
		return k.stadium ? "You can't play that Stadium right now." : 'Only Stadium cards go on the Stadium spot.';
	}
	if (drop.kind === 'play') {
		if (k.pokemon || fromZone) return fromZone ? 'Drop this Energy on one of your Pokémon.' : 'Drop this Pokémon on an empty spot on your side.';
		if (k.energy) return 'Drop this Energy on one of your Pokémon.';
		if (k.stadium) return 'Drop Stadium cards on the Stadium spot.';
		if (k.tool) return 'Drop this Tool on one of your Pokémon.';
		return "You can't play this card there.";
	}
	if (drop.foe) {
		if (k.energy || fromZone) return 'Energy attaches to your own Pokémon.';
		if (k.pokemon) return 'Play this on your side of the board.';
		if (k.tool) return 'Tools attach to your own Pokémon.';
		return "This card can't target that Pokémon.";
	}
	if (drop.empty) {
		if (k.energy || fromZone) return 'Drop Energy on a Pokémon, not an empty spot.';
		if (k.stage) return 'Drop this on the Pokémon it evolves from.';
		if (k.trainer || k.tool) return "This card doesn't play onto an empty spot.";
		if (drop.slot === 'active') return "This can't be your Active Pokémon.";
		return 'That Bench spot is not open for this card.';
	}
	if (k.energy || fromZone) {
		return acts.some(a => a.type === 'attachEnergy' || a.type === 'attachZone') ?
			"You can't attach Energy to that Pokémon." :
			(fromZone ? whyZone(snap) : whyCard(snap, id));
	}
	if (k.basic && !k.stage) return 'That spot is taken.';
	if (k.stage) return "This card can't evolve that Pokémon.";
	if (k.tool) return "You can't attach this Tool to that Pokémon.";
	if (k.stadium) return 'Drop Stadium cards on the Stadium spot.';
	if (k.trainer) return "This card can't target that Pokémon.";
	return "You can't play this card there.";
}

function whyMon(snap: TcgSnapshot, slot: TcgSlot): string {
	const me = snap.you != null ? snap.players[snap.you] : undefined;
	const mon = slot === 'active' ? me?.active : me?.bench?.[Number(slot)];
	if (!mon) return 'There is no Pokémon there.';
	if (snap.status === 'setup') return 'Finish setting up your Pokémon first.';
	if (slot !== 'active') {
		if (snap.pendingPromote != null && snap.pendingPromote === snap.you) {
			return 'Choose a different Pokémon to promote.';
		}
		return "This Pokémon can't use an Ability right now.";
	}
	const st = String(mon.status || '').toLowerCase();
	if (st === 'asleep') return 'This Pokémon is Asleep and can\'t attack or retreat.';
	if (st === 'paralyzed') return 'This Pokémon is Paralyzed and can\'t attack or retreat.';
	if (firstPlayerTurn(snap)) return "You can't attack on your first turn.";
	const energy = mon.energy || [];
	if (!energy.length) return "This Pokémon doesn't have enough Energy to attack.";
	const acts = (snap.actions || []).filter(a =>
		a.type === 'attack' || a.type === 'retreat' ||
		(a.type === 'ability' && (a.slot == null || a.slot === 'active' || sameSlot(a.slot, 'active')))
	);
	if (!acts.length) {
		// Official rules: you may still attack after retreating (once per turn).
		// `retreatedThisTurn` only blocks a second retreat — never the attack itself.
		if (me?.attachedEnergyThisTurn && !(mon.attacks || []).length) {
			return "This Pokémon can't attack or retreat right now.";
		}
		return "This Pokémon has no actions available right now.";
	}
	return "This Pokémon can't attack or retreat right now.";
}

function sameDrop(a: DropTarget | null, b: DropTarget | null): boolean {
	if (!a || !b) return a === b;
	if (a.kind !== b.kind) return false;
	if (a.kind === 'slot') {
		return a.foe === b.foe && sameSlot(a.slot!, b.slot) && !!a.empty === !!b.empty;
	}
	return true;
}

/**
 * Prize cards still face down. The opening replay snapshot is taken before prizes are set
 * out (count 0), so during setup report the full set rather than "all taken".
 */
function prizesLeft(p: TcgPlayerView, need: number, status?: string): number {
	const n = pileCount(p.prizes);
	return status === 'setup' && !n ? need : n;
}
export function scoredPoints(p: TcgPlayerView, need: number, status?: string): number {
	if (typeof p.points === 'number') return p.points;
	return Math.max(0, need - prizesLeft(p, need, status));
}

function pip(type: string, key?: string | number) {
	const t = energyElement(type) || type || 'colorless';
	return <span
		key={key}
		class={`tcg-pip t-${t}`}
		title={t}
		style={{ background: TYPE_COLOR[t] || '#9aa' }}
	>
		<img src={energySymbolUrl(t)} alt="" draggable={false} />
	</span>;
}

/** Cards sitting on a Pokémon: basic Energy, Special Energy, and Tools. */
type AttachedKind = 'energy' | 'special' | 'tool';
type AttachedCard = { cardId: string, kind: AttachedKind };

/** True for synthetic / printed Basic Energy ids (not Double Colorless, Jet, etc.). */
function isBasicEnergyCardId(cardId?: string): boolean {
	if (!cardId) return false;
	const id = cardId.toLowerCase();
	if (/^(?:energy|zone-energy)-(grass|fire|water|lightning|psychic|fighting|darkness|metal|fairy|dragon|colorless)$/.test(id)) {
		return true;
	}
	const sve = /^sve-(\d+)$/.exec(id);
	if (sve) {
		const n = Number(sve[1]);
		// sve-1..8 = basics; sve-9..16 = reverse basics. Higher ids are special prints.
		return n >= 1 && n <= 16;
	}
	return !!({
		'base1-97': 1, 'base1-98': 1, 'base1-99': 1,
		'base1-100': 1, 'base1-101': 1, 'base1-102': 1,
	} as { [k: string]: 1 })[id];
}

function attachedKindLabel(kind: AttachedKind): string {
	if (kind === 'tool') return 'Tool';
	if (kind === 'special') return 'Special Energy';
	return 'Energy';
}

function attachedCardsOf(mon: TcgPokemonView | null | undefined): AttachedCard[] {
	if (!mon) return [];
	const out: AttachedCard[] = [];
	const cards = mon.energyCards;
	if (cards?.length) {
		for (const id of cards) {
			if (!id) continue;
			out.push({ cardId: id, kind: isBasicEnergyCardId(id) ? 'energy' : 'special' });
		}
	} else {
		for (const t of mon.energy || []) {
			const el = energyElement(t) || 'colorless';
			out.push({ cardId: `energy-${el}`, kind: 'energy' });
		}
	}
	for (const id of mon.tools || []) {
		// Skip the placeholder the event applier uses when the server omits cardId.
		if (!id || id === 'tool') continue;
		out.push({ cardId: id, kind: 'tool' });
	}
	return out;
}

/** Prefer Tools and Special Energy in the visible peek so they are not buried under basics. */
function attachPeeksOf(cards: AttachedCard[], max = 3): AttachedCard[] {
	if (cards.length <= max) return cards;
	const tools = cards.filter(c => c.kind === 'tool');
	const specials = cards.filter(c => c.kind === 'special');
	const basics = cards.filter(c => c.kind === 'energy');
	const preferred = tools.concat(specials);
	if (preferred.length >= max) return preferred.slice(preferred.length - max);
	const need = max - preferred.length;
	return preferred.concat(basics.slice(Math.max(0, basics.length - need)));
}

/**
 * The pile the player browses. The committed snapshot wins over an in-progress
 * animation board, which can still be the pre-search table while the legal
 * picks (Trainers only, for a Trainer search) are already clickable.
 */
export function searchPromptState(
	committed: TcgSnapshot['pendingSearch'] | undefined,
	painted: TcgSnapshot['pendingSearch'] | undefined,
): TcgSnapshot['pendingSearch'] | undefined {
	if (committed?.ids?.length || committed?.seen?.length) return committed;
	if (painted?.ids?.length || painted?.seen?.length) return painted;
	return committed || painted;
}

/**
 * Cards the searcher is looking at, in pile order. `legal` is a card that
 * `searchPick` accepts, so the board can highlight it. A look with no `ids`
 * uses `seen`. No list means the older unique-pick rail.
 */
export function searchDeckRows(
	pending: { ids?: string[], seen?: string[], legal?: string[] } | undefined,
	picks: { type: string, pick?: string }[],
): { id: string, legal: boolean }[] | null {
	const ids = pending?.ids?.length ? pending.ids : (pending?.seen?.length ? pending.seen : null);
	if (!ids) return null;
	const offered = new Set(picks.filter(a => a.type === 'searchPick' && a.pick).map(a => String(a.pick)));
	const marked = Array.isArray(pending?.legal) ? new Set(pending.legal) : null;
	return ids.map(id => ({
		id,
		legal: marked ? marked.has(id) && offered.has(id) : offered.has(id),
	}));
}

export function promptHeading(
	snap: TcgSnapshot,
	kind: string,
	searchState?: TcgSnapshot['pendingSearch'],
): { title: string, sub?: string } {
	if (kind === 'confirm') {
		return {
			title: snap.pendingConfirm?.title || 'Confirm',
			sub: snap.pendingConfirm?.text,
		};
	}
	if (kind === 'first') return { title: 'Who goes first?' };
	if (kind === 'search') {
		const ps = searchState || snap.pendingSearch;
		if (ps?.seen?.length) {
			return {
				title: 'Look',
				// A look-only search (Metal Maker, nothing eligible) has an empty `legal`: nothing is highlighted.
				sub: ps.legal?.length ?
					'Highlighted cards can be taken. The rest stay where they are.' :
					'Only you can see these cards. Press Done to continue.',
			};
		}
		const left = ps?.left != null ? ` · ${ps.left} left` : '';
		const dest = ps?.dest === 'bench' ? ' to the Bench' :
			ps?.dest === 'active' ? ' to Active' :
			ps?.dest === 'hand' ? ' to your hand' : '';
		const zone = ps?.zone === 'discard' ? 'from your discard pile' :
			ps?.zone === 'prizes' || ps?.zone === 'prize' ? 'from your Prize cards' : 'from your deck';
		const highlight = ps?.ids?.length && !ps.seen?.length ?
			`. ${ps.ids.length} cards, in order. Highlighted cards can be taken.` :
			ps?.ids?.length ? '. Highlighted cards can be taken.' : '';
		return { title: `Choose a card${dest}`, sub: `${zone}${left}${highlight}` };
	}
	if (kind === 'pay') return { title: 'Discard Energy to retreat', sub: snap.pendingRetreatPay?.need != null ? `Choose ${snap.pendingRetreatPay.need}` : undefined };
	if (kind === 'energyDiscard') {
		const left = snap.pendingDiscardEnergy?.need;
		return { title: 'Discard Energy', sub: left != null ? `Choose ${left}` : undefined };
	}
	if (kind === 'mulligan') return { title: 'Mulligan', sub: 'Place Basics on the Bench, or keep your hand' };
	if (kind === 'endTurn') return { title: 'End your turn?', sub: 'You still have actions available' };
	if (kind === 'prize') return { title: 'Take a Prize card', sub: 'Select a Prize from your prize row' };
	if (kind === 'copy') {
		const ask = snap.pendingCopyAttack?.ask;
		const from = choiceFromText(snap.pendingCopyAttack?.from, iid => findChoiceMon(snap, iid)?.mon.name || '', cardLabel);
		if (ask) return { title: ask, sub: from || undefined };
		return {
			title: snap.pendingCopyAttack?.title || 'Make a choice',
			sub: snap.pendingCopyAttack?.text,
		};
	}
	return { title: 'Choose' };
}

/**
 * A row of a `copy` prompt the engine is waiting on from this seat. Such a choice can belong to the player
 * whose turn it is not (Erika: "then your opponent may draw up to 3 cards"); its rows reuse the attack shape,
 * so the turn check must not treat them as an attack. Only a row the engine currently offers this seat counts.
 * An ordinary attack or Ability off-turn is not one: there is no `pendingCopyAttack` then (the engine and
 * `applyTcgEvent` set it only for the chooser).
 */
export function isOwnCopyPromptRow(snap: TcgSnapshot, a: TcgAction): boolean {
	if (!snap.pendingCopyAttack || (a.type !== 'attack' && a.type !== 'ability')) return false;
	return (snap.actions || []).some(x => x.type === a.type && x.index === a.index && x.pick === a.pick &&
		x.slot === a.slot && x.foeSlot === a.foeSlot && x.bench === a.bench && x.targetSlot === a.targetSlot);
}

/** Yes and skip for one attack: the printed "you may" choice, not two copies of the attack. */
function optionalAttackPair(acts: TcgAction[], index: number): { yes: TcgAction, no: TcgAction } | null {
	const group = acts.filter(a => a.type === 'attack' && a.index === index);
	if (group.length !== 2) return null;
	const yes = group.find(a => a.pick === 'yes');
	const no = group.find(a => a.pick === 'skip');
	if (!yes || !no) return null;
	return { yes, no };
}

/**
 * The printing a labeled choice stands for, when the choice is a card (Gwynn's "discard a Pokémon", a Tool to pick, ...):
 * the row's `pick` is that card's printing id, `label` only its name. Two printings of one name are two rows with the
 * same label, so the choice has to be shown by the card, not the name. Null for any other choice ("done", an attack name).
 */
export function copyPickCardId(a: TcgAction): string | null {
	if (a.type !== 'attack' || typeof a.pick !== 'string') return null;
	return /^(?:[A-Za-z0-9]+-)+[A-Za-z]{0,6}\d+[A-Za-z]?$/.test(a.pick) ? a.pick : null;
}

export function describeAction(a: TcgAction, snap: TcgSnapshot, opts?: { targetOnly?: boolean, moveOnly?: boolean }): string {
	const me = snap.players[snap.you ?? 0];
	const foe = snap.players[snap.you === 0 ? 1 : 0];
	const handId = (i?: number) => (Array.isArray(me?.hand) && i != null ? me.hand[i] : '') || '';
	const cardAt = (i?: number) => cardLabel(handId(i));
	const slotName = (s?: TcgSlot) => s === 'active' ? 'Active' : s == null ? '' : `Bench ${Number(s) + 1}`;
	const monAt = (s?: TcgSlot, side?: TcgPlayerView | undefined) => {
		const p = side || me;
		if (!p || s == null) return '';
		if (s === 'active') return p.active?.name || 'Active';
		return p.bench?.[Number(s)]?.name || slotName(s);
	};
	// Two of the same name (two Trubbish) need the slot, or every choice looks identical.
	const labeledMon = (s: TcgSlot | undefined, side?: TcgPlayerView) => {
		const name = monAt(s, side);
		if (!name || s == null || !side) return name || slotName(s);
		let same = side.active?.name === name ? 1 : 0;
		for (const b of side.bench || []) if (b?.name === name) same++;
		if (same < 2) return name;
		if (s === 'active') return `${name} (Active)`;
		return `${name} (Bench ${Number(s) + 1})`;
	};
	const targetIsFoe = a.foeSlot != null;
	// Prefer explicit foe fields; otherwise resolve against both sides.
	const targetLabel = (() => {
		if (a.foeSlot != null) return monAt(a.foeSlot, foe) || `Foe ${slotName(a.foeSlot)}`;
		if (a.targetSlot != null) {
			const mine = monAt(a.targetSlot, me);
			const theirs = monAt(a.targetSlot, foe);
			if (mine && !theirs) return mine;
			if (theirs && !mine) return theirs;
			if (targetIsFoe) return theirs || mine || slotName(a.targetSlot);
			return mine || theirs || slotName(a.targetSlot);
		}
		if (a.bench != null && (a.type === 'retreat' || a.type === 'promote' || a.type === 'ability' || a.type === 'attack')) {
			return monAt(a.bench, me) || slotName(a.bench);
		}
		// Attack `slot` is the friendly Pokémon the move heals or switches, not a second copy of the move.
		if (a.type === 'attack' && a.slot != null) return labeledMon(a.slot, me);
		if (a.slot != null && a.type !== 'attack' && a.type !== 'ability') return monAt(a.slot, me) || slotName(a.slot);
		return '';
	})();
	const moveName = (() => {
		if (a.type === 'ability') {
			if (a.name) return a.name;
			const mon = a.slot === 'active' ? me?.active : me?.bench?.[Number(a.slot)];
			return mon?.abilities?.[a.index]?.name || 'Ability';
		}
		if (a.type === 'attack') {
			// A choice that is not an attack (a card, an Energy, "Done") carries what is chosen.
			if (a.label) return a.label;
			if (a.name) return a.name;
			return me?.active?.attacks?.[a.index]?.name || `Attack ${(a.index ?? 0) + 1}`;
		}
		return '';
	})();
	switch (a.type) {
	case 'setupDone': return 'Ready';
	case 'setupReturn': return 'Return to hand';
	case 'endTurn': return 'End Turn';
	case 'setActive': return `Active: ${cardAt(a.hand)}`;
	case 'setBench': return `Bench: ${cardAt(a.hand)}`;
	case 'playBasic': return `Bench ${cardAt(a.hand)}`;
	case 'evolve': return `Evolve ${monAt(a.slot)} → ${cardAt(a.hand)}`;
	case 'attachEnergy': return `Attach ${cardAt(a.hand)} to ${monAt(a.slot)}`;
	case 'attachTool': return `Tool ${cardAt(a.hand)} → ${monAt(a.slot)}`;
	case 'playTrainer': {
		const errata = a.errata ? ' (errata)' : '';
		return (targetLabel ? `Play ${cardAt(a.hand)} → ${targetLabel}` : `Play ${cardAt(a.hand)}`) + errata;
	}
	case 'playStadium': return `Stadium ${cardAt(a.hand)}`;
	case 'useStadium': return 'Use Stadium';
	case 'ability': {
		if (opts?.moveOnly) return moveName;
		if (opts?.targetOnly && targetLabel) return targetLabel;
		if (targetLabel) return `${moveName} → ${targetLabel}`;
		return moveName;
	}
	case 'retreat': return `Retreat → ${monAt(a.bench)}`;
	case 'payEnergy': return 'Discard Energy';
	case 'discardEnergyPick': return 'Discard this Energy';
	case 'attack': {
		if (opts?.moveOnly) return moveName;
		if (opts?.targetOnly && targetLabel) return targetLabel;
		if (targetLabel) return `${moveName} → ${targetLabel}`;
		return moveName;
	}
	case 'searchPick': return `Take ${cardLabel(a.pick)}`;
	case 'searchDone': return 'Done searching';
	case 'discardPick': return `Discard ${cardAt(a.hand)}`;
	case 'confirmYes': return 'Yes';
	case 'confirmNo': return 'No';
	case 'promote': return `Promote ${monAt(a.bench)}`;
	case 'chooseFirst': return a.goFirst ? 'Go first' : 'Go second';
	case 'mulliganBench': return `Bench ${cardAt(a.hand)}`;
	case 'mulliganDone': return 'Keep hand';
	case 'takePrize': return `Take Prize ${Number(a.index) + 1}`;
	case 'attachZone': return `Attach Energy → ${monAt(a.slot)}`;
	case 'pickSlot': {
		const pickSide = a.foeSlot != null ? foe : me;
		const pick = a.foeSlot != null ? a.foeSlot : a.slot;
		return `Choose ${monAt(pick, pickSide) || slotName(pick)}`;
	}
	case 'playVunion': return `Play ${a.name || 'V-UNION'}`;
	default: return a.type;
	}
}

/** True when several actions share a move/ability identity but differ by board target. */
export function actionTargetKey(a: TcgAction): string {
	if (a.foeSlot != null) return `f:${a.foeSlot}`;
	if (a.targetSlot != null) return `t:${a.targetSlot}`;
	if (a.bench != null) return `b:${a.bench}`;
	if (a.slot != null) return `s:${a.slot}`;
	return '';
}
export function actionIdentityKey(a: TcgAction): string {
	if (a.type === 'attack') {
		const pick = (a.pick === 'yes' || a.pick === 'skip') ? '' : (a.pick || '');
		return `attack:${a.index != null ? a.index : (a.name || '')}:${pick}`;
	}
	if (a.type === 'ability') {
		return `ability:${a.index != null ? a.index : (a.name || '')}:${a.slot ?? ''}:${a.pick || ''}`;
	}
	return `${a.type}:${a.name || a.index || ''}`;
}
export function isMultiTargetGroup(acts: TcgAction[], a: TcgAction): boolean {
	const id = actionIdentityKey(a);
	const keys = new Set<string>();
	for (const x of acts) {
		if (actionIdentityKey(x) !== id) continue;
		const k = actionTargetKey(x);
		if (k) keys.add(k);
	}
	return keys.size > 1;
}

/** Every legal action that is the same attack/ability as `a` (all board targets). */
export function moveTargetGroup(acts: TcgAction[], a: TcgAction): TcgAction[] {
	const id = actionIdentityKey(a);
	return acts.filter(x => actionIdentityKey(x) === id);
}

/**
 * One row per board target. Optional "you may" yes/skip pairs for the same target
 * collapse here; the may-prompt runs after the target is chosen.
 */
export function uniqueTargetActions(group: TcgAction[]): TcgAction[] {
	const seen: string[] = [];
	return group.filter(a => {
		const k = actionTargetKey(a) || `_:${seen.length}`;
		if (seen.includes(k)) return false;
		seen.push(k);
		return true;
	});
}

/**
 * Target picker row: Pokémon name + which spot it sits in (Active / Bench N),
 * and whether it is yours or the opponent's.
 */
export function describeMoveTarget(a: TcgAction, snap: TcgSnapshot): { name: string, slot: string, foe: boolean } {
	const me = snap.players[snap.you ?? 0];
	const foe = snap.players[snap.you === 0 ? 1 : 0];
	const slotWord = (s?: TcgSlot | null) => {
		if (s === 'active') return 'Active';
		if (s == null) return '';
		return `Bench ${Number(s) + 1}`;
	};
	const monName = (s: TcgSlot | undefined, side?: TcgPlayerView) => {
		if (!side || s == null) return '';
		if (s === 'active') return side.active?.name || '';
		return side.bench?.[Number(s)]?.name || '';
	};
	if (a.foeSlot != null) {
		const slot = slotWord(a.foeSlot);
		return { name: monName(a.foeSlot, foe) || slot || 'Opponent', slot, foe: true };
	}
	if (a.targetSlot != null) {
		const mine = monName(a.targetSlot, me);
		const theirs = monName(a.targetSlot, foe);
		const slot = slotWord(a.targetSlot);
		if (theirs && !mine) return { name: theirs, slot, foe: true };
		if (mine) return { name: mine, slot, foe: false };
		return { name: slot || 'Pokémon', slot, foe: false };
	}
	if (a.bench != null && (a.type === 'retreat' || a.type === 'promote' || a.type === 'ability' || a.type === 'attack')) {
		const slot = slotWord(a.bench);
		return { name: monName(a.bench, me) || slot, slot, foe: false };
	}
	if (a.type === 'attack' && a.slot != null) {
		const slot = slotWord(a.slot);
		return { name: monName(a.slot, me) || slot, slot, foe: false };
	}
	if (a.slot != null && a.type === 'ability') {
		const slot = slotWord(a.slot);
		return { name: monName(a.slot, me) || slot, slot, foe: false };
	}
	const fallback = describeAction(a, snap, { targetOnly: true });
	return { name: fallback || 'Pokémon', slot: '', foe: false };
}

/**
 * Retreat actions that differ only by which bench they switch to are one menu row.
 * A different cost, name, or effect stays its own row. `bench` is chosen afterward.
 */
export function retreatSignature(a: TcgAction): string {
	const keys = Object.keys(a).filter(k => k !== 'type' && k !== 'bench').sort();
	return keys.map(k => `${k}:${JSON.stringify(a[k])}`).join('|');
}

/**
 * One bench: send that action. Several benches of the same retreat: ask which.
 * `bench` is the second click. A different signature is a different choice.
 */
export function retreatChoice(actions: TcgAction[], clicked: TcgAction, bench?: number): TcgAction | 'pick' | null {
	if (clicked.type !== 'retreat') return null;
	const sig = retreatSignature(clicked);
	const group = actions.filter(a => a.type === 'retreat' && retreatSignature(a) === sig);
	if (bench == null) {
		if (group.length === 1) return group[0];
		return group.length > 1 ? 'pick' : null;
	}
	const hit = group.filter(a => a.bench === bench);
	return hit.length === 1 ? hit[0] : null;
}

/**
 * A trainer whose plays are deck indexes (`"0,1"`) and whose actions carry the
 * private pile (`lookPile`). One action is already decided; several need a choice.
 */
export function deckLookPrompt(acts: TcgAction[], hand: number): {
	pile: string[], need: number, note: string, actions: TcgAction[],
} | null {
	const group = acts.filter(a =>
		a.type === 'playTrainer' && a.hand === hand && !hasBoardTarget(a) &&
		Array.isArray(a.lookPile) && a.lookPile.length > 0 && deckLookIndexes(a.pick)
	);
	if (group.length < 2) return null;
	const pile = group[0].lookPile as string[];
	const need = deckLookIndexes(group[0].pick)?.length || 0;
	if (need < 1) return null;
	if (group.some(a => (deckLookIndexes(a.pick)?.length || 0) !== need)) return null;
	return {
		pile,
		need,
		note: String(group[0].lookNote || `Choose ${need}.`),
		actions: group,
	};
}

/** The legal play whose pick indexes are exactly `picked`. */
export function deckLookAction(actions: TcgAction[], picked: number[]): TcgAction | null {
	const key = picked.slice().sort((a, b) => a - b).join(',');
	return actions.find(a => {
		const idxs = deckLookIndexes(a.pick);
		if (!idxs) return false;
		return idxs.slice().sort((x, y) => x - y).join(',') === key;
	}) || null;
}

function deckLookIndexes(pick: unknown): number[] | null {
	const raw = String(pick ?? '');
	if (!/^\d+(,\d+)*$/.test(raw)) return null;
	const idxs = raw.split(',').map(Number);
	if (idxs.some(i => !Number.isInteger(i) || i < 0)) return null;
	return idxs;
}

/**
 * Action-menu rows. Does not remove anything from the server's legal-action list.
 * Multi-target attacks/abilities collapse to one row (target picker opens on click).
 * Optional "you may" pairs collapse to one row. Retreats collapse by signature.
 */
export function visibleMenuActions(menu: TcgAction[]): TcgAction[] {
	const seenRetreat: string[] = [];
	const seenMove: string[] = [];
	return menu.filter(a => {
		if (a.type === 'retreat') {
			const sig = retreatSignature(a);
			if (seenRetreat.includes(sig)) return false;
			seenRetreat.push(sig);
			return true;
		}
		if (a.type === 'attack' || a.type === 'ability') {
			const id = actionIdentityKey(a);
			if (seenMove.includes(id)) return false;
			seenMove.push(id);
			return true;
		}
		return true;
	});
}

export function menuActionsForSlot(acts: TcgAction[], slot: TcgSlot): TcgAction[] {
	if (slot === 'active') {
		return acts.filter(a =>
			a.type === 'attack' ||
			a.type === 'retreat' ||
			(a.type === 'setupReturn' && a.slot === 'active') ||
			(a.type === 'ability' && (a.slot == null || a.slot === 'active' || sameSlot(a.slot, 'active')))
		);
	}
	return acts.filter(a =>
		(a.type === 'ability' && actionTouchesSlot(a, slot)) ||
		(a.type === 'useStadium' && actionTouchesSlot(a, slot)) ||
		(a.type === 'promote' && typeof slot === 'number' && a.bench === slot) ||
		(a.type === 'setupReturn' && typeof slot === 'number' && a.slot === slot)
	);
}

/** The Stadium spot sends this when it is the only way to use the Stadium. Several picks or Bench targets wait for that card or Pokémon. */
export function stadiumClickAction(acts: TcgAction[]): TcgAction | null {
	const uses = acts.filter(a => a.type === 'useStadium');
	return uses.length === 1 ? uses[0] : null;
}

export function stadiumUseHint(acts: TcgAction[]): string {
	const uses = acts.filter(a => a.type === 'useStadium');
	if (uses.length < 2) return '';
	if (uses.some(a => a.pick)) return 'Drag a card onto the Stadium.';
	if (uses.some(a => a.bench != null)) return 'Choose a Pokémon.';
	return 'Choose how to use the Stadium.';
}

/**
 * Wave-TCG `takePrize.index` is 0-based into the prizes still in the pile.
 * Each take splices that entry, so later indexes shift. These are the original
 * slots (0 = Prize 1) still face down, in that same order.
 * With no take history and a short pile, only the count is known: the tail is
 * treated as taken, because the slot identity was not in the log.
 */
export function prizeSlotsAfterTakes(max: number, takes: number[]): number[] {
	const available: number[] = [];
	for (let i = 0; i < max; i++) available.push(i);
	for (const raw of takes) {
		const index = Number(raw);
		if (!Number.isInteger(index) || index < 0 || index >= available.length) continue;
		available.splice(index, 1);
	}
	return available;
}

export type PrizeRailSlot = {
	slot: number,
	taken: boolean,
	/** Index into the current pile, or null once that original slot is gone. */
	serverIndex: number | null,
};

/**
 * `picked` holds indexes the player just sent, still aimed at the current pile.
 * Pass them only while the snapshot count has not dropped yet; once `takes`
 * already accounts for the snapshot, pass an empty list.
 */
export function bindPrizeSlots(
	max: number,
	remaining: number,
	takes: number[],
	picked: number[]
): PrizeRailSlot[] {
	const n = Math.max(0, max);
	const left = Math.max(0, Math.min(n, remaining));
	let available: number[];
	if (!takes.length && left < n) {
		available = [];
		for (let i = 0; i < left; i++) available.push(i);
	} else {
		available = prizeSlotsAfterTakes(n, takes);
	}
	const taken: boolean[] = [];
	for (let i = 0; i < n; i++) taken[i] = !available.includes(i);
	for (const raw of picked) {
		const index = Number(raw);
		if (!Number.isInteger(index) || index < 0 || index >= available.length) continue;
		const slot = available[index];
		if (slot != null) taken[slot] = true;
	}
	const out: PrizeRailSlot[] = [];
	for (let slot = 0; slot < n; slot++) {
		const serverIndex = available.indexOf(slot);
		out.push({
			slot,
			taken: !!taken[slot],
			serverIndex: serverIndex < 0 ? null : serverIndex,
		});
	}
	return out;
}

/** Indexes clicked in this prompt, dropped once the snapshot pile has shrunk. */
export function pendingPrizePicks(pickBase: number, remaining: number, picked: number[]): number[] {
	if (!(pickBase > 0) || remaining < pickBase) return [];
	return picked.slice();
}

/**
 * The Take Prize choice arrives on the same payload as the attack that caused it.
 * That payload is painted immediately so other choices stay clickable, which
 * opened this prompt on top of the attack and Knock Out clips. Keep it hidden
 * until those clips finish. A later prize-only payload (no attack or KO) shows
 * right away. `fxKind` covers the last frame, after playback has already stopped
 * but the Knock Out class is still on the card.
 */
export function prizePromptWaits(
	playing: boolean,
	events?: { type?: string }[] | null,
	fxKind?: string | null,
): boolean {
	if (fxKind === 'attack' || fxKind === 'ability' || fxKind === 'ko' || fxKind === 'damage') return true;
	if (!playing || !events?.length) return false;
	for (let i = 0; i < events.length; i++) {
		const t = events[i]?.type;
		if (t === 'attack' || t === 'ability' || t === 'ko' || t === 'damage') return true;
	}
	return false;
}

/** `throughIndex` < 0 counts the whole log (a live game). Replay seek passes the stop. */
export function collectPrizeTakes(
	items: { events?: TcgEvent[] | null }[],
	throughIndex: number
): [number[], number[]] {
	const takes: [number[], number[]] = [[], []];
	const end = throughIndex < 0 ? items.length - 1 : throughIndex;
	for (let i = 0; i <= end && i < items.length; i++) {
		const events = items[i].events || [];
		for (const ev of events) {
			if (ev.type !== 'act' || !ev.action || ev.action.type !== 'takePrize') continue;
			const seat: 0 | 1 | null = ev.seat === 1 ? 1 : ev.seat === 0 ? 0 : null;
			if (seat == null) continue;
			const index = Number(ev.action.index);
			if (!Number.isFinite(index)) continue;
			takes[seat].push(index);
		}
	}
	return takes;
}

class TcgCardFace extends preact.Component<{
	cardId?: string, image?: string, name?: string, size?: CardSize, selected?: boolean,
	back?: boolean, playable?: boolean, arrive?: boolean, fanIndex?: number, fanCount?: number,
	dragging?: boolean, pocket?: boolean,
	/** Still flying to this hand: its place is kept, and it shows when the flight lands. */
	incoming?: boolean,
	onClick?: (ev?: MouseEvent) => void,
	onInspect?: (p: Preview) => void,
	onPointerDown?: (e: PointerEvent) => void,
	/** Long-press opened the preview; cancel a play that pointer-up would send. */
	onHold?: () => void,
}, { noArt?: string }> {
	holdTimer: number | null = null;
	held = false;
	holdX = 0;
	holdY = 0;
	clearHold() {
		if (this.holdTimer != null) window.clearTimeout(this.holdTimer);
		this.holdTimer = null;
	}
	inspect = (ev?: Event) => {
		const { cardId, image, name, back, onInspect } = this.props;
		if (back || !cardId || !onInspect) return;
		ev?.preventDefault();
		ev?.stopPropagation();
		onInspect({ cardId, image, name });
	};
	onPointerDown = (ev: PointerEvent) => {
		if (ev.button != null && ev.button !== 0) return;
		ev.stopPropagation();
		this.held = false;
		this.holdX = ev.clientX;
		this.holdY = ev.clientY;
		this.props.onPointerDown?.(ev);
		if (!this.props.onInspect || this.props.back || !this.props.cardId) return;
		this.clearHold();
		this.holdTimer = window.setTimeout(() => {
			this.holdTimer = null;
			this.held = true;
			this.props.onHold?.();
			this.inspect();
		}, 420);
	};
	onPointerUp = () => {
		this.clearHold();
	};
	onPointerMove = (ev: PointerEvent) => {
		if (this.holdTimer == null) return;
		if (Math.hypot(ev.clientX - this.holdX, ev.clientY - this.holdY) > 8) this.clearHold();
	};
	onClick = (ev: MouseEvent) => {
		if (this.held) {
			this.held = false;
			ev.preventDefault();
			ev.stopPropagation();
			return;
		}
		if (!this.props.onClick) return; // let parents handle (e.g. discard pile browse)
		ev.stopPropagation();
		this.props.onClick(ev);
	};
	/** No picture for this card: remember it, and show the text face. */
	markNoArt(cardId: string) {
		noArtCards[cardId] = true;
		if (this.state.noArt !== cardId) this.setState({ noArt: cardId });
	}
	onImgLoad = (ev: Event) => {
		const img = ev.currentTarget as HTMLImageElement | null;
		const cardId = this.props.cardId;
		if (!img || !cardId || this.props.image || !scrydexUrl(img.src)) return;
		if (sampleNoArt(img)) this.markNoArt(cardId);
	};
	onImgError = (ev: Event) => {
		const img = ev.currentTarget as HTMLImageElement | null;
		const cardId = this.props.cardId;
		if (!img || !cardId) return;
		const step = Number(img.dataset.fallback || '0');
		const forcePocket = !!this.props.pocket || isPocketCardId(cardId);
		// The CORS copy is only for sampling Scrydex; the other hosts load plain.
		img.removeAttribute('crossorigin');

		if (forcePocket) {
			// 1) Limitless SM → 2) Limitless full → stop (keep broken rather than paper CDN)
			if (step < 1) {
				img.dataset.fallback = '1';
				img.src = cardArt(cardId, { pocket: true, large: false });
				return;
			}
			if (step < 2) {
				img.dataset.fallback = '2';
				img.src = cardArt(cardId, { pocket: true, large: true });
				return;
			}
			return;
		}

		// Paper / Standard: Scrydex (primary) → pokemontcg.io → hires → Scrydex large
		if (step < 1) {
			img.dataset.fallback = '1';
			img.src = paperTcgIoArt(cardId, false);
			return;
		}
		if (step < 2) {
			img.dataset.fallback = '2';
			img.src = paperTcgIoArt(cardId, true);
			return;
		}
		if (step < 3) {
			img.dataset.fallback = '3';
			img.src = scrydexArt(cardId, true);
			return;
		}
		if (!this.props.image) this.markNoArt(cardId);
	};
	override render() {
		const {
			cardId, image, name, size, selected, back, playable, arrive, fanIndex, fanCount, dragging,
			pocket, onInspect,
		} = this.props;
		const src = image || (cardId ? cardArt(cardId, pocket ? { pocket: true } : undefined) : '');
		const cls = [
			'tcg-card', `tcg-card-${size || 'md'}`,
			selected ? 'tcg-card-selected' : '',
			back ? 'tcg-card-back' : '',
			this.props.onClick || this.props.onPointerDown || onInspect ? 'tcg-card-click' : '',
			playable ? 'tcg-card-playable' : '',
			arrive ? 'tcg-card-arrive' : '',
			this.props.incoming ? 'tcg-card-incoming' : '',
			dragging ? 'tcg-card-dragging' : '',
			fanCount ? 'tcg-card-fan' : '',
		].filter(Boolean).join(' ');
		const label = name || (cardId ? cardLabel(cardId) : '');
		const noArt = !back && !!cardId && !image && (this.state.noArt === cardId || !!noArtCards[cardId]);
		const mid = fanCount ? (fanCount - 1) / 2 : 0;
		const style = fanCount != null && fanIndex != null ? {
			'--fan': String(fanIndex - mid),
			zIndex: selected ? 30 : fanIndex + 1,
		} as any : undefined;
		return <button
			type="button" class={cls} style={style}
			onClick={onInspect || this.props.onClick ? this.onClick : undefined}
			onPointerDown={onInspect || this.props.onPointerDown ? this.onPointerDown : undefined}
			onPointerUp={this.onPointerUp}
			onPointerCancel={this.onPointerUp}
			onPointerMove={this.onPointerMove}
			onContextMenu={onInspect ? this.inspect : ev => ev.preventDefault()}
			title={label} aria-label={label || undefined} aria-pressed={selected}
		>
			<span class="tcg-card-inner">
				{back || !src ?
					<CardBackFace /> :
					noArt ?
						<NoArtFace cardId={cardId} label={label} /> :
						<img
							key={src}
							crossOrigin={scrydexUrl(src) ? 'anonymous' : undefined}
							src={src} alt={label} draggable={false}
							onLoad={this.onImgLoad} onError={this.onImgError}
						/>}
			</span>
		</button>;
	}
}

class TcgMon extends preact.Component<{
	mon: TcgPokemonView | null, slot: TcgSlot, size?: CardSize, selected?: boolean,
	activeSpot?: boolean, legal?: boolean, foe?: boolean, pkFx?: PkFx | null,
	dropOk?: boolean, dropHot?: boolean, dropLabel?: string,
	/** Just flipped face up (end of setup). */
	reveal?: boolean,
	/** Its card is still flying to this slot: hidden until it lands. */
	incoming?: boolean,
	/** Attached cards still flying to it: not counted until they land. */
	landing?: number,
	onClick?: (ev?: MouseEvent) => void, onInspect?: (p: Preview) => void,
	/** Open the attached Energy/Tool browser for this Pokémon. */
	onAttachBrowse?: (cards: AttachedCard[], mon: TcgPokemonView) => void,
	/** A board choice offers this Pokémon: it takes keyboard focus, and Enter or Space chooses it. */
	keyPick?: boolean,
	/** What the current board choice moves comes off this Pokémon. */
	choiceFrom?: boolean,
}> {
	baseEl: HTMLElement | null = null;
	holdTimer: number | null = null;
	held = false;
	clearHold() {
		if (this.holdTimer != null) window.clearTimeout(this.holdTimer);
		this.holdTimer = null;
	}
	onPointerDown = (ev: PointerEvent) => {
		const mon = this.props.mon;
		if (!mon || !this.props.onInspect) return;
		if (ev.button != null && ev.button !== 0) return;
		this.held = false;
		this.clearHold();
		this.holdTimer = window.setTimeout(() => {
			this.holdTimer = null;
			this.held = true;
			this.props.onInspect?.({ cardId: mon.cardId, image: mon.image, name: mon.name });
		}, 420);
	};
	onPointerUp = () => {
		this.clearHold();
	};
	onClick = (ev: MouseEvent) => {
		if (this.held) {
			this.held = false;
			ev.preventDefault();
			ev.stopPropagation();
			return;
		}
		this.props.onClick?.(ev);
	};

	hpEl: HTMLElement | null = null;
	hpFill: HTMLElement | null = null;
	hpRaf: number | null = null;
	/** Number and bar together. The bar used to follow a CSS width transition that the hit re-render cut off. */
	writeHp(hp: number) {
		if (this.hpEl) this.hpEl.textContent = String(hp);
		const max = this.props.mon?.maxHp || 0;
		if (this.hpFill && max > 0) {
			const pct = Math.max(0, Math.min(100, (hp / max) * 100));
			this.hpFill.style.width = `${pct}%`;
		}
	}
	/** Tick the HP number and bar from the old value to the new one instead of snapping. */
	tweenHp(from: number, to: number) {
		if (from === to) return;
		if (tcgReduceMotion()) {
			this.writeHp(to);
			return;
		}
		if (!this.hpEl && !this.hpFill) return;
		if (this.hpRaf != null) cancelAnimationFrame(this.hpRaf);
		const start = performance.now();
		// Drain across the hit beat. A fixed 0.65s bar finished during the name banner
		// on slow speeds, and fast mode was snapping it when the hit class cleared.
		const dur = fxMs(780);
		this.hpEl?.classList.add(to < from ? 'hp-dropping' : 'hp-rising');
		this.writeHp(from); // preact already wrote `to`; start from the old value
		const tick = (now: number) => {
			const t = Math.min(1, (now - start) / dur);
			const e = 1 - (1 - t) ** 3;
			this.writeHp(Math.round(from + (to - from) * e));
			if (t < 1) {
				this.hpRaf = requestAnimationFrame(tick);
			} else {
				this.hpRaf = null;
				this.writeHp(to);
				this.hpEl?.classList.remove('hp-dropping', 'hp-rising');
			}
		};
		this.hpRaf = requestAnimationFrame(tick);
	}
	override componentWillUnmount() {
		if (this.hpRaf != null) cancelAnimationFrame(this.hpRaf);
		this.clearHold();
	}
	override componentDidUpdate(prev: this['props']) {
		if (prev.mon?.iid !== this.props.mon?.iid && this.hpRaf != null) {
			// A different Pokémon now sits here: do not finish the old one's tick on its number.
			cancelAnimationFrame(this.hpRaf);
			this.hpRaf = null;
			this.hpEl?.classList.remove('hp-dropping', 'hp-rising');
		}
		if (prev.pkFx?.cls && !this.props.pkFx?.cls && this.hpRaf != null && this.props.mon) {
			// The hit beat ended. Leave the printed number and the bar on the board value.
			cancelAnimationFrame(this.hpRaf);
			this.hpRaf = null;
			this.hpEl?.classList.remove('hp-dropping', 'hp-rising');
			this.writeHp(this.props.mon.hp);
		}
		if (prev.mon && this.props.mon && prev.mon.iid === this.props.mon.iid && prev.mon.hp !== this.props.mon.hp) {
			this.tweenHp(prev.mon.hp, this.props.mon.hp);
		} else if (this.props.mon && this.hpEl && this.hpRaf == null && this.hpEl.textContent !== String(this.props.mon.hp)) {
			// The tween writes the text node directly; preact skips a rewrite when the
			// vnode text did not change, so make sure the final number is the real one.
			this.hpEl.textContent = String(this.props.mon.hp);
		}
		const cur = this.props.pkFx;
		const was = prev.pkFx;
		if (!cur?.cls || !this.baseEl) return;
		if (cur.tick == null || cur.tick === was?.tick) return;
		const el = this.baseEl;
		const keepLunge = !!(cur.contact && was?.contact && /\bfx-lunge\b/.test(cur.cls) && /\bfx-lunge\b/.test(was.cls || ''));
		if (keepLunge) {
			// Windup → hit: leave the contact lunge WAAPI running.
			if (cur.dataFx) el.setAttribute('data-fx', cur.dataFx);
			return;
		}
		// Restart hit/heal CSS when the same mon takes another sequential hit.
		const prevParts = (was?.cls || '').split(/\s+/).filter(Boolean);
		const parts = cur.cls.split(/\s+/).filter(Boolean);
		for (const c of prevParts) el.classList.remove(c);
		for (const c of parts) el.classList.remove(c);
		el.removeAttribute('data-fx');
		if (typeof el.getAnimations === 'function') {
			el.getAnimations().forEach(a => {
				if (a.id === 'tcg-lunge') return;
				a.cancel();
			});
		}
		el.style.animation = 'none';
		void el.offsetWidth;
		el.style.animation = '';
		for (const c of parts) el.classList.add(c);
		if (cur.dataFx) el.setAttribute('data-fx', cur.dataFx);
	}

	openAttachBrowse = (ev: MouseEvent) => {
		ev.preventDefault();
		ev.stopPropagation();
		const mon = this.props.mon;
		if (!mon || !this.props.onAttachBrowse) return;
		const cards = attachedCardsOf(mon);
		if (!cards.length) return;
		this.props.onAttachBrowse(cards, mon);
	};

	override render() {
		const {
			mon, selected, activeSpot, legal, foe, pkFx,
			dropOk, dropHot, dropLabel,
			onClick, onInspect, size,
		} = this.props;
		const cardSize: CardSize = size || (activeSpot ? 'lg' : 'sm');
		if (!mon) {
			return <div
				class={`tcg-mon empty ${activeSpot ? 'active' : ''} ${legal ? 'legal' : ''} ${selected ? 'selected' : ''} ${dropOk ? 'drop-ok' : ''} ${dropHot ? 'drop-hot' : ''}`}
				onClick={onClick}
				data-drop-slot={String(this.props.slot)}
				data-drop-foe={foe ? '1' : '0'}
				data-drop-empty="1"
			>
				<span>{activeSpot ? 'Active' : 'Bench'}</span>
				{dropHot && dropLabel && <em class="tcg-drop-tag">{dropLabel}</em>}
			</div>;
		}
		const pct = mon.maxHp ? Math.max(0, Math.min(100, (mon.hp / mon.maxHp) * 100)) : 0;
		const hpTone = pct > 50 ? 'ok' : pct > 25 ? 'mid' : 'low';
		if (mon.faceDown) {
			const incoming = this.props.incoming ? ' is-incoming' : '';
			return <div
				ref={el => { this.baseEl = el as HTMLElement | null; }}
				class={`tcg-mon face-down ${activeSpot ? 'active' : ''} ${foe ? 'foe' : ''} ${pkFx?.cls || ''}${incoming}`}
				data-iid={mon.iid}
				data-drop-slot={String(this.props.slot)}
				data-drop-foe={foe ? '1' : '0'}
				data-drop-empty="0"
				title="Face-down Pokémon"
				aria-label="Face-down Pokémon"
				onContextMenu={ev => ev.preventDefault()}
			>
				<TcgCardFace cardId="" name="Face-down Pokémon" size={cardSize} back />
			</div>;
		}
		const attached = attachedCardsOf(mon);
		const peeks = attachPeeksOf(attached, 3);
		const toolCount = attached.filter(c => c.kind === 'tool').length;
		const specialCount = attached.filter(c => c.kind === 'special').length;
		const attachTitle = [
			attached.length ? `${attached.length} attached` : '',
			specialCount ? `${specialCount} Special Energy` : '',
			toolCount ? `${toolCount} Tool` : '',
			'click to view',
		].filter(Boolean).join(' · ');
		const cls = [
			'tcg-mon', activeSpot ? 'active' : '', foe ? 'foe' : '',
			selected ? 'selected' : '', legal ? 'legal' : '',
			dropOk ? 'drop-ok' : '', dropHot ? 'drop-hot' : '',
			attached.length ? 'has-attach' : '',
			toolCount ? 'has-tool' : '',
			specialCount ? 'has-special' : '',
			pkFx?.cls || '',
			this.props.reveal ? 'fx-reveal' : '',
			this.props.incoming ? 'is-incoming' : '',
			this.props.choiceFrom ? 'choice-from' : '',
		].filter(Boolean).join(' ');
		return <div
			ref={el => { this.baseEl = el as HTMLElement | null; }}
			class={cls}
			title={mon.name}
			aria-label={mon.name}
			{...(this.props.keyPick ? { 'tabIndex': 0, 'role': 'button', 'aria-pressed': selected ? 'true' : 'false' } : {})}
			onKeyDown={this.props.keyPick ? (ev: KeyboardEvent) => {
				if (ev.key !== 'Enter' && ev.key !== ' ') return;
				ev.preventDefault();
				this.props.onClick?.(ev as unknown as MouseEvent);
			} : undefined}
			onClick={this.onClick}
			onPointerDown={this.onPointerDown}
			onPointerUp={this.onPointerUp}
			onPointerCancel={this.onPointerUp}
			onContextMenu={ev => {
				ev.preventDefault();
				if (mon) this.props.onInspect?.({ cardId: mon.cardId, image: mon.image, name: mon.name });
			}}
			data-iid={mon.iid}
			data-fx={pkFx?.dataFx || undefined}
			data-fx-tick={pkFx?.tick != null ? String(pkFx.tick) : undefined}
			data-el={pkFx?.element || undefined}
			data-lunge-to={pkFx?.contact && pkFx.targetIid ? pkFx.targetIid : undefined}
			data-drop-slot={String(this.props.slot)}
			data-drop-foe={foe ? '1' : '0'}
			data-drop-empty="0"
		>
			{pkFx?.note && <span class="tcg-fx-note" key={`note${pkFx.tick ?? ''}`} aria-hidden="true">{pkFx.note}</span>}
			{attached.length > 0 && this.props.onAttachBrowse && <>
				<button
					type="button"
					class="tcg-attach-stack"
					title={attachTitle}
					aria-label={`Attached cards (${attached.length})${toolCount ? `, ${toolCount} tool` : ''}${specialCount ? `, ${specialCount} special energy` : ''}`}
					onClick={this.openAttachBrowse}
					onPointerDown={(ev: any) => ev.stopPropagation()}
				>
					{peeks.map((a, i) =>
						<span
							key={`${a.kind}-${a.cardId}-${i}`}
							class={`tcg-attach-peek kind-${a.kind}`}
							data-card={a.cardId}
							style={{ '--i': String(i), '--n': String(peeks.length) } as any}
							aria-hidden="true"
						>
							<img
								src={cardArt(a.cardId) || cardBackArt()}
								alt=""
								draggable={false}
								onError={(ev: any) => {
									const img = ev?.currentTarget as HTMLImageElement | null;
									if (!img) return;
									const step = Number(img.dataset.fallback || '0');
									if (step < 1) {
										img.dataset.fallback = '1';
										img.src = paperTcgIoArt(a.cardId, false) || scrydexArt(a.cardId, false);
										return;
									}
									if (step < 2) {
										img.dataset.fallback = '2';
										img.src = scrydexArt(a.cardId, false) || cardBackArt();
										return;
									}
									if (step < 3) {
										img.dataset.fallback = '3';
										img.src = cardBackArt();
									}
								}}
							/>
						</span>
					)}
				</button>
				{/* Sibling of the stack so z-index can sit above the Pokémon card. */}
				<em
					class={`tcg-attach-count${attached.length > (this.props.landing || 0) ? '' : ' is-incoming'}`}
					title={attachTitle}
					onClick={this.openAttachBrowse}
					onPointerDown={(ev: any) => ev.stopPropagation()}
				>{Math.max(0, attached.length - (this.props.landing || 0))}</em>
			</>}
			<TcgCardFace
				cardId={mon.cardId} image={mon.image} name={mon.name} size={cardSize}
				onClick={onClick} onInspect={onInspect}
			/>
			<div class={`tcg-hp ${hpTone}`} title={`${mon.hp} / ${mon.maxHp || mon.hp} HP`}>
				<span class="tcg-hp-val" ref={el => { this.hpEl = el as HTMLElement | null; }}>{mon.hp}</span>
				<span class="tcg-hp-track" aria-hidden="true">
					<span class="tcg-hp-fill" ref={el => { this.hpFill = el as HTMLElement | null; }} style={{ width: `${pct}%` }}></span>
				</span>
			</div>
			{/* Type icons + Tool badge for a glance. Mini cards peek left of Active and under Bench. */}
			{((mon.energy || []).length > 0 || toolCount > 0) &&
				<div class="tcg-energy-row" aria-hidden="true">
					{(mon.energy || []).map((t, i) => pip(t, i))}
					{toolCount > 0 && <span class="tcg-tool-pip" title={toolCount === 1 ? 'Tool attached' : `${toolCount} Tools attached`}>T</span>}
				</div>}
			{statusBadges(mon).length > 0 &&
				<div class="tcg-status">
					{statusBadges(mon).map(b => <span key={b.id} class={b.id} title={b.title}>{b.label}</span>)}
				</div>}
			{pkFx?.moveName && pkFx?.moveTag &&
				<div
					class={`tcg-move-banner k-${pkFx.moveTag === 'Ability' ? 'ability' : 'attack'}`}
					aria-hidden="true"
				>
					<strong>{pkFx.moveTag}</strong>
					<em>{pkFx.moveName}</em>
				</div>}
			{pkFx?.status != null && <StatusBurst kind={pkFx.status} />}
			{pkFx?.element && !pkFx.energyType && /\bfx-hit\b/.test(pkFx.cls || '') &&
				<TypeHit type={pkFx.element} style={pkFx.hitStyle} tick={pkFx.tick} />}
			{pkFx?.energyType &&
				<span
					class={`tcg-energy-attach-fx t-${pkFx.energyType}`}
					style={{ background: TYPE_COLOR[pkFx.energyType] || '#facc15' }}
					aria-hidden="true"
				>
					<img src={energySymbolUrl(pkFx.energyType)} alt="" draggable={false} />
				</span>}
			{dropHot && dropLabel && <em class="tcg-drop-tag">{dropLabel}</em>}
		</div>;
	}
}

class TcgPoints extends preact.Component<{ scored: number, need: number, pocket: boolean }> {
	override render() {
		const { scored, need, pocket } = this.props;
		const n = Math.max(need, 1);
		return <div
			class={`tcg-points ${pocket ? 'as-points' : 'as-prizes'}`}
			title={pocket ? `${scored} / ${n} points` : `${n - scored} prizes left`}
		>
			{Array.from({ length: n }, (_, i) =>
				<span class={`tcg-point ${i < scored ? 'on' : ''}`}></span>
			)}
			<small>{pocket ? `${scored}/${n}` : `${n - scored}`}</small>
		</div>;
	}
}

/** Face-down prize column — Live style (left rail). */
class TcgPrizeRail extends preact.Component<{
	remaining: number, max: number, foe?: boolean,
	takes?: number[],
	picked?: number[],
	takeActs?: TcgAction[], onTake?: (a: TcgAction) => void,
}> {
	override render() {
		const { remaining, max, foe, takeActs, onTake, takes, picked } = this.props;
		const n = Math.max(max, 1);
		const left = Math.max(0, Math.min(n, remaining));
		const slots = bindPrizeSlots(n, left, takes || [], picked || []);
		const shown = slots.filter(s => !s.taken).length;
		return <div class={`tcg-prizes ${foe ? 'foe' : 'me'}`} title={`${shown} Prize card${shown === 1 ? '' : 's'}`}>
			<strong class="tcg-prize-count">{shown}</strong>
			<div class="tcg-prize-stack">
				{slots.map(s => {
					const act = (!foe && !s.taken && s.serverIndex != null && takeActs) ?
						(takeActs.find(a => Number(a.index) === s.serverIndex) || null) : null;
					return <button
						type="button"
						key={s.slot}
						class={`tcg-prize ${s.taken ? 'taken' : 'up'} ${act ? 'takeable' : ''}`}
						disabled={!act}
						onClick={act && onTake ? () => onTake(act) : undefined}
						aria-label={s.taken ? 'Taken' : `Prize ${s.slot + 1}`}
					>
						<CardBackFace />
					</button>;
				})}
			</div>
		</div>;
	}
}

export class TcgBoard extends preact.Component<{
	snapshot: TcgSnapshot,
	/** Seat drawn at the bottom. Spectators keep `snapshot.you === null`. */
	viewpoint?: 0 | 1,
	events: TcgEvent[],
	/** True while this batch's clip is still playing. Prize choices wait it out. */
	playing?: boolean,
	/** How many of `events` have started. A board that mounts mid-batch carries on from there. */
	resumeAt?: number,
	fxKey: number,
	/** Bumps when playback is cancelled (skip / replay) so the current beat stops. */
	halt?: number,
	/** Board after this beat. Display keeps `snapshot` until the beat finishes. */
	fxSnapshot?: TcgSnapshot | null,
	waiting: boolean,
	/** Server rejected a click. `noticeN` bumps so the same text can show again. */
	notice?: string,
	noticeN?: number,
	ended?: boolean,
	/** Engine failure. Not a win, a loss, or a draw, and not a replay. */
	aborted?: boolean,
	stopReason?: string,
	/** Winner display name; `null`/empty with ended = tie or unknown. */
	winnerName?: string | null,
	onAct: (action: TcgAction) => void,
	/** Fired when a beat starts, including events that share that beat. */
	onEvent?: (ev: TcgEvent) => void,
	onFxDone?: () => boolean,
	paused?: boolean,
	onTogglePause?: () => void,
	onReplay?: () => void,
	onSkip?: () => void,
	/** A live beat is on screen or queued: the player gets Skip animations (`onSkip`, the jump to the newest board). */
	animating?: boolean,
	/** When false, post-game Play/Replay/Skip chrome is omitted (parent control strip owns it). */
	showReplayControls?: boolean,
	/**
	 * Take-prize indexes already in the log, per seat. Each index was into the
	 * pile at the time of that take (the server splices, so they are not original slots).
	 */
	prizeTakes?: [number[], number[]],
}> {
	override state = {
		selectedHand: null as number | null,
		energyPick: false,
		retreatPick: false,
		/** Retreat rows that share this signature; bench is chosen next. */
		retreatSig: null as string | null,
		fx: null as FxBeat | null,
		pkFx: {} as { [iid: string]: PkFx },
		/** Kept in the vacated slot while KO FX plays (snapshot already removed the mon). */
		koGhost: null as KoGhost | null,
		/** iids that have already fainted this payload — stay hidden after the KO beat. */
		koHide: {} as { [iid: string]: true },
		/** Incoming Pokémon shown in its destination slot during a place / switch-in beat. */
		placeIn: null as { seat: number, slot: TcgSlot, mon: TcgPokemonView } | null,
		/**
		 * Effects of beats that have already played in this batch, applied on top of the
		 * pre-batch snapshot so the board reacts at the beat instead of at commit:
		 * cards leave your hand as they are played, HP drops with each hit, energy lands.
		 */
		/** Pokémon that left Active for the Bench slot the switcher came from. */
		placeOut: null as { seat: number, slot: TcgSlot, mon: TcgPokemonView } | null,
		live: { hand: {}, hp: {}, energy: {}, prizes: {}, deck: {}, handN: {}, discardN: {}, points: {}, gained: {} } as {
			hand: { [cardId: string]: number },
			hp: { [iid: string]: number },
			energy: { [iid: string]: string[] },
			prizes: { [seat: number]: number },
			deck: { [seat: number]: number },
			handN: { [seat: number]: number },
			discardN: { [seat: number]: number },
			points: { [seat: number]: number },
			/** Card ids that entered your hand on a beat already playing (draw, prize, search). */
			gained: { [seat: number]: string[] },
		},
		/** fxKey the live overlay was built for; an overlay from an earlier batch is ignored. */
		liveKey: -1,
		inspect: null as Preview | null,
		/** The open server choice is set aside to look at the board; nothing is answered. */
		promptPeek: false,
		menuSlot: null as TcgSlot | null,
		endTurnConfirm: false,
		/** "You may" on an attack. Yes and No are the two attack actions; Cancel attacks nothing. */
		attackMay: null as { yes: TcgAction, no: TcgAction, title: string, text: string } | null,
		/** Clicked an untargeted Trainer: confirm before it leaves the hand. */
		trainerUse: null as null | { action: TcgAction, name: string },
		/** Rectangle around both Active spots, measured while a Trainer is dragged. */
		playField: null as null | { top: number, left: number, width: number, height: number },
		/**
		 * Attack/ability that needs a board target. One menu button opens this list;
		 * picking a target sends that action (or an optional "you may" prompt first).
		 */
		moveTargetPick: null as null | {
			title: string,
			kind: 'attack' | 'ability',
			actions: TcgAction[],
			cost?: string[],
			damage?: string,
		},
		/** Prize indexes already sent for the current prompt. */
		prizePicked: [] as number[],
		/** How many prizes this prompt asked for. The prompt closes once that many are sent. */
		prizeGoal: 0,
		/** Prize pile size when this prompt's clicks were sent. 0 = none in flight. */
		prizePickBase: 0,
		/** Go First / Go Second was sent. Hide that prompt until the server drops the choice. */
		firstSent: false,
		drag: null as DragState | null,
		/** Why the play the player just tried is illegal, anchored at the pointer. */
		refuse: null as { text: string, x: number, y: number } | null,
		/** Staged Energy indexes for retreat pay (multi-select). */
		payPicked: [] as number[],
		/** Browse discard pile: 'me' | 'foe'. */
		discardBrowse: null as null | 'me' | 'foe',
		/** Browse Energy / Tool cards attached to a Pokémon. */
		attachBrowse: null as null | { title: string, cards: AttachedCard[] },
		/** Active-effects list opened from the HUD button. */
		effectsOpen: false,
		/** Brief board hint when a Pokémon has no actions (mirrors refuse). */
		slotHint: '' as string,
		/** Deck-index trainer (Explorer's Guidance and the same shape). Indexes into lookPile. */
		deckLook: null as { hand: number, picked: number[] } | null,
		/**
		 * Board choice (battle-tcg-choice.ts): the Pokémon whose attached cards are open, under the prompt key it was
		 * opened for, and the row already sent from it. A different key means another prompt: it is ignored and cleared.
		 */
		boardPick: null as (BoardChoiceLocal & { sent?: string }) | null,
	};
	timer: number | null = null;
	tableEl: HTMLElement | null = null;
	dragMoved = false;
	/** Set when a long-press opened the preview, so pointer-up does not play the card. */
	holdConsumed = false;
	/** Last-seen board mons by iid — survives snapshot removal so KO can animate. */
	monMemory: { [iid: string]: KoGhost } = {};
	/** Mons removed on the latest snapshot, held until their KO (or the next unrelated beat). */
	freshOut: { [iid: string]: true } = {};
	/** Knockouts whose animation already finished. A later snapshot must not revive them. */
	koFinished: { [iid: string]: true } = {};
	lastSnap: TcgSnapshot | null = null;
	/** Opponent setup placeholders seen face down, and the ones flipping up on this snapshot. */
	faceDownSeen: { [iid: string]: true } = {};
	revealing: { [iid: string]: true } = {};
	/** Pre-draw hand per seat — snapshot already has the new hand when shuffle FX runs. */
	handMemory: { [seat: number]: { ids: string[] | null, count: number } } = {};
	/** Events that arrived while a beat was still playing. */
	fxPending: TcgEvent[] = [];
	fxBusy = false;
	/** `fxKey` whose opening coin/first beat has finished. Until then, hide Go First. */
	playedFlipKey = -1;

	stashHands(snap: TcgSnapshot | null | undefined) {
		if (!snap?.players) return;
		snap.players.forEach((p, seat) => {
			if (!p) return;
			this.handMemory[seat] = { ids: handIds(p.hand), count: pileCount(p.hand) };
		});
	}

	/** Cards that appeared in hand since the last stash (for draw FX when event.ids missing). */
	inferDrawnIds(seat: number, n: number): string[] | null {
		const prev = this.handMemory[seat]?.ids;
		const curr = handIds((this.props.fxSnapshot || this.props.snapshot).players[seat]?.hand);
		if (!curr?.length) return null;
		if (!prev?.length) return curr.slice(Math.max(0, curr.length - n));
		const left = curr.slice();
		for (const id of prev) {
			const ix = left.indexOf(id);
			if (ix >= 0) left.splice(ix, 1);
		}
		if (left.length) return left;
		return curr.slice(Math.max(0, curr.length - n));
	}

	override componentDidMount() {
		if (typeof loadTcgCardIndex !== 'undefined') void loadTcgCardIndex();
		this.stashHands(this.props.snapshot);
		// The room keeps the last batch's events after it ends. A board that mounts while nothing is playing
		// (a resize across the phone layout, a reconnect) must not play that batch again on top of its result.
		if (this.props.playing) this.playFx(this.props.events, this.props.resumeAt || 0);
		else this.playedFlipKey = this.props.fxKey;
		window.addEventListener('pointermove', this.onDragMove);
		window.addEventListener('pointerup', this.onDragEnd);
		window.addEventListener('pointercancel', this.onDragEnd);
		window.addEventListener('keydown', this.onKeyDown);
		window.addEventListener('resize', this.onChoiceResize);
	}
	/** Where every on-board Pokémon was before this render (FLIP "first"). */
	flipRects: { [iid: string]: { left: number, top: number, width: number, height: number, slot: string } } = {};
	/** Attacker iid currently in a contact lunge (WAAPI). */
	lungeIid = '';
	/** Bench/active identity. A pixel shift with the same slot is not a move. */
	monSlotKey(el: HTMLElement) {
		return `${el.getAttribute('data-drop-foe') || ''}:${el.getAttribute('data-drop-slot') || ''}`;
	}
	/** Layout box relative to the table, ignoring any transform mid-animation (lunge, shake, hover). */
	layoutBox(el: HTMLElement) {
		let left = 0;
		let top = 0;
		let node: HTMLElement | null = el;
		while (node && node !== this.tableEl) {
			left += node.offsetLeft;
			top += node.offsetTop;
			node = node.offsetParent as HTMLElement | null;
		}
		return { left, top, width: el.offsetWidth, height: el.offsetHeight };
	}
	override componentWillUpdate() {
		this.flipRects = {};
		const root = this.tableEl;
		if (!root || tcgReduceMotion()) return;
		root.querySelectorAll<HTMLElement>('.tcg-mon[data-iid]').forEach(el => {
			const iid = el.dataset.iid;
			if (iid) this.flipRects[iid] = { ...this.layoutBox(el), slot: this.monSlotKey(el) };
			if (iid) this.lastMonBox[iid] = el.getBoundingClientRect();
		});
	}
	/**
	 * Glide any Pokémon whose slot changed from its old box to the new one (FLIP "last/invert/play"). `instant`: the board
	 * jumped (Skip animations, a resync): every card is placed at once, and a glide or lunge still running stops.
	 */
	flipMoved(instant = false) {
		const root = this.tableEl;
		const first = this.flipRects;
		this.flipRects = {};
		if (!root || typeof (root as any).animate !== 'function') return;
		if (instant) {
			root.querySelectorAll<HTMLElement>('.tcg-mon').forEach(el => {
				el.getAnimations?.().forEach(a => {
					if (a.id === 'tcg-flip' || a.id === 'tcg-lunge') a.cancel();
				});
				el.classList.remove('is-flipping');
			});
			return;
		}
		root.querySelectorAll<HTMLElement>('.tcg-mon[data-iid]').forEach(el => {
			const iid = el.dataset.iid;
			const was = iid && first[iid];
			if (!was) return;
			// An attack makes the half position:relative, which changes offsetParent and
			// makes every card in that half look like it jumped to the middle. Only a
			// real slot change (switch, promote) should glide.
			if (was.slot === this.monSlotKey(el)) return;
			// Beats with their own entrance/exit motion keep it.
			// (A switch-in keeps its glow but glides from its real bench spot instead of the canned offset.)
			if (/\bfx-(place|ko|reveal|lunge|hit)\b/.test(el.className)) return;
			const fx = this.state.fx;
			if (fx && /^(attack|ability|damage|heal|ko)$/.test(fx.kind || '')) {
				if (iid === fx.iid || iid === fx.targetIid || (fx.hits || []).some(h => h.iid === iid)) return;
			}
			if (iid && this.state.pkFx[iid]) return;
			const now = this.layoutBox(el);
			const dx = was.left - now.left;
			const dy = was.top - now.top;
			if (!now.width || !now.height) return;
			if (Math.abs(dx) < 2 && Math.abs(dy) < 2) return;
			const sx = was.width && now.width ? was.width / now.width : 1;
			const sy = was.height && now.height ? was.height / now.height : 1;
			el.getAnimations?.().forEach(a => {
				if (a.id === 'tcg-flip') a.cancel();
			});
			el.classList.add('is-flipping');
			const anim = el.animate([
				{ transform: `translate(${dx}px, ${dy}px) scale(${sx}, ${sy})`, filter: 'drop-shadow(0 10px 10px rgba(0,0,0,0.35))' },
				{ transform: `translate(${dx * 0.4}px, ${dy * 0.4}px) scale(${1 + (sx - 1) * 0.4}, ${1 + (sy - 1) * 0.4})`, filter: 'drop-shadow(0 14px 14px rgba(0,0,0,0.4))', offset: 0.5 },
				{ transform: 'translate(0, 0) scale(1, 1)', filter: 'drop-shadow(0 0 0 rgba(0,0,0,0))' },
			], { duration: fxMs(820), easing: 'cubic-bezier(.22,.72,.2,1)', fill: 'forwards' });
			anim.id = 'tcg-flip';
			const done = () => {
				anim.cancel();
				el.classList.remove('is-flipping');
			};
			anim.onfinish = done;
			anim.oncancel = () => el.classList.remove('is-flipping');
		});
	}
	/** The board jumped (Skip animations, a resync) and no beat has played since: cards are placed, not glided. */
	haltJump = false;
	override componentDidUpdate(prev: this['props'], prevState: this['state']) {
		if (this.props.halt !== prev.halt) this.haltJump = true;
		this.flipMoved(this.haltJump);
		this.placeFirstBanner();
		if (this.state.selectedHand != null && this.state.selectedHand !== prevState.selectedHand) this.revealSelectedHand();
		if ((this.props.noticeN || 0) !== (prev.noticeN || 0) && this.props.notice) {
			this.showRefuse(this.props.notice);
		}
		// A card sent from the picker that the server refused, or a new snapshot that still asks the same choice
		// (an answer always changes it): the card was not applied, so the picker is usable again.
		const sentPick = this.state.boardPick;
		if (sentPick?.sent && ((this.props.noticeN || 0) !== (prev.noticeN || 0) || this.props.snapshot !== prev.snapshot)) {
			const now = this.boardChoice();
			if (now?.kind === 'attached' && now.key === sentPick.key) {
				this.setState({ boardPick: { key: sentPick.key, iid: sentPick.iid } });
			}
		}
		if (this.props.halt !== prev.halt) {
			this.clearFlights();
			this.attackCtx = null;
			this.fxPending = [];
			this.fxBusy = false;
			this.playedFlipKey = this.props.fxKey;
			if (this.timer != null) window.clearTimeout(this.timer);
			this.timer = null;
			this.clearFx();
			this.payQueue = [];
			this.paySending = false;
			if (this.state.prizePicked.length || this.state.prizeGoal || this.state.prizePickBase || this.state.firstSent) {
				this.setState({ prizePicked: [], prizeGoal: 0, prizePickBase: 0, firstSent: false });
			}
		} else if (this.state.prizePicked.length || this.state.prizeGoal || this.state.prizePickBase) {
			// Forget the picks once this prompt's choices are gone, or once the
			// snapshot pile has shrunk — those indexes were into the old pile.
			const live = this.acts();
			let any = false;
			for (let j = 0; j < live.length; j++) {
				if (live[j].type === 'takePrize') {
					any = true;
					break;
				}
			}
			const snap = this.props.snapshot;
			const you = snap.you;
			const me = you != null ? snap.players[you] : undefined;
			const pocket = !!(me?.energyZone || snap.format?.energyZone);
			const need = snap.format?.prizes || (pocket ? 3 : 6);
			const leftNow = me ? prizesLeft(me, need, snap.status) : 0;
			const acked = this.state.prizePickBase > 0 && leftNow < this.state.prizePickBase;
			if (!any || acked) this.setState({ prizePicked: [], prizeGoal: 0, prizePickBase: 0 });
		} else if (this.state.firstSent) {
			// The in-progress board falls back to the pre-click actions between beats.
			// Only the committed snapshot means the server has left this choice.
			const committed = this.props.snapshot.actions || [];
			let stillFirst = false;
			for (let j = 0; j < committed.length; j++) {
				if (committed[j].type === 'chooseFirst') {
					stillFirst = true;
					break;
				}
			}
			if (!stillFirst) this.setState({ firstSent: false });
		}
		if (this.props.fxKey !== prev.fxKey) {
			this.stashHands(prev.fxSnapshot || prev.snapshot);
			this.playFx(this.props.events);
		} else if (
			this.props.snapshot !== prev.snapshot && this.props.snapshot === this.props.fxSnapshot
		) {
			// A newer choice was painted under the beat still on screen.
			this.boardSnap = null;
			this.foldedThrough = -1;
			this.foldedRequest = false;
			this.stashHands(this.props.snapshot);
		} else if (this.state.fx !== prevState.fx) {
			requestAnimationFrame(() => this.aimFlyers());
			if (prevState.fx?.kind === 'shuffleHand' && this.state.fx?.kind !== 'shuffleHand') {
				this.stashHands(this.props.snapshot);
			}
		} else if (this.props.snapshot !== prev.snapshot && this.state.fx?.kind !== 'shuffleHand') {
			this.stashHands(this.props.snapshot);
		}
		if (this.props.snapshot !== prev.snapshot) this.flushPayQueue();
	}
	override componentWillUnmount() {
		this.clearFlights();
		if (this.inboundRaf != null) cancelAnimationFrame(this.inboundRaf);
		this.fxPending = [];
		this.fxBusy = false;
		if (this.timer != null) window.clearTimeout(this.timer);
		this.stopRefuseTimer();
		window.removeEventListener('pointermove', this.onDragMove);
		window.removeEventListener('pointerup', this.onDragEnd);
		window.removeEventListener('pointercancel', this.onDragEnd);
		window.removeEventListener('keydown', this.onKeyDown);
		window.removeEventListener('resize', this.onChoiceResize);
		this.stopChoiceLayout();
	}

	onKeyDown = (e: KeyboardEvent) => {
		if (e.key !== 'Escape') return;
		if (this.choiceBackable()) {
			this.choiceBack();
			return;
		}
		if (this.state.moveTargetPick) {
			this.setState({ moveTargetPick: null });
			return;
		}
		if (this.state.trainerUse) {
			this.setState({ trainerUse: null });
			return;
		}
		if (this.state.attachBrowse) {
			this.setState({ attachBrowse: null });
			return;
		}
		if (this.state.discardBrowse) {
			this.setState({ discardBrowse: null });
			return;
		}
		this.closeInspect();
	};

	clearFx() {
		// The KO hold ends with the beat. The commit that follows can be the first
		// render where the Pokémon is gone; that must not put the picture back.
		Object.keys(this.freshOut).forEach(iid => { this.koFinished[iid] = true; });
		if (this.state.fx?.kind === 'ko' && this.state.fx.iid) this.koFinished[this.state.fx.iid] = true;
		this.freshOut = {};
		this.cancelLunges();
		this.boardSnap = null;
		this.liveNow = { hand: {}, hp: {}, energy: {}, prizes: {}, deck: {}, handN: {}, discardN: {}, points: {}, gained: {} };
		this.setState({ fx: null, pkFx: {}, koGhost: null, koHide: {}, placeIn: null, placeOut: null, live: this.liveNow });
	}

	cancelLunges() {
		this.lungeIid = '';
		const root = this.tableEl;
		if (!root) return;
		root.querySelectorAll<HTMLElement>('.tcg-mon').forEach(el => {
			if (typeof el.getAnimations !== 'function') return;
			el.getAnimations().forEach(a => {
				if (a.id === 'tcg-lunge' || a.id === 'tcg-flip') a.cancel();
			});
		});
	}

	/**
	 * Synchronous copy of state.live. Each batch starts from an empty overlay: its
	 * snapshot already includes everything earlier batches animated, so carrying the
	 * old overlay over would subtract that damage (or remove that hand card) twice.
	 */
	liveNow: TcgBoard['state']['live'] = {
		hand: {}, hp: {}, energy: {}, prizes: {}, deck: {}, handN: {}, discardN: {}, points: {}, gained: {},
	};
	static readonly NO_LIVE: TcgBoard['state']['live'] = {
		hand: {}, hp: {}, energy: {}, prizes: {}, deck: {}, handN: {}, discardN: {}, points: {}, gained: {},
	};
	/**
	 * Snapshot after every event whose beat has started. The committed snapshot stays
	 * put until the batch ends, so piles, the hand, the turn badge, and the board
	 * read this instead of waiting for that commit.
	 */
	boardSnap: TcgSnapshot | null = null;
	/** Last index of `events` already applied onto boardSnap. Reset when the array changes. */
	foldedThrough = -1;
	foldedRequest = false;
	/**
	 * HP to draw until each hit lands. The live payload already carries the
	 * post-damage board, so without this the bar is at the new value before the
	 * attack animation starts and the drop never plays.
	 */
	hpShown: { [iid: string]: number } | null = null;
	hpHoldKey = -1;
	syncHpHold() {
		const fxKind = this.state.fx?.kind;
		const combat = fxKind === 'attack' || fxKind === 'ability' || fxKind === 'damage' ||
			fxKind === 'heal' || fxKind === 'ko';
		if (!this.props.playing && !combat) {
			this.hpShown = null;
			this.hpHoldKey = -1;
			return;
		}
		if (!this.props.playing || this.hpHoldKey === this.props.fxKey) return;
		const events = this.props.events || [];
		const next: { [iid: string]: number } = {};
		let any = false;
		for (let i = 0; i < events.length; i++) {
			const ev = events[i];
			if ((ev.type !== 'damage' && ev.type !== 'heal') || !ev.iid) continue;
			if (!(Number(ev.amount) || 0) && ev.hp == null) continue;
			const mem = this.monMemory[ev.iid];
			if (!mem || mem.hp == null) continue;
			const target = ev.hp != null ? Number(ev.hp) : null;
			if (target != null && target === mem.hp) continue;
			if (next[ev.iid] == null) {
				next[ev.iid] = mem.hp;
				any = true;
			}
		}
		this.hpHoldKey = this.props.fxKey;
		this.hpShown = any ? next : null;
	}
	/** Paint one damage or heal result. Called when that hit's beat starts. */
	revealHeldHp(e?: TcgEvent | null) {
		if (!e || !this.hpShown || !e.iid) return;
		if (e.type !== 'damage' && e.type !== 'heal') return;
		if (this.hpShown[e.iid] == null) return;
		if (!(Number(e.amount) || 0) && e.hp == null) return;
		if (e.hp != null) {
			this.hpShown[e.iid] = Number(e.hp);
			return;
		}
		const amt = Number(e.amount) || 0;
		const cur = this.hpShown[e.iid];
		this.hpShown[e.iid] = e.type === 'heal' ? cur + amt : Math.max(0, cur - amt);
	}
	withHeldHp(mon: TcgPokemonView | null): TcgPokemonView | null {
		if (!mon || !this.hpShown) return mon;
		const hp = this.hpShown[mon.iid];
		if (hp == null || hp === mon.hp) return mon;
		return { ...mon, hp };
	}
	foldEvents(events: TcgEvent[], inclusiveIndex: number) {
		if (inclusiveIndex < 0) return;
		// First paint already is the post-batch board; folding the same events would double them.
		if (this.props.snapshot === this.props.fxSnapshot) return;
		if (!this.boardSnap) {
			this.boardSnap = cloneTcgSnapshot(this.props.snapshot);
			this.foldedThrough = -1;
		}
		const board = this.boardSnap;
		const from = this.foldedThrough + 1;
		if (inclusiveIndex < from) return;
		for (let k = from; k <= inclusiveIndex && k < events.length; k++) {
			applyBatchEvent(board, events, k);
			if (events[k].type === 'request' && this.props.fxSnapshot?.actions) {
				board.actions = this.props.fxSnapshot.actions;
				this.foldedRequest = true;
			}
		}
		normalizeTcgEnergy(board);
		this.foldedThrough = Math.min(inclusiveIndex, events.length - 1);
	}
	/** Fold a beat that is now playing into the live overlay (see state.live). */
	applyLive(_fx: FxBeat, _you: number | null | undefined) {
		// Pile counts, HP, and the hand come from boardSnap. A second overlay would add them twice.
		return TcgBoard.NO_LIVE;
	}

	noteRemovals(snap: TcgSnapshot) {
		if (this.lastSnap && this.lastSnap !== snap) {
			const prev = this.lastSnap;
			Object.keys(this.monMemory).forEach(iid => {
				if (findMonView(prev.players, iid) && !findMonView(snap.players, iid)) {
					if (!this.koFinished[iid]) this.freshOut[iid] = true;
				}
			});
			// Setup placeholders that just turned face up get one flip animation.
			this.revealing = {};
			Object.keys(this.faceDownSeen).forEach(iid => {
				const now = findMonView(snap.players, iid);
				if (now && !now.faceDown) {
					this.revealing[iid] = true;
					delete this.faceDownSeen[iid];
				} else if (!now) {
					delete this.faceDownSeen[iid];
				}
			});
		}
		snap.players.forEach(p => {
			if (p?.active?.faceDown) this.faceDownSeen[p.active.iid] = true;
			(p?.bench || []).forEach(m => { if (m?.faceDown) this.faceDownSeen[m.iid] = true; });
		});
		this.lastSnap = snap;
	}

	rememberMons(snap: TcgSnapshot) {
		snap.players.forEach((p, seat) => {
			if (p.active) {
				this.monMemory[p.active.iid] = {
					iid: p.active.iid,
					cardId: p.active.cardId,
					name: p.active.name,
					image: p.active.image,
					hp: p.active.hp,
					maxHp: p.active.maxHp,
					seat,
					slot: 'active',
				};
			}
			(p.bench || []).forEach((m, i) => {
				if (!m) return;
				this.monMemory[m.iid] = {
					iid: m.iid,
					cardId: m.cardId,
					name: m.name,
					image: m.image,
					hp: m.hp,
					maxHp: m.maxHp,
					seat,
					slot: i,
				};
			});
		});
	}

	ghostForKo(fx: FxBeat, e: TcgEvent): KoGhost | null {
		if (!fx.iid) return null;
		const mem = this.monMemory[fx.iid];
		if (mem) return mem;
		const seat = fx.seat ?? e.seat ?? 0;
		const slot: TcgSlot = e.bench ? 0 : 'active';
		return {
			iid: fx.iid,
			cardId: fx.cardId || e.cardId || '',
			name: fx.extra || e.name || 'Pokémon',
			seat,
			slot,
		};
	}

	/** Pokémon removed by a KO that has not finished animating, if it occupied this slot. */
	koHold(seat: number, slot: TcgSlot, liveIid?: string): TcgPokemonView | null {
		let held: TcgPokemonView | null = null;
		Object.keys(this.freshOut).forEach(iid => {
			if (held || this.koFinished[iid]) return;
			const mem = this.monMemory[iid];
			if (!mem || mem.seat !== seat || !sameSlot(mem.slot, slot)) return;
			if (liveIid && liveIid === iid) return;
			held = {
				iid: mem.iid,
				cardId: mem.cardId,
				name: mem.name,
				image: mem.image,
				hp: mem.hp ?? 0,
				maxHp: mem.maxHp ?? mem.hp ?? 0,
			};
		});
		return held;
	}
	/** Slot of this picture on the board the player is looking at. */
	slotOfIid(iid: string, foe: boolean, fallback: TcgSlot): TcgSlot {
		const view = this.boardSnap || this.props.snapshot;
		const home = findMonSlot(view.players, iid);
		if (!home) return fallback;
		const meIndex = (this.props.viewpoint === 0 || this.props.viewpoint === 1) ?
			this.props.viewpoint : (view.you === 1 ? 1 : 0);
		if ((home.seat !== meIndex) !== foe) return fallback;
		return home.slot;
	}

	displayMon(seat: number, slot: TcgSlot, live: TcgPokemonView | null): TcgPokemonView | null {
		const placed = this.state.placeIn;
		const displaced = this.state.placeOut;
		// A place beat can still be showing the pre-switch cards after the board
		// has moved. Covering the cell then sends the cell's slot for the other Pokémon.
		const overlayAgrees = (entry: { seat: number, slot: TcgSlot, mon: TcgPokemonView } | null) => {
			if (!entry) return false;
			const home = findMonSlot((this.boardSnap || this.props.snapshot).players, entry.mon.iid);
			if (!home) return true;
			return home.seat === entry.seat && sameSlot(home.slot, entry.slot);
		};
		if (overlayAgrees(placed) && placed!.seat === seat && sameSlot(placed!.slot, slot)) {
			return this.withHeldHp(placed!.mon);
		}
		if (overlayAgrees(displaced) && displaced!.seat === seat && sameSlot(displaced!.slot, slot)) {
			return this.withHeldHp(displaced!.mon);
		}
		// Keep the real card through the KO. Swapping it for a copy made it vanish and pop back.
		if (live && this.state.koHide[live.iid]) return null;
		if (live && placed && live.iid === placed.mon.iid && overlayAgrees(placed) &&
			!(placed.seat === seat && sameSlot(placed.slot, slot))) return null;
		if (live && displaced && live.iid === displaced.mon.iid && overlayAgrees(displaced) &&
			!(displaced.seat === seat && sameSlot(displaced.slot, slot))) return null;
		// The snapshot already has the replacement. Keep the Knocked Out card in this
		// slot until its KO beat finishes, so the attack and KO have something to play on.
		const knocked = this.koHold(seat, slot, live?.iid);
		if (knocked) return this.withHeldHp(knocked);
		if (live) return this.withHeldHp(live);
		const g = this.state.koGhost;
		if (g && g.seat === seat && sameSlot(g.slot, slot)) {
			return this.withHeldHp({
				iid: g.iid,
				cardId: g.cardId,
				name: g.name,
				image: g.image,
				hp: g.hp ?? 0,
				maxHp: g.maxHp ?? g.hp ?? 0,
			});
		}
		const fx = this.state.fx;
		let held: TcgPokemonView | null = null;
		Object.keys(this.monMemory).forEach(iid => {
			if (held) return;
			const mem = this.monMemory[iid];
			if (!mem || mem.seat !== seat || !sameSlot(mem.slot, slot)) return;
			if (findMonView(this.props.snapshot.players, iid)) return;
			const knocking = (fx?.kind === 'ko' && fx.iid === iid) || !!this.freshOut[iid];
			if (!knocking) return;
			held = {
				iid: mem.iid,
				cardId: mem.cardId,
				name: mem.name,
				image: mem.image,
				hp: mem.hp ?? 0,
				maxHp: mem.maxHp ?? mem.hp ?? 0,
			};
		});
		return this.withHeldHp(held);
	}

	/** Cards in flight right now, so a cut beat, a halt, or an unmount can take them off at once. */
	flights: { el: HTMLElement, anim: Animation | null, marks?: HTMLElement[], giving?: HTMLElement | null }[] = [];
	/**
	 * Cards still in the air to a hand, a pile, or a Pokémon: the board already holds them (it is the beat's result),
	 * so where they are going leaves them out until they land (`withoutInbound`, the hand and the Pokémon). One key per
	 * flight; the cards past the five that fly share the last flight's key and land with it.
	 */
	inbound: { key: number, zone: FlightZoneKind, seat: number, cardId: string, iid?: string, part?: 'attach' }[] = [];
	inboundKey = 0;
	/** Each beat's inbound keys, by its planned flights, registered before the beat renders (`expectFlights`). */
	expected: { fx: FxBeat, keys: (number | null)[] }[] = [];
	/** Hold the destinations of a beat's flights before it renders, so no card shows there before it arrives. */
	expectFlights(fx: FxBeat) {
		if (this.expected.some(x => x.fx === fx) || tcgReduceMotion()) return;
		const plan = cardFlights(fx);
		const keys = plan.map(f => {
			const to = f.to;
			if (to.zone === 'prizes' || to.zone === 'zone') return null;
			const key = ++this.inboundKey;
			this.inbound.push({ key, zone: to.zone, seat: to.seat, cardId: f.cardId, iid: to.iid, part: to.part });
			return key;
		});
		// More cards than flights (up to five fly): the rest land with the last flight to the same place.
		const seats = fx.perSeat ? fx.perSeat.map((s, seat) => ({ seat, n: s.n || 0, ids: s.ids })) :
			[{ seat: plan[0]?.to.seat ?? -1, n: fx.n || fx.ids?.length || plan.length, ids: fx.ids }];
		seats.forEach(({ seat, n, ids }) => {
			const to = plan.filter(f => f.to.seat === seat);
			const last = plan.lastIndexOf(to[to.length - 1]);
			if (last < 0 || keys[last] == null) return;
			const at = to[to.length - 1].to;
			for (let k = to.length; k < n; k++) {
				this.inbound.push({ key: keys[last], zone: at.zone, seat, cardId: ids?.[k] || '', iid: at.iid, part: at.part });
			}
		});
		this.expected.push({ fx, keys });
		// Never hold a card for good: whatever happens to the flight, it shows after its longest possible time.
		const keep = keys.filter((k): k is number => k != null);
		if (keep.length) window.setTimeout(() => keep.forEach(k => this.landInbound(k)), fxMs(620) + fxMs(700) + 2500);
	}
	/** A flight landed, was cut, or never left: its cards show where they went. */
	landInbound(key: number | null | undefined) {
		if (key == null) return;
		const n = this.inbound.length;
		this.inbound = this.inbound.filter(x => x.key !== key);
		if (this.inbound.length === n || !this.tableEl) return;
		// Cards landing together (a deal, a big draw) show in one render, on the next frame.
		if (typeof requestAnimationFrame !== 'function') {
			this.forceUpdate();
		} else if (this.inboundRaf == null) {
			this.inboundRaf = requestAnimationFrame(() => {
				this.inboundRaf = null;
				if (this.tableEl) this.forceUpdate();
			});
		}
	}
	inboundRaf: number | null = null;
	/** A player as drawn while cards are flying to their piles: the count and the top card leave those cards out. */
	withoutInbound(p: TcgPlayerView, seat: number): TcgPlayerView {
		const coming = this.inbound.filter(x => x.seat === seat &&
			(x.zone === 'discard' || x.zone === 'lost' || x.zone === 'deck'));
		if (!coming.length || !p) return p;
		const less = <T extends string[] | { count: number } | undefined>(pile: T): T => (!pile ? pile :
			Array.isArray(pile) ? pile.slice(0, -1) : { ...pile, count: Math.max(0, pile.count - 1) }) as T;
		const out = { ...p };
		for (const x of coming) {
			if (x.zone === 'discard') {
				const d = (out.discard || []).slice();
				const at = x.cardId ? d.lastIndexOf(x.cardId) : -1;
				if (d.length) d.splice(at >= 0 ? at : d.length - 1, 1);
				out.discard = d;
			} else if (x.zone === 'lost') {
				out.lostZone = less(out.lostZone);
			} else {
				out.deck = less(out.deck);
			}
		}
		return out;
	}
	/** How many cards are flying to this seat's hand, by card id ('' for a card back). */
	handInbound(seat: number): { [cardId: string]: number } {
		const out: { [cardId: string]: number } = {};
		for (const x of this.inbound) if (x.zone === 'hand' && x.seat === seat) out[x.cardId] = (out[x.cardId] || 0) + 1;
		return out;
	}
	/** The Prize cards (or the points) of a seat, as drawn. */
	prizePile(seat: number): HTMLElement | null {
		const side = seat === this.viewSeat() ? 'me' : 'foe';
		return this.tableEl?.querySelector<HTMLElement>(`.tcg-prizes.${side}`) ||
			this.tableEl?.querySelector<HTMLElement>(`.tcg-half.${side} .tcg-points`) || null;
	}
	clearFlights() {
		for (const f of this.flights.slice()) {
			f.anim?.cancel();
			f.el.remove();
			for (const m of f.marks || []) m.classList.remove('is-incoming');
		}
		this.flights = [];
		// A landing pulse or a held mini card from a cut beat: the board shows the result as it is.
		this.tableEl?.querySelectorAll('.is-incoming, .is-landed, .is-giving').forEach(m => {
			m.classList.remove('is-incoming', 'is-landed', 'land-energy', 'land-tool', 'is-giving');
		});
		this.tableEl?.querySelectorAll<HTMLElement>('.tcg-mon').forEach(m => m.style.removeProperty('--fx-land'));
		// Nothing is in the air any more: every card shows where it is.
		this.inbound = [];
		this.expected = [];
	}
	/** Each Pokémon's last on-screen box, kept when it leaves its slot (a setup take-back flies from there). */
	lastMonBox: { [iid: string]: DOMRect } = {};
	monEl(iid?: string): HTMLElement | null {
		if (!iid || !this.tableEl) return null;
		const safe = iid.replace(/\\/g, '\\\\').replace(/"/g, '\\"');
		return this.tableEl.querySelector<HTMLElement>(`.tcg-mon[data-iid="${safe}"]`);
	}
	/** The seat drawn at the bottom of this board. */
	viewSeat(): number {
		const v = this.props.viewpoint;
		if (v === 0 || v === 1) return v;
		return this.props.snapshot.you === 1 ? 1 : 0;
	}
	/**
	 * Where a Pokémon's attached cards sit: the newest mini card of `cardId` (or the front one), else the strip they
	 * occupy (the stack is empty, or this size hides it). `peek` is that card's mini card when it is shown.
	 */
	attachSpot(mon: Element, cardId?: string): { box: DOMRect, peek: HTMLElement | null } {
		const peeks = Array.from(mon.querySelectorAll<HTMLElement>('.tcg-attach-peek'))
			.filter(p => p.getBoundingClientRect().width > 0);
		const peek = cardId ? peeks.filter(p => p.dataset.card === cardId).pop() || null : null;
		const at = peek || peeks[peeks.length - 1];
		if (at) return { box: at.getBoundingClientRect(), peek };
		const m = mon.getBoundingClientRect();
		// As tcg.css places them: under the card's bottom edge on the Bench, under its left edge in the Active Spot.
		if (mon.closest('.tcg-bench')) {
			const bench = new DOMRect(m.left + m.width * 0.27, m.bottom - m.height * 0.29, m.width * 0.46, m.height * 0.46);
			return { box: bench, peek: null };
		}
		const strip = new DOMRect(m.left - m.width * 0.34, m.top + m.height * 0.06, m.width * 0.72, m.height * 0.88);
		return { box: strip, peek: null };
	}
	/** The on-screen box of a zone, or null when it is not shown at this size. */
	zoneBox(z: FlightZone, cardId?: string): DOMRect | null {
		const root = this.tableEl;
		if (!root) return null;
		const side = z.seat === this.viewSeat() ? 'me' : 'foe';
		const shown = (el: Element | null) => {
			if (!el) return null;
			const b = el.getBoundingClientRect();
			return b.width > 0 && b.height > 0 ? b : null;
		};
		const q = (sel: string) => shown(root.querySelector(sel));
		switch (z.zone) {
		case 'deck': return q(`.tcg-pile.deck.${side}`);
		case 'discard': return q(`.tcg-pile.discard.${side}`);
		case 'lost': return q(`.tcg-lost-zone.${side}`) || q(`.tcg-pile.discard.${side}`);
		case 'prizes': return q(`.tcg-prizes.${side}`) || q(`.tcg-half.${side} .tcg-points`) || q(`.tcg-pile.deck.${side}`);
		case 'zone': return (side === 'me' && q('.tcg-ezone')) || this.zoneBox({ zone: 'hand', seat: z.seat });
		case 'mon': {
			const monEl = this.monEl(z.iid);
			if (monEl && z.part === 'attach') return this.attachSpot(monEl, cardId).box;
			return shown(monEl) || (z.iid && this.lastMonBox[z.iid]) || q(`.tcg-active-spot.${side}`);
		}
		case 'hand': {
			if (side === 'foe') return q('.tcg-hand-foe') || q('.tcg-player.foe .tcg-hand-count') || q('.tcg-player.foe');
			// The tray shows the top of each card: aim at the strip the player sees, not the box with its lift room.
			const hand = shown(root.querySelector('.tcg-hand-mine'));
			const dock = shown(root.querySelector('.tcg-dock'));
			if (!hand || !dock) return hand || dock;
			return new DOMRect(hand.left, dock.top, hand.width, Math.min(dock.height, 60));
		}
		}
		return null;
	}
	/**
	 * Fly the beat's cards between the real zones on screen (`cardFlights` says which). Visual only: the board state
	 * comes from the events and snapshots as before. Fits inside the beat at the player's FX speed; none
	 * under reduced motion, where the pile pulse and the caption remain.
	 */
	launchFlights(fx: FxBeat, after = 0) {
		const root = this.tableEl;
		// The attach burst and glow on the card wait for the landing (`--fx-land`); with no flight they play at once.
		const target = (fx.kind === 'energy' || fx.kind === 'tool') ? this.monEl(fx.iid) : null;
		target?.style.removeProperty('--fx-land');
		const at = this.expected.findIndex(x => x.fx === fx);
		const keys = at >= 0 ? this.expected.splice(at, 1)[0].keys : [];
		const plan = !root || tcgReduceMotion() ? [] : cardFlights(fx);
		if (!root || !plan.length || typeof (root as any).animate !== 'function') {
			keys.forEach(k => this.landInbound(k));
			return;
		}
		const box = root.getBoundingClientRect();
		const ms = fxMs(620);
		plan.forEach((f, k) => {
			const fl = after ? { ...f, delay: f.delay + after } : f;
			if (!this.launchFlight(fl, box, ms, keys[k])) {
				this.landInbound(keys[k]);
				return;
			}
			if (fl.land && target) target.style.setProperty('--fx-land', `${Math.round(ms * 0.88 + fl.delay)}ms`);
		});
	}
	/** One card of a beat in flight from its zone to the other. `inbound`: its key in `inbound`, released on landing. */
	launchFlight(f: CardFlight, box: DOMRect, ms: number, inbound?: number | null): boolean {
		const root = this.tableEl;
		if (!root) return false;
		const a = this.zoneBox(f.from, f.cardId);
		const b = this.zoneBox(f.to, f.cardId);
		if (!a || !b) return false;
		// An attachment: its mini card and its type pip are already on the Pokémon (the board is the result). They
		// show when the card lands, so it is never attached twice on screen, and never before it arrives.
		const marks: HTMLElement[] = [];
		const dest = f.land ? this.monEl(f.to.iid) : null;
		if (dest) {
			const peek = this.attachSpot(dest, f.cardId).peek;
			if (peek) marks.push(peek);
			const type = f.land === 'energy' ? energyTypeFromCardId(f.cardId) : '';
			const pipEl = type ? Array.from(dest.querySelectorAll<HTMLElement>(`.tcg-energy-row .tcg-pip.t-${type}`)).pop() : null;
			if (pipEl) marks.push(pipEl);
		}
		for (const m of marks) m.classList.add('is-incoming');
		const giving = f.from.zone === 'prizes' ? this.prizePile(f.from.seat) : null;
		giving?.classList.add('is-giving');
		const el = document.createElement('div');
		el.className = 'tcg-flight';
		el.setAttribute('aria-hidden', 'true');
		if (!f.cardId) {
			el.classList.add('back');
		} else if (noArtCards[f.cardId]) {
			el.classList.add('noart');
			const name = document.createElement('span');
			name.textContent = cardLabel(f.cardId);
			el.appendChild(name);
		} else {
			const img = document.createElement('img');
			img.src = cardArt(f.cardId);
			img.alt = '';
			img.draggable = false;
			img.onerror = () => {
				img.remove();
				el.classList.add('back');
			};
			el.appendChild(img);
		}
		root.appendChild(el);
		const w = el.offsetWidth || 48;
		const h = el.offsetHeight || 67;
		const x0 = a.left + a.width / 2 - box.left - w / 2;
		const y0 = a.top + a.height / 2 - box.top - h / 2;
		const dx = (b.left + b.width / 2) - (a.left + a.width / 2);
		const dy = (b.top + b.height / 2) - (a.top + a.height / 2);
		const s0 = Math.max(0.55, Math.min(1.3, a.width / w));
		const s1 = Math.max(0.45, Math.min(1.3, b.width / w));
		// A shallow arc toward the viewer: up a little at the middle, never across the cards' text.
		const lift = Math.min(36, Math.hypot(dx, dy) * 0.12);
		el.style.left = `${Math.round(x0)}px`;
		el.style.top = `${Math.round(y0)}px`;
		const mid = (s0 + s1) / 2 * 1.08;
		const anim = el.animate([
			{ transform: `translate(0, 0) scale(${s0})`, opacity: 0 },
			{ transform: `translate(${dx * 0.08}px, ${dy * 0.08 - lift * 0.3}px) scale(${s0 * 1.04})`, opacity: 1, offset: 0.12 },
			{ transform: `translate(${dx * 0.55}px, ${dy * 0.55 - lift}px) scale(${mid})`, opacity: 1, offset: 0.55 },
			{ transform: `translate(${dx}px, ${dy}px) scale(${s1})`, opacity: 1, offset: 0.88 },
			{ transform: `translate(${dx}px, ${dy}px) scale(${s1})`, opacity: 0 },
		], { duration: ms, delay: f.delay, easing: 'cubic-bezier(.22,.72,.2,1)', fill: 'both' });
		const entry = { el, anim, marks, giving };
		this.flights.push(entry);
		const done = (landed: boolean) => {
			el.remove();
			this.flights = this.flights.filter(x => x !== entry);
			if (giving && !this.flights.some(x => x.giving === giving)) giving.classList.remove('is-giving');
			for (const m of marks) {
				m.classList.remove('is-incoming');
				if (landed) this.landMark(m, f.land || 'energy');
			}
			this.landInbound(inbound);
		};
		anim.onfinish = () => done(true);
		anim.oncancel = () => done(false);
		return true;
	}
	/** The pulse where an attachment lands; it removes itself when it ends. */
	landMark(m: HTMLElement, land: 'energy' | 'tool') {
		const cls = `land-${land}`;
		m.classList.add('is-landed', cls);
		m.addEventListener('animationend', () => m.classList.remove('is-landed', cls), { once: true });
	}
	aimFlyers() {
		const root = this.tableEl;
		if (!root) return;
		const s = root.getBoundingClientRect();
		const aim = (fly: HTMLElement, fallbackSel: string, fromHand: boolean) => {
			const iid = fly.getAttribute('data-target') || '';
			const safe = iid.replace(/\\/g, '\\\\').replace(/"/g, '\\"');
			const target = (iid && root.querySelector(`.tcg-mon[data-iid="${safe}"]`)) as HTMLElement | null ||
				root.querySelector(fallbackSel);
			if (!target) {
				fly.classList.add('stay');
				return;
			}
			const t = target.getBoundingClientRect();
			const startX = fromHand ? (s.left + s.width / 2) : (s.left + s.width / 2);
			const startY = fromHand ? (s.top + s.height * 0.88) : (s.top + s.height / 2);
			const endX = t.left + t.width / 2;
			const endY = t.top + t.height / 2;
			fly.style.setProperty('--tx', `${Math.round(endX - startX)}px`);
			fly.style.setProperty('--ty', `${Math.round(endY - startY)}px`);
			fly.style.setProperty('--end-scale', String(Math.max(0.22, Math.min(0.55, t.width / 140))));
			fly.classList.add('go');
		};
		const monByIid = (iid: string) => {
			if (!iid) return null;
			const safe = iid.replace(/\\/g, '\\\\').replace(/"/g, '\\"');
			return root.querySelector(`.tcg-mon[data-iid="${safe}"]`) as HTMLElement | null;
		};
		void aim;
		const atk = root.querySelector('.fx-atk-fly') as HTMLElement | null;
		if (atk) {
			const fromEl = monByIid(atk.getAttribute('data-from') || '');
			const toEl = monByIid(atk.getAttribute('data-target') || '') ||
				root.querySelector('.tcg-mon.fx-hit');
			if (!fromEl || !toEl) {
				atk.classList.add('stay');
			} else {
				const f = fromEl.getBoundingClientRect();
				const t = toEl.getBoundingClientRect();
				const startX = f.left + f.width / 2 - s.left;
				const startY = f.top + f.height / 2 - s.top;
				const dx = (t.left + t.width / 2 - s.left) - startX;
				const dy = (t.top + t.height / 2 - s.top) - startY;
				atk.style.left = `${Math.round(startX)}px`;
				atk.style.top = `${Math.round(startY)}px`;
				atk.style.setProperty('--tx', `${Math.round(dx)}px`);
				atk.style.setProperty('--ty', `${Math.round(dy)}px`);
				atk.style.setProperty('--rot', `${Math.atan2(dy, dx) * 180 / Math.PI}deg`);
				atk.style.setProperty('--span', `${Math.max(48, Math.hypot(dx, dy))}px`);
				atk.classList.add('go');
			}
		}
		this.aimContactLunge(monByIid);
	}

	aimContactLunge(monByIid: (iid: string) => HTMLElement | null) {
		const root = this.tableEl;
		if (!root || tcgReduceMotion()) return;
		const el = root.querySelector('.tcg-mon.fx-lunge.is-contact') as HTMLElement | null;
		if (!el) return;
		const fromIid = el.getAttribute('data-iid') || '';
		const toIid = el.getAttribute('data-lunge-to') || '';
		if (!fromIid || !toIid) return;
		if (this.lungeIid === fromIid && typeof el.getAnimations === 'function' &&
			el.getAnimations().some(a => a.id === 'tcg-lunge')) {
			return;
		}
		const toEl = monByIid(toIid);
		if (!toEl || toEl === el) return;
		if (typeof el.animate !== 'function') return;
		el.getAnimations?.().forEach(a => {
			if (a.id === 'tcg-lunge') a.cancel();
		});
		const f = el.getBoundingClientRect();
		const t = toEl.getBoundingClientRect();
		const fromCx = f.left + f.width / 2;
		const fromCy = f.top + f.height / 2;
		const toCx = t.left + t.width / 2;
		const toCy = t.top + t.height / 2;
		let dx = toCx - fromCx;
		let dy = toCy - fromCy;
		const dist = Math.hypot(dx, dy) || 1;
		// Overlap the near edges instead of stacking the cards on one center.
		const kiss = Math.min(f.height, t.height, f.width, t.width) * 0.42;
		const travel = Math.max(24, dist - kiss);
		dx *= travel / dist;
		dy *= travel / dist;
		const ax = Math.round((-dy / dist) * Math.min(28, dist * 0.12));
		const ay = Math.round((dx / dist) * Math.min(28, dist * 0.12));
		const dir = el.classList.contains('foe') ? 1 : -1;
		this.lungeIid = fromIid;
		const anim = el.animate([
			{ transform: 'translate(0px, 0px) rotate(0deg)', offset: 0 },
			{
				transform: `translate(${Math.round(dx * 0.7 + ax)}px, ${Math.round(dy * 0.55 + ay)}px) rotate(${dir * -7}deg)`,
				offset: 0.36,
			},
			{
				transform: `translate(${Math.round(dx)}px, ${Math.round(dy)}px) rotate(${dir * 4}deg)`,
				offset: 0.44,
			},
			{
				transform: `translate(${Math.round(dx)}px, ${Math.round(dy)}px) rotate(0deg)`,
				offset: 0.58,
			},
			{ transform: 'translate(0px, 0px) rotate(0deg)', offset: 1 },
		], {
			duration: fxMs(1180),
			easing: 'cubic-bezier(0.22, 0.7, 0.18, 1)',
			fill: 'forwards',
		});
		anim.id = 'tcg-lunge';
		const done = () => {
			if (this.lungeIid === fromIid) this.lungeIid = '';
			anim.cancel();
		};
		anim.onfinish = done;
		anim.oncancel = () => {
			if (this.lungeIid === fromIid) this.lungeIid = '';
		};
	}

	/** `from`: the first event still to play, for a board that mounted while the room was part-way through a batch. */
	attackCtx: AttackCtx | null = null;
	/** One hit of an attack as a beat on its Pokémon: the number or mark, and the attacker's lunge or projectile into it. */
	hitBeat(h: FxHit, ctx: AttackCtx, fly: boolean): FxBeat {
		if (h.kind === 'status') {
			return {
				kind: 'status', iid: h.iid, status: h.status ?? '', src: h.src || ctx.src,
				extra: h.label || statusWord(h.status) || 'Recovered',
			};
		}
		const aimed = aimedHit(h, ctx.iid);
		return {
			kind: h.kind === 'heal' ? 'heal' : 'damage',
			iid: h.iid,
			fromIid: ctx.iid,
			amount: h.amount,
			src: h.src || ctx.src,
			extra: h.label || ctx.label,
			element: h.element || ctx.element,
			hitStyle: h.hitStyle || ctx.style,
			travel: fly && aimed && !!ctx.travel,
			contact: aimed && !!ctx.contact,
			hits: [h],
		};
	}
	/** A result outside an attack as a beat on its Pokémon: the same mark as an attack's hit, from no attacker. */
	effectBeat(h: FxHit, fx: FxBeat): FxBeat {
		if (h.kind === 'status') {
			return {
				kind: 'status', iid: h.iid, status: h.status ?? '', src: h.src,
				extra: h.label || statusWord(h.status) || 'Recovered', message: fx.message,
			};
		}
		return {
			kind: h.kind === 'heal' ? 'heal' : 'damage',
			iid: h.iid,
			amount: h.amount,
			src: h.src,
			extra: fx.extra || h.label,
			element: h.element || fx.element,
			message: fx.message,
			hits: [h],
		};
	}
	playFx(events: TcgEvent[], from = 0) {
		if (events?.length) this.haltJump = false;
		if (!events?.length || PS.prefs.noanim) {
			this.playedFlipKey = this.props.fxKey;
			if (!this.fxBusy) this.clearFx();
			return;
		}
		// A fast CPU can deliver the next action before this one finishes.
		// Keep playing the current beat, then the new events, each for a fixed time.
		if (this.fxBusy) {
			this.fxPending.push(...events);
			return;
		}
		this.fxBusy = true;
		try {
			this.runFx(events, true, from);
		} catch (err) {
			console.error(err);
			this.finishFx();
		}
	}

	/**
	 * The beat clock's one timer. A step that throws (a card or Pokémon that is no longer on the board) ends the
	 * batch: the room is waiting on `onFxDone` and, with the board held, on the commit it brings.
	 */
	later(fn: () => void, ms: number) {
		this.timer = window.setTimeout(() => {
			this.timer = null;
			try {
				fn();
			} catch (err) {
				console.error(err);
				if (this.timer != null) window.clearTimeout(this.timer);
				this.timer = null;
				this.finishFx();
			}
		}, ms);
	}

	finishFx() {
		if (this.fxPending.length) {
			const next = this.fxPending;
			this.fxPending = [];
			this.runFx(next, false);
			return;
		}
		// Drop the in-progress board before commit paints the server snapshot.
		this.boardSnap = null;
		this.foldedThrough = -1;
		this.foldedRequest = false;
		this.fxBusy = false;
		const more = this.props.onFxDone?.() ?? false;
		if (!more) this.clearFx();
		// After the parent commit, so Go First cannot paint on top of the last coin frame.
		this.playedFlipKey = this.props.fxKey;
	}

	runFx(events: TcgEvent[], fresh = true, from = 0) {
		if (this.timer != null) window.clearTimeout(this.timer);
		// New batch, new baseline: props.snapshot now already reflects the previous batch.
		// A continuation (events that arrived mid-beat) keeps the board those beats already built.
		if (fresh) {
			this.boardSnap = null;
			this.foldedRequest = false;
			this.liveNow = { hand: {}, hp: {}, energy: {}, prizes: {}, deck: {}, handN: {}, discardN: {}, points: {}, gained: {} };
		}
		this.foldedThrough = -1;
		const shown = this.props.fxSnapshot || this.props.snapshot;
		const players = shown.players;
		const you = shown.you;
		let i = Math.max(0, Math.min(from, events.length));
		let fxTick = 0;
		const HIT_MS = fxMs(1100);
		// Contact: attacker lunges into the target. Travel: projectile CSS (~0.72s × --fx-t).
		const WINDUP_MS = fxMs(520);
		const WINDUP_TRAVEL_MS = fxMs(720);
		const BEAT_MS = fxMs(850);
		// Matches tcg-move-banner-rise (0.71s × --fx-t). Attack motion starts after it.
		const NAME_MS = fxMs(710);

		const show = (fx: FxBeat, e?: TcgEvent, alsoFly: FxBeat[] = [], flyAfter = 0) => {
			if (e?.type === 'deal' && you != null) {
				const raw = (you === 0 ? e.ids0 : e.ids1) as string[] | undefined;
				const ids = Array.isArray(raw) ? raw.filter(Boolean) : [];
				if (ids.length) fx = { ...fx, ids, seat: you };
			} else if (e && Array.isArray(e.ids) && e.ids.length &&
				(e.type === 'draw' || e.type === 'find' || e.type === 'prizeTake')) {
				fx = { ...fx, ids: (e.ids as string[]).filter(Boolean), seat: fx.seat ?? (e.seat as number) };
			}
			fxTick++;
			this.revealHeldHp(e);
			let koGhost: KoGhost | null = null;
			let placeIn: TcgBoard['state']['placeIn'] = null;
			let placeOut: TcgBoard['state']['placeOut'] = null;
			const koHide = { ...this.state.koHide };
			const koStillQueued = (this.props.events || []).some(ev => ev.type === 'ko' && ev.iid && this.freshOut[ev.iid]);
			if (!koStillQueued && fx.kind !== 'ko' && fx.kind !== 'damage' && fx.kind !== 'attack' && fx.kind !== 'ability') {
				this.freshOut = {};
			}
			if (this.state.fx?.kind === 'ko' && this.state.fx.iid && fx.kind !== 'ko') {
				this.koFinished[this.state.fx.iid] = true;
				delete this.freshOut[this.state.fx.iid];
			}
			if (fx.kind === 'ko') {
				koGhost = e ? this.ghostForKo(fx, e) : (fx.iid ? this.monMemory[fx.iid] || null : null);
				if (koGhost && !fx.extra) fx.extra = koGhost.name;
				if (koGhost && (!fx.message || fx.message.includes('a Pokémon'))) {
					fx.message = `${koGhost.name} was Knocked Out`;
				}
			}
			if (fx.kind === 'place' && fx.iid) {
				const nextPlayers = this.props.fxSnapshot?.players || this.props.snapshot.players;
				const incoming = findMonView(nextPlayers, fx.iid) ||
					(fx.cardId ? {
						iid: fx.iid,
						cardId: fx.cardId,
						name: fx.extra || cardLabel(fx.cardId),
						hp: 0,
						maxHp: 0,
						image: this.monMemory[fx.iid]?.image,
					} : null);
				const dest = findMonSlot(nextPlayers, fx.iid);
				const seat = dest?.seat ?? fx.seat ?? 0;
				const slot = dest?.slot ?? fx.slot ?? 'active';
				const fromOld = findMonSlot(this.props.snapshot.players, fx.iid);
				fx.fromBench = !!(fromOld && fromOld.slot !== 'active' && slot === 'active');
				if (incoming) placeIn = { seat, slot, mon: incoming };
				if (fx.fromBench && fromOld) {
					const leaving = this.props.snapshot.players[seat]?.active;
					if (leaving && leaving.iid !== incoming?.iid) {
						placeOut = { seat, slot: fromOld.slot, mon: leaving };
					}
				}
			}
			// The beat's result is already on the board: where its cards are going leaves them out until they land.
			this.expectFlights(fx);
			for (const more of alsoFly) this.expectFlights(more);
			this.setState({
				fx, pkFx: buildPkFx(fx, fxTick), koGhost, koHide, placeIn, placeOut,
				live: this.applyLive(fx, you), liveKey: this.props.fxKey,
			}, () => {
				this.aimFlyers();
				this.launchFlights(fx);
				for (const more of alsoFly) this.launchFlights(more, flyAfter);
			});
		};

		const step = () => {
			while (i < events.length && skipEvent(events, i)) {
				this.props.onEvent?.(events[i]);
				// An attack's results land in playHit, when the hit connects.
				if (!isHitEvent(events[i].type)) this.foldEvents(events, i);
				i++;
			}
			if (i >= events.length) {
				this.finishFx();
				return;
			}
			const e = events[i];
			this.props.onEvent?.(e);
			if (e.type === 'turn' || e.type === 'checkup' || e.type === 'trainer' || e.type === 'stadium' ||
				e.type === 'over' || e.type === 'attackFailed') this.attackCtx = null;
			let fx = fxFor(e, events, i, players, you, shown);

			// Prefer private draw ids from the event; otherwise infer from hand delta for your seat.
			if ((fx.kind === 'draw' || fx.kind === 'drawEffect') && e.type === 'draw') {
				if ((!fx.ids?.length) && actorIsYou(e.seat, you) && e.seat != null) {
					const inferred = this.inferDrawnIds(e.seat, e.n || fx.n || 1);
					if (inferred?.length) {
						fx = { ...fx, ids: inferred, labels: inferred.map(cardLabel), n: inferred.length };
					}
				}
			}

			// Consecutive coin events → one staggered multi-coin beat.
			// Opening flip often arrives as coin + first; fold the announce into the coin beat.
			if (e.type === 'coin' || e.type === 'first') {
				const results: boolean[] = [];
				let j = i;
				if (e.type === 'coin') {
					results.push(!!e.heads);
					j = i + 1;
					while (j < events.length && events[j]?.type === 'coin') {
						this.props.onEvent?.(events[j]);
						results.push(!!events[j].heads);
						j++;
					}
				}
				let firstEv: TcgEvent | null = null;
				if (e.type === 'first') {
					firstEv = e;
					j = i + 1;
					// No coin event (older server): invent a face so the opening flip still animates.
					// Seat 0 won → Heads, seat 1 → Tails (matches engine mapping).
					results.push(e.seat === 0);
				} else if (events[j]?.type === 'first') {
					this.props.onEvent?.(events[j]);
					firstEv = events[j];
					j++;
				}
				const headsN = results.filter(Boolean).length;
				const tailsN = results.length - headsN;
				const whoFirst = firstEv?.seat != null ?
					actorLabel(players, firstEv.seat, you) : '';
				const youWonFirst = firstEv ? actorIsYou(firstEv.seat, you) : false;
				const coinFx: FxBeat = {
					kind: 'coin',
					coins: results,
					n: results.length,
					seat: firstEv?.seat ?? e.seat,
					extra: firstEv ? 'First' :
					(results.length === 1 ?
						(results[0] ? 'Heads' : 'Tails') :
						`${results.length} flips`),
					message: firstEv ?
						(firstEv.chooses ?
							(youWonFirst ?
								'You won the coin flip and choose who goes first' :
								`${whoFirst} won the coin flip and chooses who goes first`) :
							(youWonFirst ?
								'You won the coin flip and go first' :
								`${whoFirst} won the coin flip and goes first`)) :
						(results.length === 1 ?
							(results[0] ? 'Heads!' : 'Tails!') :
							`${headsN} Heads · ${tailsN} Tails`),
				};
				this.foldEvents(events, j - 1);
				show(coinFx);
				// Flip (~1.55s) + stagger + hold; linger a bit longer for the first-player announce.
				const wait = (firstEv ? 2700 : 2200) + Math.max(0, results.length - 1) * 200;
				i = j;
				this.later(step, wait);
				return;
			}

			// The hand is about to be cleared. Keep the cards on this beat so the shuffle can show them.
			if (e.type === 'shuffle' && e.from === 'hand' && e.seat != null) {
				const mem = this.handMemory[e.seat];
				fx = {
					...fx,
					ids: mem?.ids || undefined,
					n: Math.max(mem?.count || 0, fx.n || 0, 3),
				};
			}

			// Pile changes ride along with an attack. A discard before a place (setup,
			// playing a Pokémon) is its own beat so the log lines stay separate.
			const pileMove = (t?: string) => t === 'toDiscard' || t === 'toLost' || t === 'fromDiscard';
			if (pileMove(e.type)) {
				let j = i + 1;
				while (j < events.length && pileMove(events[j]?.type)) {
					this.props.onEvent?.(events[j]);
					j++;
				}
				const pileFx: FxBeat[] = [];
				for (let k = i; k < j; k++) pileFx.push(k === i ? fx : fxFor(events[k], events, k, players, you, shown));
				this.foldEvents(events, j - 1);
				const nxt = events[j];
				const ownBeat = nxt?.type === 'place' || nxt?.type === 'evolve';
				i = j;
				if (ownBeat) {
					this.forceUpdate();
					show(fx, e, pileFx.slice(1));
					this.later(step, BEAT_MS);
				} else {
					// No beat of their own (an Energy or a Tool discarded during an attack): the cards still fly, beside
					// the next beat, from where they sat to the pile.
					for (const p of pileFx) this.expectFlights(p);
					this.forceUpdate(() => {
						for (const p of pileFx) this.launchFlights(p);
					});
					this.later(step, 0);
				}
				return;
			}

			this.foldEvents(events, i);
			let advance = 1;
			// Pile moves that ride along with this beat (a Knock Out's discard): their cards fly with it.
			const ridePile: FxBeat[] = [];
			if (e.type !== 'attack' && e.type !== 'ability') {
				let j = i + 1;
				while (j < events.length && pileMove(events[j]?.type)) {
					this.props.onEvent?.(events[j]);
					j++;
				}
				if (j > i + 1) this.foldEvents(events, j - 1);
				for (let k = i + 1; k < j; k++) ridePile.push(fxFor(events[k], events, k, players, you, shown));
				advance = j - i;
			}
			// A switch is two places of Pokémon already in play. Setup Active + Bench are new cards.
			if (e.type === 'place') {
				const nxt = events[i + advance];
				const alreadyThere = nxt?.type === 'place' && nxt.seat === e.seat &&
					!!findMonSlot(this.boardSnap?.players, nxt.iid);
				if (alreadyThere) {
					this.props.onEvent?.(nxt);
					this.foldEvents(events, i + advance);
					advance += 1;
				}
			}

			if (e.type === 'attack' || e.type === 'ability') {
				const hits = attackHits(events, i, players);
				const hitAt = attackHitIndexes(events, i);
				if (!fx.extra) fx.extra = e.name || (e.type === 'ability' ? 'Ability' : 'Attack');
				if (!fx.message) fx.message = `${userName(e, players, events)} used ${fx.extra || ''}`;
				const atkElement = e.type === 'attack' ? attackElement(e, players) :
					(hits.find(h => h.element)?.element || 'psychic');
				const hitResolved = resolveHitStyle(fx.extra, atkElement);
				const windupMs = hitResolved.travel ? WINDUP_TRAVEL_MS : WINDUP_MS;
				// An Ability is not an attack: its results land on their Pokémon with no lunge and no projectile.
				const ctx: AttackCtx = {
					iid: e.iid || '', src: e.type === 'ability' ? 'ability' : 'attack', label: fx.extra || '',
					element: atkElement, style: hitResolved.style, travel: e.type === 'attack' && hitResolved.travel,
					contact: e.type === 'attack' && !hitResolved.travel,
				};
				this.attackCtx = ctx;
				const visible = hits.filter(visibleHit);
				const aimed = visible.filter(h => aimedHit(h, ctx.iid));
				// Only Pokémon its own result events name: the wind-up never aims at a legal choice that was not taken,
				// nor at a Pokémon that a switch has not brought in yet (those hits play later, from attackCtx).
				const firstTarget = aimed[0]?.iid || '';
				const firstAimed = aimed.length ? hits.indexOf(aimed[0]) : -1;
				// An Ability's results show on their Pokémon; only an attack aims and rings its targets.
				const targets = e.type !== 'attack' ? [] : aimed.map(h => h.iid).filter((id, k, all) => all.indexOf(id) === k);
				// Name banner first. Lunge, projectile, and the hits start after it fades.
				const launch = () => {
					show({
						...fx,
						announce: false,
						hits: [],
						element: atkElement,
						hitStyle: hitResolved.style,
						travel: !!ctx.travel && !!firstTarget,
						targetIid: e.type === 'attack' && firstTarget || undefined,
						contact: !!ctx.contact && !!firstTarget,
						targets,
					});
					let hi = 0;
					const playHit = () => {
						if (hi >= hits.length) {
							i++;
							this.later(step, visible.length ? fxMs(280) : 0);
							return;
						}
						const at = hitAt[hi];
						const hitIndex = hi;
						const h = hits[hi++];
						if (at != null) this.foldEvents(events, at);
						if (!visibleHit(h)) {
							this.later(playHit, 0);
							return;
						}
						// The wind-up already flew to the first target; each later aimed hit has its own flight.
						show(this.hitBeat(h, ctx, hitIndex !== firstAimed), at != null ? events[at] : undefined);
						this.later(playHit, HIT_MS);
					};
					this.later(playHit, windupMs);
				};
				show({
					...fx,
					hits: [],
					announce: true,
					element: atkElement,
					hitStyle: hitResolved.style,
				});
				this.later(launch, NAME_MS);
				return;
			}

			// A result of the attack that came after a switch, a discard, a coin, or a choice: still that attack's hit.
			if (this.attackCtx && isHitEvent(e.type)) {
				const ctx = this.attackCtx;
				const h = hitFromEvent(e, { ...ctx, fromIid: ctx.iid });
				i += advance;
				if (!h || !visibleHit(h)) {
					this.later(step, 0);
					return;
				}
				show(this.hitBeat(h, ctx, true), e, ridePile);
				this.later(step, HIT_MS);
				return;
			}

			// A result with no attack or Ability behind it (Checkup, a Trainer, a Tool, a Stadium): the same mark on its
			// Pokémon as an attack's (counters, prevention, heal, Special Condition), with no lunge or projectile.
			if (isHitEvent(e.type)) {
				const cause = e.type === 'damage' ? dmgSrc(events, i, players) :
					{ key: e.source?.kind || 'effect', label: sourceName(e.source) || '' };
				const h = hitFromEvent(e, { src: cause.key, label: cause.label, element: fx.element || '' });
				i += advance;
				if (!h || !visibleHit(h)) {
					this.later(step, 0);
					return;
				}
				show(this.effectBeat(h, fx), e, ridePile);
				this.later(step, Math.max(fxDuration(e), BEAT_MS));
				return;
			}

			// A 0-damage result (a tails flip that missed) updates nothing visible.
			if ((e.type === 'damage' || e.type === 'heal') && !(Number(e.amount) || 0)) {
				i += advance;
				this.later(step, 0);
				return;
			}

			// A Knock Out's cards go to the discard pile after its stamp has shown.
			show(fx, e, ridePile, e.type === 'ko' ? fxMs(700) : 0);
			let wait = Math.max(fx.kind === 'drawEffect' ? fxMs(1400) : fxDuration(e), BEAT_MS);
			if (e.type === 'request' && (e.kind === 'search' || e.kind === 'mulligan') &&
				actorIsYou((e.waiting?.[0]), you)) wait = 80;
			if (e.type === 'shuffle' && e.from !== 'hand') wait = fxMs(500);
			i += advance;
			this.later(step, wait);
		};
		// Beats before `from` already played on the board this one replaced: their results go in without a replay.
		if (i > 0) this.foldEvents(events, i - 1);
		step();
	}

	openInspect = (inspect: Preview) => {
		this.setState({ inspect });
	};
	/** Finger stayed down long enough to preview; drop the in-progress drag so it is not a play. */
	onCardHold = () => {
		this.holdConsumed = true;
		this.dragMoved = true;
		if (this.state.drag) this.setState({ drag: null });
	};
	/**
	 * A touch release sends a click to whatever is under the finger. When the release itself opened a panel (a hold's
	 * preview, a tapped Trainer's Use dialog), that is now the panel's backdrop, and the panel closed as soon as it
	 * opened. The backdrops ignore that one click.
	 */
	releaseClickUntil = 0;
	onInspectBackdrop = () => {
		if (performance.now() < this.releaseClickUntil) return;
		this.closeInspect();
	};
	onTrainerUseBackdrop = () => {
		if (performance.now() < this.releaseClickUntil) return;
		this.setState({ trainerUse: null });
	};
	closeInspect = () => {
		if (this.state.inspect) this.setState({ inspect: null });
	};

	choose = (a: TcgAction) => {
		if (this.props.snapshot.you == null) return;
		if (this.props.aborted || this.props.snapshot.status === 'aborted') return;
		const snapNow = this.props.snapshot;
		// Whose turn it is follows the beats on a held board, not the snapshot still waiting to be committed.
		const shownNow = this.boardSnap || snapNow;
		const offTurn = a.type === 'takePrize' || a.type === 'promote' || a.type === 'searchPick' ||
			a.type === 'searchDone' || a.type === 'discardPick' || a.type === 'payEnergy' ||
			a.type === 'confirmYes' || a.type === 'confirmNo' || a.type === 'pickSlot';
		if (shownNow.status === 'playing' && shownNow.turn !== snapNow.you && !offTurn && !isOwnCopyPromptRow(snapNow, a)) {
			this.showRefuse('Wait for your turn');
			if (this.state.drag) this.setState({ drag: null });
			return;
		}
		// In-flight choice, or the server says there is nothing to do. Animations do not use this lock.
		if (this.props.waiting) {
			if (this.state.drag) this.setState({ drag: null });
			return;
		}
		let prizePicked = this.state.prizePicked;
		let prizeGoal = this.state.prizeGoal;
		let prizePickBase = this.state.prizePickBase;
		let firstSent = this.state.firstSent;
		if (a.type === 'chooseFirst') firstSent = true;
		if (a.type === 'takePrize') {
			const idx = Number(a.index);
			if (prizePicked.indexOf(idx) < 0) prizePicked = prizePicked.concat(idx);
			if (!prizeGoal) {
				const pending = (this.boardSnap || this.props.snapshot).pendingPrize;
				prizeGoal = pending?.n || prizePicked.length;
			}
			if (!prizePickBase) {
				const seat = snapNow.you ?? 0;
				const who = snapNow.players[seat];
				const pocketNow = !!(who?.energyZone || snapNow.format?.energyZone);
				const needNow = snapNow.format?.prizes || (pocketNow ? 3 : 6);
				prizePickBase = who ? prizesLeft(who, needNow, snapNow.status) : 0;
			}
		}
		this.stopRefuseTimer();
		const sent: TcgAction = { ...a };
		delete sent.lookPile;
		delete sent.lookNote;
		this.setState({
			selectedHand: null, energyPick: false, retreatPick: false, retreatSig: null, drag: null,
			menuSlot: null, endTurnConfirm: false, attackMay: null, trainerUse: null, prizePicked, prizeGoal, prizePickBase, firstSent,
			refuse: null, payPicked: [], discardBrowse: null, attachBrowse: null, moveTargetPick: null, playField: null,
			slotHint: '', deckLook: null, boardPick: null, promptPeek: false,
		});
		this.noteHandGone(a);
		this.props.onAct(sent);
	};

	refuseTimer: number | null = null;
	slotHintTimer: number | null = null;
	stopRefuseTimer() {
		if (this.refuseTimer != null) window.clearTimeout(this.refuseTimer);
		this.refuseTimer = null;
	}
	stopSlotHintTimer() {
		if (this.slotHintTimer != null) window.clearTimeout(this.slotHintTimer);
		this.slotHintTimer = null;
	}
	/** Leave the reason on screen after the pointer comes up. */
	showRefuse(text: string, x?: number, y?: number) {
		if (!text) return;
		this.stopRefuseTimer();
		this.stopSlotHintTimer();
		const cx = x ?? (typeof window !== 'undefined' ? Math.round(window.innerWidth / 2) : 0);
		const cy = y ?? (typeof window !== 'undefined' ? Math.round(window.innerHeight / 3) : 0);
		this.setState({ refuse: { text, x: cx, y: cy }, slotHint: text });
		this.refuseTimer = window.setTimeout(() => {
			this.refuseTimer = null;
			this.setState({ refuse: null });
		}, 2400);
		this.slotHintTimer = window.setTimeout(() => {
			this.slotHintTimer = null;
			this.setState({ slotHint: '' });
		}, 2400);
	}

	/** The player's own hand order for this game (ids, display order); null until read. */
	handOrder: string[] | null = null;
	handOrderGame = '';
	handGone: HandGone[] = [];
	/** The hand as last drawn: ids and true hand indexes, in display order. */
	handShown: { id: string, i: number }[] = [];

	syncHandOrder(hand: string[], snap: TcgSnapshot): string[] {
		const game = `${snap.id || ''}:${snap.you ?? ''}`;
		if (this.handOrderGame !== game) {
			this.handOrderGame = game;
			this.handOrder = readHandOrder(game);
			this.handGone = [];
		}
		const now = Date.now();
		const { order, left } = reconcileHandOrder(this.handOrder || hand, hand, this.handGone.filter(g => now - g.at < 15000));
		this.handGone = left;
		if (order.join('\n') !== this.handOrder?.join('\n')) {
			this.handOrder = order;
			writeHandOrder(game, order);
		}
		return order;
	}

	/** Reorder only: nothing is sent. */
	moveHand(from: number, to: number): boolean {
		const order = this.handOrder;
		if (!order || from === to || to < 0 || to >= order.length) return false;
		this.handOrder = moveHandOrder(order, from, to);
		writeHandOrder(this.handOrderGame, this.handOrder);
		this.forceUpdate();
		return true;
	}

	/** The copies this action takes from my hand keep their own place in my order when they leave. */
	noteHandGone(a: TcgAction) {
		if (this.props.snapshot.discardRequest?.fromFoe && a.type === 'discardPick') return;
		const at = Date.now();
		const shown = this.handShown;
		for (const i of [a.hand, a.pair]) {
			if (typeof i !== 'number') continue;
			for (let pos = 0; pos < shown.length; pos++) {
				if (shown[pos].i !== i) continue;
				this.handGone.push({ id: shown[pos].id, pos, at });
				break;
			}
		}
	}

	handEl(): HTMLElement | null {
		return this.tableEl?.querySelector('.tcg-hand-mine') as HTMLElement | null;
	}

	/** Over the hand strip: the hand's width, the dock's height. The raised cards' box reaches over the Bench. */
	pointInHand(x: number, y: number): boolean {
		const hand = this.handEl();
		const dock = this.tableEl?.querySelector('.tcg-dock') as HTMLElement | null;
		if (!hand || !dock) return false;
		const r = hand.getBoundingClientRect();
		const d = dock.getBoundingClientRect();
		return x >= r.left && x <= r.right && y >= d.top && y <= d.bottom;
	}

	/** Where a card dropped at `x` goes: the number of other cards whose middle is left of it. */
	handDropIndex(x: number, dragged: number): number {
		const cards = Array.from(this.handEl()?.querySelectorAll<HTMLElement>(':scope > .tcg-card') || []);
		let to = 0;
		cards.forEach((c, j) => {
			if (j === dragged) return;
			const r = c.getBoundingClientRect();
			if (r.left + r.width / 2 < x) to++;
		});
		return to;
	}

	/** A drag near either end of an overflowing hand scrolls it. */
	edgeScrollHand(x: number, y: number) {
		const hand = this.handEl();
		if (!hand || hand.scrollWidth <= hand.clientWidth) return;
		const r = hand.getBoundingClientRect();
		if (y < r.top - 24 || y > r.bottom + 24) return;
		const edge = Math.min(48, r.width / 4);
		if (x < r.left + edge) hand.scrollLeft -= 14;
		else if (x > r.right - edge) hand.scrollLeft += 14;
	}

	onHandKey = (ev: KeyboardEvent) => {
		if (!ev.shiftKey || (ev.key !== 'ArrowLeft' && ev.key !== 'ArrowRight')) return;
		const hand = ev.currentTarget as HTMLElement | null;
		const card = (ev.target as HTMLElement | null)?.closest?.('.tcg-card');
		if (!hand || !card) return;
		const cards = Array.from(hand.querySelectorAll(':scope > .tcg-card'));
		const from = cards.indexOf(card);
		if (from < 0) return;
		ev.preventDefault();
		const to = from + (ev.key === 'ArrowLeft' ? -1 : 1);
		if (!this.moveHand(from, to)) return;
		requestAnimationFrame(() => {
			(hand.querySelectorAll(':scope > .tcg-card')[to] as HTMLElement | undefined)?.focus();
		});
	};

	/** The hold preview of one of my hand cards moves it left or right (touch has no Shift+arrow). */
	moveInspected(by: -1 | 1) {
		const at = this.state.inspect?.handAt;
		if (at == null || !this.moveHand(at, at + by)) return;
		this.setState({ inspect: { ...this.state.inspect!, handAt: at + by } });
	}

	acts(): TcgAction[] {
		if (this.props.snapshot.you == null) return [];
		// The request beat is when the new choices exist.
		if (this.foldedRequest && this.boardSnap) return this.boardSnap.actions || [];
		// Until then, a batch on the held board has spent the old ones (your own action's batch plays there too).
		if (this.props.playing && this.props.fxSnapshot && this.props.snapshot !== this.props.fxSnapshot) return [];
		return this.props.snapshot.actions || [];
	}

	/** My hand's card ids on the board `acts()` reads. */
	handIdsNow(): string[] | null {
		const snap = this.foldedRequest && this.boardSnap ? this.boardSnap : this.props.snapshot;
		return snap.you == null ? null : handIds(snap.players[snap.you]?.hand);
	}

	/** `actionsForDrop`, with the copies of one paired Stadium play counted once (`samePairedPlays`). */
	dropHits(acts: TcgAction[], drag: DragSource, drop: DropTarget): TcgAction[] {
		return samePairedPlays(actionsForDrop(acts, drag, drop), drag.hand, this.handIdsNow());
	}

	zoneActs(): TcgAction[] {
		return this.acts().filter(a => a.type === 'attachZone');
	}

	startHandDrag = (i: number, cardId: string, e: PointerEvent) => {
		if (this.props.snapshot.you == null) return;
		if (e.button != null && e.button !== 0) return;
		e.preventDefault();
		this.dragMoved = false;
		this.holdConsumed = false;
		this.setState({
			selectedHand: null,
			energyPick: false,
			retreatPick: false,
			drag: {
				source: 'hand', hand: i, cardId,
				name: cardId, image: cardArt(cardId, { large: true }),
				x: e.clientX, y: e.clientY,
				originX: e.clientX, originY: e.clientY,
				active: false, over: null, hint: '',
			},
		});
		(e.currentTarget as HTMLElement | null)?.setPointerCapture?.(e.pointerId);
	};

	startZoneDrag = (e: PointerEvent) => {
		if (e.button != null && e.button !== 0) return;
		const hits = this.zoneActs();
		if (!hits.length) {
			const text = whyCant(this.props.snapshot, { zone: true }, this.props.waiting);
			if (text) this.showRefuse(text, e.clientX, e.clientY);
			return;
		}
		e.preventDefault();
		this.dragMoved = false;
		const zone = this.props.snapshot.players[this.props.snapshot.you ?? 0]?.energyZone;
		this.setState({
			selectedHand: null,
			energyPick: false,
			retreatPick: false,
			drag: {
				source: 'zone',
				cardId: zone?.type || 'energy',
				name: `${zone?.type || 'Energy'} Energy`,
				x: e.clientX, y: e.clientY,
				originX: e.clientX, originY: e.clientY,
				active: false, over: null, hint: 'Drag onto a Pokémon',
			},
		});
	};

	/** Union of both Active spots, in arena-local pixels. Trainers drop inside this rectangle. */
	playFieldBox(): { top: number, left: number, width: number, height: number } | null {
		const table = this.tableEl;
		const host = (table?.querySelector('.tcg-arena') as HTMLElement | null) || table;
		if (!host) return null;
		const spots = host.querySelectorAll('.tcg-active-spot');
		if (!spots.length) return null;
		const origin = host.getBoundingClientRect();
		let top = Infinity;
		let left = Infinity;
		let right = -Infinity;
		let bottom = -Infinity;
		spots.forEach(node => {
			const r = (node as HTMLElement).getBoundingClientRect();
			if (r.width < 2 || r.height < 2) return;
			top = Math.min(top, r.top);
			left = Math.min(left, r.left);
			right = Math.max(right, r.right);
			bottom = Math.max(bottom, r.bottom);
		});
		if (!isFinite(top)) return null;
		const pad = Math.max(6, Math.min(14, origin.width * 0.012));
		let boxLeft = left - origin.left - pad;
		let boxTop = top - origin.top - pad;
		let boxWidth = right - left + pad * 2;
		let boxHeight = bottom - top + pad * 2;
		const edge = 4;
		if (boxLeft < edge) {
			boxWidth -= edge - boxLeft;
			boxLeft = edge;
		}
		if (boxTop < edge) {
			boxHeight -= edge - boxTop;
			boxTop = edge;
		}
		if (boxLeft + boxWidth > origin.width - edge) boxWidth = origin.width - edge - boxLeft;
		if (boxTop + boxHeight > origin.height - edge) boxHeight = origin.height - edge - boxTop;
		return {
			top: boxTop,
			left: boxLeft,
			width: Math.max(48, boxWidth),
			height: Math.max(48, boxHeight),
		};
	}

	pointInActiveField(x: number, y: number): boolean {
		const box = this.playFieldBox();
		const host = (this.tableEl?.querySelector('.tcg-arena') as HTMLElement | null) || this.tableEl;
		if (!box || !host) return false;
		const origin = host.getBoundingClientRect();
		const left = origin.left + box.left;
		const top = origin.top + box.top;
		return x >= left && x <= left + box.width && y >= top && y <= top + box.height;
	}

	hitDrop(x: number, y: number): DropTarget | null {
		const el = document.elementFromPoint(x, y) as HTMLElement | null;
		if (!el) return null;
		const mon = el.closest('.tcg-mon');
		let slotHit: DropTarget | null = null;
		if (mon) {
			const slotRaw = mon.getAttribute('data-drop-slot');
			if (slotRaw != null) {
				const cell: TcgSlot = slotRaw === 'active' ? 'active' : Number(slotRaw);
				const foe = mon.getAttribute('data-drop-foe') === '1';
				const iid = mon.getAttribute('data-iid') || '';
				slotHit = {
					kind: 'slot',
					slot: iid ? this.slotOfIid(iid, foe, cell) : cell,
					foe,
					empty: mon.getAttribute('data-drop-empty') === '1',
				};
			}
		}
		if (el.closest('[data-drop="stadium"]')) return { kind: 'stadium' };
		if (el.closest('[data-drop="discard"]')) return { kind: 'discard' };
		if (this.pointInActiveField(x, y)) {
			// A Pokémon under the rectangle still wins when this drag can use that slot.
			const drag = this.state.drag;
			if (slotHit && drag && actionsForDrop(this.acts(), drag, slotHit).length) return slotHit;
			return { kind: 'play' };
		}
		return slotHit;
	}

	onDragMove = (e: PointerEvent) => {
		const drag = this.state.drag;
		if (!drag) return;
		const dx = e.clientX - drag.originX;
		const dy = e.clientY - drag.originY;
		const active = drag.active || Math.hypot(dx, dy) > 8;
		if (active) {
			this.dragMoved = true;
			this.holdConsumed = false;
		}
		const over = active ? this.hitDrop(e.clientX, e.clientY) : null;
		let hint = drag.hint;
		let refuse = this.state.refuse;
		const inHand = active && drag.source === 'hand' && !(over && this.dropHits(this.acts(), drag, over).length) &&
			this.pointInHand(e.clientX, e.clientY);
		if (active && drag.source === 'hand') this.edgeScrollHand(e.clientX, e.clientY);
		if (inHand) {
			hint = 'Drop to reorder';
			refuse = null;
		} else if (active && over) {
			const hits = this.dropHits(this.acts(), drag, over);
			hint = hits.length ? dropVerb(hits[0]) : '';
			const text = hits.length ? '' : whyCant(this.props.snapshot, {
				hand: drag.hand, drop: over, source: drag.source, zone: drag.source === 'zone',
			}, this.props.waiting);
			refuse = text ? { text, x: e.clientX, y: e.clientY } : null;
		} else if (active) {
			const mine = drag.hand != null ? this.acts().filter(a => actionTouchesHand(a, drag.hand!)) : [];
			const trainerField = mine.some(a => a.type === 'playTrainer' && !hasBoardTarget(a));
			hint = drag.source === 'zone' ? 'Drop on a Pokémon' :
				trainerField ? 'Drop on the Active field' : 'Drop on a valid target';
			const idle = drag.source === 'hand' && drag.hand != null &&
				!this.acts().some(a => actionTouchesHand(a, drag.hand!));
			const text = idle ? whyCant(this.props.snapshot, { hand: drag.hand }, this.props.waiting) : '';
			refuse = text ? { text, x: e.clientX, y: e.clientY } : null;
		}
		if (active) this.stopRefuseTimer();
		if (
			active === drag.active &&
			e.clientX === drag.x && e.clientY === drag.y &&
			sameDrop(over, drag.over) && hint === drag.hint &&
			(refuse?.text || '') === (this.state.refuse?.text || '')
		) return;
		this.setState({
			drag: { ...drag, x: e.clientX, y: e.clientY, active, over, hint },
			playField: active ? this.playFieldBox() : null,
			refuse,
		});
	};

	/** Scroll the hand just enough that the selected card is fully inside it. Never scrolls on a hand change. */
	revealSelectedHand() {
		const hand = this.tableEl?.querySelector('.tcg-hand-mine') as HTMLElement | null;
		const card = hand?.querySelector('.tcg-card-selected') as HTMLElement | null;
		if (!hand || !card || hand.scrollWidth <= hand.clientWidth) return;
		const view = hand.getBoundingClientRect();
		const at = card.getBoundingClientRect();
		const by = handScrollToReveal(at.left, at.right, view.left, view.right);
		if (by) hand.scrollLeft += by;
	}

	onHandWheel = (ev: WheelEvent) => {
		const hand = ev.currentTarget as HTMLElement | null;
		if (!hand) return;
		const unit = ev.deltaMode === 1 ? 16 : ev.deltaMode === 2 ? hand.clientWidth : 1;
		const next = handWheelTarget(ev.deltaX * unit, ev.deltaY * unit, hand.scrollLeft, hand.clientWidth, hand.scrollWidth);
		if (next == null) return;
		ev.preventDefault();
		hand.scrollLeft = next;
	};

	onDragEnd = (e: PointerEvent) => {
		const drag = this.state.drag;
		if (this.holdConsumed) {
			this.holdConsumed = false;
			this.releaseClickUntil = performance.now() + 450;
			if (drag) this.setState({ drag: null, playField: null, refuse: null });
			return;
		}
		if (!drag) return;
		// The browser took the gesture (a swipe that scrolls the hand): nothing was clicked or dropped.
		if (e.type === 'pointercancel') {
			this.setState({ drag: null, playField: null, refuse: null });
			return;
		}
		const over = drag.active ? this.hitDrop(e.clientX, e.clientY) : null;
		const hits = over ? this.dropHits(this.acts(), drag, over) : [];
		// Dropped back in the hand strip, on no legal target: a reorder, never a play.
		const toHand = drag.active && drag.source === 'hand' && !hits.length && this.pointInHand(e.clientX, e.clientY);
		if (toHand && drag.hand != null) {
			const from = this.handShown.findIndex(h => h.i === drag.hand);
			this.setState({ drag: null, playField: null, refuse: null });
			if (from >= 0) this.moveHand(from, this.handDropIndex(e.clientX, from));
			return;
		}
		if (drag.active && hits.length === 1) {
			this.choose(hits[0]);
			return;
		}
		if (drag.active && hits.length > 1) {
			if (drag.hand != null && this.openDeckLook(drag.hand)) return;
			// Several legal plays share this drop. Do not send the first one; say why nothing happened.
			if (drag.hand != null) {
				this.setState({
					drag: null, playField: null, refuse: null,
					selectedHand: drag.hand, energyPick: false, retreatPick: false, menuSlot: null,
				});
				this.showRefuse(ambiguousPlayText(hits), e.clientX, e.clientY);
				return;
			}
			this.setState({ drag: null, playField: null });
			return;
		}
		if (!drag.active && drag.source === 'hand' && drag.hand != null) {
			this.setState({ drag: null, playField: null });
			this.releaseClickUntil = performance.now() + 450;
			this.clickHand(drag.hand, e);
			return;
		}
		if (!drag.active && drag.source === 'zone') {
			this.setState({ drag: null, playField: null });
			this.clickEnergy(e);
			return;
		}
		const text = whyCant(this.props.snapshot, {
			hand: drag.hand,
			drop: over,
			source: drag.source,
			zone: drag.source === 'zone',
		}, this.props.waiting);
		this.setState({ drag: null, playField: null, inspect: null, refuse: null });
		if (text) this.showRefuse(text, e.clientX, e.clientY);
	};

	clickHand = (i: number, ev?: { clientX: number, clientY: number }) => {
		const hits = samePairedPlays(this.acts().filter(a => actionTouchesHand(a, i)), i, this.handIdsNow());
		if (this.openDeckLook(i)) return;
		const needsTarget = hits.some(a => hasBoardTarget(a));
		const free = hits.filter(a => !hasBoardTarget(a));
		// Untargeted Trainers wait for Use / Cancel. Drag onto the Active field still plays them.
		if (!needsTarget && free.length === 1 && free[0].type === 'playTrainer') {
			const id = handCardId(this.props.snapshot, i);
			this.stopRefuseTimer();
			this.setState({
				trainerUse: { action: free[0], name: cardLabel(id) || 'Trainer' },
				selectedHand: null,
				energyPick: false,
				retreatPick: false,
				menuSlot: null,
				refuse: null,
				drag: null,
				playField: null,
				deckLook: null,
			});
			return;
		}
		if (hits.length === 1 && !hasBoardTarget(hits[0])) {
			this.choose(hits[0]);
			return;
		}
		// Several untargeted plays of one kind: no Pokémon or field tells them apart, so selecting the card would lead
		// nowhere. Different kinds (Active or Bench in setup) are told apart by where the card is dropped.
		if (!needsTarget && free.length > 1 && free.every(a => a.type === free[0].type)) {
			if (ev) this.showRefuse(ambiguousPlayText(free), ev.clientX, ev.clientY);
			else this.showRefuse(ambiguousPlayText(free));
			return;
		}
		if (!hits.length) {
			const id = handCardId(this.props.snapshot, i);
			const stadium = id ? this.acts().find(a => a.type === 'useStadium' && a.pick === id) : undefined;
			if (stadium) {
				this.choose(stadium);
				return;
			}
			const text = whyCant(this.props.snapshot, { hand: i }, this.props.waiting);
			if (text && ev) this.showRefuse(text, ev.clientX, ev.clientY);
			return;
		}
		this.stopRefuseTimer();
		this.setState({
			selectedHand: this.state.selectedHand === i ? null : i,
			energyPick: false,
			retreatPick: false,
			menuSlot: null,
			refuse: null,
			deckLook: null,
		});
	};

	clickStadium = (ev?: { clientX: number, clientY: number }) => {
		const acts = this.acts();
		const selected = this.state.selectedHand;
		if (selected != null) {
			const id = handCardId(this.props.snapshot, selected);
			const hit = id ? acts.find(a => a.type === 'useStadium' && a.pick === id) : undefined;
			if (hit) {
				this.choose(hit);
				return;
			}
		}
		const chosen = stadiumClickAction(acts);
		if (chosen) {
			this.choose(chosen);
			return;
		}
		const text = stadiumUseHint(acts);
		if (text && ev) this.showRefuse(text, ev.clientX, ev.clientY);
	};

	openDeckLook(hand: number): boolean {
		const prompt = deckLookPrompt(this.acts(), hand);
		if (!prompt) return false;
		this.stopRefuseTimer();
		this.setState({
			deckLook: { hand, picked: [] },
			selectedHand: null,
			drag: null,
			energyPick: false,
			retreatPick: false,
			retreatSig: null,
			menuSlot: null,
			refuse: null,
		});
		return true;
	}

	toggleDeckLook = (index: number) => {
		const cur = this.state.deckLook;
		if (!cur) return;
		const prompt = deckLookPrompt(this.acts(), cur.hand);
		if (!prompt || index < 0 || index >= prompt.pile.length) return;
		const has = cur.picked.includes(index);
		if (!has && cur.picked.length >= prompt.need) return;
		const picked = has ? cur.picked.filter(i => i !== index) : cur.picked.concat(index);
		this.setState({ deckLook: { hand: cur.hand, picked } });
	};

	confirmDeckLook = () => {
		const cur = this.state.deckLook;
		if (!cur) return;
		const prompt = deckLookPrompt(this.acts(), cur.hand);
		if (!prompt || cur.picked.length !== prompt.need) return;
		const action = deckLookAction(prompt.actions, cur.picked);
		if (!action) return;
		this.choose(action);
	};

	menuActs(slot: TcgSlot): TcgAction[] {
		return menuActionsForSlot(this.acts(), slot);
	}

	/** The board choice for the committed snapshot and the player's own step in it. */
	boardChoice(): BoardChoice | null {
		return boardChoiceOf(this.props.snapshot, this.state.boardPick);
	}

	/** The Pokémon in that spot on the committed snapshot. */
	iidAt(slot: TcgSlot, foe: boolean): string {
		const snap = this.props.snapshot;
		const seat = foe ? (snap.you === 0 ? 1 : 0) : (snap.you ?? 0);
		const p = snap.players[seat];
		const mon = slot === 'active' ? p?.active : p?.bench?.[Number(slot)];
		return mon?.iid || '';
	}

	/** A slot pick names a slot only; `pendingSlotPick.side` says whose Pokémon it is. */
	slotPickSide(foe: boolean): boolean {
		const side = this.props.snapshot.pendingSlotPick?.side;
		return !side || (side === 'foe') === foe;
	}

	/** Board-choice marks for one Pokémon: offered (keyboard), opened, or what is moving comes off it. */
	choiceMonProps(
		mon: TcgPokemonView | null, slot: TcgSlot, foe: boolean
	): { selected?: true, keyPick?: true, choiceFrom?: true } {
		const choice = this.boardChoice();
		if (!choice || !mon) return {};
		const out: { selected?: true, keyPick?: true, choiceFrom?: true } = {};
		if (choice.from?.iid === mon.iid) out.choiceFrom = true;
		if (choice.kind === 'attached') {
			if (!choiceSourceFor(choice, mon.iid)) return out;
			out.keyPick = true;
			if (choice.picked?.iid === mon.iid) out.selected = true;
			return out;
		}
		if (this.slotLegal(slot, foe)) out.keyPick = true;
		return out;
	}

	/**
	 * The pick sent from this board choice, or ''. One sent under an earlier prompt does not count: the next prompt of
	 * the same effect (Giacomo's next Pokémon) can offer the same pick, and must not start out marked as sent.
	 */
	choiceSent(choice: BoardChoice | null): string {
		const pick = this.state.boardPick;
		return pick?.sent && pick.key === choice?.key ? pick.sent : '';
	}

	/** Open a source Pokémon's attached cards. Local only: nothing is sent until a card is chosen. */
	clickChoiceSource(choice: BoardChoice & { kind: 'attached' }, iid: string, ev?: MouseEvent) {
		if (this.props.waiting || this.choiceSent(choice)) return;
		const src = choiceSourceFor(choice, iid);
		if (!src) {
			this.showRefuse('Choose one of the highlighted Pokémon.', ev?.clientX, ev?.clientY);
			return;
		}
		this.stopRefuseTimer();
		this.choiceFocusPicker = true;
		this.setState({ boardPick: { key: choice.key, iid: src.iid }, refuse: null, menuSlot: null, selectedHand: null });
	}

	/** Send the row for one attached card, as the engine offered it. */
	pickChoiceCard = (card: BoardChoiceCard) => {
		const choice = this.boardChoice();
		if (choice?.kind !== 'attached' || this.props.waiting || this.choiceSent(choice)) return;
		// Only a row still in the current legal actions is sent.
		const row = choice.picked?.cards.find(c => c.row.pick === card.row.pick)?.row;
		if (!row) return;
		const iid = choice.picked!.iid;
		this.choose(row);
		this.setState({ boardPick: { key: choice.key, iid, sent: String(row.pick) } });
	};

	choiceBackable(): boolean {
		const choice = this.boardChoice();
		return !!choice && choice.kind === 'attached' && choice.sources.length > 1 && !!choice.picked &&
			!this.choiceSent(choice) && !this.props.waiting;
	}

	/** Back to choosing the Pokémon. Nothing was sent, so nothing is undone on the server. */
	choiceBack = () => {
		if (!this.choiceBackable()) return;
		const iid = this.state.boardPick?.iid || '';
		this.setState({ boardPick: null });
		const base = (this as unknown as { base?: HTMLElement }).base;
		if (base?.querySelector) {
			queueMicrotask(() => base.querySelector<HTMLElement>(`.tcg-mon[data-iid="${iid}"]`)?.focus({ preventScroll: true }));
		}
	};

	slotActionable(slot: TcgSlot, foe = false): boolean {
		const choice = this.boardChoice();
		if (choice?.kind === 'attached') return !!choiceSourceFor(choice, this.iidAt(slot, foe));
		if (foe) return this.slotLegal(slot, true);
		if (this.menuActs(slot).length) return true;
		return this.slotLegal(slot, false);
	}

	clickSlot = (slot: TcgSlot, foe = false, ev?: MouseEvent) => {
		if (this.state.drag?.active) return;
		const host = ((ev?.target || ev?.currentTarget) as HTMLElement | null)?.closest?.('.tcg-mon');
		const iid = host?.getAttribute('data-iid') || '';
		if (iid) slot = this.slotOfIid(iid, foe, slot);
		const boardPrompt = this.boardChoice();
		if (boardPrompt?.kind === 'attached') {
			this.clickChoiceSource(boardPrompt, iid || this.iidAt(slot, foe), ev);
			return;
		}
		const acts = this.acts();
		const snap = this.props.snapshot;
		const at = (text: string) => {
			if (text) this.showRefuse(text, ev?.clientX, ev?.clientY);
		};
		const { selectedHand, energyPick, retreatPick, menuSlot } = this.state;
		if (selectedHand != null) {
			const me = snap.players[snap.you ?? 0];
			const opp = snap.players[snap.you === 0 ? 1 : 0];
			const side = foe ? opp : me;
			const empty = !(slot === 'active' ? side?.active : side?.bench?.[Number(slot)]);
			const drop: DropTarget = { kind: 'slot', slot, foe, empty };
			const hits = actionsForDrop(acts, { source: 'hand', hand: selectedHand }, drop);
			if (hits.length === 1) return this.choose(hits[0]);
			const legacy = acts.filter(a => actionTouchesHand(a, selectedHand) && actionTouchesSlot(a, slot));
			if (legacy.length === 1) return this.choose(legacy[0]);
			// `whyCant` is empty for a drop that has matches, so several matches would say nothing.
			if (hits.length > 1) return at(ambiguousPlayText(hits));
			at(whyCant(snap, { hand: selectedHand, drop }, this.props.waiting));
			return;
		}
		if (!foe && energyPick) {
			const hits = this.zoneActs().filter(a => actionTouchesSlot(a, slot));
			if (hits.length === 1) return this.choose(hits[0]);
			at(whyCant(snap, { zone: true, drop: { kind: 'slot', slot, foe, empty: false }, source: 'zone' }, this.props.waiting));
			return;
		}
		if (retreatPick) {
			const sig = this.state.retreatSig;
			const probe = acts.find(a => a.type === 'retreat' && (sig == null || retreatSignature(a) === sig));
			const choice = probe && typeof slot === 'number' ? retreatChoice(acts, probe, slot) : null;
			if (choice && choice !== 'pick') return this.choose(choice);
			at(foe || slot === 'active' ? 'Choose a Benched Pokémon to switch in.' : "This Pokémon can't retreat there.");
			return;
		}
		const promo = acts.filter(a => a.type === 'promote' && typeof slot === 'number' && a.bench === slot);
		if (promo.length === 1 && !this.menuActs(slot).some(a => a.type === 'ability')) {
			return this.choose(promo[0]);
		}
		const pick = this.slotPickSide(foe) ? acts.filter(a => a.type === 'pickSlot' && actionTouchesSlot(a, slot)) : [];
		if (pick.length === 1) return this.choose(pick[0]);
		if (boardPrompt?.kind === 'slot') {
			at('Choose one of the highlighted Pokémon.');
			return;
		}
		if (foe) {
			const hits = foeSlotTargets(acts, slot);
			if (hits.length === 1) return this.choose(hits[0]);
			at(hits.length ? 'Choose a specific target for that effect.' : 'That Pokémon is not a valid target.');
			return;
		}
		// Live-style: tap your Pokémon to open its action menu.
		const menu = this.menuActs(slot);
		if (menu.length) {
			this.stopRefuseTimer();
			this.stopSlotHintTimer();
			this.setState({
				menuSlot: menuSlot === slot ? null : slot,
				selectedHand: null,
				energyPick: false,
				refuse: null,
				slotHint: '',
			});
			return;
		}
		this.setState({ menuSlot: null });
		const why = whyCant(snap, { slot }, this.props.waiting) || whyMon(snap, slot) ||
			"This Pokémon has no actions available right now.";
		at(why);
	};

	pickMenuAction = (a: TcgAction) => {
		const menu = this.state.menuSlot != null ? this.menuActs(this.state.menuSlot) : this.acts();
		if ((a.type === 'attack' || a.type === 'ability') && isMultiTargetGroup(menu, a)) {
			const group = moveTargetGroup(menu, a);
			const snap = this.props.snapshot;
			const me = snap.players[snap.you ?? 0];
			const title = describeAction(a, snap, { moveOnly: true });
			const monAtk = a.type === 'attack' ? me?.active?.attacks?.[a.index as number] : null;
			const cost = (a.cost as string[] | undefined) || monAtk?.cost;
			const damage = (a.damage as string | undefined) || monAtk?.damage;
			this.setState({
				moveTargetPick: {
					title,
					kind: a.type,
					actions: group,
					cost,
					damage,
				},
				menuSlot: null,
				selectedHand: null,
				energyPick: false,
				retreatPick: false,
			});
			return;
		}
		if (a.type === 'attack') {
			const pair = optionalAttackPair(menu, a.index);
			if (pair) {
				const text = pair.yes.text || pair.no.text || 'Use this attack\'s extra effect?';
				this.setState({
					attackMay: { yes: pair.yes, no: pair.no, title: pair.yes.name || 'Attack', text },
					menuSlot: null,
				});
				return;
			}
		}
		if (a.type === 'retreat') {
			const choice = retreatChoice(this.acts(), a);
			if (choice && choice !== 'pick') return this.choose(choice);
			if (choice === 'pick') {
				this.setState({
					retreatPick: true, retreatSig: retreatSignature(a),
					menuSlot: null, selectedHand: null, energyPick: false,
				});
			}
			return;
		}
		this.choose(a);
	};

	/** Second step after a multi-target attack/ability button: pick which Pokémon. */
	pickMoveTarget = (a: TcgAction) => {
		const pick = this.state.moveTargetPick;
		if (!pick) return;
		const sameTarget = pick.actions.filter(x => actionTargetKey(x) === actionTargetKey(a));
		const pool = sameTarget.length ? sameTarget : [a];
		if (a.type === 'attack') {
			const pair = optionalAttackPair(pool, a.index);
			if (pair) {
				const text = pair.yes.text || pair.no.text || 'Use this attack\'s extra effect?';
				this.setState({
					attackMay: { yes: pair.yes, no: pair.no, title: pair.yes.name || pick.title || 'Attack', text },
					moveTargetPick: null,
				});
				return;
			}
		}
		this.setState({ moveTargetPick: null });
		this.choose(pool[0] || a);
	};

	clickEnergy = (ev?: { clientX: number, clientY: number }) => {
		const hits = this.zoneActs();
		if (!hits.length) {
			const text = whyCant(this.props.snapshot, { zone: true }, this.props.waiting);
			if (text && ev) this.showRefuse(text, ev.clientX, ev.clientY);
			return;
		}
		if (hits.length === 1) {
			this.choose(hits[0]);
			return;
		}
		this.setState({ energyPick: true, selectedHand: null, retreatPick: false, menuSlot: null });
	};

	slotDropMeta(slot: TcgSlot, foe: boolean, empty: boolean): { dropOk: boolean, dropHot: boolean, dropLabel: string } {
		const drag = this.state.drag;
		if (!drag?.active) {
			return { dropOk: this.slotLegal(slot, foe), dropHot: false, dropLabel: '' };
		}
		const drop: DropTarget = { kind: 'slot', slot, foe, empty };
		const hits = actionsForDrop(this.acts(), drag, drop);
		const hot = sameDrop(drag.over, drop);
		return {
			dropOk: hits.length > 0,
			dropHot: hot && hits.length > 0,
			dropLabel: hits.length ? dropVerb(hits[0]) : '',
		};
	}

	slotLegal(slot: TcgSlot, foe = false): boolean {
		const acts = this.acts();
		const { selectedHand, energyPick, retreatPick, drag } = this.state;
		if (drag?.active) {
			const empty = true; // refined in slotDropMeta
			return canDropAnywhere(acts, drag, { kind: 'slot', slot, foe, empty }) ||
				canDropAnywhere(acts, drag, { kind: 'slot', slot, foe, empty: false });
		}
		if (selectedHand != null) {
			const forEmpty = actionsForDrop(acts, { source: 'hand', hand: selectedHand }, {
				kind: 'slot', slot, foe, empty: true,
			});
			const forOcc = actionsForDrop(acts, { source: 'hand', hand: selectedHand }, {
				kind: 'slot', slot, foe, empty: false,
			});
			if (forEmpty.length || forOcc.length) return true;
			return acts.some(a => actionTouchesHand(a, selectedHand) && actionTouchesSlot(a, slot));
		}
		if (!foe && (energyPick || !retreatPick) && this.zoneActs().some(a => actionTouchesSlot(a, slot))) {
			return true;
		}
		if (retreatPick) {
			const sig = this.state.retreatSig;
			return typeof slot === 'number' && acts.some(a =>
				a.type === 'retreat' && a.bench === slot && (sig == null || retreatSignature(a) === sig)
			);
		}
		if (acts.some(a => a.type === 'promote' && typeof slot === 'number' && a.bench === slot)) return true;
		if (this.slotPickSide(foe) && acts.some(a => a.type === 'pickSlot' && actionTouchesSlot(a, slot))) return true;
		if (foe) return foeSlotTargets(acts, slot).length > 0;
		return false;
	}

	clearSel = () => {
		this.payQueue = [];
		this.paySending = false;
		this.setState({
			selectedHand: null, energyPick: false, retreatPick: false, retreatSig: null, drag: null, inspect: null,
			menuSlot: null, endTurnConfirm: false, attackMay: null, trainerUse: null, moveTargetPick: null, payPicked: [],
			discardBrowse: null, attachBrowse: null, playField: null, slotHint: '',
		});
	};

	/**
	 * Remaining payEnergy indexes after the first choose was sent.
	 * Highest-first order keeps lower indexes stable as the server splices energy.
	 */
	payQueue: number[] = [];
	/** True from Confirm/auto-pay until the retreatPay prompt closes. */
	paySending = false;

	togglePayEnergy = (index: number, need: number, pays: TcgAction[]) => {
		if (this.payQueue.length || this.paySending) return;
		this.setState(prev => {
			const cur = prev.payPicked;
			const at = cur.indexOf(index);
			if (at >= 0) return { payPicked: cur.filter(i => i !== index) };
			if (cur.length >= need) return null;
			const next = cur.concat(index);
			// Retreat cost 1: pick is enough — skip the Confirm click.
			if (need === 1) {
				queueMicrotask(() => this.confirmPayEnergy(pays, next));
			}
			return { payPicked: need === 1 ? [] : next };
		});
	};

	confirmPayEnergy = (pays: TcgAction[], pickedOverride?: number[]) => {
		if (this.payQueue.length || this.paySending) return;
		const need = this.props.snapshot.pendingRetreatPay?.need ?? 0;
		const picked = pickedOverride || this.state.payPicked;
		if (!need || picked.length !== need) return;
		// Highest index first so earlier splices do not shift later picks.
		const indexes = picked.slice().sort((a, b) => b - a);
		const first = pays.find(p => p.index === indexes[0]);
		if (!first) return;
		this.paySending = true;
		this.payQueue = indexes.slice(1);
		this.stopRefuseTimer();
		this.stopSlotHintTimer();
		this.setState({
			selectedHand: null, energyPick: false, retreatPick: false, drag: null,
			menuSlot: null, endTurnConfirm: false, attackMay: null, trainerUse: null,
			refuse: null, payPicked: [], discardBrowse: null, attachBrowse: null, moveTargetPick: null, playField: null,
			slotHint: '',
		});
		this.props.onAct(first);
	};

	/** Send the next queued payEnergy once the server offers a fresh retreatPay prompt. */
	flushPayQueue = () => {
		const pays = this.acts().filter(a => a.type === 'payEnergy');
		if (!pays.length) {
			this.payQueue = [];
			this.paySending = false;
			return;
		}
		if (!this.payQueue.length) return;
		const nextIdx = this.payQueue[0];
		const act = pays.find(p => Number(p.index) === nextIdx);
		if (!act) {
			this.payQueue = [];
			this.paySending = false;
			return;
		}
		this.payQueue = this.payQueue.slice(1);
		this.props.onAct(act);
	};

	openDiscardBrowse = (side: 'me' | 'foe', ev?: MouseEvent) => {
		if (this.state.drag?.active) return;
		ev?.stopPropagation();
		const snap = this.props.snapshot;
		const me = snap.players[snap.you ?? 0];
		const foe = snap.players[snap.you === 0 ? 1 : 0];
		const pile = side === 'me' ? me?.discard : foe?.discard;
		if (!pile?.length) {
			this.showRefuse('The discard pile is empty.', ev?.clientX, ev?.clientY);
			return;
		}
		this.setState({ discardBrowse: side, attachBrowse: null, menuSlot: null, inspect: null });
	};

	/**
	 * The Trainer this seat played that started the current board choice, from the `trainer` event in the batch
	 * that opened it. Kept while the choice lasts (later steps arrive in batches without that event). A board that
	 * joins mid-choice has no such event and shows no card.
	 */
	choiceSource = '';
	noteChoiceSource(choice: BoardChoice | null) {
		if (!choice) {
			this.choiceSource = '';
			return;
		}
		const you = this.props.snapshot.you;
		const events = this.props.events || [];
		for (let i = events.length - 1; i >= 0; i--) {
			const e = events[i];
			if (e.type === 'trainer' && e.seat === you && e.cardId) {
				this.choiceSource = String(e.cardId);
				return;
			}
		}
	}

	/** Where a Pokémon sits, from the chooser's side: "Your Bench 2", "Opponent's Active". */
	choiceSpot(seat: number, slot: TcgSlot): string {
		const side = seat === this.props.snapshot.you ? 'Your' : "Opponent's";
		return `${side} ${slot === 'active' ? 'Active' : `Bench ${Number(slot) + 1}`}`;
	}

	/** The choice bar and picker on the page, kept so a resize can place them again. */
	choiceBarEl: HTMLElement | null = null;
	choicePickerEl: HTMLElement | null = null;
	choicePickerIid = '';
	/** The picker just opened: focus its first card when it is placed. */
	choiceFocusPicker = false;
	onChoiceResize = () => {
		this.placeChoiceUi();
		this.placeFirstBanner();
	};
	firstBannerEl: HTMLElement | null = null;
	firstBannerRef = (el: unknown) => {
		this.firstBannerEl = el as HTMLElement | null;
		this.placeFirstBanner();
	};
	/**
	 * The setup "who goes first" banner: as close as it fits to the gap between the two Active Spots, on a spot that
	 * covers no card, pile, plate, or control. Short landscape and narrow boards leave little or no gap there, so it
	 * steps sideways, then up or down, until it is clear. Without both Active Spots it keeps its CSS place.
	 */
	placeFirstBanner() {
		const b = this.firstBannerEl;
		const table = this.tableEl;
		if (!b?.isConnected || !table) return;
		b.style.left = '';
		b.style.top = '';
		// The card in each spot (a face-down setup card, or the empty slot): the spot itself can be wider than the card.
		const foeSpot = table.querySelector('.tcg-active-spot.foe');
		const meSpot = table.querySelector('.tcg-active-spot.me');
		const foe = foeSpot?.querySelector('.tcg-mon') || foeSpot;
		const me = meSpot?.querySelector('.tcg-mon') || meSpot;
		if (!foe || !me) return;
		const t = table.getBoundingClientRect();
		const f = foe.getBoundingClientRect();
		const m = me.getBoundingClientRect();
		const upper = f.top < m.top ? f : m;
		const lower = upper === f ? m : f;
		const w = b.offsetWidth;
		const h = b.offsetHeight;
		const blocks = Array.from(table.querySelectorAll(FIRST_BANNER_BLOCKS))
			.map(el => el.getBoundingClientRect())
			.filter(r => r.width > 0 && r.height > 0);
		const clear = (cx: number, cy: number) => {
			const l = cx - w / 2, r = cx + w / 2, tp = cy - h / 2, bt = cy + h / 2;
			if (l < t.left + 4 || r > t.right - 4 || tp < t.top + 4 || bt > t.bottom - 4) return false;
			return blocks.every(q => Math.min(r, q.right) - Math.max(l, q.left) <= 1 ||
				Math.min(bt, q.bottom) - Math.max(tp, q.top) <= 1);
		};
		const cx0 = (upper.left + upper.right) / 2;
		const cy0 = (upper.bottom + lower.top) / 2;
		const step = 6;
		for (let dy = 0; dy <= 30; dy++) {
			const cy = cy0 + (dy % 2 ? 1 : -1) * Math.ceil(dy / 2) * step;
			for (let dx = 0; dx <= 2 * Math.ceil(t.width / step); dx++) {
				const cx = cx0 + (dx % 2 ? 1 : -1) * Math.ceil(dx / 2) * step;
				if (!clear(cx, cy)) continue;
				b.style.left = `${Math.round(cx - t.left)}px`;
				b.style.top = `${Math.round(cy - t.top)}px`;
				return;
			}
		}
		// Nowhere clear: the gap between the Active Spots.
		b.style.left = `${Math.round(cx0 - t.left)}px`;
		b.style.top = `${Math.round(cy0 - t.top)}px`;
	}
	choiceBarRef = (el: unknown) => {
		this.choiceBarEl = el as HTMLElement | null;
		this.placeChoiceUi();
	};

	/**
	 * Placement runs when the bar or picker renders, but the board can still be settling then (its Pokémon move a
	 * little after mount). The table, its halves, the opened Pokémon, the picker, and the bar are observed, and any
	 * size change places them again on the next frame.
	 */
	choiceObserver: ResizeObserver | null = null;
	choiceObserved: Element[] = [];
	choiceFrame = 0;
	scheduleChoicePlace = () => {
		if (this.choiceFrame || typeof requestAnimationFrame === 'undefined') return;
		this.choiceFrame = requestAnimationFrame(() => {
			this.choiceFrame = 0;
			this.placeChoiceUi();
		});
	};
	observeChoiceLayout(els: (Element | null | undefined)[]) {
		if (typeof ResizeObserver === 'undefined') return;
		const list = els.filter((e): e is Element => !!e);
		// Observing an element reports it once; observe again only when the set changes, or each report would loop.
		if (list.length === this.choiceObserved.length && list.every((e, i) => e === this.choiceObserved[i])) return;
		if (!this.choiceObserver) this.choiceObserver = new ResizeObserver(this.scheduleChoicePlace);
		this.choiceObserver.disconnect();
		for (const e of list) this.choiceObserver.observe(e);
		this.choiceObserved = list;
	}
	stopChoiceLayout() {
		this.choiceObserver?.disconnect();
		this.choiceObserved = [];
		if (this.choiceFrame && typeof cancelAnimationFrame !== 'undefined') cancelAnimationFrame(this.choiceFrame);
		this.choiceFrame = 0;
	}

	placeChoiceUi() {
		if (typeof window === 'undefined') return;
		const picker = this.choicePickerEl?.isConnected ? this.choicePickerEl : null;
		const bar = this.choiceBarEl?.isConnected ? this.choiceBarEl : null;
		if (!picker && !bar) {
			this.stopChoiceLayout();
			return;
		}
		if (picker) this.placeChoicePicker(picker, this.choicePickerIid, bar);
		if (bar) this.placeChoiceBar(bar, picker);
		const table = (picker || bar)!.closest('.tcg-table');
		this.observeChoiceLayout([
			// Array.from, not a spread: the build turns a NodeList spread into concat, which keeps the list itself.
			table, ...(table ? Array.from(table.querySelectorAll('.tcg-half')) : []),
			picker && table?.querySelector(`.tcg-mon[data-iid="${this.choicePickerIid}"]`), picker, bar,
		]);
		if (picker && this.choiceFocusPicker) {
			this.choiceFocusPicker = false;
			picker.querySelector<HTMLElement>('.tcg-choice-card')?.focus({ preventScroll: true });
		}
	}

	/** A box relative to the table. */
	choiceRel(r: DOMRect, t: DOMRect): ChoiceRect {
		return { left: r.left - t.left, top: r.top - t.top, right: r.right - t.left, bottom: r.bottom - t.top };
	}

	/**
	 * Board chrome the bar and picker keep in view when they can: the opponent's plate and the turn badge in the HUD,
	 * and the host's controls drawn over the table from outside it (the timer in the board shell, the phone's
	 * Board/Chat toggle). Those last two paint above anything inside the table, which is its own stacking context.
	 */
	choiceChrome(table: HTMLElement, t: DOMRect): ChoiceRect[] {
		const els: Element[] = [];
		table.querySelectorAll('.tcg-hud-top .tcg-player.foe, .tcg-hud-top .tcg-turn-badge').forEach(e => els.push(e));
		table.parentElement?.querySelectorAll(':scope > .tcg-chrome.live > *').forEach(e => els.push(e));
		table.closest('.tcg-mobile-board')?.parentElement?.querySelectorAll(':scope > .tcg-view-toggle')
			.forEach(e => els.push(e));
		const out: ChoiceRect[] = [];
		for (const e of els) {
			const r = e.getBoundingClientRect();
			if (r.width > 0 && r.height > 0) out.push(this.choiceRel(r, t));
		}
		return out;
	}

	/** The bar's full and compact sizes at its usual spot, where this leaves it. */
	measureChoiceBar(el: HTMLElement, table: HTMLElement, t: DOMRect): ChoiceBarSize {
		el.classList.remove('is-placed', 'is-compact');
		el.style.left = '';
		el.style.top = '';
		el.style.width = '';
		const home = el.getBoundingClientRect();
		el.classList.add('is-compact');
		const small = el.getBoundingClientRect();
		el.classList.remove('is-compact');
		const hud = table.querySelector('.tcg-hud-top')?.getBoundingClientRect();
		return {
			size: { w: home.width, h: home.height }, compact: { w: small.width, h: small.height },
			homeY: home.top - t.top, hudBottom: hud ? hud.bottom - t.top : null,
		};
	}

	/**
	 * The bar where it covers nothing on offer and not the open picker (planChoiceBar): its full and compact sizes
	 * are measured here, the spot is worked out there. Its measured width is kept, as moved off its centered spot it
	 * would otherwise wrap to the room left beside it and outgrow the spot.
	 */
	placeChoiceBar(el: HTMLElement, picker: HTMLElement | null) {
		const table = el.closest<HTMLElement>('.tcg-table');
		if (!table) return;
		const t = table.getBoundingClientRect();
		const measured = this.measureChoiceBar(el, table, t);
		const avoid: ChoiceRect[] = [];
		table.querySelectorAll<HTMLElement>('.tcg-mon.legal, .tcg-mon.selected, .tcg-mon.choice-from')
			.forEach(m => avoid.push(this.choiceRel(m.getBoundingClientRect(), t)));
		if (picker) avoid.push(this.choiceRel(picker.getBoundingClientRect(), t));
		const halves: ChoiceRect[] = [];
		table.querySelectorAll<HTMLElement>('.tcg-half')
			.forEach(h => halves.push(this.choiceRel(h.getBoundingClientRect(), t)));
		const plan = planChoiceBar({
			...measured, bounds: { left: 0, top: 0, right: t.width, bottom: t.height }, avoid, halves,
			chrome: this.choiceChrome(table, t),
		});
		if (plan.compact) el.classList.add('is-compact');
		el.style.left = `${plan.x}px`;
		el.style.top = `${plan.y}px`;
		el.style.width = `${(plan.compact ? measured.compact : measured.size).w}px`;
		el.classList.add('is-placed');
	}

	/**
	 * The picker next to its Pokémon, or a sheet (a strip across the top or bottom, a panel down one side) that keeps
	 * the Pokémon on offer in view and leaves the bar room (planChoicePicker). Its sizes in each form are measured
	 * here; next to its Pokémon it keeps its measured width, for the same reason as the bar.
	 */
	placeChoicePicker(el: HTMLElement | null, iid: string, bar: HTMLElement | null = null) {
		if (!el || typeof window === 'undefined' || !el.getBoundingClientRect) return;
		const table = el.closest<HTMLElement>('.tcg-table');
		const mon = table?.querySelector<HTMLElement>(`.tcg-mon[data-iid="${iid}"]`);
		if (!table || !mon) return;
		const t = table.getBoundingClientRect();
		const forms = ['is-anchored', 'is-sheet', 'is-strip', 'is-panel', 'is-top', 'is-bottom', 'is-left', 'is-right'];
		const reset = () => {
			el.classList.remove(...forms);
			el.style.left = '';
			el.style.top = '';
			el.style.width = '';
			el.style.height = '';
		};
		reset();
		const natural = el.getBoundingClientRect();
		el.classList.add('is-sheet', 'is-strip');
		el.style.width = `${t.width}px`;
		const stripH = el.getBoundingClientRect().height;
		el.classList.replace('is-strip', 'is-panel');
		el.style.width = '';
		el.style.height = `${t.height}px`;
		const panelW = el.getBoundingClientRect().width;
		reset();
		const others: ChoiceRect[] = [];
		table.querySelectorAll<HTMLElement>('.tcg-mon.legal, .tcg-mon.choice-from').forEach(x => {
			if (x !== mon) others.push(this.choiceRel(x.getBoundingClientRect(), t));
		});
		const halves: ChoiceRect[] = [];
		table.querySelectorAll<HTMLElement>('.tcg-half')
			.forEach(h => halves.push(this.choiceRel(h.getBoundingClientRect(), t)));
		const plan = planChoicePicker({
			bounds: { left: 0, top: 0, right: t.width, bottom: t.height },
			own: this.choiceRel(mon.getBoundingClientRect(), t), others, halves,
			anchored: { w: natural.width, h: natural.height }, stripH, panelW, narrow: window.innerWidth <= 640,
			chrome: this.choiceChrome(table, t), bar: bar ? this.measureChoiceBar(bar, table, t) : undefined,
		});
		el.style.left = `${plan.x}px`;
		el.style.top = `${plan.y}px`;
		if (plan.mode === 'anchored') {
			el.classList.add('is-anchored');
			el.style.width = `${natural.width}px`;
			return;
		}
		el.classList.add('is-sheet', plan.mode === 'strip' ? 'is-strip' : 'is-panel', `is-${plan.side}`);
		el.style.width = `${plan.w}px`;
		el.style.height = `${plan.h}px`;
	}

	renderBoardChoice(choice: BoardChoice) {
		const snap = this.props.snapshot;
		const sending = !!this.props.waiting || !!this.choiceSent(choice);
		const nameOfMon = (iid: string) => findChoiceMon(snap, iid)?.mon.name || '';
		const context = choiceFromText(choice.from, nameOfMon, cardLabel);
		const picked = choice.kind === 'attached' ? choice.picked : null;
		let sub = context;
		if (choice.kind === 'attached' && !picked) {
			const tools = choice.sources.every(s => s.cards.every(c => c.ref.zone === 'tool'));
			sub = `Choose a highlighted Pokémon, then its ${tools ? 'Tool' : 'Energy'}.`;
		}
		const src = this.choiceSource;
		return <div class={`tcg-choice${sending ? ' is-sending' : ''}`}>
			{choice.kind === 'attached' && picked && this.renderChoicePicker(choice, picked, sending)}
			<div class="tcg-choice-bar" ref={this.choiceBarRef}>
				{src && <img class="tcg-choice-source" src={cardArt(src)} alt="" title={cardLabel(src)} draggable={false} />}
				<div class="tcg-choice-text" role="status" aria-live="polite">
					{src && <em>{cardLabel(src)}</em>}
					<strong>{choice.ask}</strong>
					{sub && <span>{sub}</span>}
				</div>
				{choice.kind === 'attached' && choice.done &&
					<button type="button" class="tcg-btn quiet" disabled={sending} onClick={() => this.choose(choice.done!)}>Done</button>}
			</div>
		</div>;
	}

	renderChoicePicker(choice: BoardChoice & { kind: 'attached' }, src: BoardChoiceSource, sending: boolean) {
		const back = this.choiceBackable();
		return <div
			class="tcg-choice-picker" role="group" aria-label={`Cards on ${src.mon.name}`}
			ref={el => {
				this.choicePickerEl = el as HTMLElement | null;
				this.choicePickerIid = src.iid;
				this.placeChoiceUi();
			}}
		>
			<div class="tcg-choice-picker-head">
				<strong>{src.mon.name}</strong>
				<span>{this.choiceSpot(src.seat, src.slot)}</span>
				{back && <button type="button" class="tcg-btn quiet" onClick={this.choiceBack}>Back</button>}
			</div>
			<div class="tcg-choice-cards">
				{src.cards.map(card => {
					const name = cardLabel(card.ref.cardId);
					const copies = card.ref.at.length;
					const sent = this.choiceSent(choice) === String(card.row.pick);
					return <button
						type="button" key={String(card.row.pick)}
						class={`tcg-choice-card${sent ? ' is-sent' : ''}`}
						disabled={sending}
						aria-label={copies > 1 ? `${name}, ${copies} copies` : name}
						onClick={() => this.pickChoiceCard(card)}
					>
						<img
							class="tcg-choice-card-art" src={cardArt(card.ref.cardId) || cardBackArt()} alt=""
							draggable={false} data-card={card.ref.cardId}
							ref={el => {
								const img = el as HTMLImageElement | null;
								if (img) img.onerror = () => choiceArtFallback(img);
							}}
						/>
						<span class="tcg-choice-card-name">{name}</span>
						{copies > 1 && <span class="tcg-choice-card-count">×{copies}</span>}
					</button>;
				})}
			</div>
		</div>;
	}

	openAttachBrowse = (cards: AttachedCard[], mon: TcgPokemonView) => {
		if (this.state.drag?.active) return;
		if (!cards.length) return;
		const name = mon.name || cardLabel(mon.cardId);
		this.setState({
			attachBrowse: { title: `${name} — attached`, cards },
			discardBrowse: null, menuSlot: null, inspect: null,
		});
	};

	override render() {
		const snap = this.props.snapshot;
		// The painted snapshot already includes this beat. An older folded board would
		// hide the cards the player is allowed to click while the animation is still up.
		if (this.boardSnap && snap === this.props.fxSnapshot) {
			this.boardSnap = null;
			this.foldedThrough = -1;
			this.foldedRequest = false;
		}
		this.noteRemovals(snap);
		// Already shown face up on an earlier beat of this batch; don't flip them again at commit.
		if (this.boardSnap) {
			this.boardSnap.players.forEach(p => {
				const mons = [p?.active, ...(p?.bench || [])];
				mons.forEach(m => { if (m && !m.faceDown) delete this.faceDownSeen[m.iid]; });
			});
		}
		const meIndex: 0 | 1 = (this.props.viewpoint === 0 || this.props.viewpoint === 1) ?
			this.props.viewpoint : (snap.you === 1 ? 1 : 0);
		const foeIndex: 0 | 1 = meIndex === 0 ? 1 : 0;
		// Events already played this batch. Choices still come from the committed snapshot until the request.
		const view = this.boardSnap || snap;
		// Capture pre-hit HP before rememberMons overwrites it with this batch's result.
		this.syncHpHold();
		this.rememberMons(view);
		noteTcgCardNames(snap);
		if (view !== snap) noteTcgCardNames(view);
		// Piles leave out the cards still flying to them; they count when they land.
		const me = this.withoutInbound(view.players[meIndex], meIndex);
		const foe = this.withoutInbound(view.players[foeIndex], foeIndex);
		if (!me || !foe) {
			return <div class="tcg-table"><div class="tcg-felt"></div><p class="tcg-waiting">Waiting for TCG snapshot…</p></div>;
		}
		const benchSize = snap.format?.benchSize || Math.max(me.bench.length, foe.bench.length, 3);
		// Fan keeps pre-batch indexes so a click still matches the server hand.
		// Played cards leave on their beat; drawn and prize cards join on theirs.
		// Once the request beat has folded in, the actions on screen were built for the board as it is now,
		// so its hand indexes are the ones to click.
		const handBase = this.foldedRequest && this.boardSnap ? this.boardSnap : snap;
		const fanned = snap.you != null ?
			handFan(handIds(handBase.players[meIndex]?.hand), me.hand) : null;
		const viewIds = snap.you != null && meIndex === snap.you ? handAsIds(me.hand) : null;
		const myHand = fanned && viewIds ? orderHandFan(fanned, this.syncHandOrder(viewIds, snap)) : fanned;
		this.handShown = myHand ? myHand.map(({ id, i }) => ({ id, i })) : [];
		// Cards still flying to a hand keep their place there, out of sight until they land: the newest arrivals of
		// those ids in my hand, the last backs in a hand shown as a count.
		const meComing = this.handInbound(meIndex);
		const meHandComingN = Object.values(meComing).reduce((a, b) => a + b, 0);
		const foeHandComing = Object.values(this.handInbound(foeIndex)).reduce((a, b) => a + b, 0);
		const handHidden: { [fan: number]: true } = {};
		if (myHand && meHandComingN) {
			const left = { ...meComing };
			for (let k = myHand.length - 1; k >= 0; k--) {
				if (!myHand[k].arrive) continue;
				const id = left[myHand[k].id] ? myHand[k].id : left[''] ? '' : null;
				if (id == null) continue;
				left[id]--;
				handHidden[k] = true;
			}
		}
		const shuffleFx = this.state.fx?.kind === 'shuffleHand' ? this.state.fx : null;
		const shufflingMine = !!(shuffleFx && shuffleFx.seat === meIndex);
		const shufflingFoe = !!(shuffleFx && shuffleFx.seat !== meIndex);
		const shuffleHandIds = shufflingMine ? (shuffleFx?.ids || null) : null;
		const shuffleHandCount = shufflingMine ?
			Math.max(shuffleHandIds?.length || 0, shuffleFx?.n || 0, 3) :
			0;
		const allActs = this.acts();
		const firstPending = this.state.firstSent ? [] : allActs.filter(a => a.type === 'chooseFirst');
		const fxNow = this.state.fx;
		// Hide Go First for the whole opening batch (deal/shuffle/...) until the coin overlay is gone.
		// Do not key this off `waiting` — a |request| can flip that false while earlier beats play.
		const holdFirstPrompt = fxNow?.kind === 'coin' || fxNow?.kind === 'first' ||
			(hasOpeningFlipEvents(this.props.events) && this.playedFlipKey !== this.props.fxKey);
		const choosingFirst = firstPending.length > 0 || holdFirstPrompt;
		// Who goes first, in the middle of the board during setup; not before the coin overlay has shown the result.
		const firstBanner = firstPlayerBanner(view, snap.you, holdFirstPrompt);
		const ahead = view !== snap;
		const turnSeat = typeof view.turn === 'number' ? view.turn : null;
		const turnOwner = turnSeat != null ? view.players[turnSeat] : null;
		const yourTurn = snap.you != null && !this.props.ended && !this.props.waiting && allActs.length > 0;
		// While beats are playing, the badge follows the turn event instead of leftover actions.
		const isMyTurnSeat = snap.you != null && snap.you === meIndex && (
			ahead ? (view.status === 'playing' && turnSeat === snap.you) :
			(allActs.length > 0 ||
				(view.status === 'playing' && turnSeat === snap.you && !this.props.waiting))
		);
		// After the game, a replay re-shows earlier snapshots: label those by turn, not "End".
		const replaying = !!this.props.ended && view.status !== 'over';
		const displayTurn = view.turnNumber || 1;
		const turnWho = this.props.ended && !replaying ? '' :
			choosingFirst && view.status === 'setup' ? 'Coin flip' :
			view.status === 'setup' ? 'Setup phase' :
			isMyTurnSeat ? 'Your turn' :
			turnOwner ? `${turnOwner.name}'s turn` : '';
		const pocket = !!(me.energyZone || snap.format?.energyZone);
		const need = snap.format?.prizes || (pocket ? 3 : 6);
		const inspect = this.state.inspect;
		const inspectSrc = inspect ?
			(inspect.image ? inspect.image.replace('_SM.webp', '.webp') : cardArt(inspect.cardId, { large: true })) : '';

		const padBench = (list: TcgPokemonView[]) => {
			const out: (TcgPokemonView | null)[] = list.slice();
			while (out.length < benchSize) out.push(null);
			return out;
		};

		const endTurn = allActs.find(a => a.type === 'endTurn');
		const startBattle = allActs.find(a => a.type === 'setupDone');
		const confirms = allActs.filter(a => a.type === 'confirmYes' || a.type === 'confirmNo');
		const firsts = holdFirstPrompt ? [] : firstPending;
		const searches = allActs.filter(a => a.type === 'searchPick' || a.type === 'searchDone');
		const discards = allActs.filter(a => a.type === 'discardPick');
		const pays = allActs.filter(a => a.type === 'payEnergy');
		const energyDiscards = allActs.filter(a => a.type === 'discardEnergyPick');
		if (!pays.length && (this.state.payPicked.length || this.payQueue.length || this.paySending)) {
			// Prompt closed; drop staged picks without fighting the current render.
			queueMicrotask(() => {
				this.payQueue = [];
				this.paySending = false;
				if (this.state.payPicked.length) this.setState({ payPicked: [] });
			});
		}
		const prizeActs = allActs.filter(a => a.type === 'takePrize');
		const picked = this.state.prizePicked;
		const myPrizesLeft = prizesLeft(me, need, view.status);
		const pendingPicks = pendingPrizePicks(this.state.prizePickBase, myPrizesLeft, picked);
		const prizeGoal = this.state.prizeGoal || view.pendingPrize?.n || 0;
		// Close the whole prompt on the click that finishes the set, not when the server catches up.
		const prizeDone = prizeGoal > 0 && picked.length >= prizeGoal;
		const prizes = prizeDone ? [] : prizeActs.filter(a => !pendingPicks.includes(Number(a.index)));
		// The server lists Take Prize on the attack payload. Leave the cards and
		// the prompt alone until the attack and Knock Out clips have finished.
		const prizeHeld = prizePromptWaits(!!this.props.playing, this.props.events, this.state.fx?.kind);
		const prizesOffered = prizeHeld ? [] : prizes;
		const mulligans = allActs.filter(a => a.type === 'mulliganBench' || a.type === 'mulliganDone');
		const useStadium = allActs.find(a => a.type === 'useStadium');
		const selectedHand = this.state.selectedHand;
		const selectedHandActs = selectedHand != null ?
			allActs.filter(a => actionTouchesHand(a, selectedHand)) : [];
		const selectedHandNeedTarget = selectedHandActs.filter(a => hasBoardTarget(a));
		const orphanActs = allActs.filter(a => {
			if (['attack', 'ability', 'endTurn', 'setupDone', 'setupReturn', 'attachZone', 'retreat',
				'confirmYes', 'confirmNo', 'chooseFirst', 'searchPick', 'searchDone',
				'discardPick', 'payEnergy', 'discardEnergyPick', 'takePrize', 'mulliganBench', 'mulliganDone',
				'useStadium', 'playBasic', 'setActive', 'setBench', 'evolve', 'attachEnergy',
				'attachTool', 'playTrainer', 'playStadium', 'promote', 'pickSlot'].includes(a.type)) return false;
			return true;
		});

		const drag = this.state.drag;
		const dragging = !!(drag && drag.active);
		const playDropHits = drag?.active ?
			actionsForDrop(allActs, drag, { kind: 'play' }) :
			(selectedHand != null ? actionsForDrop(allActs, { source: 'hand', hand: selectedHand }, { kind: 'play' }) : []);
		const selectedCard = selectedHand != null ? handCardId(snap, selectedHand) : '';
		const stadiumDropHits = drag?.active ?
			actionsForDrop(allActs, drag, { kind: 'stadium' }) :
			(selectedHand != null ? actionsForDrop(allActs, { source: 'hand', hand: selectedHand, cardId: selectedCard }, { kind: 'stadium' }) : []);
		const discardDropHits = drag?.active ?
			actionsForDrop(allActs, drag, { kind: 'discard' }) :
			(selectedHand != null ? actionsForDrop(allActs, { source: 'hand', hand: selectedHand }, { kind: 'discard' }) : []);
		const playHot = dragging && drag.over?.kind === 'play' && playDropHits.length > 0;
		const stadiumHot = dragging && drag.over?.kind === 'stadium' && stadiumDropHits.length > 0;
		const discardHot = dragging && drag.over?.kind === 'discard' && discardDropHits.length > 0;

		const aborted = !!this.props.aborted || snap.status === 'aborted';
		const seated = snap.you != null && !this.props.ended && !aborted;
		const boardChoice = seated ? this.boardChoice() : null;
		// A Pokémon opened under another prompt (answered, changed, resynced) is not carried over.
		const openPick = this.state.boardPick;
		if (openPick && (boardChoice?.kind !== 'attached' || boardChoice.key !== openPick.key)) {
			queueMicrotask(() => {
				const now = this.state.boardPick;
				const current = this.boardChoice();
				if (now && (current?.kind !== 'attached' || current.key !== now.key)) this.setState({ boardPick: null });
			});
		}
		this.noteChoiceSource(boardChoice);
		let hint = (dragging && drag.hint) || '';
		if (!this.props.ended && !aborted && seated && !hint) {
			const prizeLeft = prizeDone ? 0 : (view.pendingPrize ? view.pendingPrize.n - picked.length : 0);
			const lines = [
				view.pendingConfirm?.text || '',
				this.state.energyPick ? 'Choose a Pokémon for Energy' : '',
				this.state.retreatPick ? 'Choose a Benched Pokémon to switch in' : '',
				this.state.menuSlot != null ? 'Choose an action' : '',
				selectedHand != null && selectedHandNeedTarget.length ?
					`Drag or tap a Pokémon for ${describeAction(selectedHandNeedTarget[0], snap)}` : '',
				selectedHand != null ? 'Drag onto a Pokémon, or the Active field for a Trainer' : '',
				view.pendingSearch ? 'Choose a card from the search' : '',
				view.pendingDiscard ?
					`Drag ${view.pendingDiscard.need} card${view.pendingDiscard.need === 1 ? '' : 's'} to Discard` : '',
				view.pendingRetreatPay ?
					`Select ${view.pendingRetreatPay.need} Energy to discard for retreat` : '',
				this.state.slotHint || '',
				view.pendingPromote != null && view.pendingPromote === snap.you ?
					'Choose a Benched Pokémon to promote' : '',
				prizesOffered.length && view.pendingPrize ?
					`Take ${Math.max(1, prizeLeft)} Prize card${prizeLeft === 1 ? '' : 's'}` : '',
				prizesOffered.length ? 'Take a Prize card' : '',
				discards.length ? 'Drag a card to Discard' : '',
				firstPending.length && !holdFirstPrompt ? 'Choose who goes first' : '',
				snap.status === 'setup' && !(firstPending.length && !holdFirstPrompt) ?
					(allActs.some(a => a.type === 'setupReturn') ?
						'Drag Basics to Active / Bench · Tap one to take it back · Ready' :
						'Drag Basics to Active / Bench, then Ready') : '',
				yourTurn ? 'Drag cards to play · Tap your Pokémon to attack' : '',
			];
			hint = lines.find(line => line) || '';
		}

		const foeHoldsTurn = view.status === 'setup' ?
			me.setup === 'done' && foe.setup !== 'done' :
			turnSeat != null && !isMyTurnSeat;
		const waitingOpp = !this.props.ended && !aborted && snap.you != null && !allActs.length && foeHoldsTurn &&
			(this.props.waiting || !yourTurn);
		const st = this.state;
		let winName = (this.props.winnerName != null && this.props.winnerName !== '') ?
			this.props.winnerName :
			(snap.winner != null ? snap.players[snap.winner]?.name : null);
		if (st.fx?.kind === 'over') {
			if (st.fx.tie) winName = null;
			else {
				const fromFx = (/^(.+?)\s+wins?\.?$/i.exec(String(st.fx.extra || '')))?.[1];
				if (fromFx && !/^draw$/i.test(fromFx)) winName = fromFx;
			}
		}
		// Same node from the ending beat through the result, so the fade-in does not replay.
		// Hidden while scrubbing/replaying an earlier turn so the board at that turn is readable.
		const showWinBanner = !aborted && (st.fx?.kind === 'over' || (!!this.props.ended && !st.fx && !replaying));
		const stopText = this.props.stopReason || snap.winReason || 'The game stopped because a card effect failed.';

		const fxSpeed = PS.prefs.tcgfxspeed || 'normal';
		const tableClass = [
			'tcg-table', pocket ? 'pocket' : 'live',
			yourTurn || isMyTurnSeat ? 'tcg-your-turn' : '',
			dragging ? 'is-dragging' : '',
			boardChoice && !st.fx ? 'is-choosing' : '',
			st.fx ? `kind-${st.fx.kind}` : '',
			`fx-${fxSpeed}`,
		].filter(Boolean).join(' ');
		const monFx = (mon: TcgPokemonView | null) => mon ? ({
			pkFx: st.pkFx[mon.iid] || null,
			// Its own card, or cards attached to it, still in the air: they show when they land.
			incoming: this.inbound.some(x => x.zone === 'mon' && !x.part && x.iid === mon.iid),
			landing: this.inbound.filter(x => x.zone === 'mon' && x.part === 'attach' && x.iid === mon.iid).length,
		}) : {};
		const foeActive = this.displayMon(foeIndex, 'active', foe.active);
		const meActive = this.displayMon(meIndex, 'active', me.active);

		const stadiumId = foe.stadium || me.stadium || '';
		// A two-card Legendary Stadium shows both halves, side by side, in the one Stadium spot.
		const stadiumPair = foe.stadiumIds || me.stadiumIds;
		const stadiumCards = stadiumId && stadiumPair && stadiumPair.length > 1 ? stadiumPair : stadiumId ? [stadiumId] : [];
		const canAttach = allActs.some(a => a.type === 'attachZone');
		const zone = me.energyZone;

		return <div
			class={tableClass}
			style={{ '--fx-t': String(tcgFxScale() || 1) } as any}
			ref={el => { this.tableEl = el as HTMLElement | null; }}
			onContextMenu={ev => ev.preventDefault()}
		>
			<div class="tcg-felt"></div>
			{firstBanner && <div class="tcg-first-banner" role="status" aria-live="polite" ref={this.firstBannerRef}>
				{firstBanner}
			</div>}
			{/* No full-board veil flash on attack / damage / KO — FX stay on the cards. */}
			{waitingOpp && !(st.fx && (st.fx.kind === 'turn' || st.fx.kind === 'search' || st.fx.kind === 'mulligan')) &&
				<div class="tcg-wait-banner" aria-live="polite">
					<em>Waiting</em>
					<strong>for opponent</strong>
				</div>}
			{aborted && !replaying &&
				<div class="tcg-win-banner stopped" aria-live="polite">
					<em>Match stopped</em>
					<strong>{stopText}</strong>
				</div>}
			{showWinBanner &&
				<div class={`tcg-win-banner${winName ? '' : ' tie'}`} aria-live="polite">
					{winName ?
						<><em>{winName}</em><strong>won</strong></> :
						<em>Draw</em>}
				</div>}
			{inspect && <div class="tcg-inspect" onClick={this.onInspectBackdrop} role="dialog" aria-label="Card inspection">
				<div class="tcg-inspect-card" onClick={ev => ev.stopPropagation()}>
					{inspect.cardId && !inspect.image && noArtCards[inspect.cardId] ?
						<span class="tcg-inspect-noart">
							<NoArtFace cardId={inspect.cardId} label={inspect.name || cardLabel(inspect.cardId)} />
						</span> :
						inspectSrc ? <img src={inspectSrc} alt={inspect.name || inspect.cardId} /> : null}
					{inspect.handAt != null && myHand && myHand.length > 1 && <div class="tcg-inspect-move">
						<button
							type="button" class="button" disabled={inspect.handAt <= 0}
							onClick={() => this.moveInspected(-1)}
						><i class="fa fa-arrow-left" aria-hidden></i> Move left</button>
						<button
							type="button" class="button" disabled={inspect.handAt >= myHand.length - 1}
							onClick={() => this.moveInspected(1)}
						>Move right <i class="fa fa-arrow-right" aria-hidden></i></button>
					</div>}
					<button type="button" class="tcg-inspect-close" onClick={this.closeInspect}>Close</button>
					<p class="tcg-inspect-hint">Hold a card to preview · tap outside to close</p>
				</div>
			</div>}

			<header class="tcg-hud-top">
				<div class="tcg-player foe">
					<span class="tcg-avatar foe" aria-hidden="true">{(foe.name || '?').slice(0, 1).toUpperCase()}</span>
					<div class="tcg-player-meta">
						<span class="tcg-name">{foe.name}</span>
						<span class="tcg-player-tags">
							<span
								class="tcg-hand-count" title={`${pileCount(foe.hand)} in hand`}
								aria-label={`${pileCount(foe.hand)} cards in hand`}
							>{pileCount(foe.hand)}</span>
						</span>
					</div>
				</div>
				<div class={`tcg-turn-badge${yourTurn || isMyTurnSeat ? ' yours' : ''}${this.props.ended && !replaying ? ' over' : ''}${!isMyTurnSeat && turnSeat != null ? ' foe' : ''}`}>
					<span class="tcg-turn-num">
						{aborted && !replaying ? 'Stopped' :
							this.props.ended && !replaying ? 'End' :
							view.status === 'setup' ? (choosingFirst ? 'Coin' : 'Setup') : `Turn ${displayTurn}`}
					</span>
					{turnWho && <span class="tcg-turn-who">{turnWho}</span>}
				</div>
				{/* Balances the grid so the turn badge stays centered; chrome (Timer) docks here. */}
				<div class="tcg-hud-tools">
					{(view.effectsActive?.length) ? <button
						type="button"
						class={`tcg-effects-btn${st.effectsOpen ? ' is-open' : ''}`}
						aria-expanded={st.effectsOpen}
						aria-label="Active effects"
						title="Active effects"
						onClick={() => this.setState({ effectsOpen: !st.effectsOpen })}
					>
						<svg class="tcg-effects-icon" viewBox="0 0 24 24" aria-hidden="true">
							<path d="M12 2.2l1.7 6.1L20 10l-6.3 1.7L12 17.8l-1.7-6.1L4 10l6.3-1.7L12 2.2z" />
						</svg>
						{view.effectsActive.length > 1 && <span class="tcg-effects-count">{view.effectsActive.length}</span>}
					</button> : null}
					{st.effectsOpen && !!view.effectsActive?.length && <>
						<button
							type="button" class="tcg-effects-backdrop" aria-label="Close active effects"
							onClick={() => this.setState({ effectsOpen: false })}
						/>
						<div class="tcg-effects-pop" role="dialog" aria-label="Active effects">
							{view.effectsActive.map((row, i) =>
								<p key={row.id || i}>{row.summary}</p>)}
						</div>
					</>}
				</div>
			</header>

			<div class="tcg-dock-foe" title={`${pileCount(foe.hand)} in hand`}>
				<div class={`tcg-hand tcg-hand-foe${shufflingFoe ? ' is-shuffling' : ''}`}>
					{(shufflingFoe ?
						Array.from({ length: Math.max(shuffleFx?.n || pileCount(foe.hand), 3) }, (_, i) => i) :
						Array.from({ length: pileCount(foe.hand) }, (_, i) => i)
					).map(i => {
						const count = shufflingFoe ?
							Math.max(shuffleFx?.n || pileCount(foe.hand), 3) :
							Math.max(1, pileCount(foe.hand));
						return <TcgCardFace
							key={`fh${i}`} back size="sm"
							fanIndex={i} fanCount={count}
							incoming={!shufflingFoe && i >= pileCount(foe.hand) - foeHandComing}
						/>;
					})}
				</div>
			</div>

			<div class={`tcg-arena${pocket ? ' is-pocket' : ' is-live'}`}>
				{/* Opponent half: bench (top) → Active (toward center); prizes left, deck right */}
				<section class="tcg-half foe">
					<div class="tcg-side left">
						{(pileCount(foe.lostZone) > 0 || !pocket) &&
							<div
								class={`tcg-lost-zone foe${pileCount(foe.lostZone) > 0 ? ' has-cards' : ''}`}
								title={`Opponent Lost Zone: ${pileCount(foe.lostZone)}`}
							>
								<div class="tcg-lost-zone-inner">
									<strong class="tcg-lost-zone-count">{pileCount(foe.lostZone)}</strong>
									<em class="tcg-lost-zone-label">Lost Zone</em>
								</div>
							</div>}
						{!pocket ?
							<TcgPrizeRail
								remaining={prizesLeft(foe, need, view.status)} max={need} foe
								takes={(this.props.prizeTakes && this.props.prizeTakes[foeIndex]) || []}
							/> :
							<TcgPoints scored={scoredPoints(foe, need, view.status)} need={need} pocket />}
					</div>
					<div class="tcg-field">
						<div class="tcg-zone tcg-bench foe">
							{padBench(foe.bench).map((mon, i) => {
								const shown = this.displayMon(foeIndex, i, mon);
								const ghost = !mon && !!shown;
								return <TcgMon
									key={`fb${i}`} mon={shown} slot={i} foe size="sm"
									reveal={!!shown && !!this.revealing[shown.iid]}
									legal={!ghost && this.slotActionable(i, true)}
									{...(ghost ? {} : this.choiceMonProps(shown, i, true))}
									{...this.slotDropMeta(i, true, !mon)}
									{...monFx(shown)}
									onClick={ghost ? undefined : (ev: MouseEvent) => this.clickSlot(i, true, ev)}
									onInspect={this.openInspect}
									onAttachBrowse={this.openAttachBrowse}
								/>;
							})}
						</div>
						<div class="tcg-zone tcg-active-spot foe">
							<TcgMon
								mon={foeActive} slot="active" activeSpot foe size="lg"
								reveal={!!foeActive && !!this.revealing[foeActive.iid]}
								legal={!foe.active && foeActive ? false : this.slotActionable('active', true)}
								{...(!foe.active && foeActive ? {} : this.choiceMonProps(foeActive, 'active', true))}
								{...this.slotDropMeta('active', true, !foe.active)}
								{...monFx(foeActive)}
								onClick={!foe.active && foeActive ? undefined : (ev: MouseEvent) => this.clickSlot('active', true, ev)}
								onInspect={this.openInspect}
								onAttachBrowse={this.openAttachBrowse}
							/>
						</div>
					</div>
					<div class="tcg-side right">
						<div class="tcg-pile deck foe" title={`Opponent deck: ${pileCount(foe.deck)}`}>
							<div class="tcg-pile-stack">
								<span></span><span></span>
								<div class="tcg-card tcg-card-sm tcg-card-back">
									<span class="tcg-card-inner"><CardBackFace /></span>
								</div>
							</div>
							<strong class="tcg-pile-count">{pileCount(foe.deck)}</strong>
							<em class="tcg-pile-label">Deck</em>
						</div>
						<div
							class="tcg-pile discard foe"
							title="Opponent discard — click to view"
							role="button"
							onClick={(ev: MouseEvent) => this.openDiscardBrowse('foe', ev)}
						>
							<div class="tcg-pile-stack">
								<span></span><span></span>
								{foe.discard?.length ?
									<TcgCardFace
										cardId={foe.discard[foe.discard.length - 1]} size="sm" onInspect={this.openInspect}
									/> :
									<div class="tcg-card tcg-card-sm tcg-card-back">
										<span class="tcg-card-inner"><CardBackFace /></span>
									</div>}
							</div>
							<strong class="tcg-pile-count">{foe.discard?.length || 0}</strong>
							<em class="tcg-pile-label">Discard</em>
						</div>
					</div>
				</section>

				{/* Stadium — floats mid-board on the left. Stadium cards only; Trainers use the Active field. */}
				<div
					class={`tcg-stadium-row side-left${stadiumDropHits.length ? ' drop-ok' : ''}${stadiumHot ? ' drop-hot' : ''}`}
				>
					<div
						class={`tcg-stadium${stadiumCards.length > 1 ? ' paired' : ''}${useStadium ? ' usable' : ''}` +
							`${stadiumDropHits.length ? ' drop-ok' : ''}${stadiumHot ? ' drop-hot' : ''}`}
						data-drop="stadium"
					>
						{stadiumCards.length ?
							stadiumCards.map(id => <TcgCardFace
								key={id}
								cardId={id} name={cardLabel(id)} size="sm"
								onClick={useStadium ? (ev: { clientX: number, clientY: number }) => this.clickStadium(ev) : undefined}
								onInspect={this.openInspect}
							/>) :
							<div class="tcg-stadium-empty"><span>Stadium</span></div>}
					</div>
					{stadiumHot &&
						<em class="tcg-drop-tag">{dropVerb(stadiumDropHits[0])}</em>}
				</div>
				{dragging && playDropHits.length > 0 && this.state.playField &&
					<div
						class={`tcg-play-field drop-ok${playHot ? ' drop-hot' : ''}`}
						style={{
							top: `${this.state.playField.top}px`,
							left: `${this.state.playField.left}px`,
							width: `${this.state.playField.width}px`,
							height: `${this.state.playField.height}px`,
						}}
					>
						<em class="tcg-play-label">Play</em>
					</div>}

				{/* Your half: Active → bench; prizes/energy left, deck+discard right */}
				<section class="tcg-half me">
					<div class="tcg-side left">
						{(pileCount(me.lostZone) > 0 || !pocket) &&
							<div
								class={`tcg-lost-zone me${pileCount(me.lostZone) > 0 ? ' has-cards' : ''}`}
								title={`Lost Zone: ${pileCount(me.lostZone)}`}
							>
								<div class="tcg-lost-zone-inner">
									<strong class="tcg-lost-zone-count">{pileCount(me.lostZone)}</strong>
									<em class="tcg-lost-zone-label">Lost Zone</em>
								</div>
							</div>}
						{!pocket ?
							<TcgPrizeRail
								remaining={myPrizesLeft} max={need}
								takes={(this.props.prizeTakes && this.props.prizeTakes[meIndex]) || []}
								picked={pendingPicks}
								takeActs={prizesOffered} onTake={a => this.choose(a)}
							/> :
							<>
								<TcgPoints scored={scoredPoints(me, need, view.status)} need={need} pocket />
								{(zone || pocket) && <button
									type="button"
									class={`tcg-ezone ${canAttach ? 'ready' : 'off'} ${this.state.energyPick ? 'pick' : ''} ${drag?.source === 'zone' && dragging ? 'dragging' : ''}`}
									onPointerDown={(ev: any) => this.startZoneDrag(ev)}
									title={canAttach ? 'Drag onto a Pokémon to attach' : ''}
								>
									<span
										class="tcg-ezone-now"
										style={{ background: TYPE_COLOR[zone?.type || 'colorless'] || '#888' }}
									>
										<img src={energySymbolUrl(zone?.type)} alt="" draggable={false} />
									</span>
									{zone?.next && <span
										class="tcg-ezone-next"
										style={{ background: TYPE_COLOR[zone.next] || '#888' }}
									>
										<img src={energySymbolUrl(zone.next)} alt="" draggable={false} />
									</span>}
								</button>}
							</>}
					</div>
					<div class="tcg-field">
						<div class="tcg-zone tcg-active-spot me">
							<TcgMon
								mon={meActive} slot="active" activeSpot size="lg"
								reveal={!!meActive && !!this.revealing[meActive.iid]}
								legal={!me.active && meActive ? false : this.slotActionable('active')}
								{...this.slotDropMeta('active', false, !me.active)}
								{...monFx(meActive)}
								selected={this.state.menuSlot === 'active'}
								{...(!me.active && meActive ? {} : this.choiceMonProps(meActive, 'active', false))}
								onClick={!me.active && meActive ? undefined : (ev: MouseEvent) => this.clickSlot('active', false, ev)}
								onInspect={this.openInspect}
								onAttachBrowse={this.openAttachBrowse}
							/>
						</div>
						<div class="tcg-zone tcg-bench me">
							{padBench(me.bench).map((mon, i) => {
								const shown = this.displayMon(meIndex, i, mon);
								const ghost = !mon && !!shown;
								return <TcgMon
									key={`mb${i}`} mon={shown} slot={i} size="sm"
									reveal={!!shown && !!this.revealing[shown.iid]}
									legal={!ghost && this.slotActionable(i)}
									{...this.slotDropMeta(i, false, !mon)}
									{...monFx(shown)}
									selected={!ghost && this.state.menuSlot === i}
									{...(ghost ? {} : this.choiceMonProps(shown, i, false))}
									onClick={ghost ? undefined : (ev: MouseEvent) => this.clickSlot(i, false, ev)}
									onInspect={this.openInspect}
									onAttachBrowse={this.openAttachBrowse}
								/>;
							})}
						</div>
					</div>
					<div class="tcg-side right">
						<div class="tcg-pile deck me" title={`Deck: ${pileCount(me.deck)}`}>
							<div class="tcg-pile-stack">
								<span></span><span></span>
								<div class="tcg-card tcg-card-sm tcg-card-back">
									<span class="tcg-card-inner"><CardBackFace /></span>
								</div>
							</div>
							<strong class="tcg-pile-count">{pileCount(me.deck)}</strong>
							<em class="tcg-pile-label">Deck</em>
						</div>
						<div
							class={`tcg-pile discard me${discardDropHits.length ? ' drop-ok' : ''}${discardHot ? ' drop-hot' : ''}`}
							title="Discard — click to view"
							data-drop="discard"
							role="button"
							onClick={(ev: MouseEvent) => {
								if (discardDropHits.length && this.state.selectedHand != null) return;
								this.openDiscardBrowse('me', ev);
							}}
						>
							<div class="tcg-pile-stack">
								<span></span><span></span>
								{me.discard?.length ?
									<TcgCardFace
										cardId={me.discard[me.discard.length - 1]} size="sm" onInspect={this.openInspect}
									/> :
									<div class="tcg-card tcg-card-sm tcg-card-back">
										<span class="tcg-card-inner"><CardBackFace /></span>
									</div>}
							</div>
							<strong class="tcg-pile-count">{me.discard?.length || 0}</strong>
							<em class="tcg-pile-label">Discard</em>
							{discardHot && <em class="tcg-drop-tag">Discard</em>}
						</div>
					</div>
				</section>
			</div>

			<div class="tcg-dock">
				<div
					class={`tcg-hand tcg-hand-mine${shufflingMine ? ' is-shuffling' : ''}`}
					onWheel={this.onHandWheel}
					onKeyDown={this.onHandKey}
					style={{ '--card-count': String(myHand ? myHand.length : (shufflingMine ? shuffleHandCount : pileCount(me.hand))) } as any}
				>
					{shufflingMine ? (
						shuffleHandIds?.length ?
							shuffleHandIds.map((id, i) =>
								<TcgCardFace
									key={`sh-${id}-${i}`} cardId={id} size="md"
									pocket={pocket}
									fanIndex={i} fanCount={shuffleHandIds.length}
								/>
							) :
							Array.from({ length: shuffleHandCount }, (_, i) =>
								<TcgCardFace
									key={`shb${i}`} back size="md"
									fanIndex={i} fanCount={shuffleHandCount}
								/>
							)
					) : myHand ? myHand.map(({ id, i, arrive }, fan) => {
						const playable = i >= 0 && allActs.some(a => actionTouchesHand(a, i));
						// Key by "nth copy of this card", not by index, so playing one card lets the
						// rest glide over instead of remounting every card to its right.
						let nth = 0;
						for (let j = 0; j < fan; j++) if (myHand[j].id === id) nth++;
						return <TcgCardFace
							key={`${id}#${nth}`} cardId={id} size="md"
							pocket={pocket}
							arrive={arrive}
							incoming={!!handHidden[fan]}
							selected={i >= 0 && this.state.selectedHand === i}
							playable={playable}
							dragging={i >= 0 && drag?.source === 'hand' && drag.hand === i && dragging}
							fanIndex={fan} fanCount={myHand.length}
							onPointerDown={i >= 0 ? (ev: any) => this.startHandDrag(i, id, ev) : undefined}
							onHold={this.onCardHold}
							onInspect={(p: Preview) => this.openInspect({ ...p, handAt: fan })}
						/>;
					}) : Array.from({ length: pileCount(me.hand) }, (_, i) =>
						<TcgCardFace key={i} back size="md" incoming={i >= pileCount(me.hand) - meHandComingN} />
					)}
				</div>
				<div class="tcg-player me dock">
					<div class="tcg-player-meta">
						<span class="tcg-player-tags">
							<span
								class="tcg-hand-count" title={`${pileCount(me.hand)} in hand`}
								aria-label={`${pileCount(me.hand)} cards in hand`}
							>{pileCount(me.hand)}</span>
						</span>
					</div>
					<span class="tcg-avatar me" aria-hidden="true">{(me.name || '?').slice(0, 1).toUpperCase()}</span>
				</div>
			</div>

			{boardChoice && !st.fx && seated ?
				this.renderBoardChoice(boardChoice) :
				hint && <div class="tcg-float-hint">{hint}</div>}

			{this.props.ended && this.props.showReplayControls !== false && <div class="tcg-replay-controls">
				<button type="button" class="button" onClick={this.props.onTogglePause}>
					<i class={`fa fa-${this.props.paused ? 'play' : 'pause'}`} aria-hidden></i> {this.props.paused ? 'Play' : 'Pause'}
				</button>
				<button type="button" class="button" onClick={this.props.onReplay}>
					<i class="fa fa-undo" aria-hidden></i> Replay
				</button>
				<button type="button" class="button" onClick={this.props.onSkip}>
					<i class="fa fa-fast-forward" aria-hidden></i> Skip to end
				</button>
			</div>}
			{snap.you != null && <div class="tcg-live-controls">
				{this.props.animating && !this.props.ended && this.props.onSkip && <button
					type="button" class="tcg-skip-fx" onClick={this.props.onSkip}
					title="Finish the animations now and show the current board"
				>
					Skip animations <i class="fa fa-forward" aria-hidden></i>
				</button>}
				{(this.state.selectedHand != null || this.state.energyPick || this.state.retreatPick ||
					this.state.menuSlot != null || this.state.moveTargetPick != null) &&
					<button type="button" class="tcg-cancel" onClick={this.clearSel}>Cancel</button>}
				{orphanActs.map((a, i) =>
					<button type="button" key={`o${i}`} class="tcg-atk quiet" onClick={() => this.choose(a)}>
						{describeAction(a, snap)}
					</button>
				)}
				{startBattle && <button type="button" class="tcg-start" onClick={() => this.choose(startBattle)}>
					Ready
				</button>}
				{endTurn && <button
					type="button" class="tcg-endturn" onClick={() => {
						const busy = allActs.some(a =>
							a.type === 'attack' || a.type === 'ability' || a.type === 'attachZone' ||
							a.type === 'playBasic' || a.type === 'playTrainer' || a.type === 'playStadium' ||
							a.type === 'evolve' || a.type === 'attachEnergy' || a.type === 'attachTool' ||
							a.type === 'retreat' || a.type === 'useStadium'
						);
						if (busy && !PS.prefs.tcgskipendturn) this.setState({ endTurnConfirm: true, menuSlot: null });
						else this.choose(endTurn);
					}}
				>
					End<br />Turn
				</button>}
			</div>}

			{this.state.menuSlot != null && (() => {
				const slot = this.state.menuSlot;
				const menu = this.menuActs(slot);
				const mon = slot === 'active' ? me.active : me.bench[Number(slot)];
				return <div class="tcg-action-menu" onClick={this.clearSel}>
					<div class={`tcg-action-sheet ${slot === 'active' ? 'active' : 'bench'}`} onClick={ev => ev.stopPropagation()}>
						<header>
							<strong>{mon?.name || 'Pokémon'}</strong>
							<button type="button" class="tcg-cancel" onClick={this.clearSel}>✕</button>
						</header>
						<div class="tcg-action-list">
							{visibleMenuActions(menu).map((a, i) => {
								const multi = isMultiTargetGroup(menu, a);
								if (a.type === 'attack') {
									const atk = a.label ? null : a.name || a.cost || a.damage ?
										{ name: a.name, cost: a.cost, damage: a.damage } :
										me.active?.attacks?.[a.index];
									const label = describeAction(a, snap, { moveOnly: multi });
									return <button
										type="button" key={i}
										class={`tcg-atk${multi ? ' needs-target' : ''}`}
										onClick={() => this.pickMenuAction(a)}
									>
										<span class="tcg-atk-cost">{(atk?.cost || []).map((t, j) => pip(t, j))}</span>
										<span class="tcg-atk-name">{label}</span>
										{atk?.damage && <span class="tcg-atk-dmg">{atk.damage}</span>}
										{multi && <span class="tcg-atk-pick">Choose target</span>}
									</button>;
								}
								if (a.type === 'ability') {
									const label = describeAction(a, snap, { moveOnly: multi });
									return <button
										type="button" key={i}
										class={`tcg-atk ability${multi ? ' needs-target' : ''}`}
										onClick={() => this.pickMenuAction(a)}
									>
										<span class="tcg-atk-kind">Ability</span>
										<span class="tcg-atk-name">{label}</span>
										{multi && <span class="tcg-atk-pick">Choose target</span>}
									</button>;
								}
								if (a.type === 'retreat') {
									const retreatRows = visibleMenuActions(menu).filter(x => x.type === 'retreat');
									const label = retreatRows.length > 1 ?
										(a.name ? `Retreat: ${a.name}` : a.text ? `Retreat: ${a.text}` : 'Retreat') :
										'Retreat';
									return <button type="button" key={i} class="tcg-atk quiet" onClick={() => this.pickMenuAction(a)}>
										{label}
									</button>;
								}
								return <button type="button" key={i} class="tcg-atk quiet" onClick={() => this.pickMenuAction(a)}>
									{describeAction(a, snap)}
								</button>;
							})}
						</div>
					</div>
				</div>;
			})()}

			{this.state.moveTargetPick && (() => {
				const pick = this.state.moveTargetPick!;
				const targets = uniqueTargetActions(pick.actions);
				const close = () => this.setState({ moveTargetPick: null });
				return <div class="tcg-prompt kind-target-pick">
					<div class="tcg-prompt-veil" onClick={close}></div>
					<div class="tcg-prompt-panel">
						<div class="tcg-prompt-banner">
							<strong>{pick.title}</strong>
							<span>Choose a target · {targets.length}</span>
							<button type="button" class="tcg-cancel" onClick={close}>✕</button>
						</div>
						<div class="tcg-target-move">
							<div class="tcg-target-move-head">
								<span class={`tcg-atk-kind ${pick.kind === 'ability' ? 'ability' : ''}`}>
									{pick.kind === 'ability' ? 'Ability' : 'Attack'}
								</span>
								<strong class="tcg-target-move-name">{pick.title}</strong>
								{(pick.cost?.length || pick.damage) &&
									<span class="tcg-target-meta">
										{!!pick.cost?.length &&
											<span class="tcg-atk-cost">{pick.cost.map((t, j) => pip(t, j))}</span>}
										{pick.damage && <span class="tcg-atk-dmg">{pick.damage}</span>}
									</span>}
							</div>
						</div>
						<div class="tcg-prompt-select">
							<div class="tcg-action-list tcg-target-list">
								{targets.map((a, i) => {
									const t = describeMoveTarget(a, snap);
									const spot = t.slot ?
										(t.foe ? `Opponent's ${t.slot}` : `Your ${t.slot}`) :
										(t.foe ? "Opponent's Pokémon" : 'Your Pokémon');
									return <button
										type="button"
										key={`${actionTargetKey(a)}-${i}`}
										class={`tcg-atk tcg-target-row${pick.kind === 'ability' ? ' ability' : ''}${t.foe ? ' foe' : ' me'}`}
										onClick={() => this.pickMoveTarget(a)}
									>
										<span class={`tcg-target-slot ${t.foe ? 'foe' : 'me'}`}>{spot}</span>
										<span class="tcg-atk-name">{t.name}</span>
									</button>;
								})}
							</div>
							<button type="button" class="tcg-btn quiet" onClick={close}>Cancel</button>
						</div>
					</div>
				</div>;
			})()}

			{(() => {
				const look = this.state.deckLook;
				const prompt = look ? deckLookPrompt(allActs, look.hand) : null;
				const blocked = !!(searches.length || discards.length || pays.length || prizeActs.length ||
					mulligans.length || confirms.length || firstPending.length);
				if (look && (!prompt || blocked)) {
					queueMicrotask(() => {
						if (this.state.deckLook) this.setState({ deckLook: null });
					});
					return null;
				}
				if (!prompt || !look || snap.you == null) return null;
				const ready = look.picked.length === prompt.need;
				const titleId = Array.isArray(me?.hand) ? me.hand[look.hand] : '';
				return <div class="tcg-prompt kind-look">
					<div class="tcg-prompt-veil"></div>
					<div class="tcg-prompt-panel">
						<div class="tcg-prompt-banner">
							<strong>{cardLabel(titleId) || 'Look at your deck'}</strong>
							<span>{prompt.note} Selected {look.picked.length} / {prompt.need}</span>
						</div>
						<div class="tcg-prompt-select">
							<div class="tcg-prompt-rail">
								{prompt.pile.map((id, index) =>
									<div
										key={`${index}-${id}`}
										class={`tcg-prompt-card${look.picked.includes(index) ? ' is-picked' : ''}`}
									>
										<TcgCardFace
											cardId={id} size="md"
											onClick={() => this.toggleDeckLook(index)}
											onInspect={this.openInspect}
										/>
										<em>{cardLabel(id)}</em>
									</div>
								)}
							</div>
							<div class="tcg-prompt-acts">
								<button type="button" class="tcg-btn quiet" onClick={() => this.setState({ deckLook: null })}>Cancel</button>
								<button type="button" class="tcg-btn primary" disabled={!ready} onClick={this.confirmDeckLook}>
									Confirm
								</button>
							</div>
						</div>
					</div>
				</div>;
			})()}

			{this.state.trainerUse && <div class="tcg-prompt kind-trainer-use">
				<div class="tcg-prompt-veil" onClick={this.onTrainerUseBackdrop}></div>
				<div class="tcg-prompt-panel">
					<div class="tcg-prompt-banner">
						<strong>{this.state.trainerUse.name}</strong>
						<span>Trainer</span>
					</div>
					<div class="tcg-prompt-dialog">
						<p>Use this card?</p>
						<div class="tcg-prompt-acts">
							<button type="button" class="tcg-btn quiet" onClick={() => this.setState({ trainerUse: null })}>Cancel</button>
							<button type="button" class="tcg-btn primary" onClick={() => this.choose(this.state.trainerUse!.action)}>Use</button>
						</div>
					</div>
				</div>
			</div>}

			{(() => {
				const searchPicks = searches.filter(a => a.type === 'searchPick');
				const searchDone = searches.find(a => a.type === 'searchDone');
				const searchState = searchPromptState(snap.pendingSearch, view.pendingSearch);
				const mulliganPlaces = mulligans.filter(a => a.type === 'mulliganBench');
				const mulliganKeep = mulligans.find(a => a.type === 'mulliganDone');
				const yes = confirms.find(a => a.type === 'confirmYes');
				const no = confirms.find(a => a.type === 'confirmNo');
				const goFirst = firsts.find(a => a.goFirst);
				const goSecond = firsts.find(a => !a.goFirst);
				const may = this.state.attackMay;
				const copies = snap.pendingCopyAttack && boardChoice?.kind !== 'attached' ?
					allActs.filter(a => a.type === 'attack' || a.type === 'ability') : [];
				const kind =
					may ? 'may' :
					this.state.endTurnConfirm && endTurn ? 'endTurn' :
					confirms.length ? 'confirm' :
					firsts.length ? 'first' :
					copies.length ? 'copy' :
					searchPicks.length || searchDone ? 'search' :
					energyDiscards.length ? 'energyDiscard' :
					pays.length ? 'pay' :
					mulligans.length ? 'mulligan' :
					prizesOffered.length ? 'prize' : '';
				if (!kind || snap.you == null) {
					if (st.promptPeek) queueMicrotask(() => this.setState({ promptPeek: false }));
					return null;
				}
				const head = kind === 'may' && may ? { title: may.title } : promptHeading(snap, kind, searchState);
				// The board's own confirmations (an optional attack effect, End Turn) close with a tap; no need to peek.
				const peekable = kind !== 'may' && kind !== 'endTurn';
				const peeking = peekable && st.promptPeek;
				// The toggle sits beside the prompt, not in it: the prompt's buttons are its choices.
				return <><div class={`tcg-prompt kind-${kind}${peeking ? ' is-peeking' : ''}`}>
					<div class="tcg-prompt-veil"></div>
					<div class="tcg-prompt-panel" aria-hidden={peeking || undefined}>
						<div class="tcg-prompt-banner">
							<strong>{head.title}</strong>
							{head.sub && <span>{head.sub}</span>}
						</div>

						{kind === 'may' && may && <div class="tcg-prompt-dialog">
							<p>{may.text}</p>
							<div class="tcg-prompt-acts">
								<button type="button" class="tcg-btn quiet" onClick={() => this.setState({ attackMay: null })}>Cancel</button>
								<button type="button" class="tcg-btn danger" onClick={() => this.choose(may.no)}>No</button>
								<button type="button" class="tcg-btn primary" onClick={() => this.choose(may.yes)}>Yes</button>
							</div>
						</div>}

						{kind === 'confirm' && <div class="tcg-prompt-dialog">
							{snap.pendingConfirm?.text && <p>{snap.pendingConfirm.text}</p>}
							<div class="tcg-prompt-acts">
								{no && <button type="button" class="tcg-btn danger" onClick={() => this.choose(no)}>No</button>}
								{yes && <button type="button" class="tcg-btn primary" onClick={() => this.choose(yes)}>Yes</button>}
							</div>
						</div>}

						{kind === 'endTurn' && endTurn && <div class="tcg-prompt-dialog">
							<p>You still have actions available. End turn anyway?</p>
							<div class="tcg-prompt-acts">
								<button type="button" class="tcg-btn quiet" onClick={() => this.setState({ endTurnConfirm: false })}>Cancel</button>
								<button type="button" class="tcg-btn primary" onClick={() => this.choose(endTurn)}>End Turn</button>
							</div>
						</div>}

						{kind === 'first' && <div class="tcg-prompt-dialog">
							<div class="tcg-prompt-acts wide">
								{goFirst && <button type="button" class="tcg-btn primary" onClick={() => this.choose(goFirst)}>Go First</button>}
								{goSecond && <button type="button" class="tcg-btn quiet" onClick={() => this.choose(goSecond)}>Go Second</button>}
							</div>
						</div>}

						{kind === 'copy' && (() => {
							// A choice of cards shows the cards (same art and printing as in the hand), like a deck search does.
							const cardRows = copies.filter(a => copyPickCardId(a));
							const plainRows = copies.filter(a => !copyPickCardId(a));
							// The engine names the card on the row; the client's own name list may not have loaded yet.
							const nameOf = (a: TcgAction) => a.label || cardLabel(copyPickCardId(a)!);
							const sameName = (a: TcgAction) => cardRows.filter(x => nameOf(x) === nameOf(a)).length > 1;
							return <div class="tcg-prompt-dialog">
								{snap.pendingCopyAttack?.text && <p>{snap.pendingCopyAttack.text}</p>}
								{cardRows.length > 0 && <div class="tcg-prompt-rail is-copy">
									{cardRows.map((a, i) => {
										const id = copyPickCardId(a)!;
										return <div key={`${a.index}-${id}-${i}`} class="tcg-prompt-card is-legal" title={`${nameOf(a)} (${id})`}>
											<TcgCardFace
												cardId={id} size="md"
												onClick={() => this.choose(a)}
												onInspect={this.openInspect}
											/>
											<em>{nameOf(a)}{sameName(a) && <span class="tcg-prompt-print">{id}</span>}</em>
										</div>;
									})}
								</div>}
								{plainRows.length > 0 && <div class="tcg-prompt-acts wide">
									{plainRows.map((a, i) =>
										<button type="button" key={`${a.type}-${a.index}-${a.pick}-${i}`} class="tcg-btn primary" onClick={() => this.choose(a)}>
											{describeAction(a, snap)}
										</button>
									)}
								</div>}
							</div>;
						})()}

						{kind === 'search' && <div class="tcg-prompt-select">
							<div class={`tcg-prompt-rail${searchState?.ids?.length && !searchState?.seen?.length ? ' is-deck' : ''}`}>
								{(() => {
									const rows = searchDeckRows(searchState, searchPicks);
									if (rows) return rows.map((row, i) => {
										const action = row.legal ? searchPicks.find(a => a.pick === row.id) : undefined;
										return <div key={`${i}-${row.id}`} class={`tcg-prompt-card${row.legal ? ' is-legal' : ' is-illegal'}`}>
											<TcgCardFace
												cardId={row.id} size="md"
												onClick={action ? () => this.choose(action) : undefined}
												onInspect={this.openInspect}
											/>
											<em>{cardLabel(row.id)}</em>
										</div>;
									});
									if (searchPicks.length) return searchPicks.map((a, i) =>
										<div key={`${a.pick}-${i}`} class="tcg-prompt-card is-legal">
											<TcgCardFace
												cardId={a.pick} size="md"
												onClick={() => this.choose(a)}
												onInspect={this.openInspect}
											/>
											<em>{cardLabel(a.pick)}</em>
										</div>
									);
									return <p class="tcg-prompt-note">No matching cards</p>;
								})()}
							</div>
							{searchDone && <button type="button" class="tcg-btn quiet" onClick={() => this.choose(searchDone)}>Done</button>}
						</div>}

						{kind === 'energyDiscard' && <div class="tcg-prompt-select">
							<p class="tcg-prompt-note">
								{`Choose ${snap.pendingDiscardEnergy?.need ?? 1} Energy. Each choice discards one card.`}
							</p>
							<div class="tcg-prompt-energy">
								{energyDiscards.map((a, i) => {
									const idx = Number(a.index);
									const cardId = snap.pendingDiscardEnergy?.cardIds?.[idx] || '';
									return <button
										type="button" key={`${idx}-${cardId}`}
										class="tcg-prompt-orb"
										title={cardId ? cardLabel(cardId) : 'Energy'}
										onClick={() => this.choose(a)}
									>
										<span class="tcg-prompt-orb-label">{cardId ? cardLabel(cardId) : 'Energy'}</span>
									</button>;
								})}
							</div>
						</div>}

						{kind === 'pay' && <div class="tcg-prompt-select">
							<p class="tcg-prompt-note">
								{this.payQueue.length || this.paySending ?
									'Discarding Energy…' :
									`Select ${snap.pendingRetreatPay?.need ?? 1} Energy attached to your Active Pokémon`}
								{!(this.payQueue.length || this.paySending) && !!this.state.payPicked.length &&
									` · Selected ${this.state.payPicked.length} / ${snap.pendingRetreatPay?.need ?? 1}`}
							</p>
							<div class="tcg-prompt-energy">
								{pays.map((a, i) => {
									const idx = Number(a.index);
									const t = me.active?.energy?.[idx] || 'colorless';
									const cardId = me.active?.energyCards?.[idx] || '';
									const picked = this.state.payPicked.indexOf(idx) >= 0;
									const need = snap.pendingRetreatPay?.need ?? 1;
									const busy = !!(this.payQueue.length || this.paySending);
									const full = !picked && this.state.payPicked.length >= need;
									return <button
										type="button" key={i}
										class={`tcg-prompt-orb${picked ? ' is-picked' : ''}${full || busy ? ' is-dim' : ''}`}
										style={{ background: TYPE_COLOR[t] || '#888' }}
										title={cardId ? `${cardLabel(cardId)} (${t})` : t}
										disabled={full || busy}
										onClick={() => this.togglePayEnergy(idx, need, pays)}
									>
										<img class="tcg-prompt-orb-icon" src={energySymbolUrl(t)} alt="" draggable={false} />
										<span class="tcg-prompt-orb-label">{cardId ? cardLabel(cardId) : t}</span>
									</button>;
								})}
							</div>
							<div class="tcg-prompt-acts">
								<button
									type="button" class="tcg-btn quiet"
									disabled={!!(this.payQueue.length || this.paySending)}
									onClick={() => this.setState({ payPicked: [] })}
								>Clear</button>
								<button
									type="button" class="tcg-btn primary"
									disabled={!!(this.payQueue.length || this.paySending) ||
										this.state.payPicked.length !== (snap.pendingRetreatPay?.need ?? 0)}
									onClick={() => this.confirmPayEnergy(pays)}
								>Confirm</button>
							</div>
						</div>}

						{kind === 'mulligan' && <div class="tcg-prompt-select">
							<div class="tcg-prompt-rail">
								{mulliganPlaces.map((a, i) =>
									<div key={i} class="tcg-prompt-card">
										<TcgCardFace
											cardId={Array.isArray(me.hand) ? me.hand[a.hand] : ''}
											size="md"
											onClick={() => this.choose(a)}
											onInspect={this.openInspect}
										/>
										<em>Bench</em>
									</div>
								)}
							</div>
							{mulliganKeep && <button type="button" class="tcg-btn primary" onClick={() => this.choose(mulliganKeep)}>Keep Hand</button>}
						</div>}

						{kind === 'prize' && <div class="tcg-prompt-dialog">
							<p>Click a face-down Prize on your prize row.</p>
							<div class="tcg-prompt-acts">
								{prizes.map((a, i) =>
									<button type="button" key={i} class="tcg-btn quiet" onClick={() => this.choose(a)}>
										Prize {Number(a.index) + 1}
									</button>
								)}
							</div>
						</div>}
					</div>
				</div>
				{peekable && <button
					type="button" class={`tcg-prompt-toggle${peeking ? ' is-peeking' : ''}`}
					onClick={() => this.setState({ promptPeek: !peeking })}
					title={peeking ? 'Show the choice again' : 'Hide this choice to look at the board; it stays open'}
				>
					{peeking ?
						<><i class="fa fa-arrow-up" aria-hidden></i> Back to {head.title}</> :
						<><i class="fa fa-eye" aria-hidden></i> View board</>}
				</button>}</>;
			})()}

			{dragging && drag && <div
				class="tcg-drag-ghost"
				style={{ left: `${drag.x}px`, top: `${drag.y}px` }}
			>
				{drag.source === 'zone' ?
					<span
						class="tcg-drag-orb"
						style={{ background: TYPE_COLOR[String(drag.cardId || 'colorless')] || '#888' }}
					>
						<img src={energySymbolUrl(String(drag.cardId || 'colorless'))} alt="" draggable={false} />
					</span> :
					(drag.cardId ?
						<img
							src={drag.image || cardArt(drag.cardId)}
							alt={drag.name || ''}
							draggable={false}
							onError={(ev: any) => {
								const img = ev?.currentTarget as HTMLImageElement | null;
								if (!img || !drag.cardId) return;
								const step = Number(img.dataset.fallback || '0');
								if (isPocketCardId(drag.cardId)) {
									if (step < 1) {
										img.dataset.fallback = '1';
										img.src = cardArt(drag.cardId, { pocket: true, large: true });
									}
									return;
								}
								if (step < 1) {
									img.dataset.fallback = '1';
									img.src = paperTcgIoArt(drag.cardId, true);
									return;
								}
								if (step < 2) {
									img.dataset.fallback = '2';
									img.src = scrydexArt(drag.cardId, true);
								}
							}}
						/> :
						<CardBackFace />)}
				{drag.hint && <em>{drag.hint}</em>}
			</div>}
			{this.state.discardBrowse && (() => {
				const side = this.state.discardBrowse;
				const pile = (side === 'me' ? me.discard : foe.discard) || [];
				const title = side === 'me' ? 'Your discard pile' : 'Opponent discard pile';
				return <div class="tcg-prompt kind-discard-browse">
					<div class="tcg-prompt-veil" onClick={() => this.setState({ discardBrowse: null })}></div>
					<div class="tcg-prompt-panel">
						<div class="tcg-prompt-banner">
							<strong>{title}</strong>
							<span>{pile.length} card{pile.length === 1 ? '' : 's'}</span>
							<button
								type="button" class="tcg-cancel"
								onClick={() => this.setState({ discardBrowse: null })}
							>✕</button>
						</div>
						<div class="tcg-prompt-select">
							<div class="tcg-prompt-rail">
								{pile.length ? [...pile].reverse().map((id, i) =>
									<div key={`${id}-${i}`} class="tcg-prompt-card">
										<TcgCardFace cardId={id} size="md" onInspect={this.openInspect} />
										<em>{cardLabel(id)}</em>
									</div>
								) : <p class="tcg-prompt-note">Empty</p>}
							</div>
							<button
								type="button" class="tcg-btn quiet"
								onClick={() => this.setState({ discardBrowse: null })}
							>Close</button>
						</div>
					</div>
				</div>;
			})()}
			{this.state.attachBrowse && (() => {
				const browse = this.state.attachBrowse!;
				const close = () => this.setState({ attachBrowse: null });
				return <div class="tcg-prompt kind-attach-browse">
					<div class="tcg-prompt-veil" onClick={close}></div>
					<div class="tcg-prompt-panel">
						<div class="tcg-prompt-banner">
							<strong>{browse.title}</strong>
							<span>{browse.cards.length} card{browse.cards.length === 1 ? '' : 's'}</span>
							<button type="button" class="tcg-cancel" onClick={close}>✕</button>
						</div>
						<div class="tcg-prompt-select">
							<div class="tcg-prompt-rail">
								{browse.cards.map((c, i) =>
									<div key={`${c.kind}-${c.cardId}-${i}`} class={`tcg-prompt-card kind-${c.kind}`}>
										<TcgCardFace cardId={c.cardId} size="md" onInspect={this.openInspect} />
										<em>
											<span class={`tcg-attach-tag kind-${c.kind}`}>{attachedKindLabel(c.kind)}</span>
											{cardLabel(c.cardId)}
										</em>
									</div>
								)}
							</div>
							<button type="button" class="tcg-btn quiet" onClick={close}>Close</button>
						</div>
					</div>
				</div>;
			})()}
			{this.state.refuse && <div
				class="tcg-refuse"
				style={{ left: `${this.state.refuse.x}px`, top: `${this.state.refuse.y}px` }}
				role="status"
			>{this.state.refuse.text}</div>}
			<FxOverlay
				key={st.fx ? `${st.fx.kind}-${st.fx.iid || ''}-${st.fx.amount || ''}-${Object.values(st.pkFx)[0]?.tick || 0}` : 'fx'}
				fx={st.fx} you={snap.you} viewpoint={meIndex}
				names={snap.players.map(p => p?.name || '')}
			/>
		</div>;
	}
}

if (typeof window !== 'undefined') {
	(window as any).TcgBoard = TcgBoard;
}
